// data.js — MumHQ constants, camp database, and location utilities

export const COLORS = {
  // ── MumHQ brand blues (from logo) ──────────────────────────────────────────
  navy:      "#0D1B3E",   // dark navy — primary brand / header
  blue:      "#1A56DB",   // rich blue — buttons, active states
  blueMid:   "#2D7DD2",   // mid blue — secondary actions
  blueLight: "#4DA3FF",   // light blue — highlights, chips
  bluePale:  "#A8D4FF",   // pale sky — backgrounds, soft tones
  blueGhost: "#E8F3FF",   // near-white blue — card backgrounds

  // ── Kept for compatibility (remapped to blues) ─────────────────────────────
  plum:      "#0D1B3E",   // → navy
  plumMid:   "#1A56DB",   // → blue
  plumLight: "#2D7DD2",   // → blueMid
  blush:     "#4DA3FF",   // → blueLight
  blushLight:"#E8F3FF",   // → blueGhost
  blushDark: "#2D7DD2",   // → blueMid
  sage:      "#2D7DD2",   // → blueMid
  sageMid:   "#4DA3FF",   // → blueLight
  sageLight: "#E8F3FF",   // → blueGhost

  cream: "#F5F8FF",   // blue-tinted page background
  ink:   "#0D1B3E",   // dark navy text
  muted: "#6B7FA3",   // blue-grey muted text
  white: "#FFFFFF", border: "#E8D8E8",
  yellow: "#F9E4A0", yellowDark: "#B8960A",
  orange: "#F5C07A", orangeLight: "#FEF3E2",
  red: "#E85D75", redLight: "#FDEAED",
  green: "#4CAF7D", greenLight: "#E8F5EE",
};



export const INTEREST_OPTIONS = [
  { id: "sports", label: "⚽ Sports", color: COLORS.sage },
  { id: "arts", label: "🎨 Arts & Crafts", color: COLORS.blush },
  { id: "music", label: "🎵 Music", color: COLORS.yellow },
  { id: "dance", label: "💃 Dance", color: COLORS.orange },
  { id: "stem", label: "🔬 STEM", color: COLORS.plumLight },
  { id: "outdoors", label: "🌿 Outdoors", color: COLORS.sageMid },
  { id: "swimming", label: "🏊 Swimming", color: "#7EC8E3" },
  { id: "drama", label: "🎭 Drama", color: "#C97BC9" },
  { id: "cooking", label: "👨‍🍳 Cooking", color: COLORS.orange },
  { id: "gaming", label: "🎮 Gaming & Coding", color: "#8B9CF5" },
];


export const CHILD_AVATAR_COLORS = [
  "#4DA3FF", "#1A56DB", "#A8D4FF", "#2D7DD2", "#0D1B3E", "#7EC8E3"
];


export const NORTH_LONDON_CAMPS = [

  // ── CANONBURY ────────────────────────────────────────────────────────────────
  {
    id: "cn-1",
    name: "William Tyndale Primary School Holiday Camp",
    provider: "William Tyndale Primary School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "William Tyndale Primary School, Upper Street, N1 2GG",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at William Tyndale Primary School on Upper Street in Islington. Sports, arts and creative activities in a well-regarded community primary steps from Canonbury and Angel.",
    times: "Check with school office for holiday dates",
    website: "https://www.williamtyndale-islington.co.uk/information-for-parents/holiday-play-scheme",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-2",
    name: "Canonbury Primary School Holiday Camp",
    provider: "Canonbury Primary School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Canonbury Primary School, N1 2NS",
    postcodeSector: "N1",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Sports and crafts holiday camp at Canonbury Primary School. Football, team games, art workshops and creative activities in the heart of Canonbury village, close to Canonbury Square and New River Walk.",
    times: "Check with school office for holiday dates",
    website: "https://canonburyprimaryschool.co.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-3",
    name: "St Paul's Steiner School Holiday Workshops",
    provider: "St Paul's Steiner School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "St Paul's Steiner School, 1 St Paul's Road, N1 2QH",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact school for pricing",
    age: "5–14",
    interests: ["arts","outdoors","music","drama"],
    description: "Arts and nature holiday workshops at St Paul's Steiner School, one of London's leading Waldorf schools. Drawing, painting, beeswax modelling, music, movement and nature crafts in the Steiner tradition. Holistic, creative approach to holiday learning connecting children with art and the natural world.",
    times: "Check school website for holiday workshop dates",
    website: "https://www.stpaulswaldorfschool.org/home",
    neighbourhoods: ["Canonbury"],
    notes: "Waldorf/Steiner pedagogy — arts, nature and creativity focused. Open to families outside the school during holidays.",
  },
  {
    id: "cn-4",
    active: false, // marked not running in Oct 2026 review
    name: "The New North Academy Holiday Camp",
    provider: "New North Academy",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "New North Academy, N1 7HQ",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity holiday camp at New North Academy in Islington. Sports, creative activities and STEM workshops using the school's modern facilities. Part of ARK Schools' network of outstanding academies.",
    times: "Check with school for holiday camp dates",
    website: "https://newnorthacademy.org.uk",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-5",
    active: false, // marked not running in Oct 2026 review
    name: "De Beauvoir Primary School Holiday Camp",
    provider: "De Beauvoir Primary School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "De Beauvoir Primary School, N1 5SQ",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Sports and art holiday camp at De Beauvoir Primary School. Football, team games, creative workshops and outdoor activities in a friendly community primary serving the De Beauvoir estate and surrounding area.",
    times: "Check with school office for holiday dates",
    website: "https://debeauvoir.hackney.sch.uk",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-6",
    name: "Hackney New School Summer Camp",
    provider: "Hackney New School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Hackney New School, N1 5LS",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["music","arts","stem"],
    description: "Music and art summer camp at Hackney New School. Creative workshops in music, visual arts and digital media for secondary-age students. Part of the Hackney new school model focused on arts integration across the curriculum.",
    times: "Check school website for summer camp dates",
    website: "https://hackneynewschool.org/our-school/summer-school/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-7",
    name: "Rosemary Works School Holiday Camp",
    provider: "Rosemary Works School",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Rosemary Works School, Branch Place, N1 5PH",
    postcodeSector: "N1",
    distanceMiles: 0.9,
    cost: "Contact school for pricing",
    age: "3–11",
    interests: ["arts","outdoors","stem","drama"],
    description: "Art and multi-activity holiday camp at Rosemary Works School, a well-regarded independent school in Hoxton. Creative arts, outdoor activities and holistic learning experiences. Innovative curriculum with strong arts and outdoor education ethos extended to holiday provision.",
    times: "Check school website for holiday camp dates",
    website: "https://rosemaryworks.co.uk/nurture/wraparound-care/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cn-8",
    name: "Central Saint Martins Youth Arts Holiday Camp",
    provider: "Central Saint Martins / UAL",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Central Saint Martins, Granary Square, King's Cross, N1C 4AA",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "Contact UAL Short Courses for pricing · some subsidised places available",
    age: "8–18",
    interests: ["arts","stem","drama","music"],
    description: "Short courses and holiday workshops in art, design, fashion, film, music and drama at one of the world's leading arts universities. UAL Short Courses run regular holiday programmes for young people at Central Saint Martins' stunning King's Cross campus. A unique opportunity to experience professional creative education.",
    times: "Check arts.ac.uk/short-courses for holiday course dates",
    website: "https://www.arts.ac.uk/study-at-ual/short-courses/courses-for-teenagers",
    neighbourhoods: ["Canonbury","Camden"],
    notes: "UAL (University of the Arts London) runs short courses for young people. Some subsidised places available. Granary Square is a destination in itself with fountains and canal.",
  },
  {
    id: "cn-9",
    name: "Literacy Pirates Holiday Writing Labs",
    provider: "Literacy Pirates (formerly Hackney Pirates)",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Literacy Pirates, Laburnum Boat Club, N1 5JU",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "FREE · charity-funded programme",
    age: "8–13",
    interests: ["arts","stem","drama"],
    description: "FREE holiday creative writing and storytelling labs run by Literacy Pirates (formerly The Hackney Pirates), an educational charity founded in 2011 by North London teacher Catriona Maclay. Children make 5–6 months' reading progress per term. Written work published in high-quality illustrated books. DBS-checked volunteer mentors give individual support. Pirates: aged 8–12 in-person. All programmes designed by professional writers and educators.",
    times: "Term-time weekly sessions · holiday writing labs — check literacypirates.co.uk",
    website: "https://literacypirates.org/programme",
    neighbourhoods: ["Canonbury","Islington & Highbury"],
    notes: "FREE for eligible children in Hackney, Islington and Tower Hamlets. Awarded London Youth Bronze Quality Mark. Children's work published in illustrated books. Strong evidence base — 5–6 months reading progress per term vs 1.3 months UK average.",
  },
  {
    id: "cn-10",
    name: "Hackney City Farm Holiday Activities",
    provider: "Hackney City Farm",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Hackney City Farm, 1a Goldsmiths Row, E2 8QA",
    postcodeSector: "E2",
    distanceMiles: 1.5,
    cost: "FREE farm entry · holiday activity classes from £10–£20 · donations welcome",
    age: "5–16",
    interests: ["outdoors","stem","arts"],
    description: "Urban farm open to the public free of charge, running holiday activities for over 20 years. Farmyard animals: pigs, sheep, goats, chickens, ducks, geese, rabbits, donkeys and guinea pigs. Hackney Fine Arts holiday classes in the Main Building Studio (ages 7–16, £10:30am–5pm, traditional drawing and painting). Children's pottery every Sunday. Frizzante Italian café on site.",
    times: "Farm: Tue–Sun 10am–4:30pm (closed Mon except bank holidays) · holiday art classes check hackneycityfarm.co.uk",
    website: "https://hackneycityfarm.co.uk/see-and-do/",
    neighbourhoods: ["Canonbury","Stoke Newington"],
    phone: "020 7729 6381 · info@hackneycityfarm.co.uk",
    notes: "FREE entry to farm. Hackney Fine Arts holiday classes run in the Main Building Studio (separate booking via hackneyfineartsholidays.classforkids.io). Pottery classes also available. Buses 26, 48, 55, 394. Overground to Cambridge Heath or Hoxton.",
  },
  {
    id: "cn-11",
    name: "Shoreditch Adventure Playground Holiday Camp",
    provider: "Shoreditch Trust / Hackney Play Association",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Shoreditch Adventure Playground, N1 5JD",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "Free or low cost · HAF-funded places available",
    age: "5–14",
    interests: ["outdoors","sports","arts"],
    description: "Open-access adventure playground running holiday outdoor play and sports sessions. Climbing structures, imaginative play spaces, arts and crafts, sports and outdoor games. Free and low-cost holiday provision supported by Hackney's Holiday Activities and Food programme. SEND-friendly.",
    times: "Holiday sessions — check with Shoreditch Trust for dates",
    website: "https://londonadventureplaygrounds.org.uk/playgrounds/shoreditch-adventure-playground/",
    neighbourhoods: ["Canonbury","Islington & Highbury"],
    notes: "Free HAF-funded places for children on benefits-related free school meals. SEND-friendly provision available.",
  },
  {
    id: "cn-12",
    name: "Britannia Leisure Centre Holiday Activities",
    provider: "Better Leisure (Britannia)",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Britannia Leisure Centre, 40 Hyde Road, N1 5JU",
    postcodeSector: "N1",
    distanceMiles: 0.9,
    cost: "From £17.55/day · Hackney residents under 16 swim FREE",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Comprehensive holiday activities at Britannia Leisure Centre in Shoreditch. 25m 6-lane pool, 20m training pool, interactive flume, toddler splash deck, 6-court sports hall and rooftop tennis courts. Holiday sports camps, swim school, soft play and Junior Gym sessions. OFSTED-registered holiday playscheme.",
    times: "Holiday programmes vary — check better.org.uk",
    website: "https://www.better.org.uk/leisure-centre/london/hackney/britannia-leisure-centre",
    neighbourhoods: ["Canonbury","Islington & Highbury","Stoke Newington"],
    notes: "Under 16 Hackney residents swim free. OFSTED registered holiday playscheme. Wave machine and flume make it a popular family day out.",
  },
  {
    id: "cn-13",
    name: "De Beauvoir Community Centre Holiday Camp",
    provider: "De Beauvoir Community Centre",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Rose Lipman Building, 43 De Beauvoir Road, N1 5SQ",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Free and low cost · HAF-funded places available",
    age: "5–14",
    interests: ["arts","sports","outdoors"],
    description: "Art and multi-activity summer carnival activities at the Rose Lipman Building in De Beauvoir. Art, dancing, cooking, sports and more, including Young Hackney HAF-funded free sessions (ages 6–14 with free food). Email to register in advance. SEND-friendly provision available.",
    times: "Summer holiday programme — check younghackney.org and debeauvoirlondon.co.uk",
    website: "https://www.younghackney.org/holiday-activities-food-programme/",
    neighbourhoods: ["Canonbury"],
    notes: "Young Hackney HAF programme runs here: free for ages 6–14 eligible for benefits-related free school meals. Free food included. Email to register in advance. SEND-friendly.",
  },
  {
    id: "cn-14",
    name: "New River Green Holiday Club",
    provider: "New River Green Children's Centre / Elizabeth House",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "New River Green Children's Centre, N1 2TX",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "£30/day Islington residents · £40/day non-Islington · £15/day low income families",
    age: "3–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Elizabeth House / New River Green Children's Centre. Sports, arts, outdoor play and themed activities. Subsidised rates for Islington families on low incomes (under £35,000 household income or Universal Credit). Extended hours 9am–5:30pm.",
    times: "9am–5:30pm",
    website: "https://tothewhistlesportscamps.classforkids.io/camp/40",
    neighbourhoods: ["Canonbury","Islington & Highbury"],
    phone: "020 7690 1300 · childrensactivities@elizabeth-house.org.uk",
    notes: "Subsidised rate £15/day or £75/week for qualifying Islington residents. N4 2 postcode (Hackney) families also eligible at resident rate.",
  },
  {
    id: "cn-15",
    name: "Union Chapel Youth Music Holiday Camp",
    provider: "Union Chapel Project",
    type: "Camp",
    neighbourhood: "Canonbury",
    location: "Union Chapel, Compton Avenue, N1 2UN",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact Union Chapel for pricing · many events free or low cost",
    age: "8–18",
    interests: ["music","drama","arts"],
    description: "Music and performing arts holiday programmes at Union Chapel, one of London's most spectacular live music venues housed in a beautiful Victorian Gothic church. Youth music workshops, choir sessions, performance opportunities and arts activities. The Union Chapel Project supports community arts with subsidised and free provision for local young people.",
    times: "Check unionchapel.org.uk for holiday programme dates",
    website: "https://unionchapel.org.uk/projects/events",
    neighbourhoods: ["Canonbury"],
    phone: "020 7226 1686 · info@unionchapel.org.uk",
    notes: "A stunning live music venue and community hub. The Union Chapel Project provides free meals and support to vulnerable people alongside arts programmes. Highbury & Islington tube station 0.4 miles.",
  },


  // ── ST JOHN'S WOOD ───────────────────────────────────────────────────────────
  {
    id: "sjw-1",
    name: "Let's Leap Holiday Camp — Arnold House School",
    provider: "Let's Leap Sports Academy",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Arnold House School, 1 Loudoun Road, NW8 0LH",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact for pricing · sibling discounts (5% for 2, 6.6% for 3, 7.5% for 4) · 5% weekly discount",
    age: "3–11",
    interests: ["sports","arts","drama","outdoors","stem"],
    description: "Award-winning multi-activity holiday camp at Arnold House School with spacious sports hall, outdoor play areas and 3G astroturf pitch. Over 50 activities including archery, go-karting, arts & crafts, multi-sports and inflatable obstacle courses. DBS-checked staff. Open 8am–6pm with flexible single-day booking.",
    times: "8am–6pm · arrival by 9:15am · collection 4:30–6pm",
    website: "https://www.letsleapsportsacademy.co.uk/camp-locations/westminster-schools/arnold-house-school",
    neighbourhoods: ["St John's Wood"],
    phone: "020 3796 3860",
    notes: "Nut-free camp. All major childcare vouchers accepted. Credit-only refund policy — cancel 8+ hours before start. 3-year-olds must be fully toilet trained.",
  },
  {
    id: "sjw-2",
    name: "Let's Leap Holiday Camp — Saint Christina's School",
    provider: "Let's Leap Sports Academy",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Saint Christina's School, NW8 7PY",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "Contact for pricing · sibling and weekly discounts available",
    age: "4–11",
    interests: ["sports","arts","drama","outdoors","stem"],
    description: "Multi-activity holiday camp at Saint Christina's School in St John's Wood. 50+ activities including archery, multi-sports, arts & crafts, inflatable obstacle courses and team games. DBS-checked staff. Flexible booking — single days available. Also hosts Mother Nature Science holiday camp on the same site.",
    times: "8am–6pm · arrival by 9:15am · collection 4:30–6pm",
    website: "https://www.letsleapsportsacademy.co.uk/camp-locations/westminster-schools/saint-christinas-school",
    neighbourhoods: ["St John's Wood"],
    phone: "020 3796 3860",
    notes: "Nut-free camp. All major childcare vouchers accepted.",
  },
  {
    id: "sjw-3",
    name: "Mother Nature Science Holiday Camp — St John's Wood",
    provider: "Mother Nature Science",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Saint Christina's School, NW8 7PY",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "£295–£445/week · contact for day rates",
    age: "5–11",
    interests: ["stem","outdoors"],
    description: "Multi-award-winning science holiday camps inspiring thousands of budding scientists. Hands-on experiments, outdoor science and take-home projects. Highly acclaimed by NW8 Mums local community. 40+ science activities per week in a world-class school venue.",
    times: "Standard 9am–3pm · extended hours available",
    website: "https://www.mnature.co.uk/venues/st-johns-wood-school-holiday-half-term-camps-for-kids-london-near-me/",
    neighbourhoods: ["St John's Wood"],
    notes: "Also runs at Vita et Pax Southgate, South Hampstead High School and St Mary's Islington. Book early — fills fast.",
  },
  {
    id: "sjw-4",
    name: "Happy Campers Club",
    provider: "Happy Campers Club",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "St John's Wood Church Hall, Lords Roundabout, NW8 7NE",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Junior (3–4) £55/session (10am–2pm) · Senior (5–12) £35/session · contact for weekly rates",
    age: "3–12",
    interests: ["arts","stem","drama","sports","music"],
    description: "Premium boutique holiday camp founded by local mum Sofia Kamal. Hugely popular in the NW8 community. DICE Method: Discover, Imagine, Create, Explore. Arts & crafts, LEGO, STEM experiments, drama, sports, cooking, skateboarding, nature walks. Small groups for personalised attention. Two programmes: Early Explorers (3–4) and Senior Campers (5–12 / Yrs 1–7). Dessert Fridays!",
    times: "Junior 10am–2pm · Senior contact for times · 4-week summer programme",
    website: "https://happycampersclub.uk/",
    neighbourhoods: ["St John's Wood"],
    phone: "07548 834 444 · @happy.campers.club on Instagram",
    notes: "Founded by NW8 local Sofia Kamal. Highly recommended by NW8 Mums community group. Small boutique groups for personalised experience.",
  },
  {
    id: "sjw-5",
    name: "B_together Holiday Camp",
    provider: "B_together",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "St John's Wood, NW8 7NJ",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact for pricing",
    age: "4–8",
    interests: ["sports","arts","cooking","outdoors"],
    description: "Community-focused holiday camp for younger children in St John's Wood. Sports, cooking, art and social activities in a small, friendly and nurturing environment. Focused on building friendship, confidence and creativity in early primary years.",
    times: "Check with provider for holiday dates",
    website: "https://www.b-together.com/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjw-6",
    name: "Petite Performers Holiday Camp",
    provider: "Petite Performers",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "St John's Wood, NW8 0JT",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "£30–£50/day depending on duration and location",
    age: "3–8",
    interests: ["dance","drama","music","arts"],
    description: "Themed performing arts holiday camps for young children. Dance, drama, singing and movement with daily new themes. Warm, nurturing environment building confidence and self-expression in young performers. All abilities and experience levels welcome.",
    times: "Various — 3.5–7 hours depending on camp · starts 9am",
    website: "https://www.thepetiteperformers.com/holiday-camps",
    neighbourhoods: ["St John's Wood"],
    notes: "Also runs camps in Notting Hill and other London locations.",
  },
  {
    id: "sjw-7",
    name: "Abercorn School Holiday Camp",
    provider: "Abercorn School",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Abercorn School, 28 Abercorn Place, NW8 9XP",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact school for pricing",
    age: "2.5–13",
    interests: ["arts","sports","stem","outdoors"],
    description: "Arts and sports holiday camp at Abercorn School, a leading independent preparatory school in St John's Wood. Creative arts, sports and academic enrichment using the school's excellent facilities. Small, nurturing environment with personalised attention.",
    times: "Check school website for holiday camp dates",
    website: "https://clubspark.lta.org.uk/AbercornSportsClub/Coaching/Camps",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjw-8",
    name: "Barrow Hill Junior School Holiday Camp",
    provider: "Barrow Hill Junior School",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Barrow Hill Junior School, Bridgeman Street, NW8 7AJ",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Barrow Hill Junior School in St John's Wood. Sports, creative activities and outdoor play in a well-regarded community school in the heart of the neighbourhood.",
    times: "Check with school office for holiday dates",
    website: "https://www.sjwps.co.uk/Friends-of-Barrow-Hill/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjw-9",
    name: "St John's Wood Library Holiday Workshops",
    provider: "Westminster Libraries (St John's Wood Library)",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood Library, 20 Circus Road, NW8 6PD",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Free or low cost",
    age: "5–14",
    interests: ["arts","stem"],
    description: "Free and low-cost children's holiday workshops at St John's Wood Library. Reading, coding, craft and creative workshops during school holidays. Part of Westminster's Summer Reading Challenge. Lego Club, story times, STEM activities and after-school clubs also run throughout the year.",
    times: "Mon–Fri 9am–8pm · Sat 9am–5pm · check for holiday workshop schedule",
    website: "https://www.westminster.gov.uk/leisure-libraries-and-community/libraries/st-johns-wood-library/events",
    neighbourhoods: ["St John's Wood"],
    notes: "Free entry. Westminster Summer Reading Challenge runs July–September annually.",
  },
  {
    id: "sjw-10",
    name: "Lord's Cricket Holiday Courses",
    provider: "Lord's Cricket Ground (MCC / Lord's Indoor Cricket Centre)",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Lord's Cricket Ground, St John's Wood Road, NW8 8QN",
    postcodeSector: "NW8",
    distanceMiles: 0.2,
    cost: "Contact Lord's for pricing · multiple holiday periods",
    age: "4–18",
    interests: ["sports","outdoors"],
    description: "Holiday cricket courses at the Home of Cricket — one of the most iconic sporting venues in the world. Lord's Performance Centre and Indoor Cricket Centre run school holiday courses every year. February Half Term (ages 6–13): skill development in batting, bowling, fielding. Summer: Junior Cubs (U6, 9am–12pm), Senior Cubs (U7, 1–4pm), Ages 9–17 development courses. New for 2025: Batting & Bowling Masterclasses for ages 13–18.",
    times: "Half-term: 9am–12pm or 1–4pm · Summer: full day and half day · check lords.org",
    website: "https://www.lords.org/lords/lordsperformancecentre/holidaycourses",
    neighbourhoods: ["St John's Wood"],
    phone: "020 7616 8500 · indoorcricketcentre@lords.org",
    notes: "February Half Term 2026: 16–19 February, ages 6–13. Book early — limited spaces. St John's Wood tube station 5 mins.",
  },
  {
    id: "sjw-11",
    name: "Seymour Leisure Centre Junior Holiday Activities",
    provider: "Better Leisure (Seymour Place)",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Seymour Leisure Centre, Seymour Place, W1H 5TJ",
    postcodeSector: "W1H",
    distanceMiles: 1.2,
    cost: "Contact centre for holiday activity pricing · Westminster resident rates available",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Junior holiday activities at Seymour Leisure Centre in Marylebone. Swimming, gymnastics, sports hall activities and fitness sessions. 25m pool, learner pool, sports hall, fitness suite and dance studio. Swim school lessons and junior gymnastics run year-round.",
    times: "Mon–Fri 6:30am–10pm · Sat–Sun 7am–9pm · check for holiday programme",
    website: "https://www.better.org.uk/leisure-centre/london/westminster/seymour-leisure-centre/no_longer_active",
    neighbourhoods: ["St John's Wood"],
    notes: "Westminster resident discounts available. Marble Arch and Edgware Road tube stations nearby.",
  },
  {
    id: "sjw-12",
    name: "American School in London — Summer Camp",
    provider: "American School in London (ASL)",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "American School in London, 1 Waverley Place, NW8 0NP",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Contact ASL for pricing · international community welcome",
    age: "4–18",
    interests: ["sports","arts","stem","drama","outdoors"],
    description: "Summer camp at the American School in London using the school's outstanding facilities — 14-acre campus, indoor and outdoor pools, gymnasium, theatres, sports fields and science labs. Multi-activity and specialist programmes in sports, arts, STEM and performing arts. World-class facilities in the heart of St John's Wood.",
    times: "Check asl.org/summer for current summer camp dates and programmes",
    website: "https://www.asl.org/extracurricular/summer-programs",
    neighbourhoods: ["St John's Wood"],
    notes: "One of the best-equipped school campuses in London. Open to non-ASL students. St John's Wood tube station 5 mins.",
  },
  {
    id: "sjw-13",
    active: false, // marked not running in Oct 2026 review
    name: "St John's Wood Church Hall Holiday Camp",
    provider: "St John's Wood Church",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "St John's Wood Church Hall, Lords Roundabout, NW8 7NE",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Contact church for pricing · subsidised rates available",
    age: "4–11",
    interests: ["arts","sports","outdoors"],
    description: "Multi-activity holiday camp run at St John's Wood Church Hall, a community hub in the heart of St John's Wood. Also hosts the popular Happy Campers Club on the same site. Arts, sports and creative activities in a welcoming community setting.",
    times: "Check with church for holiday camp dates",
    website: "https://stjohnswoodchurch.org.uk",
    neighbourhoods: ["St John's Wood"],
    notes: "Same venue as Happy Campers Club (see separate listing). Community-run with subsidised rates.",
  },
  {
    id: "sjw-14",
    name: "Regent's Park Boating & Outdoor Activity Camp",
    provider: "Regent's Park Boating Lake / The Royal Parks",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "Regent's Park Boating Lake, Regent's Park, NW1 4NR",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Rowboats from £7/30 mins · Pedalos from £5/30 mins · park entry free",
    age: "5–14",
    interests: ["outdoors","sports","stem"],
    description: "Outdoor activities and nature exploration in one of London's most beautiful royal parks. Regent's Park Boating Lake (rowboats and pedalos), Open Air Theatre, ZSL London Zoo, Queen Mary's Gardens, athletics track, tennis courts and children's playgrounds. Holiday activity providers also run sessions in the park during school holidays.",
    times: "Boating: 10am–6pm (seasonal) · Park open daily until dusk",
    website: "https://sportsandleisure.royalparks.org.uk/holiday-camps",
    neighbourhoods: ["St John's Wood","Primrose Hill & Belsize Park","Camden"],
    notes: "Regent's Park Boating Lake is very popular — arrive early in summer. Multiple holiday activity providers run sessions in the park. Open Air Theatre runs June–September.",
  },
  {
    id: "sjw-15",
    name: "SJW Youth and Community Centre Holiday Camp",
    provider: "St John's Wood Youth and Community Centre",
    type: "Camp",
    neighbourhood: "St John's Wood",
    location: "St John's Wood Youth and Community Centre, NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Subsidised rates for local young people · contact for pricing",
    age: "8–16",
    interests: ["sports","arts","outdoors","stem"],
    description: "Multi-activity holiday camp at the St John's Wood Youth and Community Centre. Sports, arts, social activities and creative workshops for older children and teenagers. Inclusive, welcoming environment serving the local St John's Wood and surrounding communities.",
    times: "Check with centre for holiday programme dates",
    website: "https://www.shulinthewood.com/children-and-youth/camp/",
    neighbourhoods: ["St John's Wood"],
  },


  // ── SWISS COTTAGE ─────────────────────────────────────────────────────────────
  {
    id: "sc-1",
    active: false, // marked not running in Oct 2026 review
    name: "George Eliot Primary School Holiday Camp",
    provider: "George Eliot Primary School",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "George Eliot Primary School, Eliot Place, NW3 4QP",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at George Eliot Primary School in Swiss Cottage. Sports, arts and creative activities in a well-regarded community primary close to Swiss Cottage tube and Primrose Hill.",
    times: "Check with school office for holiday dates",
    website: "https://georgeeliot.camden.sch.uk",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sc-2",
    name: "Swiss Cottage School — Holiday Provision",
    provider: "Swiss Cottage School (Development and Research Centre)",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage School, Avenue Road, NW3 3AQ",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Contact school directly",
    age: "5–19",
    interests: ["sports","arts","stem","outdoors"],
    description: "Outstanding special school and development centre offering SEND-specialist holiday provision. Multi-activity programme tailored to children and young people with complex learning disabilities. One of the country's leading special schools with outstanding Ofsted rating. Contact directly for holiday programme details.",
    times: "Check with school for holiday provision",
    website: "https://swisscottage.camden.sch.uk/contact",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Outstanding Ofsted-rated SEND school. Holiday provision designed specifically for pupils with complex needs. Contact school directly for eligibility and booking.",
  },
  {
    id: "sc-3",
    name: "UCL Academy Summer Camp",
    provider: "UCL Academy",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "UCL Academy, Adelaide Road, NW3 3AQ",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["stem","sports","arts"],
    description: "STEM, sports and multi-activity summer camp at UCL Academy, a state-of-the-art secondary school with university-level resources. Science labs, digital facilities and sports halls. Partnered with University College London bringing outstanding academic resources to young people.",
    times: "Check school website for summer camp dates",
    website: "https://uclacademy.co.uk/",
    neighbourhoods: ["Swiss Cottage"],
    notes: "UCL Academy is a University Technical College partnership — exceptional STEM facilities for older children.",
  },
  {
    id: "sc-4",
    name: "Hampstead Fine Arts College Summer Camp",
    provider: "Hampstead Fine Arts College",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Hampstead Fine Arts College, Lambolle Road, NW3 4HS",
    postcodeSector: "NW3",
    distanceMiles: 0.7,
    cost: "Contact college for pricing",
    age: "11–18",
    interests: ["arts","stem","drama"],
    description: "Specialist arts summer camp at Hampstead Fine Arts College, North London's leading independent arts sixth form. Fine art, photography, film, illustration, graphic design and fashion. Professional studio environment. Excellent preparation for art A-levels, foundation courses and creative university applications.",
    times: "Check hfac.co.uk for summer course dates",
    website: "https://www.hampsteadfinearts.com/about/news-events/",
    neighbourhoods: ["Swiss Cottage","Hampstead"],
    notes: "Primarily for ages 14+ but some summer programmes open to younger students. Excellent preparation for GCSE and A-level art.",
  },
  {
    id: "sc-5",
    name: "Hampstead Cricket Club Junior Holiday Camp",
    provider: "Hampstead Cricket Club",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Lymington Road, West Hampstead, NW6 1HY",
    postcodeSector: "NW3",
    distanceMiles: 0.8,
    cost: "Contact club for junior camp pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Junior cricket holiday camps at one of London's oldest cricket clubs (Est. 1866). ECB-accredited coaching for all ages and abilities. Beautiful ground in West Hampstead. Junior sections from U7 upwards. Holiday camps run during summer with girls' cricket also strongly supported.",
    times: "Check club website for holiday camp dates",
    website: "https://cltc-hcc.com/cricket-2/",
    neighbourhoods: ["Swiss Cottage","Hampstead"],
    notes: "One of London's most historic cricket clubs. Also runs All Stars Cricket (5–8) and Dynamos Cricket (8–11) ECB programmes.",
  },
  {
    id: "sc-6",
    name: "Joy Mandarin Academy Summer Camp",
    provider: "Joy Mandarin Academy",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Joy Mandarin Academy, Swiss Cottage NW3 3NR",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact academy for pricing",
    age: "4–14",
    interests: ["arts","stem"],
    description: "Mandarin Chinese language immersion summer camp in Swiss Cottage. Learn Mandarin through games, songs, stories, arts and crafts, cooking and cultural activities. All levels welcome from complete beginners. Small groups for individual attention. Native-speaking teachers.",
    times: "Check for summer camp dates",
    website: "https://www.joymandarinacademy.com/holiday-course-1",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Also runs term-time Mandarin classes. Unique language learning opportunity for North London families.",
  },
  {
    id: "sc-7",
    name: "Swiss Cottage Community Centre Holiday Camp",
    provider: "Swiss Cottage Community Centre",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage Community Centre, NW3 3NG",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Subsidised rates for local families · contact centre for pricing",
    age: "5–14",
    interests: ["arts","sports","outdoors"],
    description: "Art and multi-activity holiday camp at Swiss Cottage Community Centre. Creative workshops, sports and social activities in a welcoming community hub. Inclusive and accessible with subsidised rates for Camden families.",
    times: "Check with centre for holiday dates",
    website: "https://www.better.org.uk/leisure-centre/london/camden/swiss-cottage-leisure-centre/holiday-activities",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Community-run. Subsidised rates available for Camden residents.",
  },
  {
    id: "sc-8",
    name: "Freud Museum Family Holiday Workshops",
    provider: "Freud Museum London",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Freud Museum London, 20 Maresfield Gardens, NW3 5SX",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact museum for workshop pricing · standard adult admission £15, child £8",
    age: "5–14",
    interests: ["arts","drama","stem"],
    description: "Unique holiday storytelling and craft workshops at Sigmund Freud's former family home. Children explore the fascinating house (left exactly as the Freud family arranged it in 1938) through age-appropriate storytelling, creative crafts and imaginative activities. Annual archaeology trail for children during summer. Original Freud collection, antiquities, library and famous couch on display.",
    times: "Wed–Sun 12pm–5pm · holiday workshops check freud.org.uk",
    website: "https://www.freud.org.uk/events/category/families/",
    neighbourhoods: ["Swiss Cottage","Hampstead"],
    phone: "020 7435 2002 · info@freud.org.uk",
    notes: "Annual summer holiday archaeology trail for children (no booking required). Grade II listed Victorian house. Steps to entrance — contact ahead for access needs. Finchley Road tube station 0.4 miles.",
  },
  {
    id: "sc-9",
    name: "Rising Stars at JW3 Holiday Camp",
    provider: "Rising Stars Activities at JW3",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "JW3, 341–351 Finchley Road, NW3 6ET",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact Rising Stars / JW3 for pricing",
    age: "4–12",
    interests: ["sports","arts","drama","outdoors"],
    description: "OFSTED-registered holiday sports camp in JW3's purpose-built, award-winning, air-conditioned building on Finchley Road. Dodgeball, unihoc, basketball, football, team games, dance, crafts and creative challenges. Professional secure entry system for complete peace of mind. No two days ever the same. JW3 is a vibrant Jewish cultural centre — camp open to all.",
    times: "Contact for current holiday dates",
    website: "https://bookings.risingstarsactivities.com/",
    neighbourhoods: ["Swiss Cottage"],
    phone: "jw3.org.uk · 020 7433 8988",
    notes: "Spaces very limited — book early. Located directly on Finchley Road, convenient for West Hampstead, Swiss Cottage and Belsize Park families. Finchley Road tube (Jubilee and Metropolitan lines) on the doorstep.",
  },
  {
    id: "sc-10",
    name: "Virgin Active Swiss Cottage — Junior Holiday Activities",
    provider: "Virgin Active",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Virgin Active Swiss Cottage, NW3 6LU",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Member rates · day pass from £30 · child swim from £13",
    age: "4–16",
    interests: ["swimming","sports"],
    description: "Premium health club with junior swimming and holiday activities. 25m pool, tennis, gym, group exercise and junior fitness programmes. Swim Academy lessons for all ages and abilities. Holiday swim intensives and junior activity sessions. State-of-the-art facilities near Finchley Road tube.",
    times: "Mon–Fri 6:30am–10pm · Sat–Sun 7am–9pm · check website for holiday programme",
    website: "https://www.virginactive.co.uk/kids/holiday-camps",
    neighbourhoods: ["Swiss Cottage","Primrose Hill & Belsize Park"],
    notes: "Day passes available for non-members. Junior swim academy runs year-round with holiday intensives. Finchley Road tube station adjacent.",
  },
  {
    id: "sc-11",
    name: "Camden Shotokan Karate Summer Camp",
    provider: "Camden Shotokan Karate Club",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage / Camden NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact club for camp pricing",
    age: "6–16",
    interests: ["sports"],
    description: "Shotokan karate holiday camp and training for children and young people in Swiss Cottage. Structured martial arts training developing discipline, self-confidence, fitness and focus. All abilities welcome from complete beginners. Grading opportunities for those progressing through belts.",
    times: "Summer camp dates — check with club directly",
    website: "https://www.dojovanrel.com/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sc-12",
    name: "English National Ballet Youth Holiday Workshops",
    provider: "English National Ballet",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "English National Ballet School / London Studio Centre, NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact ENB for workshop pricing",
    age: "8–18",
    interests: ["dance","drama","music"],
    description: "Holiday dance workshops and intensive courses run by English National Ballet. Classical ballet, contemporary dance and performance arts. Professional training environment with ENB's world-class faculty. Holiday workshops open to all levels from recreational to pre-professional.",
    times: "Check ballet.org.uk for holiday workshop dates",
    website: "https://www.ballet.org.uk/whats-on-classes/",
    neighbourhoods: ["Swiss Cottage"],
    notes: "ENB also runs the Youth Company (14–19) for pre-professional training. Check website for holiday workshop availability and audition requirements.",
  },
  {
    id: "sc-13",
    name: "South Hampstead Synagogue Children's Holiday Camp",
    provider: "South Hampstead Synagogue",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "South Hampstead Synagogue, Netherhall Gardens, NW3 5RZ",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact synagogue for pricing · member discounts available",
    age: "5–14",
    interests: ["arts","sports","outdoors"],
    description: "Arts and multi-activity holiday camp run by South Hampstead Synagogue. Creative workshops, sports and social activities with Jewish cultural elements in a welcoming community setting. Open to local families of all backgrounds.",
    times: "Check with synagogue for holiday camp dates",
    website: "https://www.southhampstead.org/children-youth/",
    neighbourhoods: ["Swiss Cottage","Hampstead"],
    notes: "Welcoming to all faiths during holiday camps — confirm availability with synagogue.",
  },
  {
    id: "sc-14",
    name: "O2 Centre Family Holiday Activities",
    provider: "Various providers at O2 Centre",
    type: "Camp",
    neighbourhood: "Swiss Cottage",
    location: "O2 Centre, 255 Finchley Road, NW3 6LU",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact individual providers for pricing",
    age: "5–14",
    interests: ["stem","arts","sports"],
    description: "Family holiday activities and STEM and crafts workshops run by various providers at and around the O2 Centre on Finchley Road. Coding, robotics, creative arts and multi-activity workshops. Excellent transport links — Finchley Road tube (Jubilee and Metropolitan lines) adjacent. Also home to Vue cinema, restaurants and gym.",
    times: "Check individual providers for holiday dates",
    website: "https://www.o2centre.co.uk/en/articles-listing-page/family-activities-in-hampstead",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sc-15",
    name: "Swiss Cottage Library Family Holiday Workshops",
    provider: "Camden Libraries (Swiss Cottage Library)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage Library, 88 Avenue Road, NW3 3HA",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Free or low cost · Camden Council",
    age: "5–14",
    interests: ["arts","stem"],
    description: "Free and low-cost children's holiday workshops at Swiss Cottage Library — one of Camden's busiest and best-equipped public libraries. Reading, coding, craft and creative workshops during school holidays. Part of Camden's Summer Reading Challenge programme. Also runs Lego Club, story times and STEM workshops throughout the year.",
    times: "Mon–Thu 9am–8pm · Fri 9am–6pm · Sat 9am–5pm · Sun 11am–5pm",
    website: "https://www.camden.gov.uk/swiss-cottage-library",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Free entry. Summer Reading Challenge runs annually July–September. Also hosts the Camden Music Service and regular school holiday activities. Swiss Cottage tube station adjacent.",
  },


  // ── KENTISH TOWN ─────────────────────────────────────────────────────────────
  {
    id: "kt-1",
    name: "Kentish Town CE Primary School Holiday Camp",
    provider: "Kentish Town CE Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Kentish Town CE Primary School, NW5 2TJ",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Kentish Town CE Primary School. Sports, creative arts and outdoor activities in a welcoming faith school community close to Kentish Town Road and Parliament Hill.",
    times: "Check with school office for holiday dates",
    website: "https://www.kentishtown.camden.sch.uk/event/summer-club/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-2",
    name: "Torriano Primary School Holiday Camp",
    provider: "Torriano Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Torriano Primary School, Torriano Avenue, NW5 2SJ",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Sports and art holiday camp at Torriano Primary School in Kentish Town. Football, team games, arts and creative activities in a popular community primary with good outdoor facilities.",
    times: "Check with school office for holiday dates",
    website: "https://www.torriano.camden.sch.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-3",
    name: "Brecknock Primary School Holiday Camp",
    provider: "Brecknock Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Brecknock Primary School, Brecknock Road, N7 0ND",
    postcodeSector: "N7",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Brecknock Primary School on the Kentish Town / Holloway border. Sports, creative activities and outdoor play in a well-regarded community primary.",
    times: "Check with school office for holiday dates",
    website: "https://www.brecknock.camden.sch.uk/contact-us/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-4",
    name: "Eleanor Palmer Primary School Holiday Camp",
    provider: "Eleanor Palmer Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Eleanor Palmer Primary School, NW5 1HU",
    postcodeSector: "NW5",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["arts","sports","outdoors"],
    description: "Art and sports holiday camp at Eleanor Palmer Primary School in Kentish Town. Creative workshops, painting, drawing, sports and outdoor activities in a popular community primary school.",
    times: "Check with school office for holiday dates",
    website: "https://pro-revo-sports-development.classforkids.io/camp/18",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-5",
    name: "Acland Burghley School Summer Camp",
    provider: "Acland Burghley School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Acland Burghley School, Burghley Road, NW5 1UJ",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","drama","arts","stem"],
    description: "Sports and drama summer camp at Acland Burghley School, a highly regarded Camden comprehensive. Uses the school's sports hall, theatre and arts facilities. Particularly suitable for students transitioning to secondary school.",
    times: "Check school website for summer camp dates",
    website: "https://www.aclandburghley.camden.sch.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-6",
    name: "Parliament Hill School Summer Camp",
    provider: "Parliament Hill School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Parliament Hill School, Highgate Road, NW5 1RL",
    postcodeSector: "NW5",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","arts","stem"],
    description: "Sports and arts summer camp at Parliament Hill School on Highgate Road, steps from the Heath. An outstanding girls' school with superb facilities including sports pitches, arts studios and science labs. Perfect location near Parliament Hill and Hampstead Heath.",
    times: "Check school website for summer camp dates",
    website: "https://www.parliamenthill.camden.sch.uk/Contact-Us/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-7",
    name: "Gospel Oak Sports Centre Holiday Camp",
    provider: "Gospel Oak Sports Centre / Camden Council",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Gospel Oak Sports Centre, Lismore Circus, NW5 4QH",
    postcodeSector: "NW5",
    distanceMiles: 0.8,
    cost: "Contact centre for pricing · Camden resident discounts available",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Multi-sports holiday camp at Gospel Oak Sports Centre. Football, basketball, athletics and outdoor team games using the centre's excellent facilities near Gospel Oak Overground station and Parliament Hill athletics track.",
    times: "Check camden.gov.uk for holiday sports programme",
    website: "https://holicamp.net/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-8",
    name: "Kentish Town Sports Centre Holiday Activities",
    provider: "Better Leisure / Camden Council",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Kentish Town Sports Centre, Grafton Road, NW5 3DU",
    postcodeSector: "NW5",
    distanceMiles: 0.3,
    cost: "Contact centre for pricing · Camden resident and concession rates available",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Holiday swimming and sports activities at Kentish Town Sports Centre. Swimming pool, sports hall, fitness facilities and outdoor spaces. Swim school, junior gym and multi-sports holiday activities for all ages and abilities.",
    times: "Check better.org.uk for holiday activity programme",
    website: "https://www.better.org.uk/leisure-centre/london/camden/kentish-town-sports-centre/activities",
    neighbourhoods: ["Kentish Town"],
    notes: "Camden resident rates available — ask at reception for Camden Better membership details. Also runs Girls Get Moving sessions (ages 8–14) at Talacre.",
  },
  {
    id: "kt-9",
    name: "Talacre Community Sports Centre Holiday Camp",
    provider: "Better Leisure / Camden Council",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Talacre Community Sports Centre, Dalby Street, NW5 3AF",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "30 free places for children on free school meals · Camden resident discounts available · contact for standard pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Fun-filled multi-sports holiday camp run by Camden Council at Talacre. Table tennis, badminton, pickleball, mini tennis, trampoline, gymnastics, football, basketball and outdoor games on the floodlit 3G astro pitch. All abilities welcome. 30 free places for children on free school meals (apply via Holiday Activity Fund). Girls Get Moving sessions also available (ages 8–14). ENTRANCE VIA TALACRE PARK — not Dalby Street.",
    times: "Check camden.gov.uk/during-the-school-holidays for dates",
    website: "https://www.better.org.uk/leisure-centre/london/camden/talacre-community-sports-centre",
    neighbourhoods: ["Kentish Town","Camden", "Kentish Town"],
    phone: "020 3343 6020 · talacre@gll.org",
    notes: "Entrance via Talacre Park, not Dalby Street. 30 free HAF places per holiday for FSM-eligible children — apply via camden.gov.uk. Lockers available (£1 coin, refundable). Wheelchair accessible, sensory dance studio on site.",
  },
  {
    id: "kt-10",
    name: "Kentish Town City Farm — Holiday Hub",
    provider: "Kentish Town City Farm",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Kentish Town City Farm, 1 Cressfield Close, NW5 4BN",
    postcodeSector: "NW5",
    distanceMiles: 0.6,
    cost: "Contact KTCF for pricing · booking required",
    age: "8–16",
    interests: ["outdoors","stem"],
    description: "Popular Holiday Hub at Kentish Town City Farm since 1972. Young people make new friends and have fun while learning all about life on a city farm. Animals include sheep, goats, horses, donkeys, pigs, cows, ducks and chickens. 4.5-acre site with community gardens, wildlife pond and riding arena. Forest school sessions also available (Urban Outdoors London runs Easter Forest School here). Free entry to the farm daily 9am–5pm.",
    times: "Holiday Hub: check ktcityfarm.org.uk/holidayhub for summer dates",
    website: "https://ktcityfarm.org.uk/holidayhub/",
    neighbourhoods: ["Kentish Town"],
    notes: "Farm open free daily 9am–5pm. Free parking on Grafton Road after 11am Mon–Fri and all day weekends. Nearest stations: Gospel Oak Overground, Kentish Town (Northern Line), Kentish Town West. Buses 24, 46, 134, 214, C2.",
  },
  {
    id: "kt-11",
    name: "Lauderdale House Arts Holiday Workshops",
    provider: "Lauderdale House",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Lauderdale House, Highgate Hill, N6 5HG",
    postcodeSector: "N6",
    distanceMiles: 1.2,
    cost: "Contact Lauderdale House for pricing · 020 8348 8716",
    age: "5–16",
    interests: ["arts","music","drama"],
    description: "Historic arts centre encouraging creativity in children with holiday painting classes, music sessions, drama workshops and crafting workshops led by professional artists. Situated in beautiful Waterlow Park, Highgate. Also runs term-time after-school arts clubs and music and drama sessions throughout the year.",
    times: "Holiday workshops during school breaks · check lauderdalehouse.co.uk",
    website: "https://www.lauderdalehouse.org.uk/hire-house/classes-workshops",
    neighbourhoods: ["Kentish Town","Highgate"],
    phone: "020 8348 8716",
    notes: "Set in the beautiful surroundings of Waterlow Park. Step-free access available. Café on site.",
  },
  {
    id: "kt-12",
    name: "O2 Centre Youth Holiday Activities",
    provider: "Various providers at O2 Centre",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "O2 Centre, 255 Finchley Road, NW3 6LU",
    postcodeSector: "NW3",
    distanceMiles: 1.5,
    cost: "Contact individual providers for pricing",
    age: "5–14",
    interests: ["stem","arts","sports"],
    description: "Various STEM and arts holiday activities run by providers at and near the O2 Centre on Finchley Road. Coding, robotics, creative arts and multi-activity workshops. Well-connected location with Finchley Road tube station (Jubilee and Metropolitan lines) steps away.",
    times: "Check individual providers for holiday dates",
    website: "https://www.o2centre.co.uk/en/articles-listing-page/family-activities-in-hampstead",
    neighbourhoods: ["Kentish Town","Primrose Hill & Belsize Park"],
    notes: "Multiple providers operate in this area. Also hosts Hereward House School (holiday camps listed separately).",
  },
  {
    id: "kt-13",
    name: "Kentish Town Community Centre Holiday Club",
    provider: "Kentish Town Community Centre",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Kentish Town Community Centre, Grafton Road, NW5 3DU",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Subsidised rates for local families · contact for pricing",
    age: "5–14",
    interests: ["arts","sports","outdoors"],
    description: "Community arts and activity holiday club at Kentish Town Community Centre. Creative workshops, sports and social activities for local children. Welcoming and inclusive atmosphere with subsidised rates for Camden families.",
    times: "Check with centre for holiday dates",
    website: "https://www.ktcc.org.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "kt-14",
    name: "Fleet Primary School Holiday Camp",
    provider: "Fleet Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Fleet Primary School, Agincourt Road, NW3 2QT",
    postcodeSector: "NW3",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Fleet Primary School in Belsize Park / Gospel Oak. Sports, creative arts and outdoor play in a well-regarded community primary steps from Parliament Hill athletics track and Hampstead Heath.",
    times: "Check with school office for holiday dates",
    website: "https://3acresplay.co.uk/",
    neighbourhoods: ["Kentish Town","Primrose Hill & Belsize Park"],
  },
  {
    id: "kt-15",
    name: "Rhyl Community Primary School Holiday Camp",
    provider: "Rhyl Community Primary School",
    type: "Camp",
    neighbourhood: "Kentish Town",
    location: "Rhyl Community Primary School, Rhyl Street, NW5 3HB",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Rhyl Community Primary School in Kentish Town. Sports, arts and creative activities in a friendly community primary close to Kentish Town tube and Overground stations.",
    times: "Check with school office for holiday dates",
    website: "https://www.rhylprim.camden.sch.uk/",
    neighbourhoods: ["Kentish Town"],
  },


  // ── CAMDEN ───────────────────────────────────────────────────────────────────
  {
    id: "cam-1",
    name: "Holiday Academy Camden",
    provider: "Holiday Academy",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Cavendish School, Camden, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "From £25/day · open 8:30am–6pm · childcare vouchers accepted",
    age: "3–12",
    interests: ["sports","arts","drama","outdoors","music"],
    description: "Outstanding varied day-camp programme in Camden NW1 every school holiday. Music, art, drama, sport with extra language, football, gymnastics clubs, individual tennis coaching and Friday Forest school. Flexible booking — single days available. All staff DBS checked. Currently based at Cavendish School.",
    times: "8:30am–6pm",
    website: "https://holidayacademy.co.uk/",
    neighbourhoods: ["Camden"],
    notes: "Highly rated by local parents. Flexible booking — no full week requirement. Childcare vouchers and tax-free childcare accepted.",
  },
  {
    id: "cam-2",
    name: "Argyle Primary School Holiday Camp",
    provider: "Argyle Primary School",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Argyle Primary School, Tonbridge Street, WC1H 9EL",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Argyle Primary School near King's Cross. Sports, arts and creative activities in a well-regarded community primary in the heart of Camden, steps from King's Cross St Pancras.",
    times: "Check with school office for holiday dates",
    website: "https://coramsfields.org/out-of-school-club-osc/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "cam-3",
    name: "Netley Primary School Holiday Camp",
    provider: "Netley Primary School",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Netley Primary School, NW1 8HE",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Sports and multi-activity holiday camp at Netley Primary School in Camden. Football, team games, arts and outdoor activities in a friendly community school.",
    times: "Check with school office for holiday dates",
    website: "https://www.netley.camden.sch.uk/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "cam-4",
    name: "Christ Church Primary School Holiday Camp",
    provider: "Christ Church CE Primary School",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Christ Church CE Primary School, NW1 1LT",
    postcodeSector: "NW1",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Christ Church CE Primary in Camden. Sports, creative activities and outdoor play in a welcoming faith school community setting.",
    times: "Check with school office for holiday dates",
    website: "https://www.cchurchnw1.camden.sch.uk/page/?title=Contact&pid=11",
    neighbourhoods: ["Camden"],
  },
  {
    id: "cam-5",
    name: "Camden School for Girls Summer Camp",
    provider: "Camden School for Girls",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Camden School for Girls, Sandall Road, NW5 2DB",
    postcodeSector: "NW5",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","stem","arts","outdoors"],
    description: "Summer multi-activity camp at Camden School for Girls, one of North London's highest-achieving state schools. Uses the school's sports hall, courts and excellent facilities. Science, arts and sports activities for secondary-age students.",
    times: "Check school website for summer camp dates",
    website: "https://www.camdengirls.camden.sch.uk/page/?title=Contact+Us&pid=147",
    neighbourhoods: ["Camden"],
  },
  {
    id: "cam-6",
    name: "Regent High School Summer Camp",
    provider: "Regent High School",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Regent High School, Chalton Street, NW1 1RX",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","stem","outdoors"],
    description: "Sports and STEM summer camp at Regent High School in Camden. Football, basketball and team sports alongside science and technology activities. Well-equipped school near Euston and King's Cross.",
    times: "Check school website for summer camp dates",
    website: "https://www.regenthighschool.org.uk/Summer-Activities/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "cam-7",
    name: "Camden Arts Centre Holiday Workshops",
    provider: "Camden Arts Centre",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden Arts Centre, Arkwright Road, NW3 6DG",
    postcodeSector: "NW3",
    distanceMiles: 1.5,
    cost: "Contact CAC for pricing · 020 7472 5500 · many events free",
    age: "5–16",
    interests: ["arts"],
    description: "Holiday art courses at Camden Arts Centre, a free contemporary art gallery in a Grade II listed Victorian building (former Hampstead Central Library, 1897). Short energetic courses by a team of artists using paint, clay, digital media and large-scale constructions. Half-term, Easter and summer courses for ages 5–11. Changing exhibitions free to visit. Tranquil garden and café on site.",
    times: "Wed–Sun 11am–6pm · holiday courses check camdenartcentre.org",
    website: "https://camdenartcentre.org/come-visit-us",
    neighbourhoods: ["Camden","Primrose Hill & Belsize Park", "Camden"],
    phone: "020 7472 5500",
    notes: "Free admission to gallery. Finchley Road & Frognal station 0.1mi. Step-free access. Garden café open during gallery hours.",
  },
  {
    id: "cam-8",
    name: "The Place — Contemporary Dance Holiday Courses",
    provider: "The Place",
    type: "Camp",
    neighbourhood: "Camden",
    location: "The Place, 17 Duke's Road, WC1H 9PY",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact The Place for pricing",
    age: "4–18",
    interests: ["dance","drama","music","arts"],
    description: "London's most important contemporary dance venue and training centre. Half-term and school holiday contemporary dance courses for ages 4–18. Industry-leading dance classes in a professional performance environment near Euston. All levels from beginners to advanced. Children's classes in ballet, street dance, contemporary and creative movement.",
    times: "Check theplace.org.uk for holiday course dates",
    website: "https://theplace.org.uk/study/",
    neighbourhoods: ["Camden"],
    notes: "Near Euston and King's Cross stations. World-renowned training centre — Robin Howard Dance Theatre on site. Camden Partnership Schools Programme also runs in term time.",
  },
  {
    id: "cam-9",
    name: "Roundhouse Young Creatives Holiday Programmes",
    provider: "Roundhouse",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Roundhouse, Chalk Farm Road, NW1 8EH",
    postcodeSector: "NW1",
    distanceMiles: 1.0,
    cost: "Many programmes FREE or subsidised · Creative Studios Membership from £5/month (ages 13–25)",
    age: "11–25",
    interests: ["music","drama","arts","stem"],
    description: "Year-round creative programmes for young people at the iconic Camden Roundhouse. Music production, recording, media, performing arts (poetry, dance, theatre, circus) and professional development. State-of-the-art Roundhouse Studios. Holiday workshops and intensives in music and arts. Free and subsidised places for young people from disadvantaged backgrounds.",
    times: "Studios open to members · holiday programmes check roundhouse.org.uk",
    website: "https://www.roundhouse.org.uk/young-creatives-11-30/youth-programme/",
    neighbourhoods: ["Camden"],
    phone: "020 7424 9991",
    notes: "Creative Studios Membership (13–25) gives access to recording studios, rehearsal spaces and production suites. Free and subsidised places available. Chalk Farm tube station 2 mins. Also runs annual Roundhouse Rising Festival showcasing youth work.",
  },
  {
    id: "cam-10",
    name: "ZSL London Zoo — Holiday Experiences",
    provider: "ZSL London Zoo",
    type: "Club",
    neighbourhood: "Camden",
    location: "ZSL London Zoo, Regent's Park, NW1 4RY",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Child (3–15) from £25.90 · Universal Credit tickets at reduced price · FREE for ZSL members",
    age: "5–14",
    interests: ["stem","outdoors"],
    description: "World's oldest scientific zoo (Est. 1826). 650+ animal species including Asiatic lions, gorillas, giraffes, penguins and tigers. Holiday animal experiences, keeper talks, feeding sessions and ZooTown role-play adventure (£1/child extra, must book). Junior Keeper Experience days available. Education programmes for school-age children.",
    times: "Open daily except Christmas Day · 10am–4/5/6pm (seasonal) · last entry 1hr before close",
    website: "https://www.londonzoo.org/plan-your-visit/events/vets-action-london-zoo",
    neighbourhoods: ["Camden","Primrose Hill & Belsize Park"],
    phone: "020 7722 3333",
    notes: "Universal Credit tickets available at reduced price (proof required, book online). ZooTown (£1/child) must be booked up to 3 days in advance. Accessible via bus 274 or Camden Town/Chalk Farm tube. Overnight Zoo Lodge stays also available from £155/person.",
  },
  {
    id: "cam-11",
    name: "Camden HAF Sports Holiday Camp",
    provider: "Camden Council / Active Camden",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Various venues across Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Free for Camden children eligible for benefits-related FSM · £85/week standard · Camden subsidised £65 · concession £40",
    age: "5–16",
    interests: ["sports","outdoors","arts"],
    description: "Holiday Activities and Food (HAF) programme run by Camden Council. Sports, physical activity and hot meals during school holidays for eligible children. Multiple venues across Camden including leisure centres, schools and community spaces. Free for children on benefits-related free school meals.",
    times: "Check camden.gov.uk for holiday programme locations and dates",
    website: "https://families.camden.gov.uk/parents-and-carers/haf/",
    neighbourhoods: ["Camden"],
    notes: "Free for FSM-eligible children. Camden subsidised rate also available. Check Camden's holiday programme page for current year activities and venues.",
  },
  {
    id: "cam-12",
    name: "The Winch Holiday Play Scheme",
    provider: "The Winch Community Organisation",
    type: "Camp",
    neighbourhood: "Camden",
    location: "The Winch, Plender Street, NW1 0JB",
    postcodeSector: "NW1",
    distanceMiles: 0.4,
    cost: "Standard £85/week · Camden subsidised £65 · concession £40",
    age: "4–11",
    interests: ["arts","sports","outdoors"],
    description: "Well-loved community holiday play scheme in Camden Town. Trips, crafts, sports and outdoor activities. Healthy packed lunch and snacks provided. Drop-off from Abacus Primary School and George Eliot Primary School. Strict no-nut policy. Part of Camden's wider holiday programme network.",
    times: "Full day holiday play scheme — check The Winch for current dates",
    website: "https://thewinch.org/",
    neighbourhoods: ["Camden"],
    notes: "Camden subsidised and concession rates available. A welcome pack issued to all new families. Trips are included — additional costs may apply for some outings.",
  },
  {
    id: "cam-13",
    name: "Cecil Sharp House Youth Folk Arts Courses",
    provider: "English Folk Dance and Song Society (EFDSS)",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Cecil Sharp House, 2 Regent's Park Road, NW1 7AY",
    postcodeSector: "NW1",
    distanceMiles: 0.7,
    cost: "Contact EFDSS for pricing · FREE Folk Unlimited course for learning disabled young people",
    age: "5–18",
    interests: ["music","dance","arts"],
    description: "England's dedicated folk arts centre. Youth ensembles, youth ceilidhs, holiday courses in folk music and dance. The FREE Folk Unlimited summer course (20–23 August 2025) is a creative multi-sensory folk music and dance course for learning disabled young people aged 7–25. Family barn dances and workshops throughout the year. Grade II listed building, step-free access throughout.",
    times: "Mon–Fri 9:30am–5pm · holiday courses check cecilsharphouse.org",
    website: "https://www.efdss.org/whats-on?subcat=28",
    neighbourhoods: ["Camden","Primrose Hill & Belsize Park"],
    phone: "020 7485 2206 · mail@efdss.org",
    notes: "Folk Unlimited: FREE for ages 7–13 (10:30am–12pm) and 14–25 (2–3:30pm) — Aug 2025. 5–10 mins walk from Regent's Park, ZSL London Zoo, Primrose Hill and Camden Market.",
  },
  {
    id: "cam-14",
    name: "Camden Music Service Holiday Camp",
    provider: "Camden Music Service",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Camden Music venues across NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact Camden Music Service for pricing · subsidised places may be available",
    age: "7–18",
    interests: ["music","arts","drama"],
    description: "Holiday music workshops and intensives run by the Camden Music Service. Instrumental tuition, ensemble work, music technology, theory and performance. Part of Camden's wider creative arts education provision supporting young musicians across the borough.",
    times: "Check Camden Music Service for holiday course dates",
    website: "https://camdenmusic.org/camden-music-holiday-courses/",
    neighbourhoods: ["Camden"],
    notes: "Contact Camden Music Service for current holiday programme and bursary availability.",
  },
  {
    id: "cam-15",
    name: "Castlehaven Community Association Holiday Camp",
    provider: "Castlehaven Community Association",
    type: "Camp",
    neighbourhood: "Camden",
    location: "Castlehaven Road, Camden, NW1 8RU",
    postcodeSector: "NW1",
    distanceMiles: 0.9,
    cost: "Subsidised rates for local families · contact CCA for pricing",
    age: "5–16",
    interests: ["arts","sports","outdoors"],
    description: "Trusted community organisation in Camden Town running arts, sports and multi-activity holiday provision for local children and young people. Inclusive programmes with subsidised rates for local families. Part of Camden's community holiday network. Welcoming and accessible environment.",
    times: "Check CCA for holiday programme dates",
    website: "https://www.castlehaven.org.uk/events/holiday-camp/",
    neighbourhoods: ["Camden"],
    notes: "Community-run. Subsidised rates available to ensure accessibility for all local families.",
  },


  // ── PRIMROSE HILL & BELSIZE PARK ─────────────────────────────────────────────
  {
    id: "pb-1",
    name: "Primrose Hill Primary School Holiday Camp",
    provider: "Primrose Hill Primary School",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill Primary School, Fitzroy Road, NW1 8JH",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Primrose Hill Primary School, steps from one of London's most beloved parks. Sports, arts and outdoor activities in a vibrant and well-regarded community primary. Excellent outdoor access with Primrose Hill park right on the doorstep.",
    times: "Check with school office for holiday dates",
    website: "https://www.primrosehillactivelearning.co.uk/summer-camp-1",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pb-2",
    name: "St Paul's CE Primary School Holiday Camp",
    provider: "St Paul's CE Primary School",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "St Paul's CE Primary School, Elsworthy Road, NW3 3DS",
    postcodeSector: "NW3",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Paul's CE Primary in Primrose Hill. Sports, creative activities and outdoor play. Also hosts Perform drama classes on Saturdays. Welcoming community school in a leafy location close to Primrose Hill.",
    times: "Check with school office for holiday dates",
    website: "https://www.stpaulsprimary.school/holiday-club/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    notes: "Perform drama classes run at this venue on Saturdays during term time.",
  },
  {
    id: "pb-3",
    name: "North Bridge House Prep School Summer Camp",
    provider: "North Bridge House",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "North Bridge House Prep School, 1 Gloucester Avenue, NW1 7AB",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact NBH for pricing",
    age: "4–13",
    interests: ["sports","arts","stem","drama","outdoors"],
    description: "Multi-activity summer camp at North Bridge House Prep School in Primrose Hill. Sports, creative arts, STEM and performing arts using the school's excellent facilities. A highly regarded independent prep school with a vibrant community atmosphere.",
    times: "Check nbh.co.uk for summer camp dates",
    website: "https://www.northbridgehouse.com/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    notes: "Also hosts SuperCamps at the Senior School in Hampstead. Contact NBH directly for holiday camp availability.",
  },
  {
    id: "pb-4",
    name: "Arnold House School Holiday Camp",
    provider: "Arnold House School",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Arnold House School, 1 Loudoun Road, NW8 0LH",
    postcodeSector: "NW8",
    distanceMiles: 0.9,
    cost: "Contact school for pricing",
    age: "5–13",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity holiday camp at Arnold House School in St John's Wood, one of North London's leading independent prep schools. Sports, creative arts and outdoor activities using the school's outstanding facilities. Excellent transport links — St John's Wood tube station nearby.",
    times: "Check school website for holiday camp dates",
    website: "https://www.letsleapsportsacademy.co.uk/wraparound-care/arnold-house-school",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pb-5",
    name: "Hereward House School Holiday Camp",
    provider: "Hereward House School",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Hereward House School, 14 Strathray Gardens, NW3 4NY",
    postcodeSector: "NW3",
    distanceMiles: 0.7,
    cost: "Contact school for pricing",
    age: "4–13",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity holiday camp at Hereward House School in Belsize Park, a well-regarded independent prep school for boys. Sports, creative arts and outdoor activities. Excellent pastoral care ethos extended to holiday provision.",
    times: "Check school website for holiday camp dates",
    website: "https://www.herewardhouse.co.uk/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pb-6",
    name: "UCS Pre-Prep Holiday Camp",
    provider: "University College School (UCS)",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "UCS Pre-Prep, 36 College Crescent, NW3 5LF",
    postcodeSector: "NW3",
    distanceMiles: 1.0,
    cost: "Contact UCS for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors","stem"],
    description: "Multi-activity holiday camp at UCS Pre-Prep in Swiss Cottage. Sports, creative arts and outdoor activities at one of North London's most prestigious independent schools. Also hosts Camp Beaumont at the Senior School.",
    times: "Check ucschampstead.ac.uk for holiday camp dates",
    website: "https://ucsactive.org.uk/kids-active/",
    neighbourhoods: ["Primrose Hill & Belsize Park","Hampstead"],
  },
  {
    id: "pb-7",
    name: "Primrose Hill Community Association Holiday Camp",
    provider: "Primrose Hill Community Association",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill Community Centre, 29 Hopkinsons Place, NW1 8TN",
    postcodeSector: "NW1",
    distanceMiles: 0.4,
    cost: "Subsidised rates for local families · contact PHCA for pricing",
    age: "5–14",
    interests: ["arts","sports","outdoors"],
    description: "Multi-activity holiday camp and community programmes run by the Primrose Hill Community Association. Arts, sports, outdoor activities and community events in the heart of Primrose Hill. A welcoming local hub with subsidised rates for families in the area.",
    times: "Check primrosehillca.org.uk for holiday programme dates",
    website: "https://phca.cc/events-activities/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    notes: "Community-run. Subsidised rates available. Also runs term-time clubs and community events.",
  },
  {
    id: "pb-8",
    name: "Cecil Sharp House Holiday Courses",
    provider: "English Folk Dance and Song Society (EFDSS)",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Cecil Sharp House, 2 Regent's Park Road, NW1 7AY",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact EFDSS for pricing · 020 7485 2206 · registered charity",
    age: "5–18",
    interests: ["music","dance","arts","drama"],
    description: "England's dedicated folk arts centre in a Grade II listed building near Primrose Hill and Camden Market. Holiday courses in folk music and dance. Youth ensembles, youth ceilidhs and the Folk Unlimited summer course (FREE for learning disabled young people aged 7–25). Family barn dances, workshops and special performances throughout the year. Step-free access throughout.",
    times: "Mon–Fri 9:30am–5pm (performance times vary) · holiday courses check efdss.org",
    website: "https://www.efdss.org/cecil-sharp-house",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    phone: "020 7485 2206 · mail@efdss.org",
    notes: "5–10 minutes' walk from Gloucester Gate playground in Regent's Park, ZSL London Zoo and Camden Market. Step-free access, lift, baby changing, accessible toilets. Free Folk Unlimited course for learning disabled young people (age 7–25) runs each August.",
  },
  {
    id: "pb-9",
    name: "Primrose Hill Tennis Club Junior Holiday Camps",
    provider: "Primrose Hill Tennis Club",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill Tennis Club, Fitzroy Road, NW1 8TX",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Member and non-member rates available",
    age: "5–16",
    interests: ["sports"],
    description: "Junior tennis holiday camps at Primrose Hill Tennis Club in Primrose Hill park. LTA-qualified coaching for all abilities from mini tennis through to competitive juniors. Beautiful location in one of London's most iconic parks with excellent facilities.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://parksports.co.uk/holiday-camps/mini-tennis-camps",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pb-10",
    name: "Belsize Community Library Children's Workshops",
    provider: "Belsize Community Library",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Community Library, Antrim Road, NW3 4XN",
    postcodeSector: "NW3",
    distanceMiles: 0.9,
    cost: "Free or low cost · community-run",
    age: "5–12",
    interests: ["arts","stem"],
    description: "Community-run library offering children's reading, storytelling and creative holiday workshops. Book-themed craft activities, author events, storytelling sessions and creative writing workshops. An accessible and welcoming community hub in the heart of Belsize Park.",
    times: "Check with library for holiday workshop schedule",
    website: "https://belsizelibrary.org/whats-on/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    notes: "Volunteer-run community library. Free entry and low-cost events to ensure accessibility for all local families.",
  },
  {
    id: "pb-11",
    name: "Swiss Cottage Leisure Centre Holiday Activities",
    provider: "Better Leisure (Swiss Cottage)",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Swiss Cottage Leisure Centre, 4a Adelaide Road, NW3 3NF",
    postcodeSector: "NW3",
    distanceMiles: 1.0,
    cost: "Contact Better Leisure for holiday activity pricing",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Comprehensive holiday activities at Swiss Cottage Leisure Centre. 25m main pool, 20m training pool, 170-station fitness centre, 4-court sports hall (basketball, volleyball, netball, badminton, trampolining), climbing wall, squash courts, soft play zone and outdoor 7-a-side astroturf. Swim school, junior climbing, soft play and multi-sports holiday activities.",
    times: "Check better.org.uk for holiday activity programme",
    website: "https://www.better.org.uk/leisure-centre/london/camden/swiss-cottage-leisure-centre/holiday-activities",
    neighbourhoods: ["Primrose Hill & Belsize Park","Hampstead"],
    notes: "Minutes from Swiss Cottage and Finchley Road tube stations. Café on site. Accessible facilities throughout.",
  },
  {
    id: "pb-12",
    name: "WAC Arts Summer School",
    provider: "WAC Arts",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "WAC Arts, Wellington Road, NW8 9QP",
    postcodeSector: "NW3",
    distanceMiles: 1.1,
    cost: "Contact WAC Arts for pricing · subsidised places available",
    age: "4–18",
    interests: ["drama","dance","music","arts"],
    description: "Performing and creative arts summer school at WAC Arts. Monday 9:30am–3:30pm during holidays. Drama, dance, music and creative arts sessions led by professional arts educators. WAC Arts is a celebrated North London arts education charity with over 40 years delivering accessible arts education to the community.",
    times: "Mon–Fri 9:30am–3:30pm during school holidays",
    website: "https://www.wacarts.co.uk/home/what-we-do/summer-school",
    neighbourhoods: ["Primrose Hill & Belsize Park","Hampstead"],
    notes: "Subsidised and bursary places available. WAC Arts also runs year-round youth theatre and after-school arts programmes.",
  },
  {
    id: "pb-13",
    name: "Royal Central School of Speech & Drama — Youth Summer School",
    provider: "Royal Central School of Speech and Drama",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Embassy Theatre, Eton Avenue, Swiss Cottage, NW3 3HY",
    postcodeSector: "NW3",
    distanceMiles: 1.0,
    cost: "FREE (outreach programme) · contact cssd.ac.uk for details",
    age: "8–18",
    interests: ["drama","dance","music","arts"],
    description: "Free summer school courses offered by Central's Outreach and Fair Access Team. Acting skills and fundamentals of stage and film design. Organised by one of the world's leading drama schools whose alumni include Dame Judi Dench, Kit Harington (Game of Thrones), Andrew Garfield and Riz Ahmed. Applications typically open in spring for summer courses.",
    times: "Summer school dates — check cssd.ac.uk/outreach for annual programme",
    website: "https://www.westendstage.com/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    phone: "020 7722 8183 · outreach@cssd.ac.uk",
    notes: "FREE places via outreach programme. Aimed at young people who may not otherwise access drama training. Applications required — check website for opening dates. Also offers short courses, evening classes and professional development for older students.",
  },
  {
    id: "pb-14",
    name: "St Christopher's School Holiday Camp",
    provider: "St Christopher's School",
    type: "Camp",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "St Christopher's School, 32 Belsize Lane, NW3 5AE",
    postcodeSector: "NW3",
    distanceMiles: 0.8,
    cost: "Contact school for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors","stem"],
    description: "Multi-activity holiday camp at St Christopher's School in Belsize Park, one of Camden's leading independent girls' prep schools. Sports, creative arts and outdoor activities using the school's excellent facilities in a quiet Belsize Park location.",
    times: "Check school website for holiday camp dates",
    website: "https://www.mnature.co.uk/venues/hampstead-school-holiday-half-term-camps-near-me-clubs-for-kids-in-london/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },


  // ── ALEXANDRA PALACE & WOOD GREEN ────────────────────────────────────────────
  {
    id: "ap-1",
    name: "Rhodes Avenue Primary School Holiday Camp",
    provider: "Rhodes Avenue Primary School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Rhodes Avenue Primary School, N22 7UT",
    postcodeSector: "N22",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Rhodes Avenue Primary School in Wood Green. Sports, arts and outdoor activities in a well-regarded community primary close to Alexandra Park. Good outdoor space and modern facilities.",
    times: "Check with school office for holiday dates",
    website: "https://rhodesavenue.school/parents/extended-day/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-2",
    name: "Alexandra Primary School Holiday Camp",
    provider: "Alexandra Primary School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Primary School, N22 7SZ",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Alexandra Primary School in the heart of the Alexandra Palace neighbourhood. Sports, creative activities and outdoor play in a friendly community school setting.",
    times: "Check with school office for holiday dates",
    website: "https://alexandraprimaryschool.co.uk/about-us/contact-us/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-3",
    name: "Noel Park Primary School Holiday Camp",
    provider: "Noel Park Primary School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Noel Park Primary School, N22 6LH",
    postcodeSector: "N22",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Noel Park Primary School in Wood Green. Sports, arts and creative activities in a welcoming community primary school serving the Noel Park estate area.",
    times: "Check with school office for holiday dates",
    website: "https://www.liftnoelpark.org/contact-us",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-4",
    name: "Woodside High School Summer Camp",
    provider: "Woodside High School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Woodside High School, White Hart Lane, N22 5QH",
    postcodeSector: "N22",
    distanceMiles: 1.0,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","stem","arts","outdoors"],
    description: "Summer sports and multi-activity camp at Woodside High School. Uses the school's sports hall, outdoor courts and extensive grounds. Football, basketball, athletics and team sports for secondary-age children.",
    times: "Check school website for summer camp dates",
    website: "https://www.woodsidejunior.co.uk/page/?title=Holiday+Camp&pid=100",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-5",
    name: "Heartlands High School Summer Camp",
    provider: "Heartlands High School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Heartlands High School, White Hart Lane, N22 7ST",
    postcodeSector: "N22",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","stem","arts","outdoors"],
    description: "Summer sports and multi-activity camp at Heartlands High School in Wood Green. Sports, creative and academic activities using the school's facilities. Particularly suited to children transitioning to secondary school.",
    times: "Check school website for summer camp dates",
    website: "https://heartlands.haringey.sch.uk/summer-school/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-6",
    name: "Wood Green Leisure Centre Holiday Activities",
    provider: "Better Leisure / Fusion Lifestyle",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green Leisure Centre, N22 5HQ",
    postcodeSector: "N22",
    distanceMiles: 0.5,
    cost: "Contact leisure centre for holiday activity pricing",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Holiday swimming and sports activities at Wood Green Leisure Centre. Swimming pools, sports halls and fitness facilities. Swim school lessons, junior fitness sessions and holiday multi-sports activities for all ages in the heart of Wood Green.",
    times: "Check website for holiday activity programme",
    website: "https://haringey.gov.uk/news/20260721/free-summer-fun-everyone-haringey",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    notes: "Check with the leisure centre for current holiday camp operators and programme details.",
  },
  {
    id: "ap-7",
    name: "New River Sport & Fitness Junior Holiday Camp",
    provider: "New River Sport & Fitness",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "New River Sport & Fitness, White Hart Lane, N22 5QW",
    postcodeSector: "N22",
    distanceMiles: 0.8,
    cost: "Contact centre for pricing",
    age: "5–16",
    interests: ["sports","swimming","outdoors"],
    description: "Junior sports holiday camp at New River Sport & Fitness. Swimming, sports hall activities and outdoor sports using excellent leisure facilities close to Alexandra Park. All abilities welcome from beginners to competitive juniors.",
    times: "Check website for holiday camp dates",
    website: "https://haringey.gov.uk/leisure-parks-culture/leisure-centres/new-river/events-new-river",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-8",
    name: "Alexandra Palace Children's Learning Programme",
    provider: "Alexandra Palace",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace, Alexandra Palace Way, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "Contact Alexandra Palace for programme pricing",
    age: "5–14",
    interests: ["arts","stem","outdoors","drama"],
    description: "Multi-activity children's learning programmes at the iconic 196-acre Alexandra Palace and Park. Activities across the year including craft workshops, nature exploration in the deer enclosure and parkland, pitch and putt, skate park and seasonal events. The People's Palace visited by over a million people annually.",
    times: "Check alexandrapalace.com for children's holiday programme dates",
    website: "https://www.alexandrapalace.com/creative-learning/overview/",
    neighbourhoods: ["Alexandra Palace & Wood Green","Muswell Hill","Crouch End"],
    phone: "020 8365 2121",
    notes: "196-acre park with boating lake, deer enclosure, farmers market (Sunday), soft play (2–5 year olds), pitch and putt and skate park. Children's Summer Funfair runs in August.",
  },
  {
    id: "ap-9",
    name: "Alexandra Palace Ice Rink Holiday Courses",
    provider: "Alexandra Palace",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace Ice Rink, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "4–5 day crash courses: contact for pricing · Term courses £65–£112 · Public skating from £10–£11 (under 12s from £10)",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Voted UK's number 1 ice rink in 2023. School holiday 4 & 5 day crash courses for ages 5–15: qualified coaches, half-hour lesson 10:45–11:15am Monday to Friday, then free practice skating 11:30am onwards. Skate UK levels 1–8 and Children's Skating Academy (Bronze, Silver, Gold). Ice hockey, figure skating and disco nights also available.",
    times: "Public skating: Mon 11am–5:30pm · Tue 1–5pm · Wed–Thu 11am–5pm · Fri 1–5:30pm · Sat–Sun 11am–4:30pm · Closed 6–19 July (summer season)",
    website: "https://www.alexandrapalace.com/creative-resource/creative-activities-for-half-term/",
    neighbourhoods: ["Alexandra Palace & Wood Green","Muswell Hill","Crouch End"],
    phone: "020 8365 4386 · ice.rink@alexandrapalace.com",
    notes: "Skate hire included in ticket price. All-weather indoor rink open year-round (except July closure). W3 bus from Wood Green station to Palace entrance.",
  },
  {
    id: "ap-10",
    name: "Blue House Yard Creative Workshops",
    provider: "Blue House Yard / Tenant Studios",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Blue House Yard, 5 River Park Road, Wood Green, N22 7TB",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact individual studios for workshop pricing",
    age: "5–14",
    interests: ["arts","stem"],
    description: "Vibrant creative hub in the heart of Wood Green. Multiple independent studios run children's art and craft holiday workshops — printmaking, illustration, ceramics, jewellery-making and more. Below-market-rate flexible studios for local creatives, start-ups and social entrepreneurs. 2 minutes from Wood Green tube station.",
    times: "Individual studios set their own hours — check bluehouseyard.com for events and workshops",
    website: "https://www.bluehouseyard.com/home",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    notes: "Free 2-hour parking at Morrisons N22 6SU. One step into studios — level access available via main house door. Vegan market every Sunday.",
  },
  {
    id: "ap-11",
    name: "Jacksons Lane Holiday Circus & Arts Workshops",
    provider: "Jacksons Lane Arts Centre",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "269a Archway Road, Highgate, N6 5AA",
    postcodeSector: "N6",
    distanceMiles: 1.5,
    cost: "Contact Jacksons Lane for pricing · 020 8340 5226",
    age: "5–18",
    interests: ["drama","arts","dance","music"],
    description: "Flagship arts and cultural venue in Haringey since 1975, founded to provide holiday activities for local children. Grade II listed Edwardian church conversion with 167-seat theatre, 80-capacity dance studio, spring-floored rehearsal studio and café/bar. The Home of Contemporary Circus. Holiday workshops in circus skills, drama, dance and arts. £5m refurbishment completed 2022.",
    times: "Open 10am–10pm · café 10am–6pm Tue–Sat, 10am–5pm Sun",
    website: "https://www.jacksonslane.org.uk/whats-on/classes-courses/",
    neighbourhoods: ["Alexandra Palace & Wood Green","Highgate"],
    phone: "020 8340 5226 (general) · 020 8341 4421 (box office)",
    notes: "Directly opposite Highgate tube station. Buses 43, 134, 263. Step-free routes, hearing loop, BSL interpretation available. Baby changing facilities on site.",
  },
  {
    id: "ap-12",
    name: "Bounds Green Tennis Club Junior Holiday Camps",
    provider: "Bounds Green Tennis Club",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Bounds Green Tennis Club, N11 2DD",
    postcodeSector: "N11",
    distanceMiles: 1.2,
    cost: "Member and non-member rates available",
    age: "5–16",
    interests: ["sports"],
    description: "Junior tennis holiday camps at Bounds Green Tennis Club. LTA-qualified coaching for all abilities from mini tennis through to competitive juniors. Holiday camps during Easter and summer. Friendly community club atmosphere close to Bounds Green tube station.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://clubspark.lta.org.uk/BGBTC/Coaching/Camps",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "ap-13",
    name: "Wood Green Youth Hub Holiday Programme",
    provider: "Young Haringey / Wood Green Youth Hub",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green, N22",
    postcodeSector: "N22",
    distanceMiles: 0.5,
    cost: "Free or low cost for Haringey young people",
    age: "8–18",
    interests: ["arts","stem","sports","drama","music"],
    description: "Holiday arts, media and multi-activity programmes for young people in Wood Green. Music production, filmmaking, visual arts, sports and creative workshops. Part of Haringey's youth services provision connecting young people with positive activities during school holidays.",
    times: "Check haringey.gov.uk for holiday youth programme dates",
    website: "https://haringey.gov.uk/children-young-people-families/early-help-haringey/haringey-youth-service",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    notes: "Free or subsidised for Haringey residents. Check with Haringey Council youth services for current holiday programme details.",
  },
  {
    id: "ap-14",
    name: "St Michael-at-Bowes Primary Holiday Camp",
    provider: "St Michael-at-Bowes CE Primary School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "St Michael-at-Bowes CE Primary, N22 8HE",
    postcodeSector: "N22",
    distanceMiles: 1.1,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Michael-at-Bowes CE Primary School in Bowes Park. Sports, arts and creative activities in a welcoming faith school environment close to Bowes Park railway station.",
    times: "Check with school office for holiday dates",
    website: "https://activedaycamps.com/locations-adc-2/st-michaels-church-primary-school/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    notes: "Church of England ethos. Open to all faiths during holiday camps — confirm availability with school office.",
  },
  {
    id: "ap-15",
    name: "Earlham Primary School Holiday Camp",
    provider: "Earlham Primary School",
    type: "Camp",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Earlham Primary School, N22 5HJ",
    postcodeSector: "N22",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Earlham Primary School in Wood Green. Sports, creative activities and outdoor play in a well-regarded community primary school close to Wood Green High Road and tube station.",
    times: "Check with school office for holiday dates",
    website: "https://www.earlhamprimary.com/Contact-Us/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },


  // ── BARNET & HIGH BARNET ─────────────────────────────────────────────────────
  {
    id: "bn-1",
    name: "Foulds School Holiday Camp",
    provider: "Foulds Primary School",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Foulds Primary School, Hillside Gardens, Barnet, EN5 4NR",
    postcodeSector: "EN5",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Foulds Primary School in High Barnet. Sports, arts and outdoor activities in a well-regarded community primary school close to High Barnet tube station (Northern Line). Good facilities including outdoor space.",
    times: "Check with school office for holiday dates",
    website: "https://allforsport.co.uk/product-category/holiday-camps/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bn-2",
    name: "Complete Sports Holiday Camp — Cromer Road Primary",
    provider: "Complete Sports",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Cromer Road Primary School, Cromer Road, New Barnet, EN5 5HT",
    postcodeSector: "EN5",
    distanceMiles: 1.2,
    cost: "FREE for BACE-eligible children (HAF funded) · £31.50/day standard · Universal Tax Credit covers up to 85%",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "OFSTED-registered multi-sports and activity holiday camp at Cromer Road Primary. Part of the Barnet Holiday Activities and Food (HAF) programme — free for children eligible for benefits-related free school meals. Sports, games and activities provided by Complete Sports.",
    times: "10am–2pm (HAF hours) · extended hours available at additional cost",
    website: "https://www.completesports.co.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "HAF-funded free places via barnetyouth.uk using 16-digit e-voucher code. Standard cost: £31.50/day. Universal Tax Credit can cover up to 85% of costs.",
  },
  {
    id: "bn-3",
    name: "Monken Hadley CE Primary Holiday Camp",
    provider: "Monken Hadley CE Primary School",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Monken Hadley CE Primary School, Camlet Way, Barnet, EN4 0NJ",
    postcodeSector: "EN5",
    distanceMiles: 1.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Monken Hadley CE Primary, one of Barnet's most scenic village schools on the edge of Hadley Common. Sports, arts and creative activities in beautiful green surroundings.",
    times: "Check with school office for holiday dates",
    website: "https://www.monkenhadley.barnet.sch.uk/Contact-Us/",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Ofsted Outstanding school. Close to Hadley Common and Monken Hadley village. Beautiful outdoor setting.",
  },
  {
    id: "bn-4",
    name: "Queen Elizabeth Girls School Summer Camp",
    provider: "Queen Elizabeth Girls School",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Queen Elizabeth Girls School, High Street, Barnet, EN5 5RR",
    postcodeSector: "EN5",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","stem","arts","outdoors"],
    description: "Summer multi-activity and sports camp at Queen Elizabeth Girls School, a high-achieving grammar school in High Barnet. Uses the school's extensive facilities including sports hall, courts and grounds. Also hosts Barracudas holiday camp on the same site.",
    times: "Check with school for summer camp dates",
    website: "https://www.qegschool.org.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Barracudas Holiday Camp (see separate listing) also runs at Queen Elizabeth Girls School, EN5 4DQ.",
  },
  {
    id: "bn-5",
    name: "St Catherine's RC Primary Holiday Camp",
    provider: "St Catherine's RC Primary School",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "St Catherine's RC Primary School, Vale Drive, Barnet, EN5 2ED",
    postcodeSector: "EN5",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Catherine's RC Primary in Barnet. Sports, arts and creative play in a welcoming faith school environment. Open to the wider Barnet community during school holidays.",
    times: "Check with school office for holiday dates",
    website: "https://www.st-catherines.manchester.sch.uk/page/holiday-club/170550",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Catholic ethos school. Open to all faiths during holiday camps — confirm availability with school office.",
  },
  {
    id: "bn-6",
    name: "Lyonsdown School Holiday Camps",
    provider: "Lyonsdown School",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Lyonsdown School, 3 Richmond Road, New Barnet, EN5 1JP",
    postcodeSector: "EN5",
    distanceMiles: 1.1,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["arts","outdoors","sports"],
    description: "Arts and crafts holiday camps at Lyonsdown School, a well-regarded independent girls' prep school in New Barnet. Creative workshops, painting, drawing, model-making and craft projects. Small groups with individual attention in a welcoming independent school environment.",
    times: "Check with school for holiday dates",
    website: "https://lyonsdownschool.co.uk/contact-us",
    neighbourhoods: ["Barnet & High Barnet","North Finchley"],
  },
  {
    id: "bn-7",
    name: "The Totteridge Academy Summer Sports Camp",
    provider: "The Totteridge Academy",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "The Totteridge Academy, Barnet Lane, N20 8AZ",
    postcodeSector: "N20",
    distanceMiles: 1.8,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","outdoors","stem"],
    description: "Summer sports camp at The Totteridge Academy using the school's sports facilities. Also hosts Old Elizabethans CC junior cricket winter training. Football, basketball, athletics and team sports for secondary-age children. Well-connected location near Totteridge & Whetstone tube station.",
    times: "Check with school for summer camp dates",
    website: "https://www.thetotteridgeacademy.org.uk/get-in-touch",
    neighbourhoods: ["Barnet & High Barnet","Whetstone & Totteridge"],
    notes: "Old Elizabethans CC uses this venue for junior winter cricket training (Thursday evenings Jan–March).",
  },
  {
    id: "bn-8",
    name: "Barnet Lawn Tennis Club Junior Holiday Camps",
    provider: "Barnet Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet Lawn Tennis Club, 17 Church Road, New Barnet, EN5 2DN",
    postcodeSector: "EN5",
    distanceMiles: 1.0,
    cost: "Contact club for pricing · member and non-member rates",
    age: "5–16",
    interests: ["sports"],
    description: "Junior tennis holiday camps at Barnet Lawn Tennis Club, a friendly, well-established community club. LTA-qualified coaching for all abilities from mini tennis (3–8) through to competitive juniors. Floodlit courts. Holiday camps during Easter, summer and October half-term.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://www.barnetltc.com/courses",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Mini Red, Orange and Green programmes running year-round. Very family-friendly atmosphere.",
  },
  {
    id: "bn-9",
    name: "Old Elizabethans CC Junior Summer Cricket",
    provider: "Old Elizabethans Cricket Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Old Elizabethans Memorial Playing Fields, Mays Lane, Barnet, EN5 2AG",
    postcodeSector: "EN5",
    distanceMiles: 0.9,
    cost: "Contact club for junior membership and camp pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Thriving junior cricket programme at Old Elizabethans CC, established 1940. Over 140 junior colts from Under 8 to Under 17. ECB Clubmark and ECB Focus Club status. DBS-checked coaches with ECB qualifications. Girls train Tuesdays 5:30–6:30pm, mixed Friday evenings 6:30–8pm. Holiday cricket camps also available.",
    times: "Summer: Friday evenings 6:30–8pm · Junior matches weekday evenings and Sunday mornings · Holiday camps check club website",
    website: "https://www.oeccbarnet.co.uk/a/junior-cricket-49248.html?page=1",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "ECB Clubmark and Focus Club status reflecting commitment to quality welcoming junior cricket. Strong links with local schools. Bar open after training on Fridays.",
  },
  {
    id: "bn-10",
    name: "Barnet Elizabethans RFC Junior Holiday Camps",
    provider: "Barnet Elizabethans RFC",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Byng Road, Barnet, EN5 4NP",
    postcodeSector: "EN5",
    distanceMiles: 0.7,
    cost: "Contact club · 020 8449 0040 · Free taster sessions available",
    age: "6–16",
    interests: ["sports","outdoors"],
    description: "North London's premier rugby club with a thriving junior section welcoming players from Under 6 upwards. Holiday camps and summer rugby sessions led by enthusiastic coaches. Boys and girls welcome. Family-friendly club with a well-stocked clubhouse. Annual season runs Sept–May (£150 membership with free playing jersey for new members).",
    times: "Holiday camps — contact club for summer dates · Term-time Sundays (main training day)",
    website: "https://www.berfc.co.uk/contact",
    neighbourhoods: ["Barnet & High Barnet"],
    phone: "020 8449 0040 · berfc.co.uk",
    notes: "Free taster sessions available. All ages U6–U18 and seniors. STAGS membership scheme £15/month supports junior rugby development.",
  },
  {
    id: "bn-11",
    name: "Noah's Ark Children's Venture",
    provider: "Noah's Ark Children's Venture (Macaroni Wood)",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Chipping Barnet / Barnet EN5 area",
    postcodeSector: "EN5",
    distanceMiles: 1.0,
    cost: "Contact for pricing · registered charity · bursaries available",
    age: "5–14",
    interests: ["outdoors","stem","arts"],
    description: "Registered charity with 40+ years of outdoor adventure and residential provision. Low-cost outdoor learning, den building, nature exploration, woodland craft and adventurous activities. Works with groups across Barnet enabling children to access nature and green spaces. Bursaries available for disadvantaged children.",
    times: "Check with provider for holiday camp dates",
    website: "https://www.macaroniwood.org.uk/contact-us/",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Registered charity. Bursaries and subsidised places available for disadvantaged children and groups. Excellent reputation for inclusive outdoor education.",
  },
  {
    id: "bn-12",
    name: "Barnet Youth Musical Theatre Holiday Camps",
    provider: "Barnet Youth Musical Theatre",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet / High Barnet area, EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "7–18",
    interests: ["drama","dance","music"],
    description: "Musical theatre holiday camps for young people in Barnet. Singing, acting, dancing and musical theatre workshops building to a performance showcase. All abilities welcome — no experience needed. Develops confidence, performance skills and a love of the performing arts.",
    times: "Check for holiday camp dates",
    website: "https://barnetyouth.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bn-13",
    name: "Barnet Education Arts Trust (BEAT) Holiday Arts",
    provider: "Barnet Education Arts Trust",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "BEAT Music Centre, 36 Hendon Lane, Finchley, N3 1TT",
    postcodeSector: "EN5",
    distanceMiles: 2.0,
    cost: "Contact BEAT for pricing · subsidised places may be available",
    age: "5–18",
    interests: ["music","drama","dance","arts"],
    description: "Barnet's centre of excellence for arts education. Holiday courses in music, drama, dance and visual arts. Specialist tuition, ensembles, orchestras, choirs and creative holiday workshops across Barnet. Part of the Barnet Music Service supporting thousands of young musicians across the borough.",
    times: "Holiday courses — check beatbarnet.co.uk for dates",
    website: "https://www.artsdepot.co.uk/project/beat-summer-holiday-music-experience/",
    neighbourhoods: ["Barnet & High Barnet","North Finchley","Finchley"],
    phone: "020 8359 7350",
    notes: "Subsidised places may be available. Serves the whole London Borough of Barnet.",
  },
  {
    id: "bn-14",
    name: "All Saints Arts Centre Children's Holiday Workshops",
    provider: "All Saints Arts Centre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "All Saints Church, High Street, Barnet, EN5 4BW",
    postcodeSector: "EN5",
    distanceMiles: 0.3,
    cost: "Contact for pricing · many events low cost",
    age: "5–14",
    interests: ["arts","drama","music"],
    description: "Community arts centre in the heart of High Barnet running children's holiday art workshops and creative sessions. Painting, drawing, craft projects and mixed media activities in a welcoming and accessible community setting. Part of All Saints Church's community arts programme.",
    times: "Check for holiday workshop dates",
    website: "https://www.allsaintsyouthproject.org.uk/school-holiday-schemes",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Community space — low cost and accessible holiday workshops for local families.",
  },
  {
    id: "bn-15",
    name: "Greenhill Park Adventure Activities",
    provider: "Various local providers",
    type: "Camp",
    neighbourhood: "Barnet & High Barnet",
    location: "Greenhill Park, St Albans Road, Barnet, EN5 2EU",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact providers directly",
    age: "5–14",
    interests: ["sports","outdoors","arts"],
    description: "Multi-activity outdoor holiday camps and adventure activities in Greenhill Park, a large and well-equipped Barnet park. Football pitches, tennis courts, children's play areas and open green spaces make it an ideal outdoor camp venue. Various local activity providers run sessions here during school holidays.",
    times: "Check local listings for holiday camp dates",
    website: "https://www.barnet.gov.uk/directories/parks?f%5B0%5D=facilities%3ACaf%C3%A9%20%28inside%20the%20Leisure%20Centre%29&f%5B1%5D=facilities%3ACrazy%20golf&f%5B2%5D=facilities%3AFootball%207-a-side&f%5B3%5D=facilities%3AFootball%20Jnr&f%5B4%5D=facilities%3AGardens&f%5B5%5D=facilities%3AGolf&f%5B6%5D=facilities%3AHealth%20Walks&f%5B7%5D=facilities%3AMedium%20Events&f%5B8%5D=facilities%3AMulti%20Sports%20Court&f%5B9%5D=facilities%3ANature%20Reserve&f%5B10%5D=facilities%3ANew%20Barnet%20Leisure%20Centre%20and%20Library&f%5B11%5D=facilities%3AOutdoor%20Gym&f%5B12%5D=facilities%3APlay%20Area%20Toddler%20%282-%206%20years%29&f%5B13%5D=facilities%3APrivate%20Events&f%5B14%5D=facilities%3ARugby&f%5B15%5D=facilities%3ASkate%20park&f%5B16%5D=facilities%3ATennis%20-%20Free%20of%20Charge&f%5B17%5D=facilities%3ATennis%20courts&f%5B18%5D=facilities%3AToilets&f%5B19%5D=facilities%3AWildlife%20Interest&f%5B20%5D=facilities%3Awater%20feature&f%5B21%5D=type%3A254&f%5B22%5D=type%3A258&f%5B23%5D=type%3A264&f%5B24%5D=type%3A278&f%5B25%5D=type%3A284&f%5B26%5D=type%3A287&f%5B27%5D=type%3A288&f%5B28%5D=type%3A289&f%5B29%5D=type%3A293&f%5B30%5D=type%3A294&f%5B31%5D=type%3A295&f%5B32%5D=type%3A296&f%5B33%5D=type%3A299&f%5B34%5D=type%3A300&f%5B35%5D=type%3A301&f%5B36%5D=type%3A304&f%5B37%5D=type%3A309&f%5B38%5D=type%3A310&f%5B39%5D=type%3A312&page=1",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Greenhill Park has a café, tennis courts, skateboard area, outdoor gym and children's play equipment. Check local providers and barnet.gov.uk for organised holiday activities.",
  },


  // ── WINCHMORE HILL & SOUTHGATE ───────────────────────────────────────────────
  {
    id: "wh-1",
    name: "Walker Primary School Holiday Club",
    provider: "Walker Primary School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Walker Primary School, Powys Lane, N21 3AJ",
    postcodeSector: "N21",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Walker Primary School in Winchmore Hill. Sports, arts and outdoor activities in a friendly community school. Close to Winchmore Hill Green and excellent local parks.",
    times: "Check with school office for holiday dates",
    website: "https://walkerchildrensclub.co.uk/holiday-club/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wh-2",
    name: "Highfield Primary School Holiday Camp",
    provider: "Highfield Primary School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Highfield Primary School, N21 3HE",
    postcodeSector: "N21",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Sports and multi-activity holiday camp at Highfield Primary in Winchmore Hill. Football, team games, arts and outdoor activities. Check with school office for current holiday provision.",
    times: "Check with school office for holiday dates",
    website: "https://www.highfieldprimary.co.uk/parents/school-clubs/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wh-3",
    name: "St Monica's RC Primary Holiday Camp",
    provider: "St Monica's RC Primary School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "St Monica's RC Primary School, N12 5DY",
    postcodeSector: "N12",
    distanceMiles: 1.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Monica's RC Primary School. Sports, creative activities and outdoor play in a welcoming faith school environment. Open to the wider community during holidays.",
    times: "Check with school office for holiday dates",
    website: "https://st-monicas.enfield.sch.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Catholic ethos school — open to all faiths during holiday camps. Confirm availability with school office.",
  },
  {
    id: "wh-4",
    name: "Ashmole Academy Summer Camp",
    provider: "Ashmole Academy",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Ashmole Academy, Cecil Road, Southgate, N14 5RJ",
    postcodeSector: "N14",
    distanceMiles: 1.2,
    cost: "Contact school directly · 020 8361 2703",
    age: "11–16",
    interests: ["sports","stem","arts","outdoors"],
    description: "Summer sports and multi-activity camp at Ashmole Academy, a highly regarded comprehensive in Southgate (1,800 pupils, specialist status in Science and Music). Excellent sports and science facilities. Particularly suited to pupils transitioning to secondary or preparing for the new academic year.",
    times: "Check school website for summer camp dates",
    website: "https://www.ashmoleacademy.org/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    phone: "020 8361 2703 · office@ashmoleacademy.org",
    notes: "Stagecoach Southgate also listed as an associated provision at this address. Contact school for full holiday programme.",
  },
  {
    id: "wh-5",
    active: false, // marked not running in Oct 2026 review
    name: "Palmers Green High School Holiday Camp",
    provider: "Palmers Green High School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Palmers Green High School, N13 4EY",
    postcodeSector: "N13",
    distanceMiles: 1.0,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["stem","arts","outdoors","sports"],
    description: "Multi-activity and STEM-focused holiday camp at Palmers Green High School, an independent preparatory and pre-school. Creative arts, science workshops, sports and outdoor activities using the school's excellent facilities.",
    times: "Check with school for holiday camp dates",
    website: "https://palmersgreenhigh.org.uk",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wh-6",
    name: "Keble Prep School Holiday Camps",
    provider: "Keble Prep School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Keble Prep School, Wades Hill, Winchmore Hill, N21 1BG",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact school for pricing",
    age: "4–13",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity holiday camps at Keble Prep School, a well-regarded independent prep school in Winchmore Hill. Sports, creative arts and academic enrichment activities using the school's well-equipped facilities. Beautiful grounds close to Grovelands Park.",
    times: "Check with school for holiday camp dates",
    website: "https://www.campbeaumont.co.uk/our-camps/keble-prep-school",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Independent prep school. Holiday camps may be open to non-pupils — confirm with school office.",
  },
  {
    id: "wh-7",
    name: "Southgate Hockey Centre Junior Holiday Camps",
    provider: "Southgate Hockey Club",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate Hockey Centre, Waterfall Road, N14 7JN",
    postcodeSector: "N14",
    distanceMiles: 1.4,
    cost: "Contact Southgate HC for pricing",
    age: "6–18",
    interests: ["sports","outdoors"],
    description: "One of the finest hockey facilities in the country at one of the UK's oldest and most successful clubs (three European club titles). Day camps (10am–4pm) led by qualified coaches and senior 1st team squad players. All levels welcome. Summer residential camps also available for county academy level players aged 13–18.",
    times: "Day camps 10am–4pm · summer residential camp separately bookable",
    website: "https://southgatehc.org.uk/juniors/camps/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Junior Sundays are the main club day with all age groups. Waiting list for some age groups. Club founded 1871 with international and Olympic alumni.",
  },
  {
    id: "wh-8",
    name: "Southgate Leisure Centre Holiday Activities",
    provider: "Better Leisure / Fusion Lifestyle",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate Leisure Centre, Winchmore Hill Road, N14 6AF",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact centre for holiday activity pricing",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Holiday sports and swimming activities at Southgate Leisure Centre. Swimming pools, sports halls and fitness facilities. Swim school lessons, junior fitness sessions and multi-sports holiday activities for all ages. Well-equipped leisure centre in the heart of Southgate.",
    times: "Check website for holiday activity programme",
    website: "https://www.better.org.uk/leisure-centre/london/enfield/southgate-leisure-centre/holiday-activities",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Check with Southgate Leisure Centre for current holiday camp operators and programme details.",
  },
  {
    id: "wh-9",
    name: "Grovelands Park Outdoor Activity Camps",
    provider: "Various outdoor providers",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Grovelands Park, The Bourne, Southgate, N14 6RA",
    postcodeSector: "N13",
    distanceMiles: 0.7,
    cost: "Contact provider for pricing",
    age: "5–14",
    interests: ["outdoors","stem","sports"],
    description: "Outdoor activity and nature camps in Grovelands Park, one of Enfield's most beautiful historic parks (Grade II* listed Repton landscape with boating lake). Nature exploration, outdoor games, den building and sports in stunning parkland surroundings. Perfect setting for forest school and outdoor learning camps.",
    times: "Check with park events and local providers for holiday camp dates",
    website: "https://www.sallysadventureclub.com/adventures",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Grovelands Park features a café, boating lake, bowling green, tennis courts and children's play area. Multiple outdoor providers run sessions here — check local listings.",
  },
  {
    id: "wh-10",
    name: "Chickenshed Summer Shed",
    provider: "Chickenshed Theatre",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Chickenshed Theatre, 290 Chase Side, Southgate, N14 4PE",
    postcodeSector: "N14",
    distanceMiles: 1.0,
    cost: "£200/week · 25% sibling discount · bursaries available",
    age: "5–11",
    interests: ["drama","dance","music","arts"],
    description: "Iconic inclusive theatre company since 1974. Summer Shed (ages 5–11, Years 1–6): children devise ideas, learn songs, create dances and build an unforgettable performance to share with family on Friday afternoon. Two separate weeks available (each different, book one or both). Led by Chickenshed's professional theatre-makers. Everyone welcome — no experience needed.",
    times: "9:30am–3:30pm · Week 1: 28 Jul–1 Aug · Week 2: 4–8 Aug 2025",
    website: "https://www.chickenshed.org.uk/calendar",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    phone: "020 8351 6161 · info@chickenshed.org.uk",
    notes: "25% sibling discount. Bursaries available for families who need financial support. SEND-inclusive — contact Ellie Morton or Georgie Jacobs in advance for specific requirements. Easter Shed also runs (£35/session). Over 800 young people in youth theatre year-round.",
  },
  {
    id: "wh-11",
    name: "Winchmore Hill Cricket Club Summer Camp",
    provider: "Winchmore Hill Cricket Club",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill Cricket Club, Ford's Grove, N21 3LT",
    postcodeSector: "N21",
    distanceMiles: 0.4,
    cost: "Contact club for pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Summer cricket camps at Winchmore Hill Cricket Club, one of the oldest clubs in Middlesex. ECB-accredited coaching for all ages and abilities. Junior sections from age 7 upwards with holiday camps, All Stars Cricket (5–8) and Dynamos Cricket (8–11) programmes.",
    times: "Check ECB Play-Cricket or club website for holiday camp dates",
    website: "https://www.winchmorehillcc.co.uk/view-details/557/Cricket-Camps.htm",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Also offers All Stars Cricket (5–8) and Dynamos Cricket (8–11) ECB programmes during term time and holidays.",
  },
  {
    id: "wh-12",
    name: "Southgate Tennis Club Junior Camps",
    provider: "Southgate Tennis Club",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate Tennis Club, Waterfall Road, N14 7JN",
    postcodeSector: "N14",
    distanceMiles: 1.4,
    cost: "Member and non-member rates available",
    age: "5–16",
    interests: ["sports"],
    description: "Junior tennis holiday camps at Southgate Tennis Club. LTA-qualified coaching for all levels from mini tennis through to competitive juniors. Well-established club with excellent floodlit facilities in Southgate.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://ph-tennis.co.uk/pages/tennis-southgate",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Located close to Southgate Hockey Centre. Combined hockey and tennis holiday packages may be available.",
  },
  {
    id: "wh-13",
    name: "Southgate School Summer Sports Camp",
    provider: "Southgate School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate School, Sussex Way, Cockfosters, N14 6BN",
    postcodeSector: "N14",
    distanceMiles: 1.8,
    cost: "Contact school directly",
    age: "11–16",
    interests: ["sports","outdoors","stem"],
    description: "Summer sports camp at Southgate School using the school's sports facilities. Football, basketball, athletics and team sports for secondary-age children. Good transport links via Cockfosters tube (Piccadilly Line).",
    times: "Check with school for summer camp dates",
    website: "https://kalosport.com/what-we-do/holiday-camps/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wh-14",
    name: "St Andrew's Southgate Primary Holiday Camp",
    provider: "St Andrew's CE Primary School Southgate",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "St Andrew's CE Primary School, Chase Side, N14 5HJ",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Andrew's Southgate CE Primary. Sports, arts and creative play in a welcoming faith school environment close to Grovelands Park and Southgate tube station.",
    times: "Check with school office for holiday dates",
    website: "https://www.st-andrews-southgate.enfield.sch.uk/page/?title=Contact+Us&pid=9",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Church of England ethos — open to all faiths during holiday camps. Confirm availability with school office.",
  },
  {
    id: "wh-15",
    name: "Garfield Primary School Holiday Camp",
    provider: "Garfield Primary School",
    type: "Camp",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Garfield Primary School, N11 1RW",
    postcodeSector: "N11",
    distanceMiles: 1.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Garfield Primary School in New Southgate. Sports, creative activities and outdoor play in a well-regarded community primary school serving the New Southgate and Friern Barnet area.",
    times: "Check with school office for holiday dates",
    website: "https://garfield.enfield.sch.uk/contact/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },


  // ── STOKE NEWINGTON ───────────────────────────────────────────────────────────
  {
    id: "sn-1",
    name: "Camp Beaumont — Betty Layward Primary",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Betty Layward Primary School, Clissold Road, N16 8AZ",
    postcodeSector: "N16",
    distanceMiles: 0.6,
    cost: "Contact Camp Beaumont for pricing · 03003033866",
    age: "3–14",
    interests: ["sports","arts","outdoors","drama"],
    description: "UK's most experienced day camp provider with 40 years' history. Multi-activity camps at Betty Layward Primary School in the heart of Stoke Newington. 40+ activities per week including sports, arts, crafts and outdoor games. DBS-checked enthusiastic staff. Individual bookable days available.",
    times: "9am–5pm (extended hours available)",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Stoke Newington"],
    phone: "03003033866",
    notes: "All childcare vouchers accepted. Also hosts drama experiences for 4–8s and 8–12s at this venue.",
  },
  {
    id: "sn-2",
    name: "JAG Holiday HQ — Grasmere Primary",
    provider: "Junior Adventures Group",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Grasmere Primary School, Grasmere Road, N16 0QR",
    postcodeSector: "N16",
    distanceMiles: 0.8,
    cost: "Contact JAG for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Grasmere Primary School. Outdoor exploration, team challenges, sports, games and creative activities. OFSTED registered. Level 3 qualified Early Years coaches. Extended hours 8:30am–5:30pm.",
    times: "8:30am–5:30pm",
    website: "https://www.junioradventuresgroup.co.uk/holiday-clubs/",
    neighbourhoods: ["Stoke Newington"],
    notes: "Tax-Free Childcare, Universal Credit and childcare vouchers accepted.",
  },
  {
    id: "sn-3",
    name: "Premier Education — St Mary's CE Primary",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "St Mary's CE Primary School, Church Street, N16 9ES",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact Premier Education · 20% refer-a-friend discount",
    age: "5–12",
    interests: ["sports","outdoors"],
    description: "Multi-sports holiday camp at St Mary's CE Primary School in the heart of Stoke Newington. Football, basketball, dodgeball, cricket and team games run by qualified sports coaches in a safe and encouraging environment.",
    times: "Check Premier Education website for holiday schedule",
    website: "https://www.premier-education.com/parents/venue/courses/18370/?location=E16%201LN",
    neighbourhoods: ["Stoke Newington"],
    notes: "20% refer-a-friend discount. Flexi-booking fee 10% of total.",
  },
  {
    id: "sn-4",
    name: "Betty Layward Primary School Holiday Camp",
    provider: "Betty Layward Primary School",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Betty Layward Primary School, Clissold Road, N16 8AZ",
    postcodeSector: "N16",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "School-run multi-activity holiday camp at Betty Layward Primary. Sports, arts and outdoor play in a Good (Ofsted 2023) rated school in Stoke Newington. A welcoming two-form entry school with strong extended school provision.",
    times: "Check with school office for holiday dates",
    website: "https://www.bettylayward.hackney.sch.uk/school-clubs/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "sn-5",
    name: "Grasmere Primary School Holiday Camp",
    provider: "Grasmere Primary School",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Grasmere Primary School, Grasmere Road, N16 0QR",
    postcodeSector: "N16",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Grasmere Primary School in Stoke Newington. Sports, creative activities and outdoor play. Also hosts JAG Holiday HQ on the same site.",
    times: "Check with school office for holiday dates",
    website: "https://www.grasmere.hackney.sch.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "sn-6",
    name: "Stoke Newington School Summer School",
    provider: "Stoke Newington School",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington School, Clissold Road, N16 9EX",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact school for pricing",
    age: "11–16",
    interests: ["sports","arts","stem","outdoors"],
    description: "Unique summer school opportunity for incoming Year 7 students to sample life at Stoke Newington School. Sports, creative and academic activities helping students transition to secondary school. Multi-activity programme using the school's excellent facilities.",
    times: "Check school website for summer school dates",
    website: "https://www.stokenewingtonschool.co.uk/co-curricular/summer-school",
    neighbourhoods: ["Stoke Newington"],
    notes: "Particularly aimed at students joining SNS in Year 7. Good introduction to secondary school life.",
  },
  {
    id: "sn-7",
    name: "Clissold Leisure Centre Holiday Activities",
    provider: "Better Leisure (Clissold)",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Clissold Leisure Centre, 63 Clissold Road, N16 9EX",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "From £17.55/day · Hackney residents under 16 swim FREE",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Multi-activity holiday programme at Clissold Leisure Centre next to Clissold Park. Swimming sessions, Aqua Splash, soft play (0–10), sports hall activities, Junior Gym (11–17) and gymnastics. Three pools: two 25m pools and a toddler pool with water features. Hackney residents under 16 swim free all year.",
    times: "Mon–Fri 7am–10pm · Sat–Sun 7am–8pm · holiday activities vary",
    website: "https://www.better.org.uk/leisure-centre/london/hackney/clissold-leisure-centre/holiday-activities",
    neighbourhoods: ["Stoke Newington"],
    phone: "020 8986 9222",
    notes: "Under 16 Hackney residents swim free with PAYG membership card. SEND soft play Tuesdays. Inclusive swimming classes Tuesday evenings.",
  },
  {
    id: "sn-8",
    name: "The Garden Classroom — Stoke Newington",
    provider: "The Garden Classroom",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Green spaces around Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.7,
    cost: "Contact for pricing · bursaries available",
    age: "5–14",
    interests: ["outdoors","stem","arts"],
    description: "Outdoor learning charity running nature and forest school holiday camps in green spaces across North London. Den building, bug hunts, plant identification, foraging, nature art and outdoor science. Small groups. Bursary places available for families needing financial support.",
    times: "Full day outdoor camps",
    website: "https://www.thegardenclassroom.org.uk/",
    neighbourhoods: ["Stoke Newington","Islington & Highbury", "Stoke Newington"],
    notes: "Registered charity. Bursaries available. Waterproof clothing required. Abney Park Cemetery N16 also used for outdoor sessions.",
  },
  {
    id: "sn-9",
    name: "Hackney Showroom Summer Camp",
    provider: "Hackney Showroom",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "4 Murrain Road, Kings Crescent Estate, N4 2BN",
    postcodeSector: "N4",
    distanceMiles: 1.2,
    cost: "FREE · DBS-checked youth workers",
    age: "5–13",
    interests: ["arts","sports","outdoors","drama"],
    description: "Free summer camp run in partnership with Kings Crescent Tenants Association. Art workshops every morning and games, sports, outdoor activities and water fights in the afternoon. Week 1 for ages 8–13, Week 2 for ages 5–8. Led by experienced DBS-checked youth workers. Places fill quickly — mailing list essential.",
    times: "10am–4pm",
    website: "https://hackneyshowroom.com",
    neighbourhoods: ["Stoke Newington","Islington & Highbury"],
    phone: "020 3095 9747 · hello@hackneyshowroom.com",
    notes: "FREE for local children. Places limited and fill fast — sign up to mailing list at hackneyshowroom.com. Also runs Young Artists programme (applications open July each year) for ages 8–18 in eligible boroughs.",
  },
  {
    id: "sn-10",
    name: "Stagecoach Stoke Newington — Performing Arts",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.6,
    cost: "Contact local school for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    description: "Holiday workshops and term-time performing arts classes. Singing, dancing and acting building to a performance on the final day. All abilities welcome. OFSTED registered. Tax-free childcare and childcare vouchers accepted.",
    times: "Holiday workshops during school holidays",
    website: "https://www.stagecoach.co.uk/stokenewington#workshops",
    neighbourhoods: ["Stoke Newington"],
    notes: "Contact stagecoach.co.uk to find the Stoke Newington local school and holiday dates.",
  },
  {
    id: "sn-11",
    name: "Perform — Stoke Newington Holiday Drama Camp",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.6,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["drama","dance","music","arts"],
    description: "Drama, dance and singing holiday courses. Work with theatre professionals on original music, dance and improvisation building to a mini performance on the final day with costumes and props. Develops confidence, concentration, communication and coordination. Also runs at Betty Layward Primary nearby.",
    times: "3–5 day courses",
    website: "https://www.perform.org.uk/drama-classes/stoke-newington",
    neighbourhoods: ["Stoke Newington"],
    notes: "Check website for nearest Stoke Newington venue. Betty Layward Primary N16 is a confirmed Perform venue.",
  },
  {
    id: "sn-12",
    name: "London Fields Lido Holiday Swim Camps",
    provider: "Better Leisure (London Fields Lido)",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "London Fields Lido, London Fields Westside, E8 3EU",
    postcodeSector: "E8",
    distanceMiles: 1.8,
    cost: "Hackney residents under 16 swim FREE · swim lessons from £8.50/session",
    age: "5–16",
    interests: ["swimming","outdoors","sports"],
    description: "Iconic outdoor heated lido open year-round. Holiday swim camps and intensive lessons at London Fields Lido. 50m heated outdoor pool — one of the most popular outdoor swimming destinations in London. Hackney residents under 16 swim free in Swim for All sessions. Swim school lessons and holiday crash courses available.",
    times: "Mon–Fri 6:30am–8pm · Sat–Sun 6:30am–7pm · holiday sessions vary",
    website: "https://www.lidosummerswimschool.com/",
    neighbourhoods: ["Stoke Newington"],
    notes: "Under 16 Hackney residents swim free all year round with PAYG card. Outdoor heated pool open year-round. Very popular in summer — book ahead.",
  },
  {
    id: "sn-13",
    name: "Britannia Leisure Centre Holiday Activities",
    provider: "Better Leisure (Britannia)",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Britannia Leisure Centre, 40 Hyde Road, N1 5JU",
    postcodeSector: "N1",
    distanceMiles: 1.5,
    cost: "From £17.55/day · Hackney residents under 16 swim FREE",
    age: "5–16",
    interests: ["swimming","sports","outdoors"],
    description: "Comprehensive holiday activities at Britannia Leisure Centre in Shoreditch. 25m 6-lane pool, 20m training pool, interactive flume, toddler pool with splash deck, 6-court sports hall and rooftop tennis courts. Holiday sports camps, swim school, soft play and Junior Gym sessions. OFSTED-registered holiday playscheme.",
    times: "Holiday programmes vary — check website",
    website: "https://www.better.org.uk/leisure-centre/london/hackney/britannia-leisure-centre/holiday-activities",
    neighbourhoods: ["Stoke Newington","Islington & Highbury"],
    notes: "Under 16 Hackney residents swim free. OFSTED registered holiday playscheme. Wave machine and flume make it a great family day out.",
  },
  {
    id: "sn-14",
    name: "Woodberry Down Community Holiday Camp",
    provider: "Young Hackney / Woodberry Down Community",
    type: "Camp",
    neighbourhood: "Stoke Newington",
    location: "Woodberry Down, N4 2TG",
    postcodeSector: "N4",
    distanceMiles: 1.0,
    cost: "Free for eligible Hackney young people · subsidised rates available",
    age: "5–16",
    interests: ["sports","arts","outdoors","stem"],
    description: "Community holiday programme at Woodberry Down serving the local community. Young Hackney youth hub activities including sports, arts, music and outdoor activities. Free and inclusive sessions for young people aged 10–19. SEND-friendly provision available. Hot food or refreshments included.",
    times: "Check younghackney.org for holiday programme dates",
    website: "https://www.younghackney.org/the-edge/",
    neighbourhoods: ["Stoke Newington","Islington & Highbury"],
    phone: "020 8356 1063 · sarah.fox@hackney.gov.uk",
    notes: "Free for ages 10–19 (up to 25 with SEND). Must register in advance. View summer holiday programmes at younghackney.org.",
  },


  // ── ISLINGTON & HIGHBURY ─────────────────────────────────────────────────────
  {
    id: "ih-1",
    active: false, // marked not running in Oct 2026 review
    name: "Highbury Quadrant Primary Holiday Camp",
    provider: "Highbury Quadrant Primary School",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Highbury Quadrant Primary School, Highbury New Park, N5 2DP",
    postcodeSector: "N5",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Highbury Quadrant Primary School in the heart of Highbury. Sports, arts, outdoor play and creative activities in a well-equipped local school setting.",
    times: "Check with school office for holiday dates",
    website: "https://highburyquadrant.islington.sch.uk",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Priority may be given to existing pupils — confirm wider availability with school office.",
  },
  {
    id: "ih-2",
    name: "Gillespie Primary School Holiday Club",
    provider: "Gillespie Primary School",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Gillespie Primary School, Gillespie Road, N5 1LP",
    postcodeSector: "N5",
    distanceMiles: 0.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Gillespie Primary School, steps from Arsenal's Emirates Stadium. Sports, arts and outdoor activities in a well-loved local school. Great transport links — Arsensal (Piccadilly) and Highbury & Islington tube stations nearby.",
    times: "Check with school office for holiday dates",
    website: "https://www.gillespie.islington.sch.uk/contact-us-2/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "ih-3",
    name: "Ambler Primary School Holiday Club",
    provider: "Ambler Primary School",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Ambler Primary School, Blackstock Road, N4 2DR",
    postcodeSector: "N4",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Ambler Primary School on Blackstock Road. Sports, creative activities and outdoor play in a friendly community primary school.",
    times: "Check with school office for holiday dates",
    website: "https://www.ambler.islington.sch.uk/clubs-wraparound-childcare/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "ih-4",
    name: "New River Green Children's Centre Holiday Camp",
    provider: "New River Green Children's Centre",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "New River Green Children's Centre, N1 2TX",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "£30/day Islington residents · £40/day non-Islington · £15/day low income families",
    age: "3–11",
    interests: ["sports","arts","outdoors"],
    description: "Well-regarded multi-activity holiday camp run by the council-supported New River Green Children's Centre. Sports, arts, outdoor play and themed activities. Subsidised rates available for Islington families on low incomes (under £35,000 household income or Universal Credit).",
    times: "9am–5:30pm",
    website: "https://www.islington.gov.uk/children-and-families/childcare-and-early-education/lbi-managed-nurseries/islington-council-nurseries/new-river-green-childrens-centre",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7690 1300 · childrensactivities@elizabeth-house.org.uk",
    notes: "Subsidised rate £15/day or £75/week for qualifying Islington residents. Families in N4 2 postcodes (Hackney) also eligible at resident rate.",
  },
  {
    id: "ih-5",
    name: "Arsenal in the Community Holiday Courses",
    provider: "Arsenal in the Community",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Arsenal Football Club, Highbury House, 75 Drayton Park, N5 1BU",
    postcodeSector: "N5",
    distanceMiles: 0.5,
    cost: "Contact Arsenal in the Community for pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Official Arsenal FC community holiday courses run every school holiday. Football coaching and multi-sports sessions delivered by Arsenal coaches. Learn the Arsenal Way in the shadow of the Emirates Stadium. Available every holiday period from February half term through to October.",
    times: "Check arsenal.com/community for current holiday course dates",
    website: "https://www.arsenal.com/news/holiday-courses-aH2YP2f5EwMh",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7619 5000",
    notes: "Also runs residential UK summer camps in partnership with Exsportise for ages 9–17 at venues across the UK.",
  },
  {
    id: "ih-6",
    name: "Highbury Fields Tennis Holiday Camps",
    provider: "Highbury Fields LTC / Islington Tennis",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Highbury Fields, Highbury Crescent, N5 1AR",
    postcodeSector: "N5",
    distanceMiles: 0.4,
    cost: "Contact for pricing · LTA ClubSpark booking",
    age: "5–16",
    interests: ["sports"],
    description: "Junior tennis holiday camps at the courts within beautiful Highbury Fields park. LTA-qualified coaches for all abilities from complete beginners through to competitive juniors. Excellent outdoor facilities in one of North London's finest parks.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://www.hackneytennis.co.uk/juniors/holiday-camps/4-8yrs-holiday-camp-bookings-2/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Highbury Fields is a 29-acre park with excellent sports facilities including an athletics track and swimming pool nearby.",
  },
  {
    id: "ih-7",
    name: "Highbury Fields Multi-Sports Holiday Camp",
    provider: "Islington Council / Local Provider",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Highbury Fields, Highbury Crescent, N5 1AR",
    postcodeSector: "N5",
    distanceMiles: 0.4,
    cost: "Contact Islington Council leisure for pricing",
    age: "5–14",
    interests: ["sports","outdoors"],
    description: "Multi-sports and athletics holiday camp at Highbury Fields using the park's athletics track, outdoor courts and large open spaces. Football, athletics, outdoor games and team sports in a fantastic parkland setting in the heart of Highbury.",
    times: "Check Islington Council leisure programme",
    website: "https://www.islington.gov.uk/physical-activity-parks-and-trees/parks-and-green-space/your-local-parks/highbury-fields",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Highbury Fields has an athletics track, 6 tennis courts, bowling green and large playing fields. Highbury Pool & Fitness Centre also nearby.",
  },
  {
    id: "ih-8",
    name: "Little Angel Theatre Holiday Workshops",
    provider: "Little Angel Theatre",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "14 Dagmar Passage, Cross Street, N1 2DN",
    postcodeSector: "N1",
    distanceMiles: 1.5,
    cost: "Contact Little Angel Theatre for pricing · 020 7226 1787",
    age: "5–17",
    interests: ["drama","arts","music"],
    description: "The Home of British Puppetry since 1961. Half term workshops (ages 5–11): a world of puppetry and puppet making. Summer school (ages 7–17): design and make puppets, explore performing and learn all aspects of puppet theatre production. Crafty Kids Club and Saturday Puppet Club also available.",
    times: "Half terms all day workshops · Summer school multi-day · Box office 10am–5pm 7 days",
    website: "https://www.littleangeltheatre.com/take-part/category/playcations-and-holiday-clubs/",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7226 1787 · info@littleangeltheatre.com",
    notes: "One of only 3 building-based puppet theatres in England. Nearest tube: Angel (Northern) or Highbury & Islington (Victoria). Time Credits accepted.",
  },
  {
    id: "ih-9",
    name: "Perform — Islington Holiday Drama Camp",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Various venues across N1 and N5",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["drama","dance","music","arts"],
    description: "Popular drama, dance and singing holiday courses. Work with theatre professionals on original music, dance and improvisation building to a mini performance with costumes and props on the final day. Summer 2025 theme: Peter Pan. Develops confidence, concentration, communication and coordination.",
    times: "3–5 day courses",
    website: "https://www.perform.org.uk/drama-classes/islington",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Check website for nearest Islington/Highbury venue and current dates.",
  },
  {
    id: "ih-10",
    name: "Stagecoach Islington — Performing Arts Camp",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "Contact local school for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    description: "Holiday workshops and term-time performing arts classes. Singing, dancing and acting building to a performance on the final day. All abilities welcome — no experience needed. OFSTED registered. Tax-free childcare and childcare vouchers accepted.",
    times: "Holiday workshops during school holidays",
    website: "https://www.stagecoach.co.uk/islington",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Contact stagecoach.co.uk to find the Islington local school and current holiday dates.",
  },
  {
    id: "ih-11",
    name: "The Garden Classroom Holiday Camp",
    provider: "The Garden Classroom",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Stoke Newington / Highbury area, N4 2NQ",
    postcodeSector: "N4",
    distanceMiles: 1.1,
    cost: "Contact for pricing · bursaries available",
    age: "5–14",
    interests: ["outdoors","stem","arts"],
    description: "Outdoor learning charity running nature and forest school holiday camps. Hands-on nature activities, den building, bug hunts, plant identification, foraging, nature art and outdoor science. Small groups in green spaces across North London. Bursary places available for families who need financial support.",
    times: "Full day outdoor camps",
    website: "https://www.thegardenclassroom.org.uk/schools/countryside/woodland-residentials-at-hobbs-hill/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Registered charity. Bursary places available — enquire directly. Waterproof and warm clothing required.",
  },
  {
    id: "ih-12",
    name: "London Youth Choirs — Holiday Singing Workshops",
    provider: "London Youth Choirs",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Various North London venues including N1",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "Annual membership £500 (subsidised by 65%) · financial assistance scheme available · 30% of members receive support",
    age: "7–18",
    interests: ["music","drama"],
    description: "One of the UK's leading youth choral organisations. Holiday singing workshops, vocal training, concerts and recordings. Seven choirs including Junior Choir (7–11) and Youth Choir (12–18). Members perform at prestigious venues across London and internationally. Strong financial assistance scheme available.",
    times: "Term-time weekly rehearsals · holiday workshops and residential courses",
    website: "https://www.londonyouthchoirs.com/experience-london-youth-choirs/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Financial assistance available covering fees, travel grants, concert tickets and uniform. 2023–24: 30% of members received support. Auditions required for some choirs.",
  },
  {
    id: "ih-13",
    name: "Highbury Roundhouse Youth Holiday Programmes",
    provider: "Highbury Roundhouse / Camden Roundhouse",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "Highbury Roundhouse, 71 Benwell Road, N7 8DB",
    postcodeSector: "N7",
    distanceMiles: 1.3,
    cost: "Contact Roundhouse for pricing · bursaries available",
    age: "8–18",
    interests: ["arts","music","drama","stem"],
    description: "Creative arts and music programmes for young people at the Highbury Roundhouse. Holiday workshops in music production, recording, art, filmmaking and creative projects. Connected to the iconic Camden Roundhouse. Inclusive and accessible with bursary support available.",
    times: "Check roundhouse.org.uk for holiday programme dates",
    website: "https://www.highbury-roundhouse.org.uk/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Part of the wider Roundhouse creative education programme. Free and subsidised places available for young people from disadvantaged backgrounds.",
  },
  {
    id: "ih-14",
    name: "City of London Academy Highbury Grove — Summer Camp",
    provider: "City of London Academy Highbury Grove",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "City of London Academy Highbury Grove, Highbury Grove, N5 2EQ",
    postcodeSector: "N5",
    distanceMiles: 0.5,
    cost: "Contact school for pricing",
    age: "11–16",
    interests: ["sports","outdoors","stem","arts"],
    description: "Summer sports and multi-activity camp at City of London Academy Highbury Grove. Football, basketball, athletics, team sports and creative activities using the school's excellent facilities. Particularly well-suited to children transitioning to secondary school.",
    times: "Check with school for summer camp dates",
    website: "https://www.highburygrove.cola.org.uk/what-we-offer/beyond-the-classroom/activities-week/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "ih-15",
    name: "St Mary's Islington CE Primary Holiday Club",
    provider: "St Mary's Islington / Mother Nature Science",
    type: "Camp",
    neighbourhood: "Islington & Highbury",
    location: "St Mary's Islington CE Primary School, N1 2TX",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "Mother Nature Science: £295–£445/week · contact school for other provision",
    age: "4–11",
    interests: ["stem","arts","outdoors","sports"],
    description: "Multi-activity holiday club at St Mary's Islington CE Primary. Also hosts Mother Nature Science award-winning STEM holiday camps — 40+ hands-on science activities per week. Highly rated: parents describe it as the 'best holiday camp' their children have done (10,000/10 according to one pupil!).",
    times: "Mother Nature Science: 9am–3pm · School club: check with school office",
    website: "https://www.stmaryislington.org/playscheme",
    neighbourhoods: ["Islington & Highbury"],
    phone: "Mother Nature Science: 07596188316",
    notes: "Mother Nature Science runs at St Mary's Islington. Highly reviewed by local parents. Book early — fills fast.",
  },


  // ── HAMPSTEAD GARDEN SUBURB ───────────────────────────────────────────────────
  {
    id: "gs-1",
    name: "Mini Minors & XUK Day Camp — Brookland School",
    provider: "XUK Camps",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Brookland School, Off Hill Top, NW11 6EJ",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "£200/week (9:15am–3:15pm) · £160/week half days · daily booking available · lates £20/week",
    age: "3–15",
    interests: ["sports","arts","drama","outdoors","gaming"],
    description: "Consistently top-rated camps with 20+ years at Brookland School. Mini Minors (3–6) at the infants and XUK Day (7–15) at the junior school. Discos, parachute games, football, rocket-making and more. Never need to leave the site. OFSTED registered. Owner-run by Richard and Nick Bernstein.",
    times: "9:15am–3:15pm (lates until 4:15pm available)",
    website: "https://www.xukcamps.com/day-camps/venue/",
    neighbourhoods: ["Hampstead Garden Suburb","East Finchley"],
    phone: "020 8371 9686 · office@xukcamps.com",
    notes: "Strongly advised to walk to camp. Voluntary one-way parking system during drop-off. Tax-free childcare accepted.",
  },
  {
    id: "gs-2",
    name: "Club Excel Multi-Activity Camp — Henrietta Barnett",
    provider: "Club Excel",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "The Henrietta Barnett School, Central Square, NW11 7BN",
    postcodeSector: "NW11",
    distanceMiles: 0.6,
    cost: "Camp Day 9:30am–3:30pm · Early Club 8:30am · Late Club 5:30pm · sibling discount £10/child/week",
    age: "4–14",
    interests: ["sports","arts","drama","outdoors"],
    description: "Family-run multi-activity camp since 2000 at the prestigious Henrietta Barnett School. 25+ years delivering safe, fun holiday camps. Sports, arts, drama and outdoor adventure. Multiple-day and sibling discounts. Also runs at Dame Alice Owen's School and Aldenham School.",
    times: "9:30am–3:30pm (early club 8:30am, late club 5:30pm)",
    website: "https://www.clubexcel.co.uk/location/the-henrietta-barnett-school/",
    neighbourhoods: ["Hampstead Garden Suburb","Whetstone"],
    notes: "Early bird pricing available. Book via website. Also runs Easter camps at Henrietta Barnett.",
  },
  {
    id: "gs-3",
    name: "Brookland Junior School Holiday Camp",
    provider: "Brookland Junior School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Brookland Junior School, Off Hill Top, NW11 6EJ",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Brookland Junior School in Hampstead Garden Suburb. Sports, arts and outdoor activities in a well-equipped school with excellent facilities. Also hosts the popular XUK Day Camp on the same site.",
    times: "Check with school office for holiday dates",
    website: "https://www.brooklandjuniorschool.co.uk/clubs",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gs-4",
    name: "Garden Suburb Infant School Holiday Camp",
    provider: "Garden Suburb Infant School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Garden Suburb Infant School, Childs Way, NW11 6QS",
    postcodeSector: "NW11",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–7",
    interests: ["arts","outdoors"],
    description: "Early years holiday camp at Garden Suburb Infant School. Play-based activities in a nurturing infant school environment. Arts, crafts, outdoor play and age-appropriate activities for nursery and infant-age children.",
    times: "Check with school office for holiday dates",
    website: "https://www.gardensuburbinfant.co.uk/page/?title=After+School+clubs+and+Childcare&pid=35",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gs-5",
    name: "Garden Suburb Junior School Holiday Camp",
    provider: "Garden Suburb Junior School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Garden Suburb Junior School, NW11 6EB",
    postcodeSector: "NW11",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Garden Suburb Junior School. Sports, creative activities and outdoor play in a friendly community school setting in the heart of Hampstead Garden Suburb.",
    times: "Check with school office for holiday dates",
    website: "https://www.gardensuburbjunior.co.uk/page/?title=Extra+curricular+activities&pid=322",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gs-6",
    active: false, // marked not running in Oct 2026 review
    name: "The Highland Games Multi-Activity Camp — Henrietta Barnett",
    provider: "The Highland Games",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "The Henrietta Barnett School, Central Square, NW11 7BN",
    postcodeSector: "NW11",
    distanceMiles: 0.6,
    cost: "Lunch, snacks and all equipment provided · contact for pricing",
    age: "5–15",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity camps at Henrietta Barnett School using two astro turf pitches, indoor gym and main hall. Sports and games including dodgeball, tag rugby, football, tennis, hockey, athletics and cricket. Art, crafts, bracelet making and food education. Runs every school holiday throughout the year.",
    times: "Full day camps — check website for dates",
    website: "https://thehighlandgames.co.uk/king-alfred-school",
    neighbourhoods: ["Hampstead Garden Suburb","Hampstead"],
    notes: "Lunch, snacks and all equipment provided. Trainers and leisurewear required.",
  },
  {
    id: "gs-7",
    name: "King Alfred School Holiday Camps",
    provider: "The Highland Games / King Alfred School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "King Alfred School, 149 North End Road, NW11 7HY",
    postcodeSector: "NW11",
    distanceMiles: 0.8,
    cost: "Contact for pricing · The Strings Club also runs here",
    age: "4–16",
    interests: ["sports","arts","stem","outdoors","music"],
    description: "Multi-activity holiday camps at the progressive independent King Alfred School on the edge of Hampstead Heath. Beautiful 700-pupil campus with village green, farm, woodland areas and two astro turf pitches. Sports, arts and STEM camps. The Strings Club and Next Thing Education STEM camps also run here.",
    times: "9am–3:30pm (late pick-up to 4:30pm available for some providers)",
    website: "https://nxtgen.classforkids.io/camp/40",
    neighbourhoods: ["Hampstead Garden Suburb","Hampstead"],
    notes: "Multiple providers run at this venue. Also hosts Summer STEM Camps (Building Education) and The Highland Games camps.",
  },
  {
    id: "gs-8",
    name: "Kerem School Holiday Camps",
    provider: "Kerem School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Kerem School, Norrice Lea, NW11 6QX",
    postcodeSector: "NW11",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Kerem School, a Jewish independent prep school in Hampstead Garden Suburb. Sports, arts and creative activities in a warm and welcoming school community setting.",
    times: "Check with school office for holiday dates",
    website: "https://activities.bookpebble.co.uk/activity/super-star-sport-finchley-kerem-school-holiday-camps-london-0c92394d-94be-42c2-8e45-9f999527830f",
    neighbourhoods: ["Hampstead Garden Suburb"],
    notes: "Jewish ethos school. Open to all faiths for holiday camps — confirm with school office.",
  },
  {
    id: "gs-9",
    name: "Independent Jewish Day School Holiday Camp",
    provider: "IJDS / Brookland School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Brookland School, Off Hill Top, NW11 6EJ",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact school for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp run in association with the Independent Jewish Day School at Brookland. Sports, arts and outdoor activities in a familiar school setting for pupils of the local Jewish community.",
    times: "Check with school for holiday dates",
    website: "https://www.ijds.co.uk/Contact-Us/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gs-10",
    name: "Maccabi GB Holiday Sports Camps",
    provider: "Maccabi GB",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Maccabi GB, Shield House, Harmony Way, NW4 2BZ",
    postcodeSector: "NW11",
    distanceMiles: 1.2,
    cost: "Contact Maccabi GB for pricing · bursaries available",
    age: "5–18",
    interests: ["sports","outdoors"],
    description: "UK's largest Jewish sports and community charity. Holiday sports camps across North London including football, basketball, cricket, tennis and multi-sports. Strong community ethos building sportsmanship, pride and Jewish identity through sport. Open to all abilities and backgrounds.",
    times: "Check maccabigb.org for holiday camp dates and venues",
    website: "https://www.maccabigb.org/communitysportevents",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8457 2333 · info@maccabigb.org",
    notes: "Bursaries available for families in financial need. Contact Maccabi GB directly for current holiday programme.",
  },
  {
    id: "gs-11",
    name: "Alyth Synagogue Children's Holiday Club",
    provider: "Alyth Synagogue (North Western Reform Synagogue)",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Alyth Synagogue, Alyth Gardens, NW11 7EN",
    postcodeSector: "NW11",
    distanceMiles: 0.8,
    cost: "Contact synagogue for pricing · member discounts available",
    age: "4–14",
    interests: ["arts","sports","outdoors"],
    description: "Holiday club and children's programmes run by Alyth Synagogue, one of the UK's largest Reform communities. Creative, multi-activity holiday programme with Jewish cultural elements. Welcoming to all families. Beautiful garden grounds.",
    times: "Check alyth.org.uk for holiday programme dates",
    website: "https://www.alyth.org.uk/youth/day-schemes-camps/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8457 8861 · mail@alyth.org.uk",
  },
  {
    id: "gs-12",
    active: false, // marked not running in Oct 2026 review
    name: "LJCC Kids Camps",
    provider: "London Jewish Cultural Centre (LJCC)",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Ivy House, 94–96 North End Road, NW11 7SX",
    postcodeSector: "NW11",
    distanceMiles: 0.7,
    cost: "Contact LJCC for pricing · member discounts available",
    age: "5–16",
    interests: ["arts","drama","music","stem"],
    description: "Cultural and creative holiday camps at the LJCC's beautiful Ivy House venue in Golders Green. Arts, drama, music, creative workshops and cultural programmes. Dramarama performing arts camp also runs at Ivy House — £275/week for ages 4–11.",
    times: "Check ljcc.org.uk for holiday camp dates",
    website: "https://ljcc.org.uk",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8457 5000",
    notes: "Ivy House is also a Dramarama camp venue — drama, dance and singing, £275/week, 9:30am–3pm.",
  },
  {
    id: "gs-13",
    name: "Hampstead Garden Suburb Tennis Club — Junior Camps",
    provider: "Hampstead Garden Suburb Tennis Club / Farm Walk LTC",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Farm Walk Tennis Club, Hampstead Garden Suburb, NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.9,
    cost: "Junior member discounts · non-member rates available",
    age: "5–16",
    interests: ["sports"],
    description: "Welcoming tennis club hidden in the heart of leafy Hampstead Garden Suburb. Floodlit artificial grass and clay courts. LTA-qualified coaching team available 7 days a week for private and group lessons. Holiday tennis camps during Easter and summer. Junior members get discounted prices.",
    times: "Check clubspark LTA for holiday camp dates",
    website: "https://www.farmwalktennis.net/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    notes: "2026 waiting list open. Also runs free Clubplay sessions Tue 11am–1pm and Wed evenings for members.",
  },
  {
    id: "gs-14",
    name: "King Alfred Sports Academy Holiday Camps",
    provider: "The Highland Games at King Alfred School",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "King Alfred School, 149 North End Road, NW11 7HY",
    postcodeSector: "NW11",
    distanceMiles: 0.8,
    cost: "Contact for pricing · lunch and snacks included",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Sports-focused holiday camp at King Alfred School using two astroturf pitches and indoor gym. Football, tag rugby, tennis, hockey, cricket, rounders, athletics, dodgeball and team challenges. Runs every school holiday. Captain's orders, capture the flag and multi-skills challenges included.",
    times: "Full day camps — check website for dates",
    website: "https://www.kingalfred.org.uk/",
    neighbourhoods: ["Hampstead Garden Suburb","Hampstead"],
    notes: "Midweek sports sessions also available. Runs alongside multi-activity camps at the same venue.",
  },
  {
    id: "gs-15",
    name: "Forest School Sessions at King Alfred",
    provider: "King Alfred School / Independent Forest School Provider",
    type: "Camp",
    neighbourhood: "Hampstead Garden Suburb",
    location: "King Alfred School, 149 North End Road, NW11 7HY",
    postcodeSector: "NW11",
    distanceMiles: 0.8,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["outdoors","stem"],
    description: "Forest school holiday sessions at King Alfred School on the edge of Hampstead Heath. Den building, woodland craft, plant identification, nature exploration and outdoor learning in the school's beautiful woodland grounds. Enquiry-based outdoor learning drawing on the school's progressive educational ethos.",
    times: "Check school for holiday forest school dates",
    website: "https://www.kingalfred.org.uk/contact-us/",
    neighbourhoods: ["Hampstead Garden Suburb","Hampstead"],
    notes: "King Alfred's grounds include a farm, woodland areas and a village green — ideal forest school environment.",
  },


  // ── HAMPSTEAD ─────────────────────────────────────────────────────────────────
  {
    id: "hm-1",
    name: "Camp Beaumont — UCS Hampstead",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "University College School (UCS), Frognal, NW3 6XH",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact for pricing · extended days £10/day · archery add-on £12/day",
    age: "3–16",
    interests: ["sports","arts","outdoors","stem"],
    description: "UK's most experienced day camp provider with 40 years' history. Sports, arts, crafts and games across UCS's magnificent grounds. State-of-the-art sport and recreation facilities. 40+ activities per week including archery, sports hall and creative sessions. Lunches available at £6.50/day.",
    times: "9am–5pm (extended 8am arrival and 6pm pick-up £10/day)",
    website: "https://landing.campbeaumont.co.uk/camp-Beaumont-2019/ucs.html",
    neighbourhoods: ["Hampstead"],
    notes: "DBS-checked enthusiastic sports entertainers. Individual bookable days available. All childcare vouchers accepted.",
  },
  {
    id: "hm-2",
    name: "Ultimate Activity Camps — Hampstead",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "University of Westminster Sports Ground, Regents Park Road, NW1 7TH",
    postcodeSector: "NW1",
    distanceMiles: 1.4,
    cost: "Contact for pricing · OFSTED registered",
    age: "4–14",
    interests: ["sports","stem","arts","outdoors"],
    description: "OFSTED-approved holiday camps combining exceptional care with inspiring activities. Multi-sports, STEM workshops, creative arts and team games. Qualified, DBS-checked staff. One of the top-rated holiday camp providers in the UK.",
    times: "Standard day 9am–5pm · extended hours available",
    website: "https://activedaycamps.com/locations-adc-2/ucs-active/",
    neighbourhoods: ["Hampstead"],
    notes: "Tax-free childcare and all childcare vouchers accepted. Book via website.",
  },
  {
    id: "hm-3",
    name: "South Hampstead Tennis Club — Holiday Camps",
    provider: "South Hampstead Tennis Club",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "South Hampstead Tennis Club, NW3 7SR",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for pricing · member discounts available",
    age: "5–16",
    interests: ["sports"],
    description: "Year-round tennis programme including Easter (2 weeks) and Summer (6 weeks) holiday camps. Maximum 6 children per group, grouped by ability and age. Personal attention within a fun group dynamic. Half-term camps with individual lessons and bespoke group options also available.",
    times: "Check LTA ClubSpark for camp dates",
    website: "https://clubspark.lta.org.uk/southhampsteadtennisclub",
    neighbourhoods: ["Hampstead"],
    notes: "Friendly volunteer-run club with 5 floodlit courts in NW London. Also runs social and competitive play year-round.",
  },
  {
    id: "hm-4",
    name: "Keats Community Library Holiday Workshops",
    provider: "Keats Community Library",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Keats Community Library, NW3 2RR",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Low cost / free — contact library for details",
    age: "5–14",
    interests: ["arts","stem","outdoors"],
    description: "Community library running arts, crafts and creative holiday workshops for local children. Storytelling, art projects, craft sessions and reading activities in a welcoming community space. Part of the broader Hampstead community programme.",
    times: "Check with library for holiday programme",
    website: "http://www.keatscommunitylibrary.org.uk/",
    neighbourhoods: ["Hampstead"],
    notes: "Community-run. Low cost sessions to make activities accessible to all local families.",
  },
  {
    id: "hm-5",
    name: "Hampstead Heath Riding — Pony Holiday Camp",
    provider: "Hampstead Heath Riding (City of London)",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Parliament Hill Fields / Hampstead Heath, NW5 1QR",
    postcodeSector: "NW5",
    distanceMiles: 1.0,
    cost: "Contact City of London Corporation for pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Horse riding and pony holiday camps on Hampstead Heath, managed by the City of London Corporation. Riding lessons, pony care and outdoor activities on one of London's most iconic open spaces. All abilities welcome from complete beginners to experienced riders.",
    times: "Check City of London website for holiday camp dates",
    website: "https://www.cityoflondon.gov.uk/things-to-do/green-spaces/hampstead-heath",
    neighbourhoods: ["Hampstead"],
    notes: "Contact the City of London Open Spaces Department for holiday riding camp availability and booking.",
  },
  {
    id: "hm-6",
    name: "Burgh House Art Holiday Workshops",
    provider: "Burgh House & Hampstead Museum",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Burgh House, New End Square, NW3 1LT",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Contact Burgh House for pricing · many events free",
    age: "5–14",
    interests: ["arts"],
    description: "Grade I-listed Queen Anne building in the heart of Hampstead Village. Hosts family art workshops, creative holiday activities and children's events tied to gallery exhibitions. Free to visit with an art gallery, museum and café. 5 minutes from Hampstead Tube station.",
    times: "Wed–Fri & Sun 10am–4pm · Café Wed–Fri 10am–5pm, Sat–Sun 9:30am–5:30pm",
    website: "https://www.burghhouse.org.uk/whats-on/whats-on",
    neighbourhoods: ["Hampstead"],
    phone: "020 7431 0144 · info@burghhouse.org.uk",
    notes: "Ground floor step-free access. Check website for specific holiday workshop dates. Café serves all-day brunch and lunch.",
  },
  {
    id: "hm-7",
    name: "Emmanuel Church Holiday Club",
    provider: "Emmanuel Church Hampstead",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Emmanuel Church, Lyncroft Gardens, NW6 1JY",
    postcodeSector: "NW6",
    distanceMiles: 0.8,
    cost: "Low cost / donation — contact church for details",
    age: "4–11",
    interests: ["arts","sports","outdoors"],
    description: "Community holiday club run by Emmanuel Church Hampstead. Multi-activity days with games, crafts, sports and outdoor activities. Welcoming, inclusive environment open to all local families regardless of faith background.",
    times: "Check with church for holiday dates",
    website: "https://www.emmanuel.camden.sch.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hm-8",
    name: "New End Primary School Holiday Camp",
    provider: "New End Primary School",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "New End Primary School, NW3 1HU",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at New End Primary School in the heart of Hampstead Village. Sports, arts and outdoor activities in a well-equipped central Hampstead location.",
    times: "Check with school office for holiday dates",
    website: "https://cindex.camden.gov.uk/kb5/camden/cd/service.page?id=WqZWnzcKmJs",
    neighbourhoods: ["Hampstead"],
    notes: "Priority may be given to existing pupils — confirm wider availability with school office.",
  },
  {
    id: "hm-9",
    name: "Christ Church Primary School Holiday Camp",
    provider: "Christ Church CE Primary School",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Christ Church CE Primary School, NW3 1JH",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Christ Church CE Primary School in Hampstead. Sports, creative activities and outdoor play in a friendly community school setting close to Hampstead High Street.",
    times: "Check with school office for holiday dates",
    website: "https://christchurchschool.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hm-10",
    name: "SuperCamps — Heathside Prep Hampstead",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Heathside Prep School, 16 New Church Road, NW3 1DN",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact SuperCamps for pricing",
    age: "4–11",
    interests: ["sports","arts","stem","drama","outdoors"],
    description: "Multi-activity holiday camp at Heathside Prep School. Sports, creative arts and outdoor activities using the school's well-equipped facilities in a prime Hampstead location. Run by SuperCamps, one of the UK's leading holiday camp providers.",
    times: "Check supercamps.co.uk for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Hampstead"],
    notes: "SuperCamps also run at North Bridge House Senior School nearby.",
  },
  {
    id: "hm-11",
    name: "Hampstead School Summer Sports Camp",
    provider: "Hampstead School",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Hampstead School, Westbere Road, NW2 3RT",
    postcodeSector: "NW2",
    distanceMiles: 1.2,
    cost: "Contact school for pricing",
    age: "11–16",
    interests: ["sports","outdoors"],
    description: "Secondary school-run summer sports camp for older children and teenagers. Football, basketball, athletics, team sports and fitness sessions using Hampstead School's excellent sports facilities. Particularly suited to children transitioning from primary to secondary school.",
    times: "Check with school for summer camp dates",
    website: "https://www.hampstead.school/contact-us",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hm-12",
    name: "Stagecoach Hampstead — Performing Arts",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact local school for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    description: "Holiday workshops and term-time performing arts classes in Hampstead. Singing, dancing and acting building to a performance on the final day. All abilities welcome — no experience needed. OFSTED registered. Tax-free childcare and childcare vouchers accepted.",
    times: "Holiday workshops during school holidays",
    website: "https://www.stagecoach.co.uk/highgate",
    neighbourhoods: ["Hampstead"],
    notes: "Contact stagecoach.co.uk to find the Hampstead local school and holiday workshop dates.",
  },
  {
    id: "hm-13",
    name: "Spanish Club Holiday Camp — Hampstead",
    provider: "Spanish Club",
    type: "Camp",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["arts","drama","outdoors"],
    description: "Spanish language immersion holiday camp for children in Hampstead. Learn through themed games, crafts, music, drama and food activities entirely in Spanish. Fun, friendly approach makes language learning natural and enjoyable. All levels from complete beginners welcome.",
    times: "Check for holiday dates",
    website: "https://www.lingotot.com/course/9ed0798a-4f33-4931-8aef-611dafc66d45/term/9f21d915-0ea2-4827-b3c5-2bf1cae00f64",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hm-14",
    name: "Hampstead Community Centre Kids Programmes",
    provider: "Hampstead Community Centre",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead Community Centre, 78 Hampstead High Street, NW3 2NY",
    postcodeSector: "NW3",
    distanceMiles: 0.2,
    cost: "Contact centre for pricing · subsidised rates available",
    age: "5–14",
    interests: ["arts","sports","outdoors","stem"],
    description: "Thriving community hub in the heart of Hampstead High Street. Holiday programmes including arts, sports, games and creative workshops for local children. Also runs term-time classes across a wide range of activities. Accessible and inclusive approach with subsidised rates for local families.",
    times: "Check centre for holiday programme",
    website: "https://www.hampsteadcommunitycentre.co.uk/",
    neighbourhoods: ["Hampstead"],
    notes: "Heart of the local community. Subsidised rates available — enquire directly.",
  },


  // ── FINCHLEY ──────────────────────────────────────────────────────────────────
  {
    id: "fn-1",
    name: "XUK Day Camp (Xkeys Ltd)",
    provider: "Xkeys Ltd / XUK Camps",
    type: "Camp",
    neighbourhood: "Finchley",
    location: "Brookland School, Off Hill Top, NW11 6EJ",
    postcodeSector: "NW11",
    distanceMiles: 1.8,
    cost: "£200/week (9:15am–3:15pm) · £160/week half days · daily booking available",
    age: "3–14",
    interests: ["sports","arts","drama","outdoors","gaming"],
    description: "Consistently rated top camps in the UK. XUK Day (4–12) and Mini Minors (3–6) with discos, parachute games, football, rocket-making and more. Running since 1996. OFSTED registered. Owner-run by Richard and Nick Bernstein.",
    times: "9:15am–3:15pm (lates until 4:15pm)",
    website: "https://www.xukcamps.com/day-camps/venue/",
    neighbourhoods: ["Finchley","East Finchley"],
    phone: "020 8371 9686 · office@xukcamps.com",
  },
  {
    id: "fn-2",
    name: "Supersport Holiday Camp",
    provider: "Accelerate Sports",
    type: "Camp",
    neighbourhood: "Finchley",
    location: "Woodhouse College, Woodhouse Road, N12 9EY",
    postcodeSector: "N12",
    distanceMiles: 0.8,
    cost: "Contact for pricing · early bird discounts available",
    age: "4–13",
    interests: ["sports","stem","outdoors"],
    description: "Multi-sports and activity camp at prestigious Woodhouse College with excellent indoor and outdoor facilities. Football, tag rugby, basketball, archery, skateboarding, eSports, geocaching, arts and multi-sports. Something for every child whatever the British weather.",
    times: "Check website for holiday dates",
    website: "https://www.accelerate-sports.co.uk/supersports-holiday-camp",
    neighbourhoods: ["Finchley","North Finchley"],
    notes: "Indoor and outdoor facilities mean the camp runs rain or shine. Free parking on right side of main entrance gates.",
  },
  {
    id: "fn-3",
    name: "Poolside Manor Holiday Camp",
    provider: "Poolside Manor",
    type: "Camp",
    neighbourhood: "Finchley",
    location: "Lyndhurst Gardens, Finchley Central, N3 1TD",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for pricing · enquiry@poolsidemanor.com",
    age: "3–11",
    interests: ["swimming","sports","arts"],
    description: "Family-run private swimming pool and activity centre since 1988. Themed multi-activity holiday camps every school holiday — no two weeks ever the same. Swimming, sports, crafts, art, cookery and more. OFSTED-registered camps. After-school club also available at St Mary's Primary nearby.",
    times: "Check website for holiday dates",
    website: "https://www.poolsidemanor.com/multi-activity-day-camps",
    neighbourhoods: ["Finchley"],
    phone: "enquiry@poolsidemanor.com",
    notes: "Also offers swimming crash courses, term-time lessons, Aqua Fit and lifeguard training. Pool birthday parties available at weekends.",
  },
  {
    id: "fn-4",
    name: "The Games Yard Finchley Rugby Camp",
    provider: "The Games Yard",
    type: "Camp",
    neighbourhood: "Finchley",
    location: "Finchley Rugby Club / Woodhouse College, N12 0PD",
    postcodeSector: "N12",
    distanceMiles: 1.0,
    cost: "Contact for pricing",
    age: "5–11",
    interests: ["sports","outdoors"],
    description: "Summer rugby holiday camps for primary age children. Tag rugby, touch rugby, skill drills, team games and multi-sports. Friendly coaches developing skills, teamwork and a love of rugby in a fun and safe environment.",
    times: "Check for summer camp dates",
    website: "https://www.finchleyrugby.com/news/summer-fun-at-finchley-rfc--join-the-games-yard-2991535.html",
    neighbourhoods: ["Finchley","North Finchley"],
  },
  {
    id: "fn-5",
    name: "Finchley Youth Activity Centre (FYAC)",
    provider: "FYAC",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley Youth Activity Centre, N12",
    postcodeSector: "N12",
    distanceMiles: 0.7,
    cost: "Contact for pricing",
    age: "4–19",
    interests: ["drama","dance","arts","music"],
    description: "Community youth arts centre offering drama, dance and art activities for young people aged 4–19. Holiday workshops, term-time classes and youth theatre productions. Inclusive and welcoming environment focused on creative development and self-expression.",
    times: "Check centre for holiday programme",
    website: "https://www.barnet.gov.uk/young-people/activities-young-people/finchley-youth-activity-centre",
    neighbourhoods: ["Finchley","North Finchley"],
  },

  // ── NORTH FINCHLEY ────────────────────────────────────────────────────────────
  {
    id: "nf-1",
    name: "Barracudas Holiday Camp — Barnet",
    provider: "Barracudas Activity Camps",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Queen Elizabeth Boys School, Queens Road, Barnet, EN5 4DQ",
    postcodeSector: "EN5",
    distanceMiles: 2.5,
    cost: "Flex booking available · multi-day and sibling discounts · code CLUBHUB25 for £20 off 2+ days",
    age: "4–14",
    interests: ["sports","arts","stem","swimming","outdoors"],
    description: "UK's #1 rated holiday camp with 30+ years experience. 80+ activities including archery, motor sports, water play, bouncy castles, teambuilding, sports and creative sessions. Skills Builder Courses: Discovery Squad, Motor Mania, Rocketeers, Swim School, Dance Fusion and Roblox Coding. OFSTED registered.",
    times: "Standard 8:30am–5:30pm (extended 8am–6pm available)",
    website: "https://www.barracudas.co.uk/camps/barnet",
    neighbourhoods: ["North Finchley","Whetstone"],
    phone: "01480 467 567",
    notes: "Flex option allows amendments up to last working day before attendance. DBS-checked staff. Tax-free childcare and childcare vouchers accepted. Runs Feb half term, Easter, summer and Oct half term.",
  },
  {
    id: "nf-2",
    name: "Camp Beaumont — Coppetts Wood",
    provider: "Camp Beaumont / Sport4Kids",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Coppetts Wood Primary School, Coppetts Road, N10 1JS",
    postcodeSector: "N10",
    distanceMiles: 1.2,
    cost: "Contact for pricing · 03003033866",
    age: "3–14",
    interests: ["sports","arts","outdoors"],
    description: "UK's most experienced day camp provider with 40 years' history. Nerf, outdoor learning, daily team points competition, leadership opportunities for older children. DBS-checked enthusiastic sports entertainers. Individual bookable days or longer periods available.",
    times: "9am–5pm (drop-off and pick-up flexible within hours)",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["North Finchley","Muswell Hill"],
    phone: "03003033866",
  },
  {
    id: "nf-3",
    name: "JAG Holiday HQ — Friern Barnet School",
    provider: "Junior Adventures Group",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Friern Barnet School, Friary Road, N11 3DS",
    postcodeSector: "N11",
    distanceMiles: 1.0,
    cost: "Contact JAG for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Friern Barnet School. Outdoor exploration, team challenges, sports, games, creativity with natural materials and arts & crafts. OFSTED registered. Level 3 qualified Early Years coaches.",
    times: "8:30am–5:30pm",
    website: "https://www.junioradventuresgroup.co.uk/holiday-clubs/",
    neighbourhoods: ["North Finchley"],
    notes: "Tax-Free Childcare, Universal Credit and childcare vouchers accepted.",
  },
  {
    id: "nf-4",
    name: "Premier Education Holiday Camp — Friern Barnet",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Friern Barnet School, Friary Road, N11 3DS",
    postcodeSector: "N11",
    distanceMiles: 1.0,
    cost: "Contact Premier Education for pricing · 20% refer-a-friend discount",
    age: "5–12",
    interests: ["sports","outdoors","arts"],
    description: "Multi-sports holiday camp at Friern Barnet School. Football, basketball, cricket, dodgeball, rounders and team games. Run by qualified sports coaches in a safe and encouraging environment.",
    times: "Check Premier Education website for holiday schedule",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["North Finchley"],
    notes: "20% refer-a-friend discount. Flexi-booking fee 10% of total.",
  },
  {
    id: "nf-5",
    name: "Susi Earnshaw Theatre School Holiday Camps",
    provider: "Susi Earnshaw Theatre School",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Susi Earnshaw Theatre School, 68 High Street, Barnet, EN5 5SJ",
    postcodeSector: "EN5",
    distanceMiles: 3.0,
    cost: "Contact school for holiday camp pricing",
    age: "4–18",
    interests: ["drama","dance","music","arts"],
    description: "One of London's leading stage schools (Est. 1989). Holiday camps and summer schools offering drama, singing and dance training. Famous alumni include Amy Winehouse and Alexia Khadime. Part-time holiday provision alongside the full-time school.",
    times: "Check website for holiday dates",
    website: "https://www.thebulltheatre.com/platform-performing-arts",
    neighbourhoods: ["North Finchley","Whetstone"],
    notes: "Full-time school also accepts holiday and Saturday school pupils. Closest stations: High Barnet (0.7 miles), New Barnet (1.1 miles).",
  },
  {
    id: "nf-6",
    name: "Barnet Education Arts Trust (BEAT)",
    provider: "Barnet Education Arts Trust",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "BEAT Music Centre, 36 Hendon Lane, Finchley, N3 1TT",
    postcodeSector: "N3",
    distanceMiles: 1.5,
    cost: "Contact BEAT for pricing · subsidised places may be available",
    age: "5–18",
    interests: ["music","drama","dance","arts"],
    description: "Barnet's centre of excellence for arts education. Holiday courses in music, drama, dance and visual arts. Specialist tuition, ensembles, orchestras, choirs and holiday workshops. Part of the Barnet Music Service supporting thousands of young musicians across the borough.",
    times: "Holiday courses — check website for dates",
    website: "https://beatrust.org.uk/",
    neighbourhoods: ["North Finchley","Finchley"],
    phone: "020 8359 7350",
  },
  {
    id: "nf-7",
    name: "Finchley Catholic High School Summer Sports",
    provider: "Finchley Catholic High School",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Finchley Catholic High School, N12 9QA",
    postcodeSector: "N12",
    distanceMiles: 0.9,
    cost: "Contact school directly",
    age: "8–16",
    interests: ["sports","outdoors"],
    description: "Summer sports camp at Finchley Catholic High School. Multi-sports sessions using the school's excellent facilities. Football, basketball, tennis, athletics and team games for older primary and secondary-age children.",
    times: "Check with school for summer camp dates",
    website: "https://www.finchleycatholic.org.uk/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "nf-8",
    name: "Dwight School London — Holiday Camps",
    provider: "Dwight School London",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Dwight School London, 6 Friern Barnet Lane, N11 1HJ",
    postcodeSector: "N11",
    distanceMiles: 1.1,
    cost: "Contact school for holiday camp pricing",
    age: "4–15",
    interests: ["stem","arts","sports","outdoors"],
    description: "International Baccalaureate school offering holiday camps using its premium facilities. STEM, creative arts and sports activities in a world-class independent school setting. International, inclusive environment. Part of the global Dwight Schools network.",
    times: "Check website for holiday camp dates",
    website: "https://www.dwightlondon.org/student-life/extra-curricular-programmes",
    neighbourhoods: ["North Finchley"],
    notes: "Bright Horizons back-up care partner. Contact admissions office for holiday camp availability.",
  },
  {
    id: "nf-9",
    name: "Lyonsdown School Holiday Camps",
    provider: "Lyonsdown School",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Lyonsdown School, 3 Richmond Road, Barnet, EN5 1JP",
    postcodeSector: "EN5",
    distanceMiles: 2.8,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["arts","outdoors"],
    description: "Arts and crafts holiday camps at Lyonsdown School in New Barnet. Creative workshops, painting, drawing, model-making and craft projects. Small groups with individual attention in a welcoming independent school environment.",
    times: "Check with school for holiday dates",
    website: "https://lyonsdownschool.co.uk/contact-us",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "nf-10",
    name: "Moss Hall Junior School Holiday Camp",
    provider: "Moss Hall Junior School",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Moss Hall Junior School, Nether Street, N12 8PE",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Moss Hall Junior School in North Finchley. Sports, arts and outdoor activities in a well-equipped school setting. Familiar environment for existing pupils and welcoming to the wider community.",
    times: "Check with school office for holiday dates",
    website: "https://www.mosshallplaycentre.co.uk/holiday-club/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "nf-11",
    name: "Tudor Primary School Holiday Club",
    provider: "Tudor Primary School",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "Tudor Primary School, N3 1NT",
    postcodeSector: "N3",
    distanceMiles: 1.3,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Tudor Primary School in Finchley. Sports, creative activities and outdoor play. Familiar setting for existing pupils with a welcoming environment for the wider community.",
    times: "Check with school office for holiday dates",
    website: "https://www.tudorprimary.co.uk/external-holiday-kids-clubs/",
    neighbourhoods: ["North Finchley","Finchley"],
  },
  {
    id: "nf-12",
    name: "Northside Primary School Holiday Camp",
    provider: "Northside Primary School",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Northside Primary School, N12 8RP",
    postcodeSector: "N12",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Northside Primary in North Finchley. Sports, arts and creative play in a community primary school setting. Check with school office for current holiday provision and availability.",
    times: "Check with school office",
    website: "https://sporting-kids.classforkids.io/camp/21",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "nf-13",
    name: "Barnet Taekwondo Summer Camp",
    provider: "Barnet Taekwondo",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "Finchley / North Finchley, N12",
    postcodeSector: "N12",
    distanceMiles: 0.8,
    cost: "Contact for pricing",
    age: "5–16",
    interests: ["sports"],
    description: "Taekwondo holiday camp and training for children and young people. Develops discipline, self-confidence, fitness and self-defence skills through structured martial arts training. All abilities welcome from complete beginners to experienced practitioners.",
    times: "Summer holiday camp dates — check with provider",
    website: "https://www.barnetttaekwondoacademy.com/upcoming-events-at-a-glance",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "nf-14",
    name: "Finchley Lawn Tennis Club — Junior Coaching",
    provider: "Finchley Lawn Tennis Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "Rear of Brent Way, Finchley, N3 1AR",
    postcodeSector: "N3",
    distanceMiles: 1.5,
    cost: "Contact club for holiday camp pricing · member and non-member rates available",
    age: "5–16",
    interests: ["sports"],
    description: "Small, friendly family-oriented tennis club in a leafy rural setting. Five floodlit artificial grass courts. Junior coaching and holiday camps for all levels from beginners to advanced. Also runs at Ravens LTC in North Finchley with holiday camps during school holidays.",
    times: "Check LTA ClubSpark for holiday camp dates",
    website: "https://finchleyltc.org/?page_id=9",
    neighbourhoods: ["North Finchley","Finchley"],
    notes: "Ravens LTC (Friary Road N12) also runs holiday camps — see clubspark.lta.org.uk/ravenslawntennisclub",
  },
  {
    id: "nf-15",
    name: "artsdepot Holiday Projects",
    provider: "artsdepot",
    type: "Camp",
    neighbourhood: "North Finchley",
    location: "artsdepot, 5 Nether Street, Tally Ho Corner, N12 0GA",
    postcodeSector: "N12",
    distanceMiles: 0.4,
    cost: "Contact artsdepot for pricing · 020 8369 5454",
    age: "5–18",
    interests: ["drama","dance","arts","music"],
    description: "Multi-award-winning cultural hub welcoming 269,000+ visitors annually. Summer holiday camps include Little Yoga Art Camp (yoga-inspired stories, movement, art) and Musical Theatre Camp (Summer 2025: The Wizard of Oz, ages 7–17). Free playdepot soft play on site. Healthy lunch options at cafedepot.",
    times: "Summer camps: Wed 23 Jul – Sat 30 Aug 2025 · check for half-term dates",
    website: "https://www.artsdepot.co.uk/project/camp-innovate-robotics-with-lego-kits/",
    neighbourhoods: ["North Finchley"],
    phone: "020 8369 5454 · info@artsdepot.co.uk",
    notes: "395-seat Pentland Theatre and 148-seat Studio Theatre on site. Home to London Studio Centre. Book 3+ shows and save 10% with Pick & Mix.",
  },


  // ── WHETSTONE & TOTTERIDGE ───────────────────────────────────────────────────
  {
    id: "wt-1",
    active: false, // marked not running in Oct 2026 review
    name: "All About Kidz Holiday Club",
    provider: "All About Kidz Ltd",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "The Queenswell Federation, Sweets Way, N20 0NQ",
    postcodeSector: "N20",
    distanceMiles: 0.4,
    cost: "Contact for pricing · sibling discount available",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "OFSTED-registered holiday club at The Queenswell Federation. High-quality, affordable childcare with fully qualified staff. Flexible days and extended hours 8:30am–5:30pm. Sports, arts, outdoor play and themed activities. Packed lunch required — nut-free environment.",
    times: "9am–3pm (extended 8:30am–5:30pm, additional fee)",
    website: "https://all-about-kidz-n20-ltd.classforkids.io/camps",
    neighbourhoods: ["Whetstone"],
    phone: "info@allaboutkidz.co.uk",
    notes: "OFSTED No: 2679567. Payments in advance. Sibling discounts available on request.",
  },
  {
    id: "wt-2",
    name: "Mindcatcher SportsTech Camp",
    provider: "Mindcatcher",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "All Saints CE Primary School, Oakleigh Road North, N20 9EZ",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Contact for pricing · BACE funded places available for eligible families",
    age: "8–12",
    interests: ["stem","sports","arts","gaming"],
    description: "Unique SportsTech camp combining technology, sports and arts. Lego Robotics, coding to create games and apps, football, basketball, tennis, team-building games and art & craft. OFSTED certified. Drop-off 9am, pick-up 3:30pm via children's gate off Myddleton Park.",
    times: "9am–3:30pm",
    website: "https://mindcatcher.co.uk/sportstechactivitycamp-london/",
    neighbourhoods: ["Whetstone"],
    phone: "07596188316 · deborah@mindcatcher.co.uk",
    notes: "BACE vouchers accepted via barnetyouth.uk. Tax-free childcare and childcare vouchers accepted. Enter via children's gate off Myddleton Park pathway.",
  },
  {
    id: "wt-3",
    name: "Queenswell Federation Holiday Camp",
    provider: "Queenswell Federation",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Queenswell Federation, Sweets Way, N20 0NQ",
    postcodeSector: "N20",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "3–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp run at the Queenswell Federation school site. Covers two schools (Queenswell Infant and Junior). Sports, arts and outdoor activities in a familiar, safe school environment.",
    times: "Check with school office for holiday dates",
    website: "https://www.fsfed.org/page/?title=Queenswell+Schools&pid=20",
    neighbourhoods: ["Whetstone"],
    notes: "Also hosts All About Kidz holiday club on the same site.",
  },
  {
    id: "wt-4",
    name: "All Saints CE Primary Holiday Camp",
    provider: "Mindcatcher / All Saints CE Primary",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "All Saints CE Primary School, Oakleigh Road North, N20 9EZ",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["sports","arts","stem","outdoors"],
    description: "Multi-activity holiday camp at All Saints CE Primary School, hosting Mindcatcher's SportsTech camp and other holiday provision. Sports, arts, technology and outdoor activities on school grounds.",
    times: "9am–3:30pm",
    website: "https://eequ.org/book/all-saints-coe-primary-school-with-apf-activity-camps-9539",
    neighbourhoods: ["Whetstone"],
    notes: "Mindcatcher SportsTech runs here — see separate listing. BACE-funded places available for eligible families.",
  },
  {
    id: "wt-5",
    name: "Planet Youth — Whetstone",
    provider: "Planet Youth",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Whetstone / Barnet area, N20",
    postcodeSector: "N20",
    distanceMiles: 0.8,
    cost: "From £35/day",
    age: "4–11",
    interests: ["sports","arts","drama","outdoors"],
    description: "Multi-activity holiday camp from the same team as the Muswell Hill venue. Mini Adventurers (4–7) and Kids Camp (6–11) with arts & crafts, survival skills, sports, science, dance and drama. OFSTED registered. Childcare vouchers accepted.",
    times: "10am–3pm (early club 8am, late club until 6pm)",
    website: "https://barnetyouth.uk/detail_events_v2.aspx?id=4456",
    neighbourhoods: ["Whetstone","Muswell Hill"],
    phone: "07869 213606",
  },
  {
    id: "wt-6",
    name: "Powerleague Barnet — Football Camp",
    provider: "Powerleague",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Powerleague Barnet, Swan Lane, N20 (near High Barnet)",
    postcodeSector: "N20",
    distanceMiles: 1.5,
    cost: "Contact for holiday camp pricing",
    age: "5–14",
    interests: ["sports"],
    description: "Football holiday camps on 3G astroturf pitches at Powerleague Barnet. Skills coaching, small-sided games and tournaments for all abilities. Floodlit, all-weather facility. Part of the UK's largest 5-a-side football centre network.",
    times: "Check website for holiday camp dates",
    website: "https://www.powerleague.com/kids-football-camps-barnet",
    neighbourhoods: ["Whetstone"],
    phone: "0208 361 8658",
  },
  {
    id: "wt-7",
    name: "Perform Better Gymnastics Holiday Camp",
    provider: "Perform Better SC",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "711 High Road, North Finchley, N12 0BP",
    postcodeSector: "N12",
    distanceMiles: 1.8,
    cost: "£30/half day · £50/full day · packed lunch required",
    age: "5–12",
    interests: ["sports","arts","dance"],
    description: "Award-winning gymnastics club running holiday camps. Gymnastics, tumbling, climbing wall, ninja courses, archery, box exercise, singing, dance and arts & crafts. All levels welcome. Located in the Arts Depot building on High Road North Finchley.",
    times: "9am–4pm",
    website: "https://performbettersc.co.uk/our-activities/holiday-camps/",
    neighbourhoods: ["Whetstone","North Finchley"],
    notes: "Fees non-refundable. Look for gymnastics signage on building windows. Same building as Arts Depot and Pure Gym.",
  },
  {
    id: "wt-8",
    name: "Mother Nature Science Camp",
    provider: "Mother Nature Science",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Mill Hill School, The Ridgeway, NW7 1QS",
    postcodeSector: "NW7",
    distanceMiles: 2.5,
    cost: "£295–£445/week (full week) · contact for day rate",
    age: "5–12",
    interests: ["stem","outdoors"],
    description: "Multi-award-winning science holiday camps inspiring thousands of budding scientists every school holiday. Premium school venues, hands-on experiments, outdoor science and take-home projects. Thousands of children from the UK and internationally attend each year. Outdoor science walks and science-themed games included.",
    times: "Standard 9am–3pm · extended hours available",
    website: "https://www.mnature.co.uk/venues/mill-hill-school-holiday-half-term-camps-near-me-for-kids-london/",
    neighbourhoods: ["Whetstone","North Finchley"],
    notes: "Also runs at Vita et Pax Southgate and South Hampstead High School. Book early — fills fast.",
  },
  {
    id: "wt-9",
    name: "Club Excel Holiday Camp",
    provider: "Club Excel",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "The Henrietta Barnett School, Hampstead Garden Suburb, NW11 7BN",
    postcodeSector: "NW11",
    distanceMiles: 2.2,
    cost: "Contact for pricing · sibling discount £10 per extra child per week",
    age: "4–14",
    interests: ["sports","arts","drama","outdoors"],
    description: "Established family-run multi-activity camp since 2000. 25+ years delivering safe, fun holiday camps across London and Hertfordshire. Sports, arts, drama and outdoor adventure. Standard hours 9:30am–3:30pm with early (8:30am) and late (5:30pm) clubs. Multiple-day and sibling discounts available.",
    times: "9:30am–3:30pm (early club 8:30am, late club 5:30pm)",
    website: "https://www.clubexcel.co.uk/locations/",
    neighbourhoods: ["Whetstone","North Finchley"],
    notes: "Also runs at Dame Alice Owen's School Potters Bar and Aldenham School. Book via website. Early bird pricing available.",
  },
  {
    id: "wt-10",
    name: "Active London Holiday Club",
    provider: "Active London",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Brunswick Park Primary School, Osidge Lane, N14 5DU",
    postcodeSector: "N14",
    distanceMiles: 2.0,
    cost: "Contact for pricing · 02080872515",
    age: "4–12",
    interests: ["sports","arts","outdoors"],
    description: "OFSTED-registered holiday club at Brunswick Park Primary School near Southgate. Multi-activity and sports sessions in a safe school environment. Quality childcare by experienced practitioners.",
    times: "Check website for holiday dates",
    website: "https://active-london.com/",
    neighbourhoods: ["Whetstone"],
    phone: "02080872515 · admin@active-london.com",
  },
  {
    id: "wt-11",
    name: "Creative Camp Ltd",
    provider: "Creative Camp Ltd",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Barnet / Cockfosters area, EN4 0EL",
    postcodeSector: "EN4",
    distanceMiles: 2.8,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["arts","outdoors"],
    description: "Arts and crafts-focused holiday camp in the Barnet area. Creative projects, painting, drawing, model-making and craft activities led by experienced arts practitioners. Small groups, imaginative themes and take-home projects each day.",
    times: "Check website for holiday dates",
    website: "https://barnet.cylex-uk.co.uk/company/creative-camp-ltd-27138168.html",
    neighbourhoods: ["Whetstone"],
  },
  {
    id: "wt-12",
    name: "N20 KidsClub",
    provider: "N20 KidsClub",
    type: "Club",
    neighbourhood: "Whetstone",
    location: "1105–1111 High Road, Whetstone, N20 0PT",
    postcodeSector: "N20",
    distanceMiles: 0.3,
    cost: "From £19.99/child · café on site · free parking",
    age: "0–12",
    interests: ["sports","outdoors","arts"],
    description: "Purpose-built indoor playground and soft play centre in the heart of Whetstone. Multi-level play structures, slides, ball pools, sensory play and toddler areas. Birthday parties and holiday activities. 5-minute walk from Totteridge & Whetstone tube (Northern line). Buses 34, 234 and 383 stop directly outside.",
    times: "Check website for opening hours · walk-in welcome",
    website: "https://n20kidsclub.com/#play",
    neighbourhoods: ["Whetstone"],
    notes: "Claimed and verified venue. Well-connected by public transport — one of North London's most accessible soft play centres.",
  },
  {
    id: "wt-13",
    name: "Arties Summer Art Club",
    provider: "Arties",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Whetstone, N20 0DZ",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["arts"],
    description: "Local art summer club for primary-age children in Whetstone. Painting, drawing, collage, printmaking and mixed media projects in a fun and nurturing creative environment. Small groups to give each child individual attention.",
    times: "Check with provider for summer dates",
    website: "https://www.arties.club/",
    neighbourhoods: ["Whetstone"],
  },
  {
    id: "wt-14",
    name: "Totteridge Tennis Club — Junior Camps",
    provider: "Totteridge Tennis Club",
    type: "Camp",
    neighbourhood: "Whetstone",
    location: "Totteridge Tennis Club, Great Bushey Drive, N20 8QL",
    postcodeSector: "N20",
    distanceMiles: 1.0,
    cost: "Reduced rates for members · contact for non-member pricing",
    age: "3–17",
    interests: ["sports"],
    description: "One of the oldest and best-equipped tennis clubs in the area with 9 floodlit courts — 3 Tiger Turf, 3 Euroclay and 3 hard. Holiday camps during Easter and summer (not October half-term due to LTA tournament). One of the country's top coaching teams. Both members and non-members welcome.",
    times: "Check website for camp dates",
    website: "https://clubspark.lta.org.uk/TotteridgeTennisClub/Coaching/JuniorCamps",
    neighbourhoods: ["Whetstone","Totteridge"],
    phone: "coaching@totteridgetennis.net",
    notes: "Coached by Francesca and Clinton. Age-appropriate ball and court sizes. Term-time junior programme also available all year round.",
  },
  {
    id: "wt-15",
    name: "Totteridge Junior Cricket Club",
    provider: "Totteridge Millhillians CC",
    type: "Club",
    neighbourhood: "Whetstone",
    location: "Totteridge Lane, Totteridge, N20",
    postcodeSector: "N20",
    distanceMiles: 1.2,
    cost: "Contact club for junior membership and holiday camp pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "Friendly local cricket club with a thriving junior section. ECB-accredited coaching for all ages and abilities. Holiday cricket camps and year-round junior development squads. Beautiful ground in the Totteridge greenbelt.",
    times: "Holiday camps during school holidays · junior sessions weekends",
    website: "https://www.totteridgemillhillians.com/",
    neighbourhoods: ["Whetstone","Totteridge"],
    notes: "Part of the Totteridge Millhillians CC — contact via their website for junior holiday camp availability.",
  },


  // ── CROUCH END ────────────────────────────────────────────────────────────────
  {
    id: "ce-1",
    name: "The Strings Club — Crouch End",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Coleridge Primary School, 77 Crouch End Hill, N8 8DN",
    postcodeSector: "N8",
    distanceMiles: 0.4,
    cost: "From £52.50/day · 10% off with code NEWCUS10",
    age: "4–11",
    interests: ["music","arts","drama","dance","sports"],
    description: "Award-winning 5-star rated music and multi-activity holiday camp. Mornings: guitar, violin or ukulele lessons with free instrument hire to take home. Afternoons: sport, art, drama, dance and wellbeing. Grand Concert for families at end of each week. Age-specific groups for 4–7 and 8–11.",
    times: "Standard 9:30am–4pm · Extended 8am–5:30pm",
    website: "https://www.thestringsclub.org/pages/crouch-end-holiday-camp",
    neighbourhoods: ["Crouch End","Muswell Hill","Hampstead"],
    notes: "500+ verified 5★ reviews. OFSTED registered. All childcare vouchers and tax-free childcare accepted. New for 2025: Strum Stars Camp for Years 3–6.",
  },
  {
    id: "ce-2",
    name: "Little Imaginations Early Years Camp — Crouch End",
    provider: "Little Imaginations",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Priory Park, Crouch End N8 8LJ",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "3–6",
    interests: ["arts","drama","outdoors"],
    description: "OFSTED-registered early years creative arts holiday camp in Crouch End. Crafting, treasure hunts, nature play, dance, drama, Lego, messy play and role-play adventures. Friday sessions 9am–3pm in Priory Park.",
    times: "9am–3pm",
    website: "https://www.littleimaginationscreativearts.co.uk",
    neighbourhoods: ["Crouch End","Muswell Hill","Highgate"],
    notes: "On Bright Horizons back-up care scheme. Tax-free childcare accepted.",
  },
  {
    id: "ce-3",
    name: "KidsArt! Crouch End",
    provider: "KidsArt!",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Life by Margot, 84 Park Road, N8 8JQ",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Contact for pricing · 07827 275821",
    age: "4–16",
    interests: ["arts"],
    description: "Art classes and holiday workshops at a dedicated studio. Painting, drawing, clay modelling, collage, printmaking and art history woven throughout. Sunday morning classes (9am ages 4–8, 10:10am ages 9+) and weekday after-school sessions. Holiday camp workshops also available.",
    times: "Sun 9am & 10:10am · Wed 4:10pm (4–8) & 5:15pm (9+)",
    website: "https://kidsartuk.com/crouch-end/",
    neighbourhoods: ["Crouch End"],
    notes: "Also at Highgate, Muswell Hill, Finchley. 4.7★ Google rating.",
  },
  {
    id: "ce-4",
    name: "My Gym Crouch End",
    provider: "My Gym",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "2–4 The Broadway (Basement), Crouch End, N8 9SN",
    postcodeSector: "N8",
    distanceMiles: 0.3,
    cost: "Holiday camps from £35 members · £40 non-members per day · Classes from £20.50",
    age: "6 weeks–10 years",
    interests: ["sports","outdoors"],
    description: "Purpose-built gymnastics gym in the heart of Crouch End. Holiday camps include themed gymnastics, zip wire, obstacle courses, ninja circuits, arts and crafts and a healthy snack. Classes for all ages from Tiny Tykes (6 months) through to Ninja Training (up to 10 years). No two camp days ever the same.",
    times: "Mon–Thu 9am–5pm · Sun 9am–5pm (Fri–Sat closed)",
    website: "https://www.mygym.com/crouchend",
    neighbourhoods: ["Crouch End"],
    phone: "07419 332169 · crouchend@mygym.com",
    notes: "Instagram/Facebook: mygymcrouchendkids. Holiday camp themes change — check website for upcoming dates.",
  },
  {
    id: "ce-5",
    name: "Lingotot Spanish Holiday Camp — Crouch End",
    provider: "Lingotot",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Hornsey Vale Community Centre, 60 Mayfield Road, N8 9LP",
    postcodeSector: "N8",
    distanceMiles: 0.7,
    cost: "Contact for pricing · crouchend@lingotot.com",
    age: "4–11",
    interests: ["arts","drama","outdoors"],
    description: "Spanish language immersion holiday camp — learn through play without realising it! Themed days with games, arts and crafts, music, drama, food tasks and team challenges. All activities connected to the daily theme. DBS-checked, First Aid-trained staff. Beginners and experienced learners all welcome.",
    times: "9am–3:30pm",
    website: "https://www.lingotot.com/centre/crouch-end/holiday-camps",
    neighbourhoods: ["Crouch End"],
    phone: "crouchend@lingotot.com · @lingototcrouchend",
    notes: "Also runs term-time Spanish after-school club at Rokesly Infant School (£9/session).",
  },
  {
    id: "ce-6",
    name: "Georgians Tennis Club — Junior Coaching",
    provider: "Georgians Tennis Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Georgians Tennis Club, Park Road, N8 8JJ",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Contact club for holiday camp pricing",
    age: "5–16",
    interests: ["sports"],
    description: "Well-established local tennis club with junior coaching and holiday camps. LTA-accredited coaches teaching all abilities from beginners through to competitive juniors. Beautiful courts in a friendly community club environment.",
    times: "Check website for holiday camp dates",
    website: "https://clubspark.lta.org.uk/GeorgiansTennisinthePark/Coaching",
    neighbourhoods: ["Crouch End"],
    notes: "Also offers junior membership and term-time junior squads.",
  },
  {
    id: "ce-7",
    name: "Crouch End Tennis Academy",
    provider: "Crouch End Tennis Academy",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End, N8 8RE",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "4–16",
    interests: ["sports"],
    description: "Dedicated tennis academy offering coaching for all ages and abilities. Holiday camps and intensive courses run throughout the year. LTA-qualified coaches. Mini tennis for younger children, junior squads and competitive training for older players.",
    times: "Check for holiday camp dates",
    website: "http://crouchendtennisacademy.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "ce-8",
    name: "Camp Ally Pally",
    provider: "The Breezy Club",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Alexandra Park School, Oliver Tambo Recreation Ground entrance, N22 7XL",
    postcodeSector: "N22",
    distanceMiles: 1.2,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["sports","arts","outdoors"],
    description: "Fun, active and educational multi-activity holiday camp based at Alexandra Park School. Children learn, play and explore their creative abilities with a wide range of activities catering to every child's interests. Run by The Breezy Club.",
    times: "Core hours with early drop-off from 8:30am and late pick-up until 5pm",
    website: "https://www.thebreezyclub.co.uk/",
    neighbourhoods: ["Crouch End","Muswell Hill"],
    notes: "Entrance via Oliver Tambo Recreation Ground off Albert Road — do not enter via Rhodes Avenue.",
  },
  {
    id: "ce-9",
    name: "Cape Adventure Playground",
    provider: "Awesome CIC",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "85 Crouch Hill, N8 9EG",
    postcodeSector: "N8",
    distanceMiles: 0.8,
    cost: "Free for Islington children · £6/hour childcare session for working parents",
    age: "5–13",
    interests: ["outdoors","arts","sports"],
    description: "Open-access adventure playground set alongside Parkland Walk nature reserve. Climbing structures, slides, zip wire, fire pit, MUGA pitch, bike workshop and swings. Holiday sessions 10:30am–5:30pm with free play. Indoor arts and crafts, junk modelling, mini beast hunts and nature play.",
    times: "Holidays: 10:30am–1:30pm and 2:30pm–5:30pm · Term time: 3:15pm–6:15pm",
    website: "https://awesomeadventureplay.org/playgrounds/cape/",
    neighbourhoods: ["Crouch End"],
    phone: "0203 875 3662 · matilda@awesomecic.co.uk",
    notes: "Free for children living or going to school in Islington. Registration required. Childcare hour 1:30–2:30pm is £6 for working parents.",
  },
  {
    id: "ce-10",
    name: "Perform Holiday Drama Camp",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Local venue, Crouch End N8 (check website for exact location)",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["drama","dance","music","arts"],
    description: "Popular drama, dance and singing holiday courses for 4–10s. Work with theatre professionals on original music, dance and improvisation building to a mini performance with costumes and props on the final day. Summer 2025 theme: Peter Pan. Develops the 4 Cs — Confidence, Concentration, Communication, Coordination.",
    times: "3–5 day courses",
    website: "https://www.perform.org.uk/drama-classes/st-johns-wood",
    neighbourhoods: ["Crouch End","Muswell Hill","Highgate"],
    notes: "Available across many North London venues — check website for nearest Crouch End location.",
  },
  {
    id: "ce-11",
    active: false, // marked not running in Oct 2026 review
    name: "Stagecoach Performing Arts — Crouch End",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Crouch End, N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact local school for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    description: "Holiday workshops and term-time performing arts classes. Singing, dancing and acting building to a performance on the final day. All abilities welcome — no experience needed. OFSTED registered. Tax-free childcare and childcare vouchers accepted.",
    times: "Holiday workshops run during school holidays",
    website: "https://stagecoach.co.uk",
    neighbourhoods: ["Crouch End"],
    notes: "Contact stagecoach.co.uk to find the Crouch End local school and holiday workshop dates.",
  },
  {
    id: "ce-12",
    name: "Coleridge Primary Holiday Camp",
    provider: "Premier Education at Coleridge Primary",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Coleridge Primary School, Crouch End Hill, N8 8DN",
    postcodeSector: "N8",
    distanceMiles: 0.4,
    cost: "From £123.50 for after-school clubs · contact for holiday camp pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp run by Premier Education at Coleridge Primary School. Sports, games, creative activities and outdoor play on the school site. Familiar environment for existing Coleridge pupils.",
    times: "Check Premier Education website for holiday camp schedule",
    website: "https://coleridgeprimary.net/parents/wraparound-care/",
    neighbourhoods: ["Crouch End"],
    notes: "20% refer-a-friend discount available. Flexi-booking fee 10% of total. Spaces fill fast.",
  },
  {
    id: "ce-13",
    name: "Rokesly Infant School Holiday Camp",
    provider: "Rokesly Infant School",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Rokesly Infant School, Hermiston Avenue, N8 8NH",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "3–7",
    interests: ["arts","outdoors"],
    description: "Early years holiday camp run at Rokesly Infant School. Play-based activities in a safe, familiar nursery and infant school environment. Also hosts Lingotot Spanish after-school club during term time.",
    times: "Check with school office for holiday dates",
    website: "https://www.littleimaginationscreativearts.co.uk/crouchendcamp",
    neighbourhoods: ["Crouch End"],
    notes: "Priority may be given to existing Rokesly pupils — confirm availability with school office.",
  },
  {
    id: "ce-14",
    name: "Weston Park Primary Holiday Camp",
    provider: "Weston Park Primary School",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "Weston Park Primary School, Weston Park, N8 9PP",
    postcodeSector: "N8",
    distanceMiles: 0.7,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at Weston Park Primary School in the heart of Crouch End. Sports, creative activities and outdoor play in a well-equipped school with good outdoor space close to Priory Park.",
    times: "Check with school office for holiday dates",
    website: "https://www.ekolearning.org.uk/whats-on-eko-2/holiday-clubs/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "ce-15",
    name: "St Aidan's CE Primary Holiday Camp",
    provider: "St Aidan's CE Primary School",
    type: "Camp",
    neighbourhood: "Crouch End",
    location: "St Aidan's CE Primary School, N4 3QW",
    postcodeSector: "N4",
    distanceMiles: 1.1,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday camp at St Aidan's CE Primary. Sports, arts and creative play in a community school setting close to the Crouch End and Stroud Green border.",
    times: "Check with school office for holiday dates",
    website: "https://staidansprimaryschool.org.uk/contact-us/",
    neighbourhoods: ["Crouch End"],
  },


  // ── HIGHGATE ──────────────────────────────────────────────────────────────────
  {
    id: "hg-1",
    name: "Ellie's Holiday Camp at Highgate Primary",
    provider: "Highgate Primary School (Ellie's Camp)",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Highgate Primary School, North Hill, N6 4ED",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Contact school for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Popular school-run holiday camp covering sports, arts and crafts, cooking, gardening and forest school sessions in nearby Highgate Woods. Snacks from Ellie's healthy tuck shop included. Runs Easter, May half term and first two weeks of summer.",
    times: "9am–4:30pm (extended care to 5:30pm available)",
    website: "https://highgateprimaryschool.co.uk/school-life/extended-school-day/holiday-camps/",
    neighbourhoods: ["Highgate"],
    notes: "Packed lunch required — no nuts. Priority for Highgate Primary pupils but open to wider community.",
  },
  {
    id: "hg-2",
    name: "Sparks Film School Highgate",
    provider: "Sparks Film School",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Highgate Primary School, Storey Road, North Hill, N6 4ED",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "From £300/week · animation camps £395/week · sibling discount code: sibling10",
    age: "5–18",
    interests: ["arts","stem","drama"],
    description: "Award-winning film school running since 2012. Filmmaking, animation and screenwriting holiday camps for ages 5–18. Young Filmmakers Lab for ages 15–18. Over 15,000 young directors trained. Max 15 students per group. One-day workshops also available.",
    times: "9am–3pm · term-time Saturday classes 10:30am–12:30pm",
    website: "https://sparksarts.co.uk/about-us/film-school-locations/hampstead/",
    neighbourhoods: ["Highgate"],
    phone: "020 7101 9329 · hello@sparksarts.co.uk",
    notes: "Childcare Vouchers and Childcare Choices accepted. Age groups: Studio 1 (5–7), Studio 2 (7–9), Studio 3 (9–11), Studio 4 (11–14), Studio 5 (14–18).",
  },
  {
    id: "hg-3",
    name: "Mallinson Multi-Activity Camp",
    provider: "Highgate School Mallinson Sports Centre",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Mallinson Sports Centre, Bishopswood Road, N6 4NY",
    postcodeSector: "N6",
    distanceMiles: 0.8,
    cost: "£250/week (5 days) · £200/week (4 days) · early drop £40 · late pick £40",
    age: "5–12",
    interests: ["sports","swimming","arts","outdoors"],
    description: "Premium multi-activity camp at Highgate School's 23-acre grounds. Football, netball, basketball, tennis, rounders, arts and crafts, inflatable obstacle course and swimming. Three-course buffet lunch included. 8 weeks of summer. Split into Years 1–2 and Years 3–7.",
    times: "9:30am–3:30pm (early drop from 8am, late pick to 5pm, each £40 extra)",
    website: "https://www.highgateschool.org.uk/mallinson/holiday-camps/multi-activity-camp/",
    neighbourhoods: ["Highgate"],
    phone: "020 8342 7272 · mallinsoncamps@highgateschool.org.uk",
    notes: "Non-refundable unless medical. No childcare vouchers. Contact via email or phone Mon–Fri 8am–10pm, weekends 8:30am–5pm.",
  },
  {
    id: "hg-4",
    name: "Mallinson Street Dance Camp",
    provider: "Highgate School Mallinson Sports Centre",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Mallinson Sports Centre, Bishopswood Road, N6 4NY",
    postcodeSector: "N6",
    distanceMiles: 0.8,
    cost: "£200/week (4 days) · early drop £32 · late pick £32",
    age: "7–13",
    interests: ["dance","drama","music"],
    description: "Specialist street dance camp run by The Jam London. Mixed-ability classes for Years 3–7. Dance routines, arts and crafts and daily swimming. Final-day performance for parents. Three-course buffet lunch included.",
    times: "9:30am–3:30pm (early drop from 8am, late pick to 5pm available)",
    website: "https://www.highgateschool.org.uk/mallinson/holiday-camps/multi-activity-camp/",
    neighbourhoods: ["Highgate"],
    phone: "020 8342 7272 · mallinsoncamps@highgateschool.org.uk",
    notes: "All abilities welcome. Parents invited to final-day performance.",
  },
  {
    id: "hg-5",
    name: "Mallinson DTE Camp (Design, Tech & Coding)",
    provider: "Highgate School Mallinson Sports Centre",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Mallinson Sports Centre, Bishopswood Road, N6 4NY",
    postcodeSector: "N6",
    distanceMiles: 0.8,
    cost: "Contact for pricing · mallinsoncamps@highgateschool.org.uk",
    age: "7–13",
    interests: ["stem","gaming","arts"],
    description: "Design, Technology and Engineering camp inspiring creativity, problem solving, making and coding skills in a fun and engaging way. Years 3–7. Based at Highgate School's Mallinson Sports Centre.",
    times: "9:30am–3:30pm",
    website: "https://www.highgateschool.org.uk/mallinson/holiday-camps/design-technology-engineering-camp/",
    neighbourhoods: ["Highgate"],
    phone: "020 8342 7272 · mallinsoncamps@highgateschool.org.uk",
  },
  {
    id: "hg-6",
    name: "JAG Holiday HQ — Highgate Pre-Prep",
    provider: "Junior Adventures Group",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Highgate Pre-Prep, 7 Bishopswood Road, N6 4PH",
    postcodeSector: "N6",
    distanceMiles: 0.7,
    cost: "Contact JAG for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club at Highgate Pre-Prep school. Outdoor exploration, sports, team challenges and creative activities. OFSTED No: 2646054. Level 3 qualified Early Years coaches.",
    times: "Full day — check JAG website for times",
    website: "https://www.junioradventuresgroup.co.uk/find-us/highgate-pre-prep/",
    neighbourhoods: ["Highgate"],
    notes: "Tax-Free Childcare, Universal Credit and childcare vouchers accepted. Code OUTDOOR10 for 10% off Easter.",
  },
  {
    id: "hg-7",
    name: "Highgate Art School",
    provider: "Highgate Art School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "162 Archway Road, N6 5BB",
    postcodeSector: "N6",
    distanceMiles: 0.9,
    cost: "£20/class · £100–£210/term · holiday workshops contact for pricing · all materials included",
    age: "13 months–16",
    interests: ["arts"],
    description: "Community-rooted art school since 2018. Nursery art (13m–4y), after-school art fun (5–11), Saturday art club (5–11), teen art class (12–16). Painting, drawing, ceramics, lino cutting, collage and mixed media. Max 8–15 per class. Holiday workshops also available.",
    times: "Tue 9:30am nursery · Wed/Thu after-school 4–5:30pm · Sat 3–4:30pm",
    website: "https://www.highgateartschool.com/",
    neighbourhoods: ["Highgate","Muswell Hill","Crouch End"],
    notes: "Near Highgate Station. Ground-level access, pram and wheelchair friendly. Must book in advance, no drop-ins.",
  },
  {
    id: "hg-8",
    name: "Little Imaginations Early Years Camp — Highgate",
    provider: "Little Imaginations",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.8,
    cost: "Contact for pricing",
    age: "3–6",
    interests: ["arts","drama","outdoors"],
    description: "OFSTED-registered early years creative arts camp designed specifically for children the early years camps often miss. Crafting, treasure hunts, nature play, dance, drama, Lego, messy play and role-play adventures.",
    times: "9am–3pm",
    website: "https://www.littleimaginationscreativearts.co.uk/highgate",
    neighbourhoods: ["Highgate","Muswell Hill","Crouch End"],
    notes: "On Bright Horizons back-up care scheme. Tax-free childcare accepted.",
  },
  {
    id: "hg-9",
    name: "Highgate Woods Forest School",
    provider: "Wild About Our Woods",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Highgate Wood / Queens Wood, N6 5HG",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["outdoors","stem"],
    description: "Forest school holiday camps in and around Highgate Wood and Queens Wood. Led by Janine, qualified Forest School leader with experience at Highgate Primary and local schools. Den building, nature craft, plant identification, woodland art and outdoor exploration in ancient woodland.",
    times: "Full day holiday camps",
    website: "https://www.wildaboutourwoods.com/",
    neighbourhoods: ["Highgate","Crouch End","Muswell Hill"],
    notes: "Monthly Sunday family woodland sessions also available at Queens Wood.",
  },
  {
    id: "hg-10",
    name: "Highgate Cricket Club — Junior Cricket",
    provider: "Highgate Cricket Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate Cricket Club, Shepherd's Cot, off Park Road, N6 4AH",
    postcodeSector: "N6",
    distanceMiles: 0.7,
    cost: "Contact club for holiday camp pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    description: "National award-winning junior cricket programme. Under 9s to Under 17s. All sessions run by ECB-accredited, DBS-checked, First Aid-qualified coaches. Girls teams (U11, U13, U15) who won their leagues in 2025. Holiday camps run alongside the tennis club.",
    times: "Check website for holiday camp dates",
    website: "https://highgate-cricket.co.uk/juniors/",
    neighbourhoods: ["Highgate"],
    phone: "cricket@highgate-cricket.co.uk",
  },
  {
    id: "hg-11",
    name: "Highgate Tennis Holiday Camps",
    provider: "Highgate Cricket & Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Shepherd's Cot, off Park Road, N8 8JJ",
    postcodeSector: "N8",
    distanceMiles: 0.9,
    cost: "Contact for pricing",
    age: "5–16",
    interests: ["sports"],
    description: "Popular holiday tennis camps described as among the best in North London. 12 courts, professional coaches, all abilities welcome. Beautiful surroundings. Half-term and school holiday camps. Children learn new skills and techniques in a fun, social setting.",
    times: "Check website for holiday dates",
    website: "https://highgate-tennis.co.uk/juniors/holiday-camps/",
    neighbourhoods: ["Highgate","Crouch End","Muswell Hill"],
    phone: "Coaching enquiries: 07530 425 871 · Clubhouse: 020 8340 6534",
  },
  {
    id: "hg-12",
    name: "Active Day Camps — Channing Junior School",
    provider: "Active Day Camps",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Channing Junior School (Fairseat), The Bank, N6 5HF",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "From £45/day",
    age: "3–12",
    interests: ["sports","arts","drama","outdoors"],
    description: "Multi-activity camp in the historic Fairseat building next to Waterlow Park. Sports, drama, crafts, yoga, circus skills, taekwondo, LEGO architecture and NERF. Early Years (3–4) and Multi-Activity (4–12) programmes. DBS-checked staff, Level 3 Early Years coaches.",
    times: "9am–4pm (extended care available)",
    website: "https://activedaycamps.com/locations-adc-2/channing-junior-school/",
    neighbourhoods: ["Highgate"],
    notes: "5 mins from Highgate and Archway stations. Spacious grounds, well-equipped indoor facilities.",
  },
  {
    id: "hg-13",
    name: "Active Day Camps — St Michael's CE Primary",
    provider: "Active Day Camps",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "St Michael's CE Primary School, N6 4BG",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "From £45/day",
    age: "3–12",
    interests: ["sports","arts","drama","outdoors"],
    description: "Multi-activity camp at Highgate's hidden gem. Grass and astroturf pitches, soft impact sports pitch, KS1 playground. Sports, drama, crafts, yoga, capoeira, tennis, LEGO architecture and NERF. Early Years (3–4), Multi-Activity (4–11) and Sports (6–12) programmes.",
    times: "9am–4pm (nursery children 9am–2pm per OFSTED · extended care available)",
    website: "https://activedaycamps.com/locations-adc-2/st-michaels-church-primary-school/",
    neighbourhoods: ["Highgate"],
    notes: "All staff have enhanced DBS checks. First Aiders on site.",
  },
  {
    id: "hg-14",
    name: "Mallinson Sports & Swim Camp",
    provider: "Highgate School Mallinson Sports Centre",
    type: "Camp",
    neighbourhood: "Highgate",
    location: "Mallinson Sports Centre, Bishopswood Road, N6 4NY",
    postcodeSector: "N6",
    distanceMiles: 0.8,
    cost: "£250/week (5 days) · £200/week (4 days)",
    age: "5–16",
    interests: ["swimming","sports"],
    description: "Sports and swimming camp with exclusive use of Highgate School's indoor pool and 23-acre grounds. Football, netball, basketball, tennis, rounders and free-play swimming sessions. Three-course buffet lunch included. 8 consecutive weeks in summer.",
    times: "9:30am–3:30pm (early drop from 8am, late pick to 5pm)",
    website: "https://www.highgateschool.org.uk/mallinson/holiday-camps/",
    neighbourhoods: ["Highgate"],
    phone: "020 8342 7272 · mallinsoncamps@highgateschool.org.uk",
    notes: "Non-refundable bookings. No childcare vouchers. Credit notes available for medical absences.",
  },


  // ── EAST FINCHLEY ────────────────────────────────────────────────────────────
  {
    id: "ef-1",
    name: "LBL Holiday Play Scheme",
    provider: "Little Ladybirds London (LBL)",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 9JP",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for pricing · Tax-free childcare & vouchers accepted",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "OFSTED-registered holiday play scheme based at Martin Primary School. High-quality holiday childcare from 8am–6pm. Sibling discounts available.",
    times: "8am–6pm during school holidays",
    website: "https://www.lblclub.co.uk/lblmartin",
    neighbourhoods: ["East Finchley"],
    phone: "07519 804 810",
    notes: "Tax-Free Childcare account searchable as LBL After School Club, postcode N2 9JP.",
  },
  {
    id: "ef-2",
    name: "Fixation Academy — Performing Arts Camp",
    provider: "Fixation Academy of Performing Arts",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 9JP",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "BACE funded (eligible families free) · £20/day extension · Contact for full pricing",
    age: "3–11",
    interests: ["drama","dance","music","arts"],
    description: "Award-winning performing arts camp (Est. 2012). Acting, singing, dance, drama and musical theatre. Week-long summer camps ending in a final performance at Finchley Youth Theatre. OFSTED No: 2740554.",
    times: "9am–3pm (early drop-off 8am, late pick-up 5pm) · BACE hours 9am–1pm",
    website: "https://fixation-academy-of-performing-arts.classforkids.io/",
    neighbourhoods: ["East Finchley"],
    notes: "All major childcare vouchers accepted. No nuts or sesame in packed lunches. Free parking on site.",
  },
  {
    id: "ef-3",
    name: "Martin Primary Multi-Sports Camp",
    provider: "Fixation Academy of Performing Arts",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 9JP",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for pricing · childcare vouchers accepted",
    age: "6–11",
    interests: ["sports","outdoors"],
    description: "Multi-sports holiday camp at Martin Primary School. Football, dodgeball, cricket, tennis and team games. Run alongside the performing arts camp on the same site.",
    times: "9am–3pm (early drop-off 8am, late pick-up 5pm)",
    website: "https://clubhubuk.co.uk/clubs/multi-activity-october-half-term-holiday-camp-martin-primary-east-finchley/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "ef-4",
    name: "Fixation — Performing Arts Camp (Ages 6–11)",
    provider: "Fixation Academy of Performing Arts",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 9JP",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "6–11",
    interests: ["drama","dance","music"],
    description: "Performing arts holiday camp for primary age. Dance routines, singing, acting, drama and musical theatre led by performing arts professionals. Builds confidence and self-expression.",
    times: "9am–3pm",
    website: "https://fixation-academy-of-performing-arts.classforkids.io/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "ef-5",
    name: "Fixation — Early Years Creative Camp",
    provider: "Fixation Academy of Performing Arts",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 9JP",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "3–5",
    interests: ["arts","drama","outdoors"],
    description: "Creative early years holiday camp specifically designed for pre-school and Reception children. Dance routines, songs, arts and crafts, and imaginative make-believe play led by performing arts professionals.",
    times: "9am–3pm (late pick-up to 4pm available)",
    website: "https://fixation-academy-of-performing-arts.classforkids.io/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "ef-6",
    name: "BabyBallers Finchley",
    provider: "BabyBallers",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "Christ's College Sports Hall, East End Road, N2 0SE",
    postcodeSector: "N2",
    distanceMiles: 0.8,
    cost: "Contact for session prices · taster session available",
    age: "16 months–5 years",
    interests: ["sports"],
    description: "Football and multi-skills classes for toddlers. BabyBallers (16m–2.5y), MiniBallers (2.5–3.5y) and Ballers (3.5–5y). Learning through play to develop coordination, social skills and a love of sport. Saturday and Sunday morning sessions.",
    times: "Sat & Sun 9:30–11:30am",
    website: "https://babyballers.com/local-class-academy/babyballers-finchley/",
    neighbourhoods: ["East Finchley"],
    phone: "finchley@babyballers.com · 07459 420947",
  },
  {
    id: "ef-7",
    name: "Finchley Cricket Club Holiday Camp",
    provider: "Rising Stars Activities",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Finchley Cricket Club, East End Road, N3 2TA",
    postcodeSector: "N3",
    distanceMiles: 1.5,
    cost: "From £51/day",
    age: "5–16",
    interests: ["sports","outdoors","arts","drama"],
    description: "Large outdoor holiday camp at Finchley Cricket Club. Football, cricket, dodgeball, tennis, arts & crafts, drama, gymnastics and playground games. OFSTED registered. Childcare vouchers accepted.",
    times: "10am–3pm (extended care available)",
    website: "https://risingstarsactivities.com/holiday-camps/finchley-cricket-club/",
    neighbourhoods: ["East Finchley","Whetstone"],
  },
  {
    id: "ef-8",
    name: "Kids Do Art",
    provider: "Kids Do Art",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "The Sternberg Centre, 80 East End Road, N3 2SY",
    postcodeSector: "N3",
    distanceMiles: 1.2,
    cost: "Contact for pricing",
    age: "4–16",
    interests: ["arts"],
    description: "Fun art school teaching drawing, painting and sculpture. After-school, weekend and holiday workshops at the Sternberg Centre. Also runs at King Alfred School (Hampstead) and St Michael's Highgate. DBS checked, insured, first-aid certified tutors.",
    times: "After-school, weekends and school holidays",
    website: "https://kidsdoart.co.uk/index.html",
    neighbourhoods: ["East Finchley","Hampstead","Highgate"],
  },
  {
    id: "ef-9",
    name: "The Art Stables",
    provider: "The Art Stables",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "The Sternberg Centre, 80 East End Road, N3 2SY",
    postcodeSector: "N3",
    distanceMiles: 1.2,
    cost: "Contact for pricing · 020 3302 5525",
    age: "5–16",
    interests: ["arts"],
    description: "Bright and airy art studios in converted stables at the Sternberg Centre. Oil painting, acrylics, watercolour, portrait painting, clay sculpture, lino cut and children's holiday workshops. Sunday morning children's clubs also available.",
    times: "Weekday after-school, Sunday mornings, holiday workshops",
    website: "https://www.artstables.co.uk/",
    neighbourhoods: ["East Finchley"],
    phone: "info@artstables.co.uk · 020 3302 5525",
  },
  {
    id: "ef-10",
    name: "The Learning Experience East Finchley",
    provider: "The Learning Experience",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley, N2 8AU",
    postcodeSector: "N2",
    distanceMiles: 0.7,
    cost: "Contact for pricing",
    age: "6 weeks–5 years",
    interests: ["outdoors","arts"],
    description: "Premium early years day school and nursery. Play-based learning with a nature-inspired curriculum. Holiday childcare available for nursery-age children in a safe and nurturing environment.",
    times: "Full day nursery hours",
    website: "https://thelearningexperience.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "ef-11",
    name: "Rising Stars at Archer Academy",
    provider: "Rising Stars Activities",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Archer Academy, Eagans Close, N2 8GA",
    postcodeSector: "N2",
    distanceMiles: 0.4,
    cost: "From £51/day · £209/week",
    age: "4–11",
    interests: ["sports","arts","drama","gaming"],
    description: "Popular multi-activity camp at Archer Academy. Football, dodgeball, drama, chess and creative games. OFSTED registered. DBS-checked coaches. Childcare vouchers accepted.",
    times: "10am–3pm (drop-off from 9am, extended to 4pm available)",
    website: "https://risingstarsactivities.com/holiday-camps/archer-academy/",
    neighbourhoods: ["East Finchley","Muswell Hill","Hampstead"],
  },
  {
    id: "ef-12",
    name: "Woodcraft Folk — East Finchley Elfins",
    provider: "Woodcraft Folk",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.6,
    cost: "Small weekly session fee · membership available",
    age: "6–9",
    interests: ["outdoors","arts","music"],
    description: "Weekly Elfins group for 6–9 year olds. Active games, music, arts, crafts, drama and group discussions. Builds confidence and social awareness. Part of the national Woodcraft Folk co-operative youth movement.",
    times: "Weekly term-time sessions (check with local group for day/time)",
    website: "https://woodcraft.org.uk/",
    neighbourhoods: ["East Finchley"],
    notes: "Contact via woodcraft.org.uk to find the East Finchley group.",
  },
  {
    id: "ef-13",
    name: "The Strings Club — Holy Trinity South Hampstead",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Holy Trinity School, South Hampstead, NW3 5SQ",
    postcodeSector: "NW3",
    distanceMiles: 2.1,
    cost: "Contact for pricing · 10% off with code A4K25",
    age: "4–11",
    interests: ["music","arts","dance","drama"],
    description: "Multi-activity camp with music focus at Holy Trinity School South Hampstead. Electric guitar, upcycled instruments, dance and creative arts. OFSTED registered. All childcare vouchers accepted.",
    times: "Flexible hours",
    website: "https://www.thestringsclub.org/pages/crouch-end-holiday-camp",
    neighbourhoods: ["East Finchley","Hampstead"],
  },
  {
    id: "ef-14",
    name: "Powerleague Finchley — Kids Football Camp",
    provider: "Powerleague",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Summers Lane, Barnet, N12 0RF",
    postcodeSector: "N12",
    distanceMiles: 2.3,
    cost: "Contact for holiday camp pricing · finchley@powerleague.com",
    age: "5–14",
    interests: ["sports"],
    description: "Football holiday camps on professional 3G astroturf pitches at Powerleague Finchley. Skills sessions, small-sided games and matches for all abilities. Floodlit facility with free parking.",
    times: "Check website for holiday camp dates",
    website: "https://www.powerleague.com/football-holiday-camps",
    neighbourhoods: ["East Finchley","Whetstone"],
    phone: "0208 361 8658 · finchley@powerleague.com",
  },
  {
    id: "ef-15",
    name: "Mini Minors & XUK Day Camp",
    provider: "XUK Camps",
    type: "Camp",
    neighbourhood: "East Finchley",
    location: "Brookland School, Off Hill Top, NW11 6EJ",
    postcodeSector: "NW11",
    distanceMiles: 1.8,
    cost: "£200/week (9:15am–3:15pm) · £160/week half days · daily booking available",
    age: "3–12",
    interests: ["sports","arts","drama","outdoors","gaming"],
    description: "Outstanding multi-activity holiday day camps. Mini Minors (3–6) and XUK Day (7–12) with age-appropriate activities including discos, parachute games, football, rocket-making and more. Running at Brookland School since 1996. OFSTED registered.",
    times: "9:15am–3:15pm (lates until 4:15pm available)",
    website: "https://www.xukcamps.com/day-camps/venue/",
    neighbourhoods: ["East Finchley"],
    phone: "020 8371 9686 · office@xukcamps.com",
    notes: "Closest tube: East Finchley (15 min walk). Bus 102 stops at Brookland Rise.",
  },

  // ── MUSWELL HILL ─────────────────────────────────────────────────────────────
  {
    id: "mh-1",
    name: "Planet Youth Holiday Club",
    provider: "Planet Youth",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Hollickwood Primary School, Sydney Road, N10 2NL",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "From £35/day",
    age: "4–11",
    interests: ["sports","arts","drama","outdoors"],
    description: "Mini Adventurers (4–7) and Kids Camp (6–11). Arts & crafts, survival skills, sports, science, dance, drama and water fun. Extended care 8am–6pm.",
    times: "10am–3pm (early club 8am, late club until 6pm)",
    website: "https://planetyouth.classforkids.io/",
    neighbourhoods: ["Muswell Hill"],
    phone: "07869 213606",
    notes: "OFSTED registered. Tax-free childcare and childcare vouchers accepted.",
  },
  {
    id: "mh-2",
    name: "The Strings Club Holiday Camp",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Eden Primary School, 79 Creighton Avenue, N10 1NR",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Contact for pricing · 10% off with code A4K25",
    age: "4–11",
    interests: ["music","arts","dance","drama"],
    description: "Multi-activity camp with a music focus — electric guitar, creative arts and dance. Daily newsletters sent to parents at 6pm. Flexible hours.",
    times: "Flexible hours available",
    website: "https://www.thestringsclub.org/collections",
    neighbourhoods: ["Muswell Hill","Crouch End","Hampstead"],
    notes: "OFSTED registered. All childcare vouchers and tax-free childcare accepted.",
  },
  {
    id: "mh-3",
    name: "Coldfall Primary School Holiday Camp",
    provider: "Coldfall Primary School",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Coldfall Primary School, N10 1HS",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "School-run multi-activity holiday camp. Familiar setting for existing pupils. Check with the school office for current holiday dates and pricing.",
    times: "Check with school",
    website: "https://www.coldfall.haringey.sch.uk/children-and-families/childrens-health-and-welfare/holiday-camp",
    neighbourhoods: ["Muswell Hill"],
    phone: "020 8883 1395",
  },
  {
    id: "mh-4",
    name: "Art Pole",
    provider: "Art Pole",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Various school venues, Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "5–17",
    interests: ["arts","stem"],
    description: "Fine art, design and architecture club for young people. Explores art history, sculpture, painting and design using pencils, pens, paint and more. Sessions in local schools.",
    times: "Check website for schedule",
    website: "https://www.artpole.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mh-5",
    name: "Fortismere Holiday Camp",
    provider: "Fortismere School",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Fortismere School, Tetherdown, N10 1NE",
    postcodeSector: "N10",
    distanceMiles: 0.3,
    cost: "From £100/week (activity dependent)",
    age: "3–15",
    interests: ["arts","stem","drama","sports","music"],
    description: "Wide range of camps: Art Club, Chess, Robotics & Coding, Drama, Dance, Comic Camp, Multi-Sports and Tiny Twinkles (3–6, creative arts). 4.3 stars on Google.",
    times: "9am–3pm (early drop-off from 8am)",
    website: "https://www.fortismere.haringey.sch.uk/page/?title=HOLIDAY+CAMPS&pid=284",
    neighbourhoods: ["Muswell Hill"],
    phone: "Email mdemetriou@fortismere.org.uk",
    notes: "Contact Ms Demetriou, Extended Schools Manager, for bookings.",
  },
  {
    id: "mh-6",
    name: "Fun in the Forest",
    provider: "Fun in the Forest",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Coldfall Woods, Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.7,
    cost: "Contact for pricing",
    age: "5–11",
    interests: ["outdoors","arts"],
    description: "Forest school holiday camps in Coldfall Woods since 2012. Den building, rope swings, foraging, whittling, woodland art and outdoor games. Run by qualified Forest School leader Ivana Grant.",
    times: "Full day camps",
    website: "https://www.funintheforest.co.uk/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Child-centred outdoor approach. Focuses on wellbeing and nature connection.",
  },
  {
    id: "mh-7",
    name: "Forest School at Kidz Kabin",
    provider: "Kidz Kabin",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Kidz Kabin Fortismere, opposite Coldfall Woods N10",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["outdoors","stem"],
    description: "Forest school holiday camps run by Linda Symons, Advanced Forest School Leader. Woodwork, shelter building, foraging, plant identification and woodland art next to Coldfall Woods.",
    times: "Check website for holiday dates",
    website: "https://www.forestschoolkidzkabin.com/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Skills-based outdoor learning. Run by Kidz Kabin nursery owner.",
  },
  {
    id: "mh-8",
    name: "Kidz Kabin Nursery Holiday Care",
    provider: "Kidz Kabin",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Pembroke Studios, Muswell Hill N10 1BN",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for pricing",
    age: "6 months–5 years",
    interests: ["outdoors","arts"],
    description: "Trusted family-run nursery since 1996 offering holiday childcare. Forest school, outdoor play, home-cooked meals. Ofsted outstanding. Primary school children also welcome for Forest School camps.",
    times: "Standard nursery hours",
    website: "https://www.kidzkabin.com/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mh-9",
    name: "Little Imaginations Early Years Camp",
    provider: "Little Imaginations",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Crouch End / Muswell Hill (check website for venue)",
    postcodeSector: "N10",
    distanceMiles: 1.0,
    cost: "Contact for pricing",
    age: "3–6",
    interests: ["arts","drama","outdoors"],
    description: "OFSTED-registered early years creative arts camp. Crafting, treasure hunts, nature play, dance, drama, Lego, messy play and role-play adventures. Designed specifically for children the early years camps often miss.",
    times: "9am–3pm",
    website: "https://www.littleimaginationscreativearts.co.uk/crouchendcamp",
    neighbourhoods: ["Muswell Hill","Crouch End"],
    notes: "On Bright Horizons back-up care scheme. Tax-free childcare accepted.",
  },
  {
    id: "mh-10",
    name: "KidsArt! Muswell Hill",
    provider: "KidsArt!",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "50 Hillfield Park Mews, N10 3QR",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact for pricing",
    age: "4–16",
    interests: ["arts"],
    description: "Art classes and holiday workshops. Separate sessions for ages 4–8 and 9+. Weekday afternoons and Saturday mornings.",
    times: "Weekday afternoons 4pm onwards · Sat mornings 9am",
    website: "https://kidsartuk.com/muswell-hill",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mh-11",
    name: "JAG Holiday HQ",
    provider: "Junior Adventures Group",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Our Lady of Muswell RC Primary, Pages Lane, N10 1PS",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Contact JAG for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club. Outdoor exploration, creativity with natural materials, team challenges, sports and arts & crafts. OFSTED registered.",
    times: "8:30am–5:30pm",
    website: "https://www.junioradventuresgroup.co.uk/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Tax-Free Childcare, Universal Credit and childcare vouchers all accepted.",
  },
  {
    id: "mh-12",
    name: "Energy Kids Holiday Club",
    provider: "Energy Kids",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Our Lady of Muswell Catholic Primary, N10 1PS",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Contact for pricing",
    age: "4–11",
    interests: ["sports","arts"],
    description: "After-school and holiday club at Our Lady of Muswell. Wraparound care with activities and snacks in a safe and fun environment.",
    times: "Check with provider",
    website: "https://www.junioradventuresgroup.co.uk/find-us/our-lady-of-muswell-catholic-primary-school/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Contact school office for holiday availability.",
  },
  {
    id: "mh-13",
    name: "9th Muswell Hill Scouts",
    provider: "Scout Association",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Membership fees apply",
    age: "6–14",
    interests: ["outdoors","sports","stem"],
    description: "Beavers (6–8), Cubs (8–10) and Scouts (10–14) with outdoor skills, camping, teamwork and badge activities throughout the year including holiday camps.",
    times: "Weekly meetings plus holiday activities",
    website: "https://9thmuswellhill.org.uk/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Contact via scouts.org.uk to register interest.",
  },
  {
    id: "mh-14",
    name: "Fortis Green Kids Club",
    provider: "Fortis Green Kids Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Primary School area, N10 2PT",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Paid in advance — contact for pricing",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "Popular local after-school and holiday club. Snacks provided, school pickups from N10 schools available. Active games, creative activities and free play.",
    times: "After school hours and holiday periods",
    website: "https://www.fortisgreenkidsclub.org/",
    neighbourhoods: ["Muswell Hill"],
    notes: "Childcare vouchers accepted. Advance booking required. No cancellations.",
  },
  {
    id: "mh-15",
    name: "The Breezy Club",
    provider: "The Breezy Club",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "N11 2AZ (Friern Barnet / Muswell Hill border)",
    postcodeSector: "N11",
    distanceMiles: 1.2,
    cost: "Contact for pricing",
    age: "4–12",
    interests: ["sports","arts","outdoors"],
    description: "Multi-activity holiday club covering arts, sports, games and outdoor activities for primary-age children in a welcoming and inclusive environment.",
    times: "Check website for holiday dates",
    website: "https://www.thebreezyclub.co.uk/",
    neighbourhoods: ["Muswell Hill","Whetstone"],
  },
  {
    id: "mh-16",
    name: "Alexandra Park BJJ",
    provider: "Alexandra Park BJJ",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Alexandra Park School, Rhodes Avenue, N22 7UT",
    postcodeSector: "N22",
    distanceMiles: 1.5,
    cost: "6-week intro course available — contact for pricing",
    age: "6–18",
    interests: ["sports"],
    description: "Brazilian Jiu-Jitsu for children 6+ (Saturdays 12–1pm) and teens. Builds fitness, balance, coordination, confidence and self-discipline. Founded 2013. Free intro lesson available.",
    times: "Saturday kids 12–1pm · Adult sessions Mon/Thu evenings",
    website: "https://www.alexandraparkbjj.co.uk/",
    neighbourhoods: ["Muswell Hill"],
    phone: "enquiries@alexandraparkbjj.co.uk",
  },
  {
    id: "mh-17",
    name: "Muswell Hill Primary Holiday Club",
    provider: "Muswell Hill Primary School",
    type: "Camp",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Primary School, N10 3ST",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact school directly",
    age: "4–11",
    interests: ["sports","arts","outdoors"],
    description: "School-run holiday club in a familiar setting. Multi-activity programme for primary-age children. Priority may be given to existing pupils.",
    times: "Check with school office",
    website: "https://www.muswellhillprimary.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mh-18",
    name: "The Muswell Hill Club — Junior Swim",
    provider: "The Muswell Hill Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "140 Fortis Green, N10 3EF",
    postcodeSector: "N10",
    distanceMiles: 0.3,
    cost: "Junior membership £35/month · 1-2-1 swim lessons available",
    age: "4–17",
    interests: ["swimming","sports"],
    description: "Independent gym with 10x12m heated pool. Junior membership for 16–17 year olds. 1-2-1 STA-qualified swim lessons for all ages and abilities.",
    times: "Mon–Fri 6:30am–10pm · Sat 8am–7pm · Sun 8am–5pm",
    website: "https://www.swem.uk/children-swimming-lessons-muswell-hill",
    neighbourhoods: ["Muswell Hill"],
    phone: "020 8883 0500",
  },
  {
    id: "mh-19",
    name: "Muswell Hill Centre Youth Activities",
    provider: "Muswell Hill Centre",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Centre, N10 3QJ",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact centre for pricing",
    age: "8–18",
    interests: ["arts","stem","sports"],
    description: "Youth activities, workshops and programmes for older children and teenagers. Creative, social and skills-based sessions in a community setting.",
    times: "Check centre for current schedule",
    website: "https://muswellhillcentre.org.uk/club-youth/club-youth-info",
    neighbourhoods: ["Muswell Hill"],
  },













];

export const NORTH_LONDON_NEIGHBOURHOODS = [
  "Muswell Hill", "East Finchley", "Crouch End", "Whetstone & Totteridge", "Hampstead", "Hampstead Garden Suburb", "Islington & Highbury", "Highgate", "Finchley", "North Finchley"
];

// ─── South West London Holiday Camps ───────────────────────────────────────────
// Same shape as NORTH_LONDON_CAMPS. distanceMiles measured from Clapham Common
// as the central SW London reference point — flag if a different point is preferred.
export const SOUTH_WEST_LONDON_CAMPS = [

  // ── BALHAM ───────────────────────────────────────────────────────────────────
  {
    id: "bal-1",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Balham",
    location: "Trinity St Mary's CE Primary School, SW12 8DR",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "arts"],
    description: "Music and multi-activity holiday camp at Trinity St Mary's CE Primary School in Balham. Combines music-making sessions with a broader mix of creative and active holiday activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/collections/balham",
    neighbourhoods: ["Balham"],
  },
  {
    id: "bal-2",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Balham",
    location: "La Retraite Roman Catholic Girls' School, SW12 0AB",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at La Retraite Roman Catholic Girls' School in Balham. Sports, games and creative activities for a wide primary and secondary age range.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/laretraiteschool/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "bal-3",
    name: "Schools Out Holiday Club — Ravenstone",
    provider: "Schools Out Holiday Club",
    type: "Camp",
    neighbourhood: "Balham",
    location: "Ravenstone Primary School, SW12 9SS",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "3–11",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday club at Ravenstone Primary School in Balham. Games, sports and creative sessions for younger primary-age children.",
    times: "Check with provider for holiday dates",
    website: "https://www.balhamcommunitycentre.com/schools-out-club-ravenstone",
    neighbourhoods: ["Balham"],
  },
  {
    id: "bal-4",
    name: "Schools Out Holiday Club — Balham Community Centre",
    provider: "Schools Out Holiday Club",
    type: "Camp",
    neighbourhood: "Balham",
    location: "Balham Community Centre, SW12 9HE",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "3–11",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday club at Balham Community Centre. Games, sports and creative sessions for younger children in the heart of Balham.",
    times: "Check with provider for holiday dates",
    website: "https://www.balhamcommunitycentre.com/schools-out-holiday-club-balham",
    neighbourhoods: ["Balham"],
  },
  {
    id: "bal-5",
    name: "Crazy Camps",
    provider: "Crazy Camps",
    type: "Camp",
    neighbourhood: "Balham",
    location: "The Elms Academy, SW4 9ET",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at The Elms Academy, close to the Balham/Clapham border. Sports, games and creative activities for primary and early secondary-age children.",
    times: "Check with provider for holiday dates",
    website: "https://www.crazycamps.co.uk/",
    neighbourhoods: ["Balham"],
  },

  // ── BARNES ───────────────────────────────────────────────────────────────────
  {
    id: "brn-1",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "St Paul's School, Lonsdale Road, SW13 9JT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "swimming", "arts", "outdoors"],
    description: "Multi-activity holiday camp at St Paul's School in Barnes. Sports, giant inflatables, swimming, archery tag, pedal karts, arts & crafts and team games across a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/stpaulsschool/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-2",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "St Paul's School, Lonsdale Road, SW13 9JT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "arts", "swimming"],
    description: "Ultimate Activity Camps at St Paul's School in Barnes. Sports, arts & crafts, team challenges and swimming available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-3",
    name: "NSSport Holiday Camp",
    provider: "NSSport",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "St Osmund's Catholic Primary School, Church Road, SW13 9HQ",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports"],
    description: "Football and multi-sports holiday camp at St Osmund's Catholic Primary School in Barnes. Dodgeball, team games and active play for younger children.",
    times: "Check with provider for holiday dates",
    website: "https://nssport.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-4",
    name: "Tennis4Barnes Holiday Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Holiday camp at Barn Elms Sports Trust combining tennis, cricket, football games and multi-sports for a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-5",
    name: "Tennis4Barnes Tennis Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused camp at Barn Elms Sports Trust. Coaching, drills and match play for children building up their tennis skills.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-6",
    name: "Tennis4Barnes Tennis & Cricket Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Combined tennis and cricket camp at Barn Elms Sports Trust, with fitness games worked in throughout the week.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/findus/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-7",
    name: "SkillsCoach Holiday Camp",
    provider: "SkillsCoach",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports"],
    description: "Football and multi-sports holiday camp at Barn Elms Sports Trust in Barnes, with team games throughout.",
    times: "Check with provider for holiday dates",
    website: "https://skills-coach.com/programmes/holiday-camps/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-8",
    name: "Barnes Tennis Academy Holiday Camps",
    provider: "Barnes Tennis Academy",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Rocks Lane Sports Centre, SW13 0BY",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports"],
    description: "Tennis coaching camp at Rocks Lane Sports Centre run by Barnes Tennis Academy. Coaching, match play and fitness for a broad age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "http://barnestennisacademy.com/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-9",
    name: "Barn Elms Sports Centre Holiday Camps",
    provider: "Barn Elms Sports Centre",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "Rocks Lane, SW13 0DG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Barn Elms Sports Centre. Tennis, football, athletics and cricket across the week.",
    times: "Check with provider for holiday dates",
    website: "https://www.enableleisure.co.uk/barn-elms-sports-centre-gym",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "brn-10",
    name: "Barnes Summer Play School",
    provider: "Barnes Summer Play School",
    type: "Camp",
    neighbourhood: "Barnes",
    location: "3A Station Road, SW13 0LF",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["arts", "outdoors"],
    description: "Creative summer play school on Station Road in Barnes. Arts & crafts, outdoor play and games in a relaxed, playful setting.",
    times: "Check with provider for holiday dates",
    website: "https://www.essentialsurrey.co.uk/locations/barnes-summer-play-school/",
    neighbourhoods: ["Barnes"],
  },

  // ── BATTERSEA ────────────────────────────────────────────────────────────────
  {
    id: "btt-1",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Emanuel School, Battersea Rise, SW11 1HS",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "drama", "swimming"],
    description: "Multi-activity holiday camp at Emanuel School in Battersea. Sports, arts & crafts, drama and team challenges, with swimming available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/london-clapham",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-2",
    name: "SuperCamps",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Thomas's Battersea, 28–40 Battersea High Street, SW11 3JB",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts", "stem", "outdoors"],
    description: "Multi-activity holiday camp at Thomas's Battersea. Sports, arts & crafts, games, STEM activities and outdoor play across the week.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Thomas's Battersea, SW11 3JB",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Thomas's Battersea. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/pages/balham-holiday-camp",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-4",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Thomas's Battersea, SW11 3JB",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Thomas's Battersea. Sports, creative arts, outdoor games and themed activity days for a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/county/london-day-camps",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-5",
    name: "The Little Gym Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "The Little Gym Wandsworth, 52 Wandsworth Bridge Road, SW6 2TF",
    postcodeSector: "SW6",
    distanceMiles: 2.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports", "arts"],
    description: "Gymnastics-based holiday camp at The Little Gym Wandsworth. Movement, games, obstacle courses and crafts for younger children.",
    times: "Check with provider for holiday dates",
    website: "https://wandsworthfulham.thelittlegym.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-6",
    name: "Love Basketball Holiday Camps",
    provider: "Love Basketball",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Caius House Youth Centre, 2 Holman Road, SW11 3RL",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Basketball-focused holiday camp at Caius House Youth Centre in Battersea. Coaching, fitness, competitions and team games.",
    times: "Check with provider for holiday dates",
    website: "http://www.lovebasketball.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-7",
    name: "Premier Education Holiday Camps",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Sacred Heart RC Primary School, Este Road, SW11 2TB",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Sacred Heart RC Primary School in Battersea. Archery, dodgeball, football and arts & crafts sessions.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-8",
    name: "Fit For Sport Holiday Camp",
    provider: "Fit For Sport",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Harris Academy Battersea, 401 Battersea Park Road, SW11 5AP",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Harris Academy Battersea. Active games, creative play and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://fit4sport.org.in/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-9",
    name: "Activate Camps – Tech Camp",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Emanuel School, SW11 1HS",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "8–14",
    interests: ["stem", "gaming"],
    description: "Tech-focused holiday camp at Emanuel School in Battersea. Coding, robotics, Minecraft and game design for older children.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-10",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "St Michael's Church Hall, Cobham Close, SW11 6SP",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama", "music"],
    description: "Drama-focused holiday course at St Michael's Church Hall in Battersea. Storytelling, movement and singing for younger children.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-11",
    name: "Stagecoach Holiday Workshop",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Newton Preparatory School, 149 Battersea Park Road, SW8 4BX",
    postcodeSector: "SW8",
    distanceMiles: 1.1,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Newton Preparatory School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-12",
    name: "Kiddikicks Holiday Football Camp",
    provider: "Kiddikicks",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Battersea Park Millennium Arena, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports"],
    description: "Football holiday camp at Battersea Park Millennium Arena. Coaching, skills sessions, matches and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.kiddikicks.co.uk/timetable",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-13",
    name: "Rockley Adventure Day Camps",
    provider: "Rockley Watersports",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Battersea Park, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Outdoor adventure day camp in Battersea Park. Kayaking, paddleboarding and canoeing for older children and teens.",
    times: "Check with provider for holiday dates",
    website: "https://rockley.org/courses-activities/watersports-courses/kids-holiday-camps",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-14",
    name: "Go Mammoth Kids Holiday Camps",
    provider: "Go Mammoth",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "Battersea Sports Centre, Hope Street, SW11 2DA",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–13",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Battersea Sports Centre. Football, basketball, dodgeball and games throughout the week.",
    times: "Check with provider for holiday dates",
    website: "https://www.gomammoth.co.uk/multi-sports/battersea-park-summer/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "btt-15",
    name: "Art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Battersea",
    location: "art-K Battersea, 8 Grant Road, SW11 2NU",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop in Battersea. Drawing, painting, sculpture and mixed media for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/class-wandsworth/",
    neighbourhoods: ["Battersea"],
  },

  // ── CHISWICK ─────────────────────────────────────────────────────────────────
  {
    id: "chw-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Chiswick School, Staveley Road, W4 3UN",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "stem", "outdoors", "swimming"],
    description: "Multi-activity holiday camp at Chiswick School. Sports, arts & crafts, coding, movie making, golf and aqua Olympics across a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-2",
    name: "Camp Beaumont Playtime",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Chiswick School, Staveley Road, W4 3UN",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "3–4",
    interests: ["arts", "outdoors"],
    description: "EYFS-focused holiday session at Chiswick School for the youngest children. Crafts, sensory play and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/programmes/playtime",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-3",
    name: "Après School Summer Camp",
    provider: "Après School",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "King's House Sports Ground, Riverside Drive, W4 2SH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "3–11",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-sports summer camp at King's House Sports Ground. Games, creative activities and outdoor adventures by the river.",
    times: "Check with provider for holiday dates",
    website: "https://www.apres-school.co.uk/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-4",
    name: "Rocks Lane Holiday Courses",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Rocks Lane Chiswick, Cavendish Road, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Multi-sports holiday course at Rocks Lane Chiswick. Football, tennis, padel and netball across the week.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-5",
    name: "Rocks Lane Football Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football-focused camp at Rocks Lane Chiswick. Coaching, skills sessions and matches.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/football/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-6",
    name: "Rocks Lane Tennis Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused camp at Rocks Lane Chiswick. Coaching, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/bishops-park/holiday-courses/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-7",
    name: "Rocks Lane Padel Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["sports"],
    description: "Padel-focused camp at Rocks Lane Chiswick. Coaching, games and fitness for older children and teens.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/padel/padel-holiday-camps/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-8",
    name: "Rocks Lane Netball Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["sports"],
    description: "Netball-focused camp at Rocks Lane Chiswick. Coaching, matches and skills sessions.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/netball/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-9",
    name: "The Hogarth Youth & Community Centre Holiday Club",
    provider: "The Hogarth Youth & Community Centre",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Duke Road, W4 2JR",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["arts", "sports", "cooking"],
    description: "Community holiday club on Duke Road in Chiswick. Arts & crafts, sports, cooking sessions and trips out.",
    times: "Check with provider for holiday dates",
    website: "https://hogarthtrust.org.uk/youth-programme/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-10",
    name: "The Little Gym Chiswick Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Belmont House, Belmont Road, W4 5UL",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports", "arts"],
    description: "Gymnastics-based holiday camp at Belmont House in Chiswick. Movement, obstacle courses, games and crafts.",
    times: "Check with provider for holiday dates",
    website: "https://chiswick.thelittlegym.co.uk/day-camps",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-11",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "St Michael & All Angels Church Hall, Elmwood Road, W4 3DY",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St Michael & All Angels Church Hall in Chiswick. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-12",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "ArtsEd, Bath Road, W4 1LY",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at ArtsEd in Chiswick. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-13",
    name: "Art-K Chiswick Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "art-K Chiswick Studio, W4 5TF",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the art-K Chiswick Studio. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-14",
    name: "Mad Science Holiday Camps",
    provider: "Mad Science",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Latymer Upper School Sports Centre, W6 9LR",
    postcodeSector: "W6",
    distanceMiles: 4.6,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["stem"],
    description: "Science-focused holiday camp at Latymer Upper School Sports Centre, serving Chiswick families. Experiments, engineering, STEM and chemistry.",
    times: "Check with provider for holiday dates",
    website: "https://hertsbeds.madscience.org/parents-camps",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "chw-15",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Chiswick",
    location: "Code Ninjas Chiswick, W4 5YA",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at Code Ninjas Chiswick. Robotics, Minecraft, Roblox and game design.",
    times: "Check with provider for holiday dates",
    website: "https://www.codeninjas.co.uk/enfield-london-uk",
    neighbourhoods: ["Chiswick"],
  },

  // ── CLAPHAM ──────────────────────────────────────────────────────────────────
  {
    id: "clp-1",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Eaton House The Manor, Clapham Common North Side, SW4 9RU",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Eaton House The Manor, overlooking Clapham Common. Sports, arts & crafts, team games and creative workshops.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/london-clapham",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-2",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Emanuel School, Battersea Rise, SW11 1HS",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "swimming"],
    description: "Multi-sports holiday camp at Emanuel School. Inflatables, archery tag, arts & crafts, swimming and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/emanuel-school/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-3",
    name: "Crazy Camps",
    provider: "Crazy Camps",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "The Elms Academy, Elms Road, SW4 9ET",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at The Elms Academy in Clapham. Laser tag, inflatables, games and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.crazycamps.co.uk/theelmsacademy",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-4",
    name: "Crazy Camps",
    provider: "Crazy Camps",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "St Francis Xavier College, 10 Malwood Road, SW12 8EN",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at St Francis Xavier College. Games, inflatables and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.activ-ts.co.uk/activity/clapham-camps-2/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-5",
    name: "Activ-T's Holiday Camp",
    provider: "Activ-T's",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Clapham Common West Side, SW4 9AN",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "3½–12",
    interests: ["sports", "outdoors"],
    description: "Football and multi-sports holiday camp on Clapham Common West Side. Outdoor games and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.activ-ts.co.uk/activity/clapham-camps-2/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-6",
    name: "Sport Society Multi-Sports Camp",
    provider: "Sport Society",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Thomas's Clapham, Broomwood Road, SW11 6JZ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–13",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Thomas's Clapham. PE games, skill development and competitions.",
    times: "Check with provider for holiday dates",
    website: "https://sport-society.classforkids.io/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-7",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Clapham venue (seasonal school venue), SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at a seasonal school venue in Clapham. Sports, arts & crafts and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-8",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Thomas's Clapham, SW11 6JZ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts"],
    description: "Music-focused holiday camp at Thomas's Clapham. Guitar, ukulele and violin sessions alongside drama, dance and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/pages/balham-holiday-camp",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-9",
    name: "SuperCamps",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Thomas's Clapham, SW11 6JZ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "stem", "arts"],
    description: "Multi-activity holiday camp at Thomas's Clapham. Sports, STEM activities and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-10",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "St Paul's Church Hall, Rectory Grove, SW4 0DZ",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St Paul's Church Hall in Clapham. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/drama-classes/clapham-south",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-11",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Clapham Manor Primary School, SW4 9DB",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Clapham Manor Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-12",
    name: "Little House of Science Holiday Camps",
    provider: "Little House of Science",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Clapham venue, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["stem"],
    description: "Science-focused holiday camp in Clapham. Experiments, engineering and robotics.",
    times: "Check with provider for holiday dates",
    website: "https://portal.littlehouseofscience.com/classes/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-13",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Code Ninjas Clapham, SW4 7SS",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at Code Ninjas Clapham. Roblox, Minecraft, game development and robotics.",
    times: "Check with provider for holiday dates",
    website: "https://www.codeninjas.com/camps",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-14",
    name: "art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "art-K Clapham, SW4 6DZ",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Clapham. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/class-clapham/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "clp-15",
    name: "Kiddikicks Holiday Football Camp",
    provider: "Kiddikicks",
    type: "Camp",
    neighbourhood: "Clapham",
    location: "Clapham Common, SW4 9DE",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["sports"],
    description: "Football holiday camp on Clapham Common. Coaching, skills sessions, matches and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.kiddikicks.co.uk/Venuefinder",
    neighbourhoods: ["Clapham"],
  },

  // ── EARLSFIELD ───────────────────────────────────────────────────────────────
  {
    id: "efd-1",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Burntwood School, Burntwood Lane, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-activity holiday camp at Burntwood School in Earlsfield. Giant inflatables, summer swimming, archery tag, pedal karts, arts & crafts and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/burntwoodschool/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-2",
    name: "Crazy Camps",
    provider: "Crazy Camps",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Ernest Bevin Academy, Beechcroft Road, SW17 7DF",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Laser tag and multi-sports holiday camp at Ernest Bevin Academy. Inflatables, Nerf games, arts & crafts and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.crazycamps.co.uk/ernestbevin",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-3",
    name: "Crazy Camps",
    provider: "Crazy Camps",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Fircroft Primary School, Fircroft Road, SW17 7PP",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Laser tag and multi-sports holiday camp at Fircroft Primary School. Inflatables, games and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.crazycamps.co.uk/ernestbevin",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-4",
    name: "Playball Holiday Camps",
    provider: "Playball",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "The Spencer Club, Fieldview, SW18 3HF",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–8",
    interests: ["sports"],
    description: "Multi-sports holiday camp at The Spencer Club for younger children. Movement skills, ball games, coordination and obstacle courses.",
    times: "Check with provider for holiday dates",
    website: "https://www.playballkids.com/franchise/tooting",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-5",
    name: "Spencer Lawn Tennis Club Holiday Camps",
    provider: "Spencer Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "The Spencer Club, Fieldview, SW18 3HF",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at The Spencer Club. Coaching, drills, match play and fitness for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/spencerlawntennisclub/Coaching/Camps",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-6",
    name: "The Little Gym Wandsworth Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "52 Wandsworth Bridge Road, SW6 2TF",
    postcodeSector: "SW6",
    distanceMiles: 2.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports", "arts"],
    description: "Gymnastics-based holiday camp at The Little Gym Wandsworth. Movement, obstacle courses, games and crafts.",
    times: "Check with provider for holiday dates",
    website: "https://wandsworthfulham.thelittlegym.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-7",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Swaffield Primary School, St Ann's Hill, SW18 2SA",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Swaffield Primary School. Football, dodgeball, archery and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/summer-holiday-camps/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-8",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "St Michael's Primary School, SW18 1AE",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at St Michael's Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-9",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Earlsfield church venue, SW18 3NN",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at a church venue in Earlsfield. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-10",
    name: "Art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "art-K Wandsworth, SW18 4TF",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Wandsworth. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-11",
    name: "Love Basketball Holiday Camp",
    provider: "Love Basketball",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Southfields Academy Sports Centre, SW18 5JU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Basketball-focused holiday camp at Southfields Academy Sports Centre. Coaching, fitness, competitions and games.",
    times: "Check with provider for holiday dates",
    website: "http://www.lovebasketball.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-12",
    name: "Little Kickers Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Riverside Club, SW18 1HN",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp at Riverside Club for younger children. Movement, coordination and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/southfields/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-13",
    name: "Rugbytots Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Wandsworth/Earlsfield venue, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp for younger children in the Earlsfield/Wandsworth area. Agility, movement and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Wandsworth/Class/Details/102789",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-14",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Wandsworth branch, SW18 4GQ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Wandsworth branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "efd-15",
    name: "Fit For Sport Holiday Camp",
    provider: "Fit For Sport",
    type: "Camp",
    neighbourhood: "Earlsfield",
    location: "Wandsworth school venue, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at a Wandsworth school venue. Active games, creative play and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://fit4futurefoundation.com/",
    neighbourhoods: ["Earlsfield"],
  },

  // ── EAST SHEEN ───────────────────────────────────────────────────────────────
  {
    id: "esh-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Sheen Mount Primary School, West Temple Sheen, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Sheen Mount Primary School. Sports, arts & crafts, outdoor games and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-2",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Sheen Mount Primary School, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Sheen Mount Primary School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-3",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Tower House School, 188 Sheen Lane, SW14 8LF",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Tower House School. Sports, creative arts and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/kew",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-4",
    name: "Rocks Lane Multi-Sports Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Rocks Lane Chiswick, popular with East Sheen families. Football, tennis and netball.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-5",
    name: "Sheen Lawn Tennis & Squash Club Holiday Camps",
    provider: "Sheen Lawn Tennis & Squash Club",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Parklands Close, SW14 7EH",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Sheen Lawn Tennis & Squash Club. Coaching, match play, fitness and games for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/tennislfe/Coaching/Camps",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-6",
    name: "Sheen Tennis Academy Holiday Camps",
    provider: "Sheen Tennis Academy",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Sheen Common Drive, SW14 7HA",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused holiday camp on Sheen Common Drive. Tennis, mini tennis and competitions.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/tennislfe/Coaching/Camps",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-7",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "East Sheen Primary School, Upper Richmond Road West, SW14 8ED",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at East Sheen Primary School. Football, dodgeball, archery and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-8",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "East Sheen church venue, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at a church venue in East Sheen. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-9",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Tower House School, SW14 8LF",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Tower House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-10",
    name: "Art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "art-K Richmond, TW9 2NA",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Richmond, serving East Sheen families. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-11",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Richmond branch, TW9 2PQ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Richmond branch of Code Ninjas, serving East Sheen families. Minecraft, Roblox, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-12",
    name: "Little Kickers Holiday Football Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Palewell Common Playing Fields, SW14 8RE",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp at Palewell Common Playing Fields for younger children. Movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/southfields/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-13",
    name: "Richmond Park Academy Sports Camp",
    provider: "Richmond Park Academy",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Park Avenue, SW14 8RG",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp on Park Avenue. Football, basketball, athletics and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/shene-sports-and-fitness-centre",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-14",
    name: "Forest School Holiday Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Palewell Common / Richmond Park, SW14 8RE",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp spanning Palewell Common and Richmond Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.outdoorowls.com/holiday-camps-richmond",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "esh-15",
    name: "The Little Gym Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "East Sheen",
    location: "Richmond branch, TW9 4DU",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports", "arts"],
    description: "Gymnastics-based holiday camp at The Little Gym's Richmond branch, serving East Sheen families. Movement, obstacle courses, games and crafts.",
    times: "Check with provider for holiday dates",
    website: "https://hamptonteddington.thelittlegym.co.uk/",
    neighbourhoods: ["East Sheen"],
  },

  // ── HAMPTON ──────────────────────────────────────────────────────────────────
  {
    id: "hmp-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton School, Hanworth Road, TW12 3HD",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Hampton School. Sports, arts & crafts, outdoor games and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Lady Eleanor Holles School, Hanworth Road, TW12 3HF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "swimming"],
    description: "Multi-activity holiday camp at Lady Eleanor Holles School. Sports, creative arts and team challenges, with swimming available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/kew",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Lady Eleanor Holles School, TW12 3HF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Lady Eleanor Holles School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-4",
    name: "SuperCamps",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Lady Eleanor Holles School, TW12 3HF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Lady Eleanor Holles School. Sports, arts & crafts, STEM activities and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-5",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton School, TW12 3HD",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "stem", "drama"],
    description: "Sports and activity camp at Hampton School. Cricket, hockey, netball, football and tennis, with coding and performance camps available in selected seasons.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/venues/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-6",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton Infant School, Percy Road, TW12 2ST",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Hampton Infant School. Football, dodgeball, archery and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-7",
    name: "Hampton Tennis Club Holiday Camps",
    provider: "Hampton Tennis Club",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton Tennis Club, Oldfield Road, TW12 2HQ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Hampton Tennis Club. Coaching, drills, match play and fitness for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://www.twtennis.co.uk/camps",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-8",
    name: "Hampton & Richmond Borough FC Soccer School",
    provider: "Hampton & Richmond Borough FC",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Beveree Stadium, Beaver Close, TW12 2BX",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football soccer school at Beveree Stadium. Coaching, skills sessions, tournaments and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.hamptonyouthfc.com/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-9",
    name: "Thames Young Mariners Holiday Club",
    provider: "Thames Young Mariners",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Riverside Drive, TW12 2JB",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Watersports holiday club on Riverside Drive. Sailing, kayaking, canoeing, paddleboarding and raft building.",
    times: "Check with provider for holiday dates",
    website: "https://www.surreyoutdoorlearning.uk/centres/thames-young-mariners",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-10",
    name: "Barracudas Activity Day Camp",
    provider: "Barracudas",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "LEH School, TW12 3HF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4½–14",
    interests: ["sports", "drama", "stem", "swimming"],
    description: "Multi-activity day camp at LEH School. Sports, performing arts and STEM, with aqua activities available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.barracudas.co.uk/camps/twickenham-st-catherines",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-11",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton church venue, TW12 2EB",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at a church venue in Hampton. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/drama-classes/twickenham",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-12",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Hampton School, TW12 3HD",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Hampton School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-13",
    name: "Art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "art-K Teddington, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Teddington, serving Hampton families. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-14",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Teddington branch, TW11 8HZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Teddington branch of Code Ninjas, serving Hampton families. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "hmp-15",
    name: "Forest School Holiday Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Hampton",
    location: "Bushy Park, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Bushy Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.littleforestfolk.com/holiday-camp-bookings",
    neighbourhoods: ["Hampton"],
  },

  // ── HAMPTON HILL ─────────────────────────────────────────────────────────────
  {
    id: "hhl-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, Waldegrave Park, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Newland House School. Sports, arts & crafts, outdoor games and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Newland House School. Sports, creative activities and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/oxfordshire-holiday-camps-kids",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Newland House School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-4",
    name: "SuperCamps",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Newland House School. Sports, arts & crafts, STEM activities and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-5",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "stem"],
    description: "Sports and activity camp at Newland House School. Cricket, hockey, football, tennis and netball, with seasonal coding programmes.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/venues/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-6",
    name: "Barracudas Activity Day Camp",
    provider: "Barracudas",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4½–14",
    interests: ["sports", "drama", "swimming"],
    description: "Multi-activity day camp at Newland House School. Performing arts and sports, with aqua games available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.barracudas.co.uk/camps/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-7",
    name: "Hampton Hill Lawn Tennis Club Holiday Camps",
    provider: "Hampton Hill Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Windmill Road, TW12 1RF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Hampton Hill Lawn Tennis Club. Coaching, drills, match play and mini tennis for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://hamptonhillcricket.co.uk/pages/summer-camp",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-8",
    name: "Teddington Cricket Club Holiday Camps",
    provider: "Teddington Cricket Club",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Bushy Park Sports Club, Broom Road, TW11 9PQ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–15",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Bushy Park Sports Club. Coaching, games and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.teddingtoncc.co.uk/holiday-camps",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-9",
    name: "Rugbytots Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill venue, TW12 1JW",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp for younger children in Hampton Hill. Movement skills and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-10",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "St James's Church Hall, TW12 1DQ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St James's Church Hall in Hampton Hill. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-11",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Newland House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-12",
    name: "Art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "art-K Teddington, Broad Street, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Teddington on Broad Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-13",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Teddington High Street, TW11 8HZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp on Teddington High Street. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-14",
    name: "Forest School Holiday Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Bushy Park, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Bushy Park. Bushcraft, den building, nature crafts and woodland exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.littleforestfolk.com/holiday-camp-bookings",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "hhl-15",
    name: "Thames Young Mariners Holiday Club",
    provider: "Thames Young Mariners",
    type: "Camp",
    neighbourhood: "Hampton Hill",
    location: "Riverside Drive, TW12 2JB",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Watersports holiday club on Riverside Drive. Sailing, kayaking, canoeing and paddleboarding.",
    times: "Check with provider for holiday dates",
    website: "https://www.surreyoutdoorlearning.uk/centres/thames-young-mariners",
    neighbourhoods: ["Hampton Hill"],
  },

  // ── HAMPTON WICK ─────────────────────────────────────────────────────────────
  {
    id: "hpw-1",
    name: "Sports Focus Coaching Holiday Camp",
    provider: "Sports Focus Coaching",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "St John the Baptist CE Junior School, Lower Teddington Road, KT1 4HQ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at St John the Baptist CE Junior School. Football, cricket, basketball, dodgeball, arts & crafts and team games.",
    times: "Check with provider for holiday dates",
    website: "https://sportsfocusuk.co.uk/book-camp/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-2",
    name: "Sports Focus Coaching FUEL Camp",
    provider: "Sports Focus Coaching",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "St John the Baptist CE Junior School, KT1 4HQ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["sports"],
    description: "Multi-sports FUEL holiday camp at St John the Baptist CE Junior School, combining games with healthy meals and enrichment activities.",
    times: "Check with provider for holiday dates",
    website: "https://sportsfocusuk.co.uk/book-camp/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-3",
    name: "Wilderness Club Holiday Camp",
    provider: "Wilderness Club",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "The Wilderness, Normansfield Avenue, TW11 9RP",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["outdoors"],
    description: "Forest School holiday camp at The Wilderness. Bushcraft, den building, campfires, woodland adventures and nature crafts.",
    times: "Check with provider for holiday dates",
    website: "https://brilliantplay.co.uk/forest-school/forest-school-holiday-camps/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-4",
    name: "DW Football Academy Holiday Camp",
    provider: "DW Football Academy",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Hampton Wick Royal Cricket Club, Bushy Park, KT1 4AZ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football-focused holiday camp at Hampton Wick Royal Cricket Club, Bushy Park. Coaching, skills, tournaments and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.dwsoccerschool.co.uk/services/summer-holiday-camps/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-5",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, Waldegrave Park, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Newland House School. Sports, arts & crafts, outdoor games and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-6",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Newland House School. Sports, creative activities and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/oxfordshire-holiday-camps-kids",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-7",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Newland House School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-8",
    name: "SuperCamps",
    provider: "SuperCamps",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Newland House School. Sports, arts & crafts, STEM activities and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/current-locations",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-9",
    name: "Barracudas Activity Day Camp",
    provider: "Barracudas",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4½–14",
    interests: ["sports", "drama"],
    description: "Multi-activity day camp at Newland House School. Performing arts, sports and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.barracudas.co.uk/camps/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-10",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "stem", "drama"],
    description: "Sports and activity camp at Newland House School. Cricket, football, hockey, tennis and netball, with coding and performance camps.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/venues/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-11",
    name: "Hampton Hill Lawn Tennis Club Holiday Camps",
    provider: "Hampton Hill Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Windmill Road, TW12 1RF",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Hampton Hill Lawn Tennis Club. Coaching, mini tennis, match play and fitness for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://hamptonhillcricket.co.uk/pages/summer-camp",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-12",
    name: "Teddington Cricket Club Holiday Camps",
    provider: "Teddington Cricket Club",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Bushy Park Sports Club, Broom Road, TW11 9PQ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–15",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Bushy Park Sports Club. Coaching, games, fitness and competitions.",
    times: "Check with provider for holiday dates",
    website: "https://www.teddingtoncc.co.uk/holiday-camps",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-13",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "St Mary's Church Hall, Teddington, TW11 8NA",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St Mary's Church Hall in Teddington. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-14",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Newland House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-15",
    name: "art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "art-K Teddington, Broad Street, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Teddington on Broad Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "hpw-16",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Hampton Wick",
    location: "High Street, Teddington, TW11 8HZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp on Teddington High Street. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Hampton Wick"],
  },

  // ── KEW ──────────────────────────────────────────────────────────────────────
  {
    id: "kew-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Kew Green Preparatory School, Layton House, Ferry Lane, TW9 3AF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Kew Green Preparatory School. Sports, arts & crafts, outdoor games and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-2",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Kew Green Preparatory School, TW9 3AF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Kew Green Preparatory School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-3",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Kew",
    location: "King's House Sports Ground, Riverside Drive, W4 2SH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at King's House Sports Ground. Sports, arts & crafts and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/oxfordshire-holiday-camps-kids",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-4",
    name: "Après School Holiday Camp",
    provider: "Après School",
    type: "Camp",
    neighbourhood: "Kew",
    location: "King's House Sports Ground, Riverside Drive, W4 2SH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "3–11",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-sports holiday camp at King's House Sports Ground. Football, arts & crafts, outdoor games and creative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.kingshousesportsground.co.uk/summer-holiday-camp/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-5",
    name: "Rocks Lane Holiday Camps",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Rocks Lane Chiswick, Cavendish Road, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Rocks Lane Chiswick. Football, tennis, padel and netball across the week.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-6",
    name: "Rocks Lane Tennis Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused camp at Rocks Lane Chiswick. Coaching, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-7",
    name: "Rocks Lane Football Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football-focused camp at Rocks Lane Chiswick. Coaching, skills sessions and matches.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-8",
    name: "Rocks Lane Padel Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["sports"],
    description: "Padel-focused camp at Rocks Lane Chiswick. Coaching, games and fitness for older children and teens.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-9",
    name: "Rocks Lane Netball Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Rocks Lane Chiswick, W4 3UH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["sports"],
    description: "Netball-focused camp at Rocks Lane Chiswick. Coaching, skills sessions and matches.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/holiday-courses/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-10",
    name: "Kew Tennis Club Holiday Camps",
    provider: "Kew Tennis Club",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Kew Tennis Club, Cumberland Road, TW9 3AY",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Kew Tennis Club. Coaching, mini tennis and match play for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://sportsandleisure.royalparks.org.uk/holiday-camps",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-11",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Kew",
    location: "King's House Sports Ground, W4 2SH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "stem"],
    description: "Sports and activity camp at King's House Sports Ground. Cricket, football, hockey, tennis and netball, with seasonal coding programmes.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/venues/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-12",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Kew",
    location: "St Luke's Church Hall, Sandycombe Road, TW9 3EP",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St Luke's Church Hall in Kew. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-13",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Kew College, Cumberland Road, TW9 3HJ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Kew College. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-14",
    name: "art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Kew",
    location: "art-K Richmond, TW9 2NA",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Richmond, serving Kew families. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "kew-15",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Kew",
    location: "Richmond branch, TW9 2PQ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Richmond branch of Code Ninjas, serving Kew families. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Kew"],
  },

  // ── KINGSTON ─────────────────────────────────────────────────────────────────
  {
    id: "kng-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Tiffin School, Queen Elizabeth Road, KT2 6RL",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "dance", "drama", "outdoors"],
    description: "Multi-activity holiday camp at Tiffin School. Sports, arts & crafts, dance, drama, football, archery and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Holy Cross Preparatory School, George Road, KT2 7NU",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Holy Cross Preparatory School with over 40 sports on offer. Arts & crafts, team games, climbing, fencing and outdoor adventure.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/oxfordshire-holiday-camps-kids",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-3",
    name: "Ultimate Tech Camp",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Holy Cross Preparatory School, KT2 7NU",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "8–14",
    interests: ["stem", "gaming"],
    description: "Tech-focused holiday camp at Holy Cross Preparatory School. Coding, robotics, game design, animation and digital technology.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/tech-camp",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-4",
    name: "Ultimate Performance Camp",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Holy Cross Preparatory School, KT2 7NU",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["drama", "dance", "music"],
    description: "Performing arts holiday camp at Holy Cross Preparatory School. Drama, dance, singing, musical theatre and performance skills.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/programmes/ultimate-performance",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-5",
    name: "Sports Focus Coaching Holiday Camp",
    provider: "Sports Focus Coaching",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "St John the Baptist CE Junior School, Lower Teddington Road, KT1 4HQ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at St John the Baptist CE Junior School. Football, cricket, basketball, dodgeball, team games and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://sportsfocusuk.co.uk/book-camp/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-6",
    name: "Sports Focus FUEL Holiday Camp",
    provider: "Sports Focus Coaching",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "St John the Baptist CE Junior School, KT1 4HQ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["sports"],
    description: "Multi-sports FUEL holiday camp at St John the Baptist CE Junior School, combining games with enrichment activities and healthy meals.",
    times: "Check with provider for holiday dates",
    website: "https://sportsfocusuk.co.uk/book-camp/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-7",
    name: "Kingston Grammar School Holiday Camp",
    provider: "Kingston Grammar School",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Kingston Grammar School, London Road, KT2 6PY",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-sports holiday camp at Kingston Grammar School. Swimming, football, hockey and tennis, with seasonal arts programmes.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/holy-cross-school",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-8",
    name: "Kingston Lawn Tennis Club Holiday Camps",
    provider: "Kingston Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Kingston Lawn Tennis Club, Kingston Road, KT3 3PB",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Kingston Lawn Tennis Club. Coaching, mini tennis, match play and fitness for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://www.kingstonriversideclub.org/activities-for-kids/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-9",
    name: "DW Football Academy Holiday Camp",
    provider: "DW Football Academy",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Hampton Wick Royal Cricket Club, Bushy Park, KT1 4AZ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football-focused holiday camp at Hampton Wick Royal Cricket Club, Bushy Park. Coaching, tournaments, skills and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.dwsoccerschool.co.uk/services/soccer-camps/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-10",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Tiffin School, KT2 6RL",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Tiffin School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-11",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "All Saints Church Hall, Kingston, KT1 1JP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at All Saints Church Hall in Kingston. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-12",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Tiffin Girls' School, Richmond Road, KT2 5PL",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Tiffin Girls' School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-13",
    name: "art-K Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "art-K Kingston, Castle Street, KT1 1ST",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at art-K Kingston on Castle Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-14",
    name: "Code Ninjas Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "Code Ninjas Kingston, KT2 6QJ",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at Code Ninjas Kingston. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "kng-15",
    name: "The Wilderness Holiday Camp",
    provider: "The Wilderness",
    type: "Camp",
    neighbourhood: "Kingston",
    location: "The Wilderness, Normansfield Avenue, TW11 9RP",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–11",
    interests: ["outdoors"],
    description: "Forest School holiday camp at The Wilderness. Bushcraft, campfires, den building and woodland exploration.",
    times: "Check with provider for holiday dates",
    website: "https://campwilderness.co.uk/",
    neighbourhoods: ["Kingston"],
  },

  // ── MORTLAKE ─────────────────────────────────────────────────────────────────
  {
    id: "mtl-1",
    name: "NSSport Holiday Camp",
    provider: "NSSport",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "St Mary Magdalen's Catholic Primary School, Worple Street, SW14 8HE",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports"],
    description: "Multi-sports holiday camp at St Mary Magdalen's Catholic Primary School. Football, dodgeball, cricket, basketball, team games and active play.",
    times: "Check with provider for holiday dates",
    website: "https://nssport.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-2",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "St Paul's School, Lonsdale Road, SW13 9JT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-activity holiday camp at St Paul's School. Sports, swimming, giant inflatables, archery tag and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-3",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "St Paul's School, Lonsdale Road, SW13 9JT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "swimming"],
    description: "Multi-activity holiday camp at St Paul's School. Sports, arts & crafts, team challenges and swimming.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-4",
    name: "Tennis4Barnes Holiday Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Barn Elms Sports Centre, Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Holiday camp at Barn Elms Sports Centre combining tennis, cricket and football for a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-5",
    name: "Tennis4Barnes Tennis Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Barn Elms Sports Centre, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused camp at Barn Elms Sports Centre. Coaching, drills and match play for children building up their tennis skills.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-6",
    name: "Tennis4Barnes Tennis & Cricket Camp",
    provider: "Tennis4Barnes",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Barn Elms Sports Centre, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Combined tennis and cricket camp at Barn Elms Sports Centre, with fitness worked in throughout the week.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-7",
    name: "SkillsCoach Holiday Camp",
    provider: "SkillsCoach",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Barn Elms Sports Centre, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports"],
    description: "Football and multi-sports holiday camp at Barn Elms Sports Centre. Games and competitions throughout.",
    times: "Check with provider for holiday dates",
    website: "https://skills-coach.com/programmes/holiday-camps/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-8",
    name: "Barnes Tennis Academy Holiday Camp",
    provider: "Barnes Tennis Academy",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Rocks Lane Sports Centre, SW13 0BY",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports"],
    description: "Tennis coaching camp at Rocks Lane Sports Centre run by Barnes Tennis Academy. Coaching, match play and fitness for a broad age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-9",
    name: "Barn Elms Sports Trust Holiday Camp",
    provider: "Barn Elms Sports Trust",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Barn Elms Sports Trust. Football, athletics, cricket and tennis across the week.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-10",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Sheen Mount Primary School, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Sheen Mount Primary School. Sports, arts & crafts and themed activity days.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-11",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Sheen Mount Primary School, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Sheen Mount Primary School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-12",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "East Sheen Primary School, SW14 8ED",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at East Sheen Primary School. Football, dodgeball, archery and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-13",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "East Sheen church venue, SW14 7RT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at a church venue in East Sheen, serving Mortlake families. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-14",
    name: "Stagecoach Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Tower House School, SW14 8LF",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Tower House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "mtl-15",
    name: "Forest School Holiday Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Mortlake",
    location: "Richmond Park, SW15 5JR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Richmond Park. Bushcraft, den building, woodland crafts and outdoor adventure.",
    times: "Check with provider for holiday dates",
    website: "https://www.outdoorowls.com/holiday-camps-putney",
    neighbourhoods: ["Mortlake"],
  },

  // ── NEW MALDEN ───────────────────────────────────────────────────────────────
  {
    id: "nml-1",
    name: "Kingston Grammar School Holiday Camp",
    provider: "Kingston Grammar School",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Kingston Grammar School, London Road, KT2 6PY",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-sports holiday camp at Kingston Grammar School, part of the Kingston hub serving New Malden families. Swimming, tennis, hockey, football, arts & crafts and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/holy-cross-school",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-2",
    name: "YMCA Hawker Centre Holiday Camp",
    provider: "YMCA",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Hawker Centre, Lower Ham Road, KT2 5BH",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "dance", "swimming", "outdoors"],
    description: "Multi-sports holiday camp at the YMCA Hawker Centre. Gymnastics, dance, swimming, outdoor games and fitness activities.",
    times: "Check with provider for holiday dates",
    website: "https://ymcastpaulsgroup.org/ymca-hawker-kingston/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-3",
    name: "All Star Tennis Holiday Camps",
    provider: "All Star Tennis",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "New Malden Sports Ground, Beverley Park Road, KT3 4PH",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at New Malden Sports Ground. Coaching, mini tennis, skills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-4",
    name: "New Malden Leisure Centre Holiday Camp",
    provider: "New Malden Leisure Centre",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "New Malden Leisure Centre, Pauling Road, KT3 6JF",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at New Malden Leisure Centre. Swimming, sports hall games and gymnastics.",
    times: "Check with provider for holiday dates",
    website: "https://www.placesleisure.org/centres/malden-centre/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-5",
    name: "Chessington Sports Centre Holiday Camp",
    provider: "Chessington Sports Centre",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Leatherhead Road, KT9 2NE",
    postcodeSector: "KT9",
    distanceMiles: 9.9,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Chessington Sports Centre, within the New Malden catchment. Football, dodgeball, athletics and team games.",
    times: "Check with provider for holiday dates",
    website: "https://schoolcamps.org/organiser/chessington-sports-centre",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-6",
    name: "Little Kickers New Malden Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Raynes Park / New Malden venues, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Raynes Park and New Malden venues for younger children. Coordination, ball skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/kingston/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-7",
    name: "Rugbytots New Malden Holiday Sessions",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "New Malden Methodist Church Hall, KT3 4BY",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday sessions at New Malden Methodist Church Hall for younger children. Movement skills and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-8",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Raynes Park High School, SW20 0JL",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football and multi-sport hub camp at Raynes Park High School. Coaching, fitness and competitions.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-9",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "St James' Church Hall, New Malden, KT3 5AS",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St James' Church Hall in New Malden. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-10",
    name: "Stagecoach New Malden Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Coombe Girls' School, KT3 5AL",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Coombe Girls' School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-11",
    name: "Art-K New Malden Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Art-K Studio, High Street, KT3 4BU",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on New Malden High Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-12",
    name: "Code Ninjas New Malden Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "New Malden branch, KT3 4HG",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the New Malden branch of Code Ninjas. Roblox, Minecraft, game design and robotics.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-13",
    name: "The Little Gym New Malden Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "New Malden branch, KT3 4HY",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's New Malden branch. Movement, obstacle courses and physical development.",
    times: "Check with provider for holiday dates",
    website: "https://wimbledon.thelittlegym.co.uk/summer-camps-2026",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-14",
    name: "Shannon Lawn Tennis Club Holiday Camps",
    provider: "Shannon Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Coombe Lane West, KT2 7DY",
    postcodeSector: "KT2",
    distanceMiles: 7.2,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Shannon Lawn Tennis Club. Coaching, drills, match play and fitness for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/sharnbrooklawntennisclub/Coaching/Camps",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "nml-15",
    name: "Coombe Boys' School Sports Camp",
    provider: "Coombe Boys' School",
    type: "Camp",
    neighbourhood: "New Malden",
    location: "Coombe Boys' School, KT3 6NU",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Seasonal multi-sports holiday camp at Coombe Boys' School. Football, athletics and team games.",
    times: "Check with provider for holiday dates",
    website: "https://lovetheball.com/courses/holidaycamps.html",
    neighbourhoods: ["New Malden"],
  },

  // ── PUTNEY ───────────────────────────────────────────────────────────────────
  {
    id: "ptn-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney High School, 35 Putney Hill, SW15 6BH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "dance", "drama"],
    description: "Multi-activity holiday camp at Putney High School. Sports, arts & crafts, team games, dance, drama and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney High School, SW15 6BH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "outdoors"],
    description: "Multi-sports holiday camp at Putney High School. Creative arts, STEM activities, team challenges and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney High School, SW15 6BH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at Putney High School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-4",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Putney",
    location: "St Cecilia's School, Sutherland Grove, SW18 5JR",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at St Cecilia's School. Football, hockey, netball, cricket and tennis, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-5",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney Methodist Church Hall, SW15 2PU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Putney Methodist Church Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-6",
    name: "Stagecoach Putney Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney High School / local venues, SW15 6BH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Putney High School and local venues. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-7",
    name: "art-K Putney Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Putney",
    location: "art-K Putney Studio, SW15 2SH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the art-K Putney Studio. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-8",
    name: "Code Ninjas Putney Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney branch, SW15 2SS",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Putney branch of Code Ninjas. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-9",
    name: "Little Kickers Putney Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney Leisure Centre / local halls, SW15 1BL",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Putney Leisure Centre and local halls for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/putney-wimbledon/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-10",
    name: "Rugbytots Putney Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney leisure / church halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp across Putney leisure and church halls for younger children. Agility, teamwork and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-11",
    name: "Putney Leisure Centre Holiday Camp",
    provider: "Putney Leisure Centre",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Dryburgh Road, SW15 1BL",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at Putney Leisure Centre. Swimming, sports hall games and gymnastics.",
    times: "Check with provider for holiday dates",
    website: "https://www.placesleisure.org/centres/putney-leisure-centre/centre-activities/family-kids/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-12",
    name: "Rosslyn Park Rugby Club Holiday Camp",
    provider: "Rosslyn Park Rugby Club",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Priory Lane, SW15 5JH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Rugby-focused holiday camp at Rosslyn Park Rugby Club. Coaching, skills, fitness, matches and team play.",
    times: "Check with provider for holiday dates",
    website: "https://rosslynpark.co.uk/minis-youths/camps/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-13",
    name: "Putney Lawn Tennis Club Holiday Camps",
    provider: "Putney Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Telegraph Road, SW15 3TU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Putney Lawn Tennis Club. Coaching, drills, match play and junior tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/PutneyLawnTennisClub/Coaching/Camps",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-14",
    name: "Thames Rowing Club Junior Camps",
    provider: "Thames Rowing Club",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney Embankment, SW15 1LB",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "12–18",
    interests: ["sports", "outdoors"],
    description: "Junior rowing camp on Putney Embankment. Water skills, strength training and fitness for teens.",
    times: "Check with provider for holiday dates",
    website: "https://www.thamesrc.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-15",
    name: "London Rowing Club Junior Programme",
    provider: "London Rowing Club",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Putney Embankment, SW15 1LB",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "12–18",
    interests: ["sports", "outdoors"],
    description: "Junior rowing programme on Putney Embankment. Water training, endurance and technique coaching for teens.",
    times: "Check with provider for holiday dates",
    website: "https://www.londonyouthrowing.com/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ptn-16",
    name: "Barn Elms Sports Camps",
    provider: "Barn Elms Sports Trust",
    type: "Camp",
    neighbourhood: "Putney",
    location: "Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Barn Elms, popular with Putney families. Football, tennis and cricket.",
    times: "Check with provider for holiday dates",
    website: "https://tennis4barnes.co.uk/holiday-camps/",
    neighbourhoods: ["Putney"],
  },

  // ── RAYNES PARK ──────────────────────────────────────────────────────────────
  {
    id: "rpk-1",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park High School, Bushey Road, SW20 0JL",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football and multi-sport camp at Raynes Park High School. Dodgeball, team games, fitness and tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-2",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park High School, SW20 0JL",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Raynes Park High School. Football, dodgeball, archery, athletics and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-3",
    name: "Little Kickers Raynes Park Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park / West Wimbledon venues, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Raynes Park and West Wimbledon venues for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-4",
    name: "Rugbytots Raynes Park Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Methodist Church Hall, SW20 8DN",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Raynes Park Methodist Church Hall for younger children. Agility, coordination and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Raynes-Park/Class/Details/102802",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-5",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Church Hall (various venues), SW20 8EE",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course across Raynes Park church hall venues. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-6",
    name: "Stagecoach Raynes Park Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park High School, SW20 0JL",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Raynes Park High School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-7",
    name: "Art-K Raynes Park Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Art-K Studio, Coombe Lane, SW20 0AX",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Coombe Lane. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-8",
    name: "Code Ninjas Raynes Park Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park branch, SW20 0QE",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Raynes Park branch of Code Ninjas. Minecraft, Roblox, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-9",
    name: "The Little Gym Raynes Park Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Studio, SW20 0UJ",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Raynes Park Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-10",
    name: "Raynes Park Lawn Tennis Club Holiday Camps",
    provider: "Raynes Park Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Aelwyn Road, SW20 8RQ",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Raynes Park Lawn Tennis Club. Coaching, drills, match play and junior competitions.",
    times: "Check with provider for holiday dates",
    website: "https://raynespark.communitysport.aeltc.com/Tennis/HolidayCamps",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-11",
    name: "David Lloyd Raynes Park Holiday Club",
    provider: "David Lloyd",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Bushey Road, SW20 8TE",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "swimming"],
    description: "Multi-activity holiday club at David Lloyd Raynes Park. Swimming, tennis, gymnastics, sports and fitness games.",
    times: "Check with provider for holiday dates",
    website: "https://www.davidlloyd.co.uk/clubs/raynes-park/club-facilities/kids-club/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-12",
    name: "Wimbledon Rugby Football Club Holiday Camp",
    provider: "Wimbledon Rugby Football Club",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Barham Road, SW20 0ET",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Rugby-focused holiday camp at Wimbledon Rugby Football Club, within the Raynes Park catchment. Coaching, skills, fitness and matches.",
    times: "Check with provider for holiday dates",
    website: "https://wmrfc.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-13",
    name: "Wimbledon Hawks Basketball Holiday Camp",
    provider: "Wimbledon Hawks",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Sports Hall, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Basketball-focused holiday camp at Raynes Park Sports Hall. Coaching, drills, shooting, games and tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://www.wimbledonhawks.com/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-14",
    name: "Coombe Boys' School Sports Camps",
    provider: "Coombe Boys' School",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Coombe Lane West, KT3 6NU",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Coombe Boys' School, popular with Raynes Park families. Football, athletics and team games.",
    times: "Check with provider for holiday dates",
    website: "https://lovetheball.com/courses/holidaycamps.html",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "rpk-15",
    name: "Coombe Girls' School Holiday Camps",
    provider: "Coombe Girls' School",
    type: "Camp",
    neighbourhood: "Raynes Park",
    location: "Clarence Avenue, KT3 5AL",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Coombe Girls' School, within the Raynes Park catchment. Sports, arts, STEM and games.",
    times: "Check with provider for holiday dates",
    website: "https://lovetheball.com/courses/holidaycamps.html",
    neighbourhoods: ["Raynes Park"],
  },

  // ── RICHMOND ─────────────────────────────────────────────────────────────────
  {
    id: "rch-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "King's House School, 68 King's Road, TW10 6EG",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "dance", "drama", "outdoors"],
    description: "Multi-activity holiday camp at King's House School. Sports, arts & crafts, dance, drama, outdoor games and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "King's House School, TW10 6EG",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "outdoors"],
    description: "Multi-sports holiday camp at King's House School. Creative arts, STEM, team challenges and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "King's House School, TW10 6EG",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts", "sports"],
    description: "Music-focused holiday camp at King's House School. Guitar, violin and ukulele sessions alongside drama, dance, arts & crafts and sport.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-4",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Athletic Ground, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at Richmond Athletic Ground. Rugby, hockey, netball, football and tennis, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-5",
    name: "Harlequins Rugby Foundation Holiday Camp",
    provider: "Harlequins Rugby Foundation",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Athletic Ground, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Rugby-focused holiday camp at Richmond Athletic Ground, run by the Harlequins Rugby Foundation. Coaching, skills, fitness and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.quins.co.uk/harlequins-rugby-camps",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-6",
    name: "Richmond Rugby Club Holiday Camp",
    provider: "Richmond Rugby Club",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Athletic Ground, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Rugby-focused holiday camp at Richmond Athletic Ground. Coaching, match play and skills development.",
    times: "Check with provider for holiday dates",
    website: "https://www.richmondfc.co.uk/news/summer-multi--sport-camps-2974429.html",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-7",
    name: "Rocks Lane Holiday Camps",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Old Deer Park Sports Ground, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at the Richmond hub of Rocks Lane, Old Deer Park Sports Ground. Tennis, football, padel and netball.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/chiswick/padel/padel-holiday-camps/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-8",
    name: "Rocks Lane Tennis Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Old Deer Park, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused camp at Old Deer Park. Coaching, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/tennis/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-9",
    name: "Rocks Lane Football Camp",
    provider: "Rocks Lane",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Old Deer Park, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football-focused camp at Old Deer Park. Coaching, games and skills sessions.",
    times: "Check with provider for holiday dates",
    website: "https://rockslane.co.uk/football/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-10",
    name: "Richmond Lawn Tennis Club Holiday Camps",
    provider: "Richmond Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Petersham Road, TW10 6UZ",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Richmond Lawn Tennis Club. Coaching, mini tennis and match play for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/RichmondLawnTennisClub/HolidayCamps",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-11",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "St John the Divine Church Hall, TW9 1SN",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St John the Divine Church Hall in Richmond. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-12",
    name: "Stagecoach Richmond Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "King's House School, TW10 6EG",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at King's House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-13",
    name: "Art-K Richmond Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Studio, Sheen Road, TW9 1AD",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Richmond Studio on Sheen Road. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-14",
    name: "Code Ninjas Richmond Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond branch, TW9 2PQ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Richmond branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-15",
    name: "The Little Gym Richmond Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Studio, TW9 1HY",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Richmond Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/day-camps",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-16",
    name: "Thames Young Mariners Holiday Club",
    provider: "Thames Young Mariners",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Riverside Drive, TW12 2JB",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Watersports holiday club on Riverside Drive, popular with Richmond families. Sailing, kayaking, canoeing, paddleboarding and raft building.",
    times: "Check with provider for holiday dates",
    website: "https://www.surreyoutdoorlearning.uk/centres/thames-young-mariners",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "rch-17",
    name: "Forest School Richmond Park Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Richmond",
    location: "Richmond Park, TW10 5HS",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Richmond Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.outdoorowls.com/richmond-london",
    neighbourhoods: ["Richmond"],
  },

  // ── ROEHAMPTON ───────────────────────────────────────────────────────────────
  {
    id: "rmp-1",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton University Sports Centre, Roehampton Lane, SW15 5PU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Multi-sport and football camp at Roehampton University Sports Centre. Hockey, tennis, netball, athletics, team games and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-2",
    name: "Roehampton University Sports Camp",
    provider: "Roehampton University",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton University, Parkstead House Sports Grounds, SW15 5PU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Roehampton University's Parkstead House Sports Grounds. Indoor sports hall games, outdoor athletics and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.roehamptonclub.co.uk/sports/junior-camps",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-3",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Village Hall, SW15 5HU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Roehampton Village Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-4",
    name: "Stagecoach Roehampton Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton University / local halls, SW15 5PU",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Roehampton University and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-5",
    name: "Little Kickers Roehampton Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Leisure Centre / local schools, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Roehampton Leisure Centre and local schools for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-6",
    name: "Rugbytots Roehampton Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Parish Hall, SW15 5EL",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Roehampton Parish Hall for younger children. Coordination, agility and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-7",
    name: "Roehampton Club Holiday Camps",
    provider: "Roehampton Club",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Club, Danebury Avenue, SW15 4HB",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-activity holiday camp at Roehampton Club. Tennis, swimming, squash, hockey, multi-sports and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.roehamptonclub.co.uk/sports/junior-camps",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-8",
    name: "Roehampton Tennis Camps",
    provider: "Roehampton Club",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Club Tennis Centre, SW15 4HB",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Roehampton Club Tennis Centre. Coaching, drills, match play and junior tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://www.roehamptonclub.co.uk/sports/junior-camps",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-9",
    name: "The Little Gym Roehampton Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Studio, SW15 5LR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Roehampton Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-10",
    name: "Code Ninjas Roehampton Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton branch, SW15 5LH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Roehampton branch of Code Ninjas. Roblox, Minecraft, game development and robotics.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-11",
    name: "Art-K Roehampton Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Art-K studio, Roehampton Lane, SW15 5DA",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K studio on Roehampton Lane. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-12",
    name: "Forest School Roehampton Park Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Richmond Park (Roehampton Gate), SW15 5JR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp at the Roehampton Gate entrance to Richmond Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.outdoorowls.com/putney-london",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-13",
    name: "Roehampton Cricket Club Holiday Camp",
    provider: "Roehampton Cricket Club",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Roehampton Cricket Club, Roehampton Lane, SW15 5JH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Roehampton Cricket Club. Coaching, skills, batting, bowling and matches.",
    times: "Check with provider for holiday dates",
    website: "https://www.roehamptonclub.co.uk/sports/junior-camps",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-14",
    name: "Barn Elms Sports Camps",
    provider: "Barn Elms Sports Trust",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Queen Elizabeth Walk, SW13 9SA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Barn Elms, popular with Roehampton families. Football, tennis and cricket.",
    times: "Check with provider for holiday dates",
    website: "https://www.elmssportinschools.co.uk/sports-provision/holiday-camps/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "rmp-15",
    name: "Wandsworth Common Sports Camps",
    provider: "Wandsworth Common Sports",
    type: "Camp",
    neighbourhood: "Roehampton",
    location: "Wandsworth Common, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "outdoors"],
    description: "Multi-sports holiday camp on Wandsworth Common, popular with Roehampton families. Football, rugby and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["Roehampton"],
  },

  // ── SOUTHFIELDS ──────────────────────────────────────────────────────────────
  {
    id: "sfd-1",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Wimbledon Park Sports Centre, Home Park Road, SW19 7HX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Multi-sport camp at the Wimbledon Park hub. Football, tennis, hockey, netball, athletics and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-2",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Academy, 333 Merton Road, SW18 5JU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Southfields Academy. Football, dodgeball, archery, athletics and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-3",
    name: "Stagecoach Southfields Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Academy / local halls, SW18 5JU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Southfields Academy and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-4",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Methodist Church Hall, SW19 6AF",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Southfields Methodist Church Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-5",
    name: "Rugbytots Southfields Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Community Centre, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Southfields Community Centre for younger children. Coordination, agility and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-6",
    name: "Little Kickers Southfields Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields / Wimbledon Park venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Southfields and Wimbledon Park venues for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/southfields/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-7",
    name: "Southfields Lawn Tennis Club Holiday Camps",
    provider: "Southfields Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Wimbledon Park Road, SW19 6EW",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Southfields Lawn Tennis Club. Coaching, drills, match play and junior tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://metrotennis.co.uk/camps/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-8",
    name: "Wimbledon Park Tennis & Multi-Sports Camp",
    provider: "Wimbledon Park Tennis Centre",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Wimbledon Park Tennis Centre, SW19 7HX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Tennis and multi-sports holiday camp at Wimbledon Park Tennis Centre. Mini tennis, football and multi-sports games.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/wimbledonpark/Coaching/Camps",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-9",
    name: "Wimbledon Park Sports Centre Holiday Camp",
    provider: "Wimbledon Park Sports Centre",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Home Park Road, SW19 7HX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "swimming"],
    description: "Multi-sports holiday camp at Wimbledon Park Sports Centre. Athletics, indoor games, with swimming available in selected programmes.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/wimbledonpark/Coaching/Camps",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-10",
    name: "Art-K Southfields Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Art-K Studio, Replingham Road, SW18 5LU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Replingham Road. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-11",
    name: "Code Ninjas Southfields Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields branch, SW18 5UA",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Southfields branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-12",
    name: "The Little Gym Southfields Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Studio, SW18 5JG",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Southfields Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-13",
    name: "Wimbledon Hawks Basketball Holiday Camp",
    provider: "Wimbledon Hawks",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Southfields Academy Sports Hall, SW18 5JU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Basketball-focused holiday camp at Southfields Academy Sports Hall. Coaching, drills, shooting and tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://www.wimbledonhawks.com/training",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-14",
    name: "All Star Tennis Holiday Camps",
    provider: "All Star Tennis",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Wimbledon Park / Southfields courts, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Tennis-focused holiday camp across Wimbledon Park and Southfields courts. Coaching, mini tennis and match play.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "sfd-15",
    name: "Forest School Wimbledon Park Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Southfields",
    location: "Wimbledon Park, SW19 7HX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Wimbledon Park, within the Southfields catchment. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://www.littleforestfolk.com/wimbledon",
    neighbourhoods: ["Southfields"],
  },

  // ── STREATHAM ────────────────────────────────────────────────────────────────
  {
    id: "str-1",
    name: "Better Streatham Leisure Centre Holiday Camp",
    provider: "Better",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham Leisure Centre, 390 Streatham High Road, SW16 6HX",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at Streatham Leisure Centre. Swimming, sports hall games, gymnastics, soft play and team games.",
    times: "Check with provider for holiday dates",
    website: "https://active.lambeth.gov.uk/locations/streatham/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-2",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Dunraven School, Leigham Court Road, SW16 2QB",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Dunraven School. Football, dodgeball, archery, athletics and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-3",
    name: "Activ Camps",
    provider: "Activ Camps",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Dunraven School, SW16 2QB",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-activity holiday camp at the Dunraven hub. Inflatables, archery tag, sports and arts & crafts, with swimming available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-4",
    name: "Stagecoach Streatham Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Dunraven School / local halls, SW16 2QB",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Dunraven School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-5",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham Church Hall (various venues), SW16 1BB",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course across Streatham church hall venues. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-6",
    name: "Little Kickers Streatham Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham / Norbury venues, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Streatham and Norbury venues for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-7",
    name: "Rugbytots Streatham Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham Methodist Church Hall, SW16 1EQ",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Streatham Methodist Church Hall for younger children. Agility, coordination and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-8",
    name: "Art-K Streatham Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Art-K Studio, Streatham Hill, SW2 4TP",
    postcodeSector: "SW2",
    distanceMiles: 1.3,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Streatham Hill. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-9",
    name: "Code Ninjas Streatham Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham branch, SW16 6HP",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Streatham branch of Code Ninjas. Roblox, Minecraft, game design and robotics.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-10",
    name: "The Little Gym Streatham Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham Studio, SW16 1HJ",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Streatham Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-11",
    name: "Tooting & Streatham Hockey Club Holiday Camp",
    provider: "Tooting & Streatham Hockey Club",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Tooting Bec Common, SW16 / SW17",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Hockey-focused holiday camp on Tooting Bec Common. Coaching, skills, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://syihc1.wixsite.com/website",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-12",
    name: "Streatham & Clapham High School Holiday Activities",
    provider: "Streatham & Clapham High School",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Abbotswood Road, SW16 1AW",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Streatham & Clapham High School. Sports, arts, STEM and games.",
    times: "Check with provider for holiday dates",
    website: "https://www.barracudas.co.uk/camps/streatham-clapham",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-13",
    name: "Dulwich Cricket Club Holiday Camp",
    provider: "Dulwich Cricket Club",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Turney Road, SE21 7JH",
    postcodeSector: "SE21",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Dulwich Cricket Club, popular with Streatham families. Coaching, batting, bowling and matches.",
    times: "Check with provider for holiday dates",
    website: "https://www.dulwichcc.com/cricket-holiday-camps",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-14",
    name: "Forest School Tooting Bec Common Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Tooting Bec Common, SW16 / SW17",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp on Tooting Bec Common, within the Streatham catchment. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://wild-learning.net/holiday-clubs/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "str-15",
    name: "Streatham Ice Hockey / Skating Holiday Sessions",
    provider: "Streatham Ice Arena",
    type: "Camp",
    neighbourhood: "Streatham",
    location: "Streatham Ice Arena, SW16 1RU",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports"],
    description: "Ice skating and hockey holiday sessions at Streatham Ice Arena. Skating development and fitness alongside hockey skills.",
    times: "Check with provider for holiday dates",
    website: "https://active.lambeth.gov.uk/locations/streatham/",
    neighbourhoods: ["Streatham"],
  },

  // ── SURBITON ─────────────────────────────────────────────────────────────────
  {
    id: "srb-1",
    name: "Surbiton High School Holiday Camp",
    provider: "Surbiton High School",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High School, Surbiton Crescent, KT6 4LQ",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports", "swimming", "dance", "drama", "arts", "stem"],
    description: "Multi-sports holiday camp at Surbiton High School. Swimming, tennis, dance, drama, arts & crafts and STEM activities across a wide age range.",
    times: "Check with provider for holiday dates",
    website: "https://www.mnature.co.uk/venues/kingston-upon-thames-school-holiday-half-term-camps-near-me-clubs-for-kids-in-london/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-2",
    name: "Surbiton High Girls' Prep Holiday Camp",
    provider: "Surbiton High Girls' Prep School",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High Girls' Prep School, KT6 6RL",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports", "arts", "outdoors"],
    description: "Multi-activity holiday camp at Surbiton High Girls' Prep School. Sports, arts & crafts, creative play and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.surbitonhigh.com/girls-prep-school/girls-prep-wrap-around-care/holiday-camp/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-3",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High School, KT6 4LQ",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at Surbiton High School. Sports, arts & crafts, team games and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-4",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High School, KT6 4LQ",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "swimming"],
    description: "Multi-sports holiday camp at Surbiton High School. Creative arts, STEM, team challenges and swimming.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-5",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High School, KT6 4LQ",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts"],
    description: "Music-focused holiday camp at Surbiton High School. Guitar, violin and ukulele sessions alongside drama, dance and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-6",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton Sports Ground / school hubs, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Sports and activity camp across Surbiton Sports Ground and school hubs. Football, hockey, netball, cricket and tennis.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-7",
    name: "Surbiton Rackets & Fitness Club Holiday Camps",
    provider: "Surbiton Rackets & Fitness Club",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Berrylands, KT5 8JT",
    postcodeSector: "KT5",
    distanceMiles: 7.7,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Racket sports holiday camp at Surbiton Rackets & Fitness Club. Tennis, squash, badminton and fitness training.",
    times: "Check with provider for holiday dates",
    website: "https://www.surbiton.org/holiday-camps",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-8",
    name: "Surbiton Hockey Club Junior Camps",
    provider: "Surbiton Hockey Club",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Sugden Road, KT6 7PB",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Hockey-focused junior holiday camp at Surbiton Hockey Club. Coaching, skills, match play and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://surbitonhc.com/colts/holiday-hockey-camps/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-9",
    name: "Surbiton Croquet Club Junior Sessions",
    provider: "Surbiton Croquet Club",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Cranes Park, KT5 8AB",
    postcodeSector: "KT5",
    distanceMiles: 7.7,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["sports"],
    description: "Seasonal croquet sessions at Surbiton Croquet Club. Skills, tactics and precision sports for older children and teens.",
    times: "Check with provider for holiday dates",
    website: "https://surbitoncroquet.org.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-10",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton Assembly Rooms, KT6 6RA",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Surbiton Assembly Rooms. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-11",
    name: "Stagecoach Surbiton Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton High School / local halls, KT6 4LQ",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Surbiton High School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-12",
    name: "Art-K Surbiton Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Art-K Studio, Victoria Road, KT6 4JT",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Victoria Road. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-13",
    name: "Code Ninjas Surbiton Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton branch, KT6 4NG",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Surbiton branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-14",
    name: "The Little Gym Surbiton Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Surbiton Studio, KT6 4QG",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Surbiton Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-15",
    name: "Alexandra Recreation Ground Sports Camps",
    provider: "Alexandra Recreation Ground",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Alexandra Rec Ground, KT6 4AL",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Alexandra Recreation Ground, within the Surbiton catchment. Football, cricket and athletics.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/locations/surbiton-shrewsbury-house",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "srb-16",
    name: "Elmbridge Canoe Club Holiday Sessions",
    provider: "Elmbridge Canoe Club",
    type: "Camp",
    neighbourhood: "Surbiton",
    location: "Thames Ditton / Surbiton reach, KT7 0QG",
    postcodeSector: "KT7",
    distanceMiles: 9.8,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Watersports holiday sessions at Elmbridge Canoe Club along the Thames Ditton and Surbiton reach. Canoeing, kayaking, water safety and paddlesports.",
    times: "Check with provider for holiday dates",
    website: "https://elmbridgecanoeclub.com/bookings/list/",
    neighbourhoods: ["Surbiton"],
  },

  // ── TEDDINGTON ───────────────────────────────────────────────────────────────
  {
    id: "tdd-1",
    name: "Newland House School Holiday Camp",
    provider: "Newland House School",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Newland House School, Waldegrave Park, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "outdoors"],
    description: "Multi-activity holiday camp at Newland House School. Sports, arts & crafts, STEM, outdoor games and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://newlandhouse.net/admissions/key-information/wrap-around-care/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-2",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-sports holiday camp at Newland House School. Football, swimming, arts & crafts, team games and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-3",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem"],
    description: "Multi-activity holiday camp at Newland House School. Sports, creative arts, STEM and team challenges.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-4",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Newland House School, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts"],
    description: "Music-focused holiday camp at Newland House School. Guitar, violin and ukulele sessions alongside drama, dance and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-5",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Teddington Sports Centre, TW11 9PJ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at Teddington Sports Centre. Football, cricket, hockey, tennis and netball, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-6",
    name: "Teddington Cricket Club Holiday Camp",
    provider: "Teddington Cricket Club",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Bushy Park Sports Ground, TW11 9PQ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Bushy Park Sports Ground. Batting, bowling, fielding and matches.",
    times: "Check with provider for holiday dates",
    website: "https://www.teddingtoncc.co.uk/holiday-camps",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-7",
    name: "Teddington Hockey Club Junior Camp",
    provider: "Teddington Hockey Club",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Bushy Park Sports Ground, TW11 9PQ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Hockey-focused junior holiday camp at Bushy Park Sports Ground. Coaching, skills, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://www.teddingtonhockey.club/hockey-camps",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-8",
    name: "Teddington Tennis Club Holiday Camps",
    provider: "Teddington Tennis Club",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Bushy Park Road, TW11 9NA",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Teddington Tennis Club. Coaching, mini tennis, match play and junior tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/bushyparktennis/Juniors",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-9",
    name: "Lensbury Club Holiday Camps",
    provider: "Lensbury Club",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Broom Road, TW11 9NU",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "swimming", "arts"],
    description: "Multi-activity holiday camp at the Lensbury Club. Swimming, tennis, watersports, fitness and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.lensbury.com/family-stays/kids-camps/holiday-camps/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-10",
    name: "Thames Young Mariners Holiday Club",
    provider: "Thames Young Mariners",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Riverside Drive, TW12 2JB",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider directly",
    age: "8–16",
    interests: ["outdoors", "swimming"],
    description: "Watersports holiday club on Riverside Drive, popular with Teddington families. Sailing, kayaking, canoeing, paddleboarding and raft building.",
    times: "Check with provider for holiday dates",
    website: "https://www.surreyoutdoorlearning.uk/centres/thames-young-mariners",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-11",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Teddington Methodist Church Hall, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Teddington Methodist Church Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-12",
    name: "Stagecoach Teddington Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Newland House / local halls, TW11 8PN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Newland House School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-13",
    name: "Art-K Teddington Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Art-K Studio, High Street, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Teddington High Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-14",
    name: "Code Ninjas Teddington Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Teddington branch, TW11 8HZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Teddington branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-15",
    name: "The Little Gym Teddington Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Teddington Studio, TW11 8TW",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Teddington Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-16",
    name: "Bushy Park Forest School Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Bushy Park, KT1 4AZ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Bushy Park. Bushcraft, den building, nature exploration and outdoor survival skills.",
    times: "Check with provider for holiday dates",
    website: "https://www.bushyparkactivitycamp.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tdd-17",
    name: "Teddington Sports Centre Holiday Camp",
    provider: "Teddington Sports Centre",
    type: "Camp",
    neighbourhood: "Teddington",
    location: "Broad Street, TW11 8NN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at Teddington Sports Centre. Swimming, sports hall games, gymnastics and indoor sports.",
    times: "Check with provider for holiday dates",
    website: "https://www.better.org.uk/leisure-centre/london/teddington/better-gym-teddington/holiday-activities",
    neighbourhoods: ["Teddington"],
  },

  // ── TOOTING ──────────────────────────────────────────────────────────────────
  {
    id: "ttg-1",
    name: "Better Tooting Leisure Centre Holiday Camp",
    provider: "Better",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Leisure Centre, Greaves Place, SW17 0NE",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at Tooting Leisure Centre. Swimming, sports hall games, gymnastics, soft play and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.placesleisure.org/centres/tooting-leisure-centre/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-2",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Burntwood School, Burntwood Lane, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Burntwood School. Football, dodgeball, archery, athletics and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-3",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Burntwood School, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at the Burntwood hub. Football, netball, hockey and tennis, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-4",
    name: "Stagecoach Tooting Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Burntwood School / local halls, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Burntwood School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-5",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Methodist Church Hall, SW17 9SH",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Tooting Methodist Church Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-6",
    name: "Little Kickers Tooting Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting / Balham venues, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Tooting and Balham venues for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-7",
    name: "Rugbytots Tooting Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Community Centre, SW17 9SH",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Tooting Community Centre for younger children. Agility, coordination and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-8",
    name: "Tooting Bec Lido Holiday Swimming Sessions",
    provider: "Tooting Bec Lido",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Bec Lido, SW16 1RU",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["swimming", "sports"],
    description: "Outdoor swimming holiday sessions at Tooting Bec Lido. Water confidence, games and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.placesleisure.org/centres/tooting-bec-lido/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-9",
    name: "Tooting Bec Common Forest School Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Bec Common, SW17 / SW16",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp on Tooting Bec Common. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://wild-learning.pembee.app/activity/690a1dbabc486ecbb562c29b",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-10",
    name: "Tooting & Mitcham United FC Holiday Camp",
    provider: "Tooting & Mitcham United FC",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Imperial Fields, CR4 1HS",
    postcodeSector: "CR4",
    distanceMiles: 4.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Football holiday camp at Imperial Fields, run by Tooting & Mitcham United FC. Coaching, drills, matches, fitness and tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://www.tmcommunitysportsclub.org/community-football",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-11",
    name: "Tooting Tennis Club Holiday Camps",
    provider: "Tooting Tennis Club",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Church Lane, SW17 9PP",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Tooting Tennis Club. Coaching, drills, match play and junior tournaments.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-12",
    name: "Tooting Bec Hockey Club Junior Camp",
    provider: "Tooting Bec Hockey Club",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Bec Common, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Hockey-focused junior holiday camp on Tooting Bec Common. Coaching, skills, match play and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.twchockey.co.uk/camps",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-13",
    name: "Art-K Tooting Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Art-K Studio, Upper Tooting Road, SW17 7TG",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Upper Tooting Road. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-14",
    name: "Code Ninjas Tooting Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting branch, SW17 0RJ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Tooting branch of Code Ninjas. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-15",
    name: "The Little Gym Tooting Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Tooting Studio, SW17 0QT",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Tooting Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttg-16",
    name: "Wandsworth Common Sports Camps",
    provider: "Wandsworth Common Sports",
    type: "Camp",
    neighbourhood: "Tooting",
    location: "Wandsworth Common, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "outdoors"],
    description: "Multi-sports holiday camp on Wandsworth Common, popular with Tooting families. Football, rugby and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://fis.wandsworth.gov.uk/activities-and-things-to-do/jJ-multi-sports-holiday-camps/",
    neighbourhoods: ["Tooting"],
  },

  // ── TWICKENHAM ───────────────────────────────────────────────────────────────
  {
    id: "twk-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Catherine's School, Cross Deep, TW1 4QJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "arts", "drama", "dance", "outdoors"],
    description: "Multi-activity holiday camp at St Catherine's School. Sports, arts & crafts, drama, dance, outdoor games and themed activities.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Catherine's School, TW1 4QJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "outdoors"],
    description: "Multi-sports holiday camp at St Catherine's School. Creative arts, STEM, team challenges and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Catherine's School, TW1 4QJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts"],
    description: "Music-focused holiday camp at St Catherine's School. Guitar, violin and ukulele sessions alongside drama, dance and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-4",
    name: "St James's School Holiday Camp",
    provider: "St James's School",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St James's School, Hampton Road, TW2 5NP",
    postcodeSector: "TW2",
    distanceMiles: 8.9,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at St James's School. Sports, arts & crafts and creative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/venue/st-james-preparatory-school",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-5",
    name: "Radnor House School Holiday Camp",
    provider: "Radnor House School",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Pope's Villa, Cross Deep, TW1 4QJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports", "arts", "stem", "drama", "outdoors", "swimming"],
    description: "Multi-activity holiday camp at Radnor House School, Pope's Villa. Arts, STEM, drama and outdoor games, with swimming available in selected weeks.",
    times: "Check with provider for holiday dates",
    website: "https://munchkinsports.com/book-a-holiday-camp-london/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-6",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Orleans Park School, TW1 3BB",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at the Twickenham hub, Orleans Park School. Football, hockey, netball, tennis and cricket, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-7",
    name: "Orleans Park Sports Camp",
    provider: "Orleans Park School",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Orleans Park School, TW1 3BB",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Orleans Park School. Athletics, football, basketball and indoor games.",
    times: "Check with provider for holiday dates",
    website: "https://englandsportsgroup.pembee.app/activity/67d2acc8319aea5aa7c57d96",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-8",
    name: "Twickenham Lawn Tennis Club Holiday Camps",
    provider: "Twickenham Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Margarets Drive, TW1 1QQ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis-focused holiday camp at Twickenham Lawn Tennis Club. Coaching, drills, match play and junior competitions.",
    times: "Check with provider for holiday dates",
    website: "https://clubspark.lta.org.uk/TwickenhamLawnTennisClub/Coaching/JuniorCoaching",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-9",
    name: "Teddington Cricket Club",
    provider: "Teddington Cricket Club",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Bushy Park Sports Ground, TW11 9PQ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Bushy Park Sports Ground, popular with Twickenham families. Batting, bowling, fielding and matches.",
    times: "Check with provider for holiday dates",
    website: "https://www.teddingtoncc.co.uk/holiday-camps",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-10",
    name: "Harlequins Rugby Foundation Holiday Camp",
    provider: "Harlequins Rugby Foundation",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Twickenham Stadium / community venues, TW2 7BA",
    postcodeSector: "TW2",
    distanceMiles: 8.9,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Rugby-focused holiday camp around Twickenham Stadium and community venues, run by the Harlequins Rugby Foundation. Coaching, skills, fitness and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.quins.co.uk/harlequins-rugby-camps",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-11",
    name: "RFU Rugby Camps",
    provider: "RFU",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Allianz Stadium (Twickenham Stadium), TW2 7BA",
    postcodeSector: "TW2",
    distanceMiles: 8.9,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports"],
    description: "Elite rugby holiday camp at the Allianz Stadium (Twickenham Stadium), run by the RFU. Rugby skills, elite coaching, strength & conditioning and match play.",
    times: "Check with provider for holiday dates",
    website: "https://www.englandrugby.com/run/coaching/age-grade-rugby/summer-activity",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-12",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Mary's Church Hall, TW1 1RJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at St Mary's Church Hall in Twickenham. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-13",
    name: "Stagecoach Twickenham Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "St Catherine's School / local halls, TW1 4QJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at St Catherine's School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-14",
    name: "Art-K Twickenham Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Art-K Studio, Church Street, TW1 3NJ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Church Street. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-15",
    name: "Code Ninjas Twickenham Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Twickenham branch, TW1 3DW",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Twickenham branch of Code Ninjas. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-16",
    name: "The Little Gym Twickenham Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Twickenham Studio, TW1 1EB",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Twickenham Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-17",
    name: "Forest School Marble Hill Park Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Marble Hill Park, TW1 2NL",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Marble Hill Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://brilliantplay.co.uk/marble-hill-playcentres/adventure-playground-summer-camps-fuel/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "twk-18",
    name: "Twickenham Rowing Club Junior Camps",
    provider: "Twickenham Rowing Club",
    type: "Camp",
    neighbourhood: "Twickenham",
    location: "Eel Pie Island / Embankment, TW1 3DY",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider directly",
    age: "12–18",
    interests: ["sports", "outdoors"],
    description: "Junior rowing camp around Eel Pie Island and the Twickenham Embankment. Water training, fitness and technique coaching for teens.",
    times: "Check with provider for holiday dates",
    website: "https://www.twickenhamrc.co.uk/squads/junior-squad",
    neighbourhoods: ["Twickenham"],
  },

  // ── WANDSWORTH ───────────────────────────────────────────────────────────────
  {
    id: "wds-1",
    name: "Better Wandsworth Holiday Camp",
    provider: "Better",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Leisure Centre, Mapleton Road, SW18 4DN",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "swimming"],
    description: "Indoor sports holiday camp at Wandsworth Leisure Centre. Swimming, sports hall games, gymnastics, soft play and team games.",
    times: "Check with provider for holiday dates",
    website: "https://www.better.org.uk/what-we-offer/activities/activities-for-kids",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-2",
    name: "Activate Camps",
    provider: "Activate Camps",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Burntwood School, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports", "drama"],
    description: "Sports and activity camp at the Wandsworth hub, Burntwood School. Football, hockey, netball, tennis and cricket, with performance camps also available.",
    times: "Check with provider for holiday dates",
    website: "https://www.activatecamps.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-3",
    name: "Premier Education Holiday Camp",
    provider: "Premier Education",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Burntwood School, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–12",
    interests: ["sports", "arts"],
    description: "Multi-sports holiday camp at Burntwood School. Football, dodgeball, archery, athletics and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.premier-education.com/holiday-clubs/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-4",
    name: "Stagecoach Wandsworth Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Burntwood School / local halls, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop at Burntwood School and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-5",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Methodist Church Hall, SW18 4EZ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Wandsworth Methodist Church Hall. Storytelling, movement and imaginative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-6",
    name: "Little Kickers Wandsworth Holiday Camp",
    provider: "Little Kickers",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth / Earlsfield venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–8",
    interests: ["sports"],
    description: "Football holiday camp across Wandsworth and Earlsfield venues for younger children. Coordination, movement skills and fun games.",
    times: "Check with provider for holiday dates",
    website: "https://www.littlekickers.co.uk/en-gb/locations/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-7",
    name: "Rugbytots Wandsworth Holiday Camp",
    provider: "Rugbytots",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Community Centre, SW18 4JJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–7",
    interests: ["sports"],
    description: "Rugby-based holiday camp at Wandsworth Community Centre for younger children. Agility, coordination and teamwork games.",
    times: "Check with provider for holiday dates",
    website: "https://www.rugbytots.co.uk/Class/Find",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-8",
    name: "Wandsworth Common Forest School Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Common, SW18 / SW12",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp on Wandsworth Common. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://forestfreedom.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-9",
    name: "Wandsworth Tennis & Netball Centre Holiday Camps",
    provider: "Wandsworth Tennis & Netball Centre",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Park / local courts, SW18 2PU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Tennis and netball holiday camp at Wandsworth Tennis & Netball Centre. Coaching, drills and match play.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-10",
    name: "Southfields Academy Sports Camp",
    provider: "Southfields Academy",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Southfields Academy, SW18 5JU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Southfields Academy, within the Wandsworth catchment. Football, dodgeball, athletics and games.",
    times: "Check with provider for holiday dates",
    website: "https://fis.wandsworth.gov.uk/activities-and-things-to-do/Sx-afc-wimbledon-football-holiday-club-southfields-academy/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-11",
    name: "Wandsworth Town Cricket Club Holiday Camp",
    provider: "Wandsworth Town Cricket Club",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Garratt Green, SW17 0NY",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Cricket-focused holiday camp at Garratt Green. Coaching, batting, bowling and matches.",
    times: "Check with provider for holiday dates",
    website: "https://www.ballancecoaching.com/cricket-coaching-for-children-in-wandsworth",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-12",
    name: "Wandsworth Park Riverside Activity Camp",
    provider: "Wandsworth Park",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Park, SW18 1PP",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["sports", "outdoors"],
    description: "Riverside activity camp at Wandsworth Park. Outdoor games, sports, fitness and nature activities.",
    times: "Check with provider for holiday dates",
    website: "http://the4thstreatham.net/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-13",
    name: "Art-K Wandsworth Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Art-K Studio, Old York Road, SW18 1TG",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Old York Road. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-14",
    name: "Code Ninjas Wandsworth Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth branch, SW18 4TE",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Wandsworth branch of Code Ninjas. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-15",
    name: "The Little Gym Wandsworth Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Studio, SW18 2PT",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Wandsworth Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-16",
    name: "Wandsworth Hockey Club Junior Camp",
    provider: "Wandsworth Hockey Club",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Common, SW18 / SW12",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Hockey-focused junior holiday camp on Wandsworth Common. Coaching, skills, match play and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.londonwayfarers.com/members-info/junior-hockey-camps/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "wds-17",
    name: "King George's Park Sports Camp",
    provider: "King George's Park",
    type: "Camp",
    neighbourhood: "Wandsworth",
    location: "King George's Park, SW18 4AZ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports", "outdoors"],
    description: "Multi-activity sports camp at King George's Park. Football, athletics and outdoor sports games.",
    times: "Check with provider for holiday dates",
    website: "https://allstartennis.co.uk/holiday-camps/",
    neighbourhoods: ["Wandsworth"],
  },

  // ── WIMBLEDON ────────────────────────────────────────────────────────────────
  {
    id: "wmb-1",
    name: "Camp Beaumont",
    provider: "Camp Beaumont",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "King's College School Wimbledon, Southside, SW19 4TT",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "swimming", "drama", "arts", "outdoors"],
    description: "Multi-activity holiday camp at King's College School Wimbledon. Sports, swimming, drama, arts & crafts and outdoor games.",
    times: "Check with provider for holiday dates",
    website: "https://www.campbeaumont.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-2",
    name: "Ultimate Activity Camps",
    provider: "Ultimate Activity Camps",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "King's College School Wimbledon, SW19 4TT",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–14",
    interests: ["sports", "arts", "stem", "swimming"],
    description: "Multi-sports holiday camp at King's College School Wimbledon. Creative arts, STEM, team challenges and swimming.",
    times: "Check with provider for holiday dates",
    website: "https://www.ultimateactivity.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-3",
    name: "The Strings Club",
    provider: "The Strings Club",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "King's College School Wimbledon, SW19 4TT",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["music", "drama", "dance", "arts"],
    description: "Music-focused holiday camp at King's College School Wimbledon. Guitar, violin and ukulele sessions alongside drama, dance and arts & crafts.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestringsclub.org/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-4",
    name: "Wimbledon High School GDST Holiday Camps",
    provider: "Wimbledon High School GDST",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Mansel Road, SW19 4AB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["sports", "arts", "stem", "drama", "dance"],
    description: "Multi-activity holiday camp at Wimbledon High School GDST. Sports, arts, STEM, drama and dance for a wide age range up to 16.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/venue/wimbledon-high-school",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-5",
    name: "King's College School Sports Camps",
    provider: "King's College School",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Southside Sports Centre, SW19 4TT",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports", "swimming"],
    description: "Multi-sports holiday camp at Southside Sports Centre. Cricket, rugby, football, athletics, swimming and strength & conditioning.",
    times: "Check with provider for holiday dates",
    website: "https://www.kingscamps.org/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-6",
    name: "Wimbledon Racquets & Fitness Club Holiday Camp",
    provider: "Wimbledon Racquets & Fitness Club",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Somerset Road, SW19 5EE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Racket sports holiday camp at Wimbledon Racquets & Fitness Club. Tennis, squash, badminton and fitness training.",
    times: "Check with provider for holiday dates",
    website: "https://wimbledonclub.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-7",
    name: "All England Lawn Tennis Club Junior Camps",
    provider: "All England Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Church Road (AELTC), SW19 5AE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports"],
    description: "Elite junior tennis camp at the All England Lawn Tennis Club. Coaching, elite performance training, match play and junior development.",
    times: "Check with provider for holiday dates",
    website: "https://raynespark.communitysport.aeltc.com/Tennis/HolidayCamps",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-8",
    name: "Wimbledon Lawn Tennis Museum Holiday Activities",
    provider: "Wimbledon Lawn Tennis Museum",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Church Road, SW19 5AE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Tennis-themed holiday activities at the Wimbledon Lawn Tennis Museum. Coaching clinics alongside history workshops.",
    times: "Check with provider for holiday dates",
    website: "https://www.wimbledon.com/en_GB/museum_and_tours/index.html",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-9",
    name: "The Study Prep Wimbledon Holiday Camp",
    provider: "The Study Prep",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Hill Road, SW19 4NS",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–11",
    interests: ["sports", "arts"],
    description: "Multi-activity holiday camp at The Study Prep on Wimbledon Hill Road. Sports, arts & crafts and creative play.",
    times: "Check with provider for holiday dates",
    website: "https://www.thestudyprep.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-10",
    name: "Cannizaro Park Forest School Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Cannizaro Park, SW19 4UE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Cannizaro Park. Bushcraft, den building, nature crafts and outdoor exploration.",
    times: "Check with provider for holiday dates",
    website: "https://cannizaropark.com/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-11",
    name: "Wimbledon Village Stables Holiday Activities",
    provider: "Wimbledon Village Stables",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "High Street Wimbledon, SW19 5LR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["outdoors", "sports"],
    description: "Equestrian holiday activities at Wimbledon Village Stables. Horse riding, stable management and equestrian skills.",
    times: "Check with provider for holiday dates",
    website: "https://wimbledonvillage.com/features/free-summer-holiday-activities/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-12",
    name: "Art-K Wimbledon Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Art-K Studio, Wimbledon Hill, SW19 7NE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Art-K Studio on Wimbledon Hill. Drawing, painting, sculpture and mixed media.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-13",
    name: "Code Ninjas Wimbledon Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Wimbledon branch, SW19 7NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Wimbledon branch of Code Ninjas. Roblox, Minecraft, robotics and game design.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-14",
    name: "The Little Gym Wimbledon Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Studio, SW19 7NL",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Wimbledon Studio. Movement, obstacle courses and coordination games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-15",
    name: "David Lloyd Wimbledon Holiday Camp",
    provider: "David Lloyd",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Raynes Park Road, SW19 7HS",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "3–14",
    interests: ["sports", "swimming"],
    description: "Multi-activity holiday camp at David Lloyd Wimbledon. Swimming, tennis, gymnastics and fitness.",
    times: "Check with provider for holiday dates",
    website: "https://www.davidlloyd.co.uk/tots-kids/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-16",
    name: "Wimbledon Common Adventure Camp",
    provider: "Wimbledon Common",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["outdoors"],
    description: "Outdoor adventure holiday camp on Wimbledon Common. Forest School, hiking, nature exploration and survival skills.",
    times: "Check with provider for holiday dates",
    website: "http://wild-learning.net/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-17",
    name: "Merton Abbey Mills Activity Camp",
    provider: "Merton Abbey Mills",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Merton High Street, SW19 2RD",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["arts", "outdoors"],
    description: "Creative activity camp at Merton Abbey Mills, within the Wimbledon catchment. Arts, crafts, outdoor games and creative workshops.",
    times: "Check with provider for holiday dates",
    website: "http://www.wimbledongymnastics.com/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "wmb-18",
    name: "Wimbledon College Sports Camps",
    provider: "Wimbledon College",
    type: "Camp",
    neighbourhood: "Wimbledon",
    location: "Edge Hill, SW19 4NS",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["sports"],
    description: "Multi-sports holiday camp at Wimbledon College. Football, rugby and athletics.",
    times: "Check with provider for holiday dates",
    website: "https://www.activcamps.com/wimbledoncollege/",
    neighbourhoods: ["Wimbledon"],
  },

  // ── WIMBLEDON VILLAGE ────────────────────────────────────────────────────────
  {
    id: "wmv-1",
    name: "Wimbledon Village Stables Holiday Riding Camp",
    provider: "Wimbledon Village Stables",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "High Street Wimbledon, SW19 5LR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["outdoors", "sports"],
    description: "Equestrian holiday camp at Wimbledon Village Stables. Horse riding, stable management, horse care, grooming and equestrian skills.",
    times: "Check with provider for holiday dates",
    website: "https://www.wvstables.com/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-2",
    name: "Cannizaro Park Forest School Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Cannizaro Park, West Side Common, SW19 4UE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Cannizaro Park on West Side Common. Bushcraft, den building, nature crafts, woodland exploration and survival skills.",
    times: "Check with provider for holiday dates",
    website: "https://cannizaropark.com/event/guided-walk-forest-bathing-2/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-3",
    name: "Wimbledon Common Adventure Camp",
    provider: "Wimbledon Common",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Common (Windmill area), SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["outdoors"],
    description: "Outdoor adventure holiday camp near the Windmill on Wimbledon Common, with Village access. Hiking, navigation skills, nature exploration and games.",
    times: "Check with provider for holiday dates",
    website: "https://wild-learning.net/holiday-clubs/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-4",
    name: "All England Lawn Tennis Club Junior Development Camps",
    provider: "All England Lawn Tennis Club",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Church Road (Village edge), SW19 5AE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports"],
    description: "Elite junior tennis development camp at the All England Lawn Tennis Club, on the edge of Wimbledon Village. Performance training, match play and junior development pathways.",
    times: "Check with provider for holiday dates",
    website: "https://raynespark.communitysport.aeltc.com/Tennis/HolidayCamps",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-5",
    name: "Wimbledon Racquets & Fitness Club Junior Camps",
    provider: "Wimbledon Racquets & Fitness Club",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Somerset Road, SW19 5EE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–16",
    interests: ["sports"],
    description: "Junior racket sports camp at Wimbledon Racquets & Fitness Club. Tennis, squash, badminton, racket skills and fitness training.",
    times: "Check with provider for holiday dates",
    website: "https://wimbledonclub.co.uk/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-6",
    name: "Wimbledon Village Tennis Coaching Camps",
    provider: "Wimbledon Village Tennis",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Crescent Road / Village courts, SW19 5QH",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "5–14",
    interests: ["sports"],
    description: "Tennis coaching camp at Wimbledon Village's local courts. Technique, match play and mini tennis.",
    times: "Check with provider for holiday dates",
    website: "https://westsideltc.co.uk/junior-tennis/holiday-tennis-camps-2/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-7",
    name: "Art-K Wimbledon Village Holiday Workshops",
    provider: "Art-K",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Village Studio, SW19 5EJ",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["arts"],
    description: "Art-focused holiday workshop at the Wimbledon Village Studio. Drawing, painting, sculpture, mixed media and creative development.",
    times: "Check with provider for holiday dates",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-8",
    name: "The Little Gym Wimbledon Village Holiday Camps",
    provider: "The Little Gym",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Village studio, SW19 5NH",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "3–12",
    interests: ["sports"],
    description: "Gymnastics-based holiday camp at The Little Gym's Wimbledon Village studio. Movement, coordination, obstacle courses and physical development games.",
    times: "Check with provider for holiday dates",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-9",
    name: "Perform Holiday Courses",
    provider: "Perform",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Village Hall, SW19 5DX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–10",
    interests: ["drama"],
    description: "Drama-focused holiday course at Wimbledon Village Hall. Storytelling, movement, imaginative play and confidence building.",
    times: "Check with provider for holiday dates",
    website: "https://www.perform.org.uk/holiday-courses",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-10",
    name: "Stagecoach Wimbledon Village Holiday Workshops",
    provider: "Stagecoach",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Village / local halls, SW19 5DX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    description: "Performing arts holiday workshop across Wimbledon Village and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check with provider for holiday dates",
    website: "https://www.stagecoach.co.uk/holiday-workshops/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-11",
    name: "Code Ninjas Wimbledon Village Create Camp",
    provider: "Code Ninjas",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Village branch, SW19 5NN",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "7–14",
    interests: ["stem", "gaming"],
    description: "Coding-focused holiday camp at the Wimbledon Village branch of Code Ninjas. Roblox, Minecraft, robotics and game development.",
    times: "Check with provider for holiday dates",
    website: "https://go.codeninjas.com/create",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-12",
    name: "Wimbledon Village Pony & Riding Experience Camp",
    provider: "Village Riding Schools",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Village Riding Schools (local providers), SW19 5LR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–14",
    interests: ["outdoors", "sports"],
    description: "Pony and riding experience camp with local Wimbledon Village riding schools. Pony care, riding lessons, stable routines and confidence building.",
    times: "Check with provider for holiday dates",
    website: "https://www.wvstables.com/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-13",
    name: "Richmond Park Spillover Forest Camp",
    provider: "Forest School",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Richmond Park (Robin Hood Gate access), SW15 5JR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider directly",
    age: "5–12",
    interests: ["outdoors"],
    description: "Outdoor Forest School holiday camp in Richmond Park via the Robin Hood Gate, within the Wimbledon Village catchment. Bushcraft, wildlife study and outdoor learning.",
    times: "Check with provider for holiday dates",
    website: "https://www.outdoorowls.com/holiday-camps-richmond",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-14",
    name: "Wimbledon High School Enrichment Camps",
    provider: "Wimbledon High School GDST",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Mansel Road, SW19 4AB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "4–16",
    interests: ["stem", "arts", "sports", "drama"],
    description: "Multi-activity enrichment camp at Wimbledon High School, within the Village catchment. STEM, arts, sports, drama and leadership workshops.",
    times: "Check with provider for holiday dates",
    website: "https://www.supercamps.co.uk/venue/wimbledon-high-school",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "wmv-15",
    name: "King's College School Performance Sports Camps",
    provider: "King's College School",
    type: "Camp",
    neighbourhood: "Wimbledon Village",
    location: "Southside Sports Centre, SW19 4TT",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider directly",
    age: "6–16",
    interests: ["sports", "swimming"],
    description: "Performance sports camp at Southside Sports Centre, within the Wimbledon Village catchment. Cricket, rugby, tennis, athletics, swimming and performance training.",
    times: "Check with provider for holiday dates",
    website: "https://www.marshfarm.co.uk/essex-summer-day-camp/",
    neighbourhoods: ["Wimbledon Village"],
  },

];

export const SOUTH_WEST_LONDON_NEIGHBOURHOODS = [
  "Balham", "Barnes", "Battersea", "Chiswick", "Clapham", "Earlsfield", "East Sheen", "Hampton", "Hampton Hill", "Hampton Wick", "Kew", "Kingston", "Mortlake", "New Malden", "Putney", "Raynes Park", "Richmond", "Roehampton", "Southfields", "Streatham", "Surbiton", "Teddington", "Tooting", "Twickenham", "Wandsworth", "Wimbledon", "Wimbledon Village"
];

// ─── Term-Time After School & Weekend Activities ───────────────────────────────
// Same shape as NORTH_LONDON_CAMPS, with one extra field: dayOfWeek
// Populated neighbourhood by neighbourhood, same workflow as the holiday camps.
export const TERM_TIME_ACTIVITIES = [

  // ── MUSWELL HILL ─────────────────────────────────────────────────────────────
  {
    id: "mht-1",
    name: "Virgin Active Muswell Hill — Junior Swim",
    provider: "Virgin Active",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Virgin Active Muswell Hill, N10 3SH",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Member rates · day pass available",
    age: "3–16",
    interests: ["swimming"],
    dayOfWeek: "Various — weekday afternoons & weekends",
    description: "Learn to swim with Virgin Active's 50-week swim term, building water confidence and technique from age 3 months up. Swim Academy progresses children through structured levels with qualified instructors.",
    times: "Check virginactive.co.uk for class times",
    website: "https://www.virginactive.co.uk/kids/swim",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-2",
    name: "Park Road Pools & Fitness — Junior Swim",
    provider: "Park Road Pools & Fitness (Better Leisure)",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Park Road Pools & Fitness, N8 8JN",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "From £17.55/session · Haringey resident rates available",
    age: "4–16",
    interests: ["swimming"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Swim school lessons at Park Road Pools & Fitness on the Muswell Hill / Crouch End border. Structured levels for all abilities from beginners to advanced, plus open swim sessions.",
    times: "Check better.org.uk for term-time class times",
    website: "https://www.better.org.uk/leisure-centre/london/haringey/park-road-pools-and-fitness",
    neighbourhoods: ["Muswell Hill","Crouch End"],
  },
  {
    id: "mht-3",
    name: "Coolhurst Junior Tennis & Squash",
    provider: "Coolhurst Tennis & Squash Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Coolhurst Tennis & Squash Club, Courtside, Coolhurst Road, N8 8EY",
    postcodeSector: "N10",
    distanceMiles: 0.7,
    cost: "Member and non-member rates · contact club for pricing",
    age: "4–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & Saturday mornings",
    description: "One of London's most vibrant clubs with 14 tennis courts (clay & grass) and 7 squash courts including 5 glass-backed. Junior coaching programmes for tennis and squash from beginners through to elite performance pathway (Accelerate Squash). Indoor winter facility available.",
    times: "Check coolhurst.co.uk for junior programme times",
    website: "https://coolhurst.co.uk/",
    neighbourhoods: ["Muswell Hill","Crouch End"],
    phone: "020 8340 4272",
  },
  {
    id: "mht-4",
    name: "Muswell Hill Golf Club Junior Academy",
    provider: "Muswell Hill Golf Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Golf Club, N10 3DX",
    postcodeSector: "N10",
    distanceMiles: 0.8,
    cost: "Stage 1: 6 group lessons over 8 weeks · equipment provided · contact club for pricing",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend group lessons",
    description: "The Academy pathway into golf — Stage 1 covers fundamentals and on-course confidence with all equipment provided, progressing through Stage 2 (individual lessons + course access) to Stage 3 (full club integration with assigned golf buddy). Fast-tracked membership on completion. PGA professional coaching.",
    times: "Check muswellhillgolfclub.co.uk for academy schedule",
    website: "https://www.muswellhillgolfclub.co.uk/membership/junior-golf/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-5",
    name: "Rhodes Avenue Tennis Club — Juniors",
    provider: "Rhodes Avenue Tennis Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Rhodes Avenue, N22 7UT",
    postcodeSector: "N22",
    distanceMiles: 1.0,
    cost: "Member and non-member rates available",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekend mornings",
    description: "Friendly community tennis club with junior coaching for all abilities. LTA-affiliated programme covering mini tennis through to competitive juniors in a welcoming local setting.",
    times: "Check LTA ClubSpark for class times",
    website: "https://clubspark.lta.org.uk/",
    neighbourhoods: ["Muswell Hill","Alexandra Palace & Wood Green"],
  },
  {
    id: "mht-6",
    name: "Muswell Hill Cricket Club — Juniors",
    provider: "Muswell Hill Cricket Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Cricket Club, N10 3PU",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11) programmes",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · Sunday matches",
    description: "Thriving junior cricket section with ECB-accredited coaching for all ages and abilities. All Stars and Dynamos Cricket programmes for younger players, progressing to club colts teams and senior pathway.",
    times: "Fri evening training · weekend match fixtures",
    website: "https://northlondoncricketclub.org/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-7",
    name: "North Middlesex Cricket Club — Juniors",
    provider: "North Middlesex Cricket Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "North Middlesex Cricket Club, N10 3PU",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Junior membership · ECB programmes available",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · weekend matches",
    description: "Long-established cricket club with a strong junior section. ECB-accredited coaching, All Stars and Dynamos programmes, progressing through age-group colts teams to senior cricket.",
    times: "Check club for current training schedule",
    website: "https://www.northmiddlesexcc.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-8",
    name: "Haringey Rhinos RFC — Juniors",
    provider: "Haringey Rhinos Rugby Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Haringey Rhinos RFC, Coppetts Wood, N22",
    postcodeSector: "N22",
    distanceMiles: 1.2,
    cost: "Junior membership · contact club for pricing",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Sunday mornings (main training day)",
    description: "Welcoming community rugby club with age-grade teams from U7 to U18. Mixed and girls' sections, RFU-accredited coaches. Strong family club atmosphere with a packed clubhouse on match days.",
    times: "Sunday mornings — check club for age-group times",
    website: "https://www.pitchero.com/clubs/haringeyrhinosrfc/",
    neighbourhoods: ["Muswell Hill","Alexandra Palace & Wood Green"],
  },
  {
    id: "mht-9",
    name: "Fusion Gymnastics Club",
    provider: "Fusion Gymnastics",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Fusion Gymnastics Club, N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Recreational and pre-competitive gymnastics classes for all ages and abilities. Structured progression through British Gymnastics levels with qualified coaches in a fun, supportive environment.",
    times: "Check with club for class schedule",
    website: "https://www.gymnastics-fusion.com/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-10",
    name: "Dynamic Performing Arts",
    provider: "Dynamic Performing Arts",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Dynamic Performing Arts, N10 3JA",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–18",
    interests: ["dance","drama","music"],
    dayOfWeek: "Weekday afternoons & Saturday",
    description: "Dance, drama and performing arts classes building confidence and stage skills. Wide age range from young beginners through to teens working towards performance opportunities and exams.",
    times: "Check with studio for class times",
    website: "https://www.dynamicperformingarts.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-11",
    name: "Stagecoach Muswell Hill",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Termly fees · contact local school for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Saturdays (typical Stagecoach format)",
    description: "Weekly drama, dance and singing classes building performance skills and confidence. Early Stages (4–6), Stagecoach (6–18) split by age group, with end-of-term performances. All abilities welcome, no experience needed.",
    times: "Saturday mornings/afternoons — check stagecoach.co.uk",
    website: "https://www.stagecoach.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-12",
    name: "Perform Muswell Hill",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–12",
    interests: ["drama","dance"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Weekly drama classes for younger children developing the 4 Cs — Confidence, Concentration, Communication and Coordination — through games, role-play and imaginative storytelling.",
    times: "Check perform.org.uk for local class times",
    website: "https://www.perform.org.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-13",
    name: "artsdepot Children's Classes",
    provider: "artsdepot",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "artsdepot, 5 Nether Street, Tally Ho Corner, N12 0GA",
    postcodeSector: "N12",
    distanceMiles: 1.5,
    cost: "Contact artsdepot for term fees · 020 8369 5454",
    age: "5–18",
    interests: ["drama","dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Term-time drama and dance classes at Haringey's flagship cultural venue. 167-seat Pentland Theatre and dedicated dance studio. Home to the London Studio Centre — professional-standard facilities for young performers.",
    times: "Check artsdepot.co.uk for class schedule",
    website: "https://www.artsdepot.co.uk/.co.uk",
    neighbourhoods: ["Muswell Hill","North Finchley"],
    phone: "020 8369 5454",
  },
  {
    id: "mht-14",
    name: "Fortismere Music Centre",
    provider: "Fortismere Music Centre",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Fortismere School, Tetherdown, N10 1NE",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact centre for instrumental lesson fees · instalment plans available",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekdays & Saturdays",
    description: "The heart of North London's musical community. Instrumental and vocal tuition, Junior and Youth Choirs, Fortismere Community Symphony Orchestra and School of Rock. Experienced professional teachers across all instruments, with options for graded exams or individual musical routes.",
    times: "Lessons during school day (Fortismere students) and Saturdays (public)",
    website: "https://www.fortismeremusiccentre.co.uk",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-15",
    name: "Haringey Young Musicians",
    provider: "Haringey Music Service",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Various venues, N22",
    postcodeSector: "N22",
    distanceMiles: 1.2,
    cost: "Subsidised rates · contact Haringey Music Service for pricing",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & Saturdays",
    description: "Borough-wide music service offering instrumental tuition, ensembles and choirs for Haringey's young musicians. Affordable, high-quality music education supporting progression from beginner to advanced level.",
    times: "Check Haringey Music Service for ensemble schedules",
    website: "https://haringeyyoungmusicians.co.uk/",
    neighbourhoods: ["Muswell Hill","Alexandra Palace & Wood Green"],
  },
  {
    id: "mht-16",
    name: "Kumon Muswell Hill",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Kumon Muswell Hill, N10 3HL",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Monthly subject fee · contact centre for pricing",
    age: "4–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly (typically Mon/Thu or Tue/Fri)",
    description: "Individualised maths and English tuition using the Kumon Method — self-paced worksheets building fluency and confidence. Daily home study supplements twice-weekly centre sessions. Long track record across the UK.",
    times: "Check local centre for session times",
    website: "https://www.kumon.co.uk",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-17",
    name: "Explore Learning Muswell Hill",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Explore Learning Muswell Hill, N10 3HL",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Monthly membership · contact centre for current pricing",
    age: "4–16",
    interests: ["stem"],
    dayOfWeek: "Flexible weekly sessions",
    description: "Maths and English tuition centre with a coach-led, personalised approach. Members attend flexible weekly sessions with a dedicated tuition coach tracking progress against the National Curriculum.",
    times: "Check local centre for available session slots",
    website: "https://www.explorelearning.co.uk",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-18",
    name: "Learning Tree Tutors — 11+ Preparation",
    provider: "Learning Tree Tutors",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation tutoring covering verbal reasoning, non-verbal reasoning, maths and English. Small groups or 1-2-1 sessions tailored to grammar school and independent school entrance exams.",
    times: "Check with tutors for current availability",
    website: "https://thelearningtree.tutorbird.com/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-19",
    name: "Coding with Kids London",
    provider: "Coding with Kids",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and programming classes covering Scratch, Python and game design depending on age and level. Small group sessions building computational thinking and creative tech skills.",
    times: "Check provider for current class schedule",
    website: "https://www.kumon.co.ukcodingwithkids.co",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-20",
    name: "ComputerXplorers Haringey",
    provider: "ComputerXplorers",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Haringey N10 (school-based sessions)",
    postcodeSector: "N10",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "6–14",
    interests: ["stem","gaming"],
    dayOfWeek: "After-school clubs (varies by school)",
    description: "Coding, robotics and STEM after-school clubs delivered in partnership with local primary schools. Hands-on projects with robotics kits, coding platforms and engineering challenges building digital skills through play.",
    times: "After-school sessions — check with local school",
    website: "https://mis.computerxplorers.co.uk/about/haringey",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-21",
    name: "Muswell Hill Chess Club — Juniors",
    provider: "Muswell Hill Chess Club",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact club for membership fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evening (check with club)",
    description: "Friendly junior chess club teaching strategy, tactics and sportsmanship. All levels welcome from complete beginners to tournament players, with coaching from experienced club members.",
    times: "Check with club for session times",
    website: "https://muswellhillchess.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-22",
    name: "Spanish Academy Muswell Hill",
    provider: "Spanish Academy",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturday mornings",
    description: "Spanish language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels from complete beginners to confident young speakers.",
    times: "Check provider for class schedule",
    website: "https://spanishlanguagetuition.com/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-23",
    name: "La Jolie Ronde Muswell Hill — French",
    provider: "La Jolie Ronde",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–11",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons (often after school)",
    description: "Fun, song-based French classes for young children using the long-established La Jolie Ronde method. Builds early language confidence through music, movement and play in small groups.",
    times: "Check local franchise for class times",
    website: "https://www.kumon.co.ukcodingwithkids.comlajolieronde.co.uk",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-24",
    name: "Creation Station Muswell Hill",
    provider: "The Creation Station",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for class pricing",
    age: "2–11",
    interests: ["arts"],
    dayOfWeek: "Weekday mornings (toddlers) & after-school sessions",
    description: "Open-ended, process-led art and craft classes for young children. No two sessions the same — children explore different art forms, materials and techniques each week in a relaxed, creative environment.",
    times: "Check local franchise for class schedule",
    website: "https://www.thecreationstation.co.uk",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-25",
    name: "ArtK Muswell Hill",
    provider: "ArtK",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "ArtK Muswell Hill, N10 3HP",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Drawing and art classes developing observational skills, technique and creative confidence. Structured progression through different media and styles for children and young teens.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/workshops/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-26",
    name: "Mini Picassos Art Club",
    provider: "Mini Picassos",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Friendly art club introducing children to painting, drawing and mixed media in a relaxed, encouraging setting. Small groups with plenty of individual attention from experienced art tutors.",
    times: "Check with club for current schedule",
    website: "https://www.minipicassos.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-27",
    name: "Shooting Starz Performing Arts",
    provider: "Shooting Starz",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["dance","drama"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Dance and performing arts classes blending technique with performance opportunities. Builds confidence, rhythm and stage presence through a mix of street, musical theatre and creative dance styles.",
    times: "Check provider for class times",
    website: "https://www.shootingstarz.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-28",
    name: "Dance Crazy Academy",
    provider: "Dance Crazy Academy",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Termly fees · pay-as-you-go drop-in classes also available from £6",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Ballet, tap and modern dance classes for a wide age range from toddlers through to teens. RAD and ISTD-style structured syllabus options available alongside more relaxed creative movement classes for younger children.",
    times: "Check academy for class schedule",
    website: "https://www.dancecrazystudios.co.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-29",
    name: "Muswell Hill Methodist Church Choir — Juniors",
    provider: "Muswell Hill Methodist Church",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill Methodist Church, N10 3NU",
    postcodeSector: "N10",
    distanceMiles: 0.4,
    cost: "Free or small donation — contact church for details",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal (typically weekday evening)",
    description: "Community choir welcoming young singers to learn vocal technique, sight-reading and performance through regular rehearsals and seasonal concerts. Warm, inclusive environment open to all faiths and backgrounds.",
    times: "Check with church for rehearsal schedule",
    website: "https://mhmc.org.uk/",
    neighbourhoods: ["Muswell Hill"],
  },
  {
    id: "mht-30",
    name: "London School of Capoeira — Muswell Hill",
    provider: "London School of Capoeira",
    type: "Club",
    neighbourhood: "Muswell Hill",
    location: "Muswell Hill N10",
    postcodeSector: "N10",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["sports","music","dance"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Capoeira blends martial art, dance, music and acrobatics in this energetic Brazilian discipline. Builds fitness, flexibility, rhythm and discipline in a fun, non-competitive group setting suitable for complete beginners.",
    times: "Check school for class times",
    website: "https://www.londonschoolofcapoeira.com",
    neighbourhoods: ["Muswell Hill"],
  },


  // ── EAST FINCHLEY ─────────────────────────────────────────────────────────────
  {
    id: "eft-1",
    name: "Finchley Lido Leisure Centre — Swim School",
    provider: "Better Leisure (GLL)",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "Great North Leisure Park, Chaplin Square, N12 0GL",
    postcodeSector: "N12",
    distanceMiles: 0.9,
    cost: "From £17.55/session · Barnet resident rates available",
    age: "4–16",
    interests: ["swimming"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Swim England Learn to Swim Programme across three pools — 25m main pool, leisure pool with wave machine, and outdoor seasonal lido. Structured levels for all abilities. Tube: East Finchley, or buses 263 & 382.",
    times: "Mon–Fri 6:45am–10pm · Sat–Sun 8am–5:30pm",
    website: "https://www.better.org.uk/leisure-centre/london/barnet/finchley-lido-leisure-centre/swimming-lessons",
    neighbourhoods: ["East Finchley","North Finchley"],
    phone: "020 8343 9830",
  },
  {
    id: "eft-2",
    name: "Phoenix Martial Arts East Finchley",
    provider: "Phoenix Martial Arts",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 9EB",
    postcodeSector: "N2",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Martial arts classes building discipline, fitness and self-confidence. Structured grading system with qualified instructors, suitable for complete beginners through to advanced students.",
    times: "Check club for class schedule",
    website: "http://www.phoenixkarateschool.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-3",
    name: "East Finchley Tennis Club — Juniors",
    provider: "East Finchley Tennis Club",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley Tennis Club, N2 9AU",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Member and non-member rates available",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekend mornings",
    description: "Friendly local tennis club offering junior coaching for all abilities, from mini tennis through to competitive juniors. LTA-affiliated programme with a welcoming community feel.",
    times: "Check LTA ClubSpark for class times",
    website: "https://www.clubspark.lta.org.uk",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-4",
    name: "Old Finchleians Cricket Club — Juniors",
    provider: "Old Finchleians Cricket Club",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "Finchley Cricket Club, East End Road, N3 2TA",
    postcodeSector: "N3",
    distanceMiles: 1.5,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11) programmes",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · Sunday matches",
    description: "Established cricket club with a strong junior section playing on a large outdoor ground. ECB-accredited coaching, All Stars and Dynamos Cricket programmes, progressing to age-group colts teams.",
    times: "Fri evening training · weekend matches",
    website: "https://www.finchleycricket.co.uk/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-5",
    name: "Saracens Rugby Foundation — Junior Sessions",
    provider: "Saracens Rugby Foundation",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 (community venues)",
    postcodeSector: "N2",
    distanceMiles: 0.6,
    cost: "Contact foundation for term fees",
    age: "6–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings or weekend mornings",
    description: "Community rugby sessions delivered by Saracens Rugby Foundation, the charitable arm of Premiership club Saracens. Skills development, teamwork and fitness in a fun, inclusive environment for all abilities.",
    times: "Check foundation for local session times",
    website: "https://www.saracens.com/foundation",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-6",
    name: "East Finchley Gymnastics Club",
    provider: "East Finchley Gymnastics Club",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Recreational gymnastics classes for all ages and abilities, building strength, flexibility and confidence. Structured progression through British Gymnastics levels with qualified coaches.",
    times: "Check club for class schedule",
    website: "https://performbettersc.co.uk/our-activities/holiday-camps/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-7",
    name: "Dance Crazy East Finchley",
    provider: "Dance Crazy",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 9ED",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Termly fees · pay-as-you-go drop-in classes also available",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Ballet, tap and modern dance classes spanning toddlers through to teens. Structured syllabus options alongside relaxed creative movement classes for the youngest dancers.",
    times: "Check academy for class schedule",
    website: "https://dancecrazystudios.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-8",
    name: "Anna Fiorentini Theatre & Film School",
    provider: "Anna Fiorentini Theatre & Film School",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 9BE",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Taster day £20 (deducted from first term) · monthly instalments available · Fiorentini Foundation bursaries for families who need help with fees",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Saturdays — full afternoon schedule",
    description: "Award-winning, not-for-profit stage school (Est. 2001) with branches across London. Saturday sessions cover drama, singing, dance and screen acting. Fiorentini Weenies (4–6) and Main School (7–18). Alumni have appeared in EastEnders, The Lion King and Matilda. DBS-checked, industry-trained teachers.",
    times: "Saturdays — full afternoon timetable",
    website: "https://www.afperformingarts.com",
    neighbourhoods: ["East Finchley"],
    notes: "Sibling discounts available. Apply to the Fiorentini Foundation for bursary support. Not a franchise — same principal across all branches.",
  },
  {
    id: "eft-9",
    name: "Bojangles Creative Studios",
    provider: "Bojangles Creative Studios",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 8JN",
    postcodeSector: "N2",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "4–18",
    interests: ["dance","drama","music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Performing arts studio offering dance, drama and musical theatre classes across a wide age range. Builds technique, confidence and performance experience with regular showcase opportunities.",
    times: "Check studio for class schedule",
    website: "",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-10",
    name: "East Finchley Children's Music Group",
    provider: "East Finchley Children's Music Group",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons",
    description: "Instrumental and group music lessons for children, building musical skills and confidence in a friendly local setting. Suitable for complete beginners through to more advanced young musicians.",
    times: "Check provider for current class times",
    website: "https://www.fcmg.org.uk/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-11",
    name: "Finchley Children's Music Centre",
    provider: "Finchley Children's Music Centre",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact centre for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & Saturdays",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check centre for lesson schedule",
    website: "https://www.fcmg.org.uk/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-12",
    name: "First Class Learning East Finchley",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 8DA",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact centre for current pricing",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths and English tuition centre using a structured, personalised approach to build core skills and exam confidence. Regular progress tracking against the National Curriculum.",
    times: "Check local centre for session times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/finchley-north/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-13",
    name: "Bright Young Things Education — 11+ Prep",
    provider: "Bright Young Things Education",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal and non-verbal reasoning, maths and English. Small group or 1-2-1 tuition tailored to grammar and independent school entrance requirements.",
    times: "Check provider for availability",
    website: "http://kallikids.com/en/provider-detail.cfm/page/bright-young-things-tuition/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-14",
    name: "Young Engineers North London",
    provider: "Young Engineers",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "6–14",
    interests: ["stem","gaming"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on STEM and robotics classes using LEGO-based engineering kits. Builds problem-solving, teamwork and technical skills through fun, structured weekly challenges.",
    times: "Check provider for class schedule",
    website: "https://youngengineers.org/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-15",
    name: "Tech Camp UK — Term-Time Coding",
    provider: "Tech Camp UK",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and technology classes covering Scratch, Python, web development and game design depending on age and ability. Small groups building digital literacy and computational thinking.",
    times: "Check provider for current schedule",
    website: "https://www.techcamp.org.uk/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-16",
    name: "East Finchley Chess Club — Juniors",
    provider: "East Finchley Chess Club",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly junior chess club teaching strategy and tactics in a welcoming, non-pressured environment. All levels from beginners to tournament-level players welcome.",
    times: "Check with club for session times",
    website: "https://www.barnetknights.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-17",
    name: "Lingotot North London — Spanish & French",
    provider: "Lingotot",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "3–11",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons (typically after school)",
    description: "Language immersion classes for young children, teaching Spanish or French through songs, stories, games and play. Builds early language confidence in a fun, low-pressure setting.",
    times: "Check local franchise for class times",
    website: "https://www.lingotot.com/c",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-18",
    name: "French Academy London — East Finchley",
    provider: "French Academy London",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "4–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Structured French language classes for children, progressing from early vocabulary and songs through to conversational fluency for older students. Native or fluent French-speaking tutors.",
    times: "Check academy for class schedule",
    website: "https://www.ecoledespetitsloups.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-19",
    name: "Artbox East Finchley",
    provider: "Artbox",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes covering drawing, painting and mixed media for children and young teens. Structured progression building technical skill alongside creative confidence in small group sessions.",
    times: "Check studio for class times",
    website: "https://www.artbox.co.uk/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-20",
    name: "The Little Arts Workshop",
    provider: "The Little Arts Workshop",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 9ED",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Open-ended arts and crafts classes exploring painting, collage, sculpture and mixed media. Relaxed, process-led approach encouraging creativity over perfection in a nurturing setting.",
    times: "Check with workshop for current schedule",
    website: "https://thelittleartsworkshop.com/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-21",
    name: "Clayground Collective — Children's Pottery Workshops",
    provider: "Clayground Collective",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for workshop pricing",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops (typically Saturday)",
    description: "Hands-on pottery and ceramics workshops teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check collective for workshop dates",
    website: "http://www.claygroundcollective.org/",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-22",
    name: "Photography for Kids London — East Finchley",
    provider: "Photography for Kids London",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "8–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Beginner-friendly photography classes teaching composition, lighting and editing basics. Hands-on sessions using digital cameras or smartphones, building a creative eye and technical understanding.",
    times: "Check provider for course dates",
    website: "",
    neighbourhoods: ["East Finchley"],
  },
  {
    id: "eft-23",
    name: "Young Film Academy — Term-Time Workshops",
    provider: "Young Film Academy (YFA)",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "East Finchley N2 (venue varies by course)",
    postcodeSector: "N2",
    distanceMiles: 0.6,
    cost: "Contact YFA for current course pricing",
    age: "8–18",
    interests: ["arts","stem","drama"],
    dayOfWeek: "Saturdays (term-time) — main holiday courses run in school breaks",
    description: "The UK's leading provider of practical filmmaking courses for young people aged 5–18. Hands-on filmmaking using professional equipment, working with industry professionals. BFI Film Academy network hub partner, with award ceremonies held at the BFI and Prince Charles Cinema.",
    times: "Check youngfilmacademy.co.uk for term-time and holiday course dates",
    website: "https://www.youngfilmacademy.co.uk/",
    neighbourhoods: ["East Finchley"],
    notes: "Main camps run in school holidays — check website for any term-time taster sessions in the East Finchley area.",
  },
  {
    id: "eft-24",
    name: "Cherry Tree Wood Nature Club",
    provider: "Cherry Tree Wood Nature Club",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "Cherry Tree Wood, N2 0QZ",
    postcodeSector: "N2",
    distanceMiles: 0.3,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature club in beautiful ancient woodland right next to East Finchley tube station. Den building, bug hunts, seasonal nature exploration and outdoor play in a beloved local green space.",
    times: "Check local listings for session dates",
    website: "https://friendsofcherrytreewood.home.blog/",
    neighbourhoods: ["East Finchley"],
    notes: "Cherry Tree Wood is a well-loved local nature reserve, directly adjacent to East Finchley station.",
  },
  {
    id: "eft-25",
    name: "Martin Primary School Community Sports Programme",
    provider: "Martin Primary School",
    type: "Club",
    neighbourhood: "East Finchley",
    location: "Martin Primary School, Plane Tree Walk, N2 0BT",
    postcodeSector: "N2",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–11",
    interests: ["sports","outdoors"],
    dayOfWeek: "After-school sessions (varies by sport)",
    description: "Community multi-sports programme run at Martin Primary School, open to the wider East Finchley community. Football, dodgeball, multi-skills and team games for primary-age children.",
    times: "Check with school for current programme times",
    website: "https://www.martinprimary.org.uk/premium-grants",
    neighbourhoods: ["East Finchley"],
  },


  // ── HIGHGATE ──────────────────────────────────────────────────────────────────
  {
    id: "hgt-1",
    name: "Highgate Cricket Club — Juniors",
    provider: "Highgate Cricket Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate Cricket Club, Shepherd's Cot, off Park Road, N6 4AH",
    postcodeSector: "N6",
    distanceMiles: 0.7,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11)",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · Sunday matches",
    description: "National award-winning junior cricket programme with strong girls' teams that won their leagues in 2025. ECB-accredited, DBS-checked, First Aid-qualified coaches across U9–U17 age groups.",
    times: "Fri evening training · weekend matches",
    website: "https://highgate-cricket.co.uk/juniors/",
    neighbourhoods: ["Highgate"],
    phone: "cricket@highgate-cricket.co.uk",
  },
  {
    id: "hgt-2",
    name: "Highgate Golf Club Junior Academy",
    provider: "Highgate Golf Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate Golf Club, N6 4AY",
    postcodeSector: "N6",
    distanceMiles: 0.7,
    cost: "Contact club for academy pricing",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend group lessons",
    description: "Junior golf academy with structured coaching pathway from beginners through to club membership. PGA professional instruction and access to practice facilities.",
    times: "Check club for academy schedule",
    website: "http://www.highgategc.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-3",
    name: "Highgate School Sports Centre — Community Sessions",
    provider: "Highgate School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate School, North Road, N6 5HF",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact school for community membership rates",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Multi-sport facilities at Highgate School open to the wider community for term-time coaching sessions. Football, netball, swimming and athletics using the school's excellent grounds.",
    times: "Check school for community programme times",
    website: "https://www.highgateschool.org.uk/mallinson/holiday-camps/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-4",
    name: "Highgate Harriers — Young Athletes",
    provider: "Highgate Harriers Athletics Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Parliament Hill Athletics Track, Hampstead Heath, N6 5HF",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Contact club for junior membership fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings (track) · Saturday cross country",
    description: "London's premier athletics club, founded 1879, based at the newly refurbished Parliament Hill track on Hampstead Heath. Comprehensive youth athletics from fitness sessions to competitive track, field and cross country. Strong junior pathway with London Youth Games participation.",
    times: "Check highgateharriers.org.uk for young athletes schedule",
    website: "https://www.highgateharriers.org.uk/young-athletes",
    neighbourhoods: ["Highgate","Hampstead"],
  },
  {
    id: "hgt-5",
    name: "Highgate Judo Club",
    provider: "Highgate Judo Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6 5UA",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Traditional judo classes building discipline, fitness and self-defence skills. Structured belt grading system with qualified instructors, welcoming complete beginners through to competitive judoka.",
    times: "Check club for class times",
    website: "https://britishjudocouncil.org/club-details/82",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-6",
    name: "Aikido in Highgate",
    provider: "Aikido in Highgate",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Aikido and self-defence classes teaching coordination, focus and non-aggressive conflict resolution. Suitable for complete beginners, building confidence through structured technique practice.",
    times: "Check provider for class times",
    website: "https://aikidooflondon.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-7",
    name: "Highgate Ballet School",
    provider: "Highgate Ballet School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate Ballet School, N6 5JG",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Termly fees · free trial lesson available",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Cecchetti and RAD method ballet and contemporary dance training from baby ballet through to diploma level. Also serves families from Muswell Hill, East Finchley, Crouch End, Hornsey, Archway and Kentish Town. Graceful, structured teaching in a safe, nurturing environment.",
    times: "Check school for class schedule",
    website: "https://www.highgateballetschool.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-8",
    name: "RISE Studios Highgate",
    provider: "RISE Studios",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Street dance and commercial dance classes building rhythm, confidence and performance skills. High-energy sessions covering hip hop, commercial and freestyle styles for a wide age range.",
    times: "Check studio for class times",
    website: "https://www.risedesignstudio.co.uk/en/architects-highgate",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-9",
    name: "Highgate Choral Society — Youth Programme",
    provider: "Highgate Choral Society",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact society for youth membership fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal (typically weekday evening)",
    description: "Choir and vocal training for young singers, building technique, sight-reading and performance confidence through regular rehearsals and seasonal concerts in a supportive choral community.",
    times: "Check society for rehearsal schedule",
    website: "https://www.hcschoir.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-10",
    name: "DaCapo Music Foundation",
    provider: "DaCapo Music Foundation",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact foundation for term fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Orchestra and instrumental music programme for young musicians, offering ensemble playing experience alongside individual instrumental tuition. Supports progression from beginner to advanced level.",
    times: "Check foundation for rehearsal and lesson schedule",
    website: "https://www.dacapo.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-11",
    name: "Colourstrings Music School Highgate",
    provider: "Colourstrings",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Colourstrings Music School, N6 6BA",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "4–16",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Internationally recognised music education method teaching piano, violin and other instruments through a structured, joyful pedagogical approach. Strong emphasis on musicianship alongside technique from an early age.",
    times: "Check school for lesson schedule",
    website: "https://www.colourstrings.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-12",
    name: "Channing School Weekend Music Academy",
    provider: "Channing School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Channing School, Highgate, N6 5HF",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Contact school for academy fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Saturdays",
    description: "Weekend instrumental music academy at Channing School, offering tuition across a range of instruments for children and teens. Uses the school's excellent music facilities, open to the wider community.",
    times: "Saturdays — check school for exact times",
    website: "https://www.channing.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-13",
    name: "The Actors Centre — Youth Workshops",
    provider: "The Actors Centre",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact centre for workshop fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Drama and performing arts workshops for young people, building acting technique, confidence and stage presence. Industry-informed teaching approach with a focus on craft and self-expression.",
    times: "Check centre for workshop schedule",
    website: "https://www.jacksonslane.org.uk/whats-on/classes-courses/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-14",
    name: "Highgate Literary & Scientific Institution — Youth Workshops",
    provider: "Highgate Literary & Scientific Institution",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate Literary & Scientific Institution, N6 5HX",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact HLSI for workshop fees",
    age: "8–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Creative writing and debate workshops for young people at this historic literary institution founded in 1839. Builds critical thinking, public speaking and writing craft in a stimulating intellectual setting.",
    times: "Check HLSI for workshop schedule",
    website: "http://www.hlsi.org.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-15",
    name: "Minimus — Creative History Club",
    provider: "Minimus",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "4–11",
    interests: ["arts","drama"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Creative history club bringing the past to life through storytelling, role-play and hands-on activities. Children explore historical periods and figures in an imaginative, engaging format.",
    times: "Check provider for class schedule",
    website: "https://minimusegame.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-16",
    name: "Keystone Tutors Highgate",
    provider: "Keystone Tutors",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for tuition rates",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Premium maths and English tuition with highly qualified tutors. Personalised approach building academic confidence and exam technique across a wide age range, from primary through to A-level.",
    times: "Check provider for tutor availability",
    website: "https://www.keystonetutors.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-17",
    name: "Highgate 11+ Academy",
    provider: "Highgate 11+ Academy",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact academy for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check academy for class schedule",
    website: "https://www.highgatetutors.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-18",
    name: "NumberWorks'nWords Highgate",
    provider: "NumberWorks'nWords",
    type: "Club",
    neighbourhood: "Highgate",
    location: "NumberWorks'nWords Highgate, N6 5BS",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact centre for current pricing",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths and English tuition centre using individualised assessment and structured learning plans. Builds core skills and confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://numberworksnwords.com/uk",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-19",
    name: "Coding Ninjas North London",
    provider: "Coding Ninjas",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and programming classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and creative tech skills.",
    times: "Check provider for class schedule",
    website: "https://www.codeninjas.com/camden-ldn-uk",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-20",
    name: "Robot Academy London — Highgate",
    provider: "Robot Academy London",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "7–14",
    interests: ["stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Robotics and engineering classes using hands-on kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project-based learning.",
    times: "Check academy for class schedule",
    website: "https://www.codekids.org.uk/portfolio-items/highgate-primary-school-highgate/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-21",
    name: "Highgate Chess Club — Juniors",
    provider: "Highgate Chess Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6 5HX",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly junior chess club teaching strategy, tactics and sportsmanship for all levels from complete beginners to tournament players, with coaching from experienced club members.",
    times: "Check with club for session times",
    website: "https://www.highgateschool.org.uk/news/77-clubs-and-societies/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-22",
    name: "Mandarin Tree Highgate",
    provider: "Mandarin Tree",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Mandarin Chinese language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels for children of all ages and backgrounds.",
    times: "Check provider for class schedule",
    website: "https://highgateprimaryschool.co.uk/curriculum/mandarin/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-23",
    name: "German Saturday School Highgate",
    provider: "German Saturday School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Weekly German language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Saturday mornings — check school for exact times",
    website: "http://deutschesamstagsschule.net/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-24",
    name: "Highgate Contemporary Art School",
    provider: "Highgate Contemporary Art School",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes covering drawing, painting and contemporary techniques for children and young teens. Structured progression building technical skill alongside creative confidence in small group sessions.",
    times: "Check school for class times",
    website: "https://www.highgateartschool.com/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-25",
    name: "Obby Art Studio Highgate",
    provider: "Obby Art Studio",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Obby Art Studio, N6 5JW",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Friendly art studio offering classes in painting, drawing and mixed media. Relaxed, encouraging teaching style for children of all abilities, with regular opportunities to showcase work.",
    times: "Check studio for class times",
    website: "https://obby.co.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-26",
    name: "Clay Time Pottery Studio",
    provider: "Clay Time Pottery Studio",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.claytime.london/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-27",
    name: "Highgate Photography Club — Youth Workshop",
    provider: "Highgate Photography Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact club for workshop pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check club for workshop dates",
    website: "https://hampsteadphotosoc.org.uk/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-28",
    name: "Highgate Film Makers Club",
    provider: "Highgate Film Makers Club",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Highgate N6",
    postcodeSector: "N6",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","drama","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking club for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers on creative projects.",
    times: "Check club for session schedule",
    website: "http://www.sparksarts.co.uk/locations/highgate",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-29",
    name: "Waterlow Park Nature Explorers",
    provider: "Waterlow Park Nature Explorers",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Waterlow Park, Highgate Hill, N6 5HG",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature, science and outdoor learning sessions in the beautiful 26-acre Waterlow Park. Den building, bug hunts, plant identification and seasonal nature exploration in one of Highgate's most loved green spaces.",
    times: "Check local listings for session dates",
    website: "https://www.waterlowpark.org.uk/the-park/",
    neighbourhoods: ["Highgate"],
  },
  {
    id: "hgt-30",
    name: "Lauderdale House Children's Classes",
    provider: "Lauderdale House",
    type: "Club",
    neighbourhood: "Highgate",
    location: "Lauderdale House, Waterlow Park, Highgate Hill, N6 5HG",
    postcodeSector: "N6",
    distanceMiles: 0.5,
    cost: "Charges apply — contact house for term fees",
    age: "5–16",
    interests: ["arts","drama","music"],
    dayOfWeek: "Term-time weekly classes",
    description: "Grade II* listed Tudor manor house (dating to 1582) in Waterlow Park, welcoming over 65,000 visitors a year. Runs termly art classes for ages 5–11 plus local children's groups covering drama, music and storytelling. Step-free access, lift to first floor, café on site.",
    times: "Wed–Fri 11am–4pm · Sun 10am–5pm — check website for class times",
    website: "https://www.lauderdalehouse.org.uk/whats-on/childrens-activities",
    neighbourhoods: ["Highgate"],
    phone: "020 8348 8716",
    notes: "Tube: Archway (walk up Highgate Hill) or buses 143, 210, 271 direct to Waterlow Park.",
  },


  // ── CROUCH END ────────────────────────────────────────────────────────────────
  {
    id: "cet-1",
    name: "Crouch End Festival Chorus — Youth Choir",
    provider: "Crouch End Festival Chorus",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Rehearsals in Muswell Hill, N8 area",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact CEFC for youth membership details",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with chorus",
    description: "One of Britain's leading symphonic choirs (founded 1984, around 150 singers), performing at the BBC Proms, Royal Albert Hall and Barbican under MBE-awarded conductor David Temple. Patrons include Hans Zimmer and Noel Gallagher. Younger singers can get involved through linked youth and community choir initiatives connected to the main chorus.",
    times: "Check cefc.org.uk for youth/community choir opportunities",
    website: "https://www.cefc.org.uk/",
    neighbourhoods: ["Crouch End","Muswell Hill"],
    notes: "The main CEFC is an audition-only adult choir — contact them directly for current youth or family choir programmes connected to the chorus.",
  },
  {
    id: "cet-2",
    name: "Hornsey Cricket Club — Juniors",
    provider: "Hornsey Cricket Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Hornsey CC, Tivoli Road, Crouch End, N8 8RG",
    postcodeSector: "N8",
    distanceMiles: 0.4,
    cost: "ECB All Stars (5–8) and Dynamos (8–11) · approx. £40–£50 for 8-week programme · personalised kit included",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Saturday mornings (All Stars/Dynamos) · weekday training for older juniors",
    description: "Friendly, well-established cricket club running ECB All Stars and Dynamos Cricket programmes for beginners, progressing to club colts teams. Registered ECB Dynamos venue. Every child receives kit including bat, ball, backpack and personalised t-shirt.",
    times: "Check ECB ClubSpark for current programme dates",
    website: "https://www.hornseycricketclub.co.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-3",
    name: "Hornsey Athletics Club — Juniors",
    provider: "Hornsey Athletics Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Hornsey N8 7RX",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Contact club for junior membership fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings (track) · weekend competitions",
    description: "Community athletics club offering structured coaching across track and field disciplines. Builds fitness, technique and competitive opportunity for young athletes of all abilities.",
    times: "Check club for training schedule",
    website: "https://www.runandbecome.com/london-running-clubs/london-heathside",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-4",
    name: "Crouch End Vampires Basketball Club",
    provider: "Crouch End Vampires",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community basketball club with junior teams across multiple age groups. Skills development, team play and competitive matches in a fun, inclusive club environment.",
    times: "Check club for training times",
    website: "http://crouchendvampires.co.uk/about-us",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-5",
    name: "YMCA Hornsey — Gymnastics Programme",
    provider: "YMCA Hornsey",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "YMCA Hornsey, N8 9LP",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Contact YMCA for term fees · concession rates may be available",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Recreational gymnastics classes run by YMCA Hornsey, building strength, flexibility and confidence for all abilities. Inclusive, community-focused approach with affordable pricing.",
    times: "Check YMCA for class schedule",
    website: "https://ymca.org.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-6",
    name: "London School of Capoeira — Hornsey",
    provider: "London School of Capoeira",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Hornsey N8 8JN",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["sports","music","dance"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Capoeira blends martial art, dance, music and acrobatics in this energetic Brazilian discipline. Builds fitness, flexibility, rhythm and discipline in a fun, non-competitive group setting.",
    times: "Check school for class times",
    website: "https://www.londonschoolofcapoeira.com/",
    neighbourhoods: ["Crouch End","Muswell Hill"],
  },
  {
    id: "cet-7",
    name: "Crouch End Salsa & Dance Academy",
    provider: "Crouch End Salsa & Dance Academy",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Latin dance and performing arts classes covering salsa, Latin rhythms and showmanship for a wide age range. Builds confidence, coordination and performance flair in a vibrant, energetic setting.",
    times: "Check academy for class schedule",
    website: "https://www.crouchendschoolofdance.co.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-8",
    name: "All Star Dance Academy Crouch End",
    provider: "All Star Dance Academy",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Jazz and dance classes spanning young children through to teens. Structured technique training alongside performance opportunities, building confidence and stage presence.",
    times: "Check academy for class schedule",
    website: "https://www.allstarsacademy.biz/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-9",
    name: "Kidenza Orchestra Classes",
    provider: "Kidenza",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8 9JJ",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Orchestral music classes introducing children to ensemble playing alongside instrumental tuition. Builds musicianship, listening skills and the joy of playing together.",
    times: "Check provider for class schedule",
    website: "https://www.kidenza.co.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-10",
    name: "Haringey Music Service — Saturday Centre",
    provider: "Haringey Music Service",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8 9JJ",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Subsidised rates · contact Haringey Music Service for pricing",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Saturdays",
    description: "Borough-wide Saturday music centre offering instrumental tuition, ensembles and choirs for Haringey's young musicians. Affordable, high-quality music education supporting all levels.",
    times: "Saturdays — check Haringey Music Service for exact times",
    website: "https://haringey.gov.uk/schools-learning/haringey-music-service",
    neighbourhoods: ["Crouch End","Muswell Hill"],
  },
  {
    id: "cet-11",
    name: "Crouch End Children's Bookshop — Creative Writing Workshops",
    provider: "Crouch End Children's Bookshop",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End Children's Bookshop, N8 9EA",
    postcodeSector: "N8",
    distanceMiles: 0.4,
    cost: "Contact bookshop for workshop pricing",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Creative writing and storytelling workshops at this beloved independent children's bookshop. Encourages imagination and narrative skills through guided writing exercises and storytelling games.",
    times: "Check bookshop for workshop schedule",
    website: "http://www.pickledpepperbooks.co.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-12",
    name: "The Little Learners Club",
    provider: "The Little Learners Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths and English tuition for primary-age children, building core skills and confidence through a structured, supportive approach with regular progress tracking.",
    times: "Check provider for session times",
    website: "https://thelittlelearningclub.org/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-13",
    name: "JK Educate Crouch End — 11+ Preparation",
    provider: "JK Educate",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation tutoring covering verbal reasoning, non-verbal reasoning, maths and English. Small groups or 1-2-1 sessions tailored to grammar and independent school entry.",
    times: "Check provider for availability",
    website: "https://dukestutoring.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-14",
    name: "RoboThink North London",
    provider: "RoboThink",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, coding basics and problem-solving. Builds STEM confidence through fun, structured challenges.",
    times: "Check provider for class schedule",
    website: "https://robothink.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-15",
    name: "STEM Academy London — Crouch End",
    provider: "STEM Academy London",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Science and STEM enrichment classes covering hands-on experiments, engineering challenges and scientific principles. Builds curiosity and practical understanding through interactive sessions.",
    times: "Check academy for class schedule",
    website: "https://www.londonstemacademy.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-16",
    name: "Crouch End Chess Academy",
    provider: "Crouch End Chess Academy",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact academy for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess academy teaching strategy, tactics and sportsmanship for all levels from complete beginners to tournament players, with structured coaching and regular practice games.",
    times: "Check academy for session times",
    website: "https://www.mindfulchess.org/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-17",
    name: "Club Petit Pierrot — French",
    provider: "Club Petit Pierrot",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "3–11",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons (often after school)",
    description: "Fun, immersive French classes for young children using songs, stories and play. Builds early language confidence in small groups with a focus on enjoyment over formal grammar.",
    times: "Check local franchise for class times",
    website: "https://clubpetitpierrot.co.uk/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-18",
    name: "Spanish Club for Kids — Crouch End",
    provider: "Spanish Club for Kids",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–14",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturday mornings",
    description: "Spanish language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels from complete beginners to confident young speakers.",
    times: "Check provider for class schedule",
    website: "https://www.lingotot.com/centre/crouch-end/holiday-camps",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-19",
    name: "Crouch End ArtHouse Youth Studio",
    provider: "Crouch End ArtHouse",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End ArtHouse, N8 9BT",
    postcodeSector: "N8",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Art studio offering classes in drawing and mixed media for children and young teens. Structured progression building technical skill alongside creative confidence in small group sessions.",
    times: "Check studio for class times",
    website: "https://www.arthousecrouchend.co.uk/studioworkshops/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-20",
    name: "Handmade Workshops London — Sewing & Design",
    provider: "Handmade Workshops London",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for workshop pricing",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Sewing and textile design workshops teaching practical making skills — hand-stitching, basic machine sewing and simple garment or accessory design for children and young teens.",
    times: "Check provider for workshop dates",
    website: "https://thenewcrafthouse.com/collections/workshops",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-21",
    name: "Clay Collective Crouch End",
    provider: "Clay Collective",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.clayhouselondon.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-22",
    name: "Lens Lab Youth Photography",
    provider: "Lens Lab",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "9–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check provider for course dates",
    website: "http://www.thelondonlensproject.com/",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-23",
    name: "Young Creatives Media Club",
    provider: "Young Creatives Media Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Crouch End N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","drama","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production club for older children and teens — scripting, shooting and editing short films and media projects collaboratively with peers.",
    times: "Check club for session schedule",
    website: "https://www.barnet-tv.co.uk/media-club",
    neighbourhoods: ["Crouch End"],
  },
  {
    id: "cet-24",
    name: "Parkland Walk Nature Club",
    provider: "Parkland Walk Nature Club",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Parkland Walk Nature Reserve, N8",
    postcodeSector: "N8",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature club along the much-loved Parkland Walk — a disused railway line turned linear nature reserve. Wildlife spotting, seasonal nature crafts and outdoor exploration through Crouch End and Highgate.",
    times: "Check local listings for session dates",
    website: "https://www.parkland-walk.org.uk/",
    neighbourhoods: ["Crouch End","Highgate"],
  },
  {
    id: "cet-25",
    name: "Hornsey Library — Children's Learning Club",
    provider: "Haringey Libraries (Hornsey Library)",
    type: "Club",
    neighbourhood: "Crouch End",
    location: "Hornsey Library, N8 9JA",
    postcodeSector: "N8",
    distanceMiles: 0.6,
    cost: "Free or low cost",
    age: "5–14",
    interests: ["stem","arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Free and low-cost children's learning workshops at Hornsey Library — coding, reading clubs and creative sessions during term time. Part of Haringey's borough-wide library learning programme.",
    times: "Check library for current workshop schedule",
    website: "https://www.haringey.gov.uk/libraries-sport-and-leisure/libraries/hornsey-library",
    neighbourhoods: ["Crouch End"],
  },


  // ── WHETSTONE & TOTTERIDGE ───────────────────────────────────────────────────
  {
    id: "wtt-1",
    name: "Totteridge Tennis Club — Juniors",
    provider: "Totteridge Tennis Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Totteridge Tennis Club, Great Bushey Drive, N20 8EY",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Member rates · non-member rates available · contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekend mornings (term-time programme, separate from holiday camps)",
    description: "One of the oldest and best-equipped tennis clubs in the area with 9 floodlit courts — 3 Tiger Turf, 3 Euroclay and 3 hard. Year-round junior coaching programme led by Francesca and Clinton, with age-appropriate ball and court sizes for all levels.",
    times: "Check website for term-time junior programme times",
    website: "https://clubspark.lta.org.uk/TotteridgeTennisClub/Coaching/JuniorCamps",
    neighbourhoods: ["Whetstone & Totteridge"],
    phone: "coaching@totteridgetennis.net",
  },
  {
    id: "wtt-2",
    name: "Whetstone Wanderers FC — Youth Academy",
    provider: "Whetstone Wanderers FC",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football club with a structured youth academy across multiple age groups. FA-affiliated coaching, weekend league matches and weekday training sessions building skills and teamwork.",
    times: "Check club for training and match schedule",
    website: "https://www.whetstonewanderers.org/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-3",
    name: "Totteridge Millhillians Cricket Club — Juniors",
    provider: "Totteridge Millhillians Cricket Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Totteridge Lane, Totteridge, N20 0NL",
    postcodeSector: "N20",
    distanceMiles: 0.8,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11) programmes",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · Sunday matches",
    description: "Friendly local cricket club with a thriving junior section on a beautiful ground in the Totteridge greenbelt. ECB-accredited coaching for all ages and abilities, year-round development squads.",
    times: "Fri evening training · weekend matches",
    website: "https://www.totteridgemillhillians.com/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-4",
    name: "North Middlesex Hockey Club — Juniors",
    provider: "North Middlesex Hockey Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Totteridge Lane area, N20 0NL",
    postcodeSector: "N20",
    distanceMiles: 0.8,
    cost: "Contact club for junior membership fees",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (typical hockey training day)",
    description: "Community field hockey club with junior coaching across age groups, sharing facilities with the Totteridge sports ground area. Builds stick skills, fitness and teamwork for beginners through to competitive players.",
    times: "Check club for training and match schedule",
    website: "https://northmiddlesexhockey.com/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-5",
    name: "The Totteridge Academy — Community Sports Programme",
    provider: "The Totteridge Academy",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "The Totteridge Academy, Barnet Lane, N20 8AZ",
    postcodeSector: "N20",
    distanceMiles: 0.7,
    cost: "Contact school for community programme fees",
    age: "11–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings",
    description: "Athletics and multi-sport community programme using The Totteridge Academy's facilities, open to the wider community alongside school provision. Builds fitness and sporting skills for secondary-age young people.",
    times: "Check school for community programme times",
    website: "https://www.thetotteridgeacademy.org.uk/partners",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-6",
    name: "London Eagles Trampoline Club",
    provider: "London Eagles Trampoline Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Trampoline and acrobatics classes building strength, coordination and confidence. Structured progression through British Gymnastics trampoline levels with qualified coaches for all abilities.",
    times: "Check club for class schedule",
    website: "https://www.hornseytrampoline.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-7",
    name: "Judo Club London North — Whetstone",
    provider: "Judo Club London North",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Traditional judo classes building discipline, fitness and self-defence skills. Structured belt grading system with qualified instructors, welcoming complete beginners through to competitive judoka.",
    times: "Check club for class times",
    website: "https://barnetjudo.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-8",
    name: "Totteridge Dance Academy",
    provider: "Totteridge Dance Academy",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone & Totteridge N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Termly fees · contact academy for pricing",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Ballet, modern and contemporary dance classes spanning toddlers through to teens. Structured technique training alongside creative movement classes for the youngest dancers.",
    times: "Check academy for class schedule",
    website: "https://schoolhire.co.uk/london-barnet/totteridge/dance-studio/436",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-9",
    name: "Showtime Performing Arts School",
    provider: "Showtime Performing Arts School",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Showtime Performing Arts School, N20 9EZ",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Weekly drama, dance and singing classes building performance skills and confidence. Split by age group with end-of-term performances. All abilities welcome, no experience needed.",
    times: "Check school for class schedule",
    website: "https://showtime.london/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-10",
    name: "Oakleigh Music Centre",
    provider: "Oakleigh Music Centre",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact centre for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & Saturdays",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check centre for lesson schedule",
    website: "",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-11",
    name: "Finchley Children's Music Group",
    provider: "Finchley Children's Music Group (FCMG)",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Brookland School, Off Hill Top, NW11 6EJ",
    postcodeSector: "N20",
    distanceMiles: 1.5,
    cost: "Subscription-based · bursary fund available for families in financial need",
    age: "4–18",
    interests: ["music"],
    dayOfWeek: "Sunday afternoons during term time",
    description: "London's celebrated children's choir, founded 1958. Over 150 members across Beginner, Intermediate, Senior and Chamber choirs (the latter two by audition). Regularly performs with the LSO, LPO and BBC Symphony Orchestra, with a recent UK premiere at the Barbican alongside the BBC Symphony Orchestra and BBC Singers.",
    times: "Sunday afternoons, term time, at Brookland School NW11",
    website: "https://www.fcmg.org.uk/",
    neighbourhoods: ["Whetstone & Totteridge","East Finchley","Hampstead Garden Suburb"],
    notes: "Beginner and Intermediate choirs open to all; Senior and Chamber choirs by audition. Bursary fund available so no child is excluded for financial reasons.",
  },
  {
    id: "wtt-12",
    name: "Tutors United Whetstone — Maths Tuition",
    provider: "Tutors United",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Maths tuition building core skills and exam confidence across primary and secondary levels. Small group or 1-2-1 sessions tailored to individual needs and curriculum requirements.",
    times: "Check provider for tutor availability",
    website: "https://www.tutorsunited.org/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-13",
    name: "Kip McGrath Whetstone — English Tuition",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Kip McGrath Whetstone, N20 9HR",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition centre with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.kipmcgrath.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-14",
    name: "Exam Success Whetstone — 11+ Preparation",
    provider: "Exam Success",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check provider for class schedule",
    website: "https://www.mathnasium.com/uk/maths-centres/whetstone/news/power-many-minds-how-mathnasium-whetstones-team-instructors-accelerates-progress",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-15",
    name: "Tech Talents North London — Coding",
    provider: "Tech Talents",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and programming classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/enfield/coding-school-543.html",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-16",
    name: "Junior Innovators Club — Robotics",
    provider: "Junior Innovators Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check club for class schedule",
    website: "https://www.younginnovatorsandcreators.com/contact-us",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-17",
    name: "Whetstone Chess Academy",
    provider: "Whetstone Chess Academy",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact academy for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess academy teaching strategy, tactics and sportsmanship for all levels from complete beginners to tournament players, with structured coaching and regular practice games.",
    times: "Check academy for session times",
    website: "https://www.facebook.com/WBCchessclub/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-18",
    name: "Italian School of North London — Whetstone",
    provider: "Italian School of North London",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "4–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Italian language classes for children, progressing from early vocabulary and songs through to conversational fluency for older students. Suitable for both heritage speakers and complete beginners.",
    times: "Check school for class schedule",
    website: "https://www.italianschool.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-19",
    name: "Mandarin Stars Whetstone",
    provider: "Mandarin Stars",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Mandarin Chinese language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels for children of all ages and backgrounds.",
    times: "Check provider for class schedule",
    website: "http://www.gigglingpanda.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-20",
    name: "Totteridge Arts Studio",
    provider: "Totteridge Arts Studio",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Totteridge N20",
    postcodeSector: "N20",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art and drawing classes for children and young teens, building technical skill alongside creative confidence. Structured progression through different media and styles in small group sessions.",
    times: "Check studio for class times",
    website: "https://thenewschoolart.org/art-courses/art-courses-totteridge/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-21",
    name: "Creative Makers Club — Sewing & Crafts",
    provider: "Creative Makers Club",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Sewing and craft classes teaching practical making skills — hand-stitching, basic machine sewing and simple craft projects for children and young teens.",
    times: "Check club for workshop dates",
    website: "",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-22",
    name: "Pottery Corner Barnet",
    provider: "Pottery Corner",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Barnet area, N20",
    postcodeSector: "N20",
    distanceMiles: 0.8,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "http://www.paintthetownpottery.co.uk/",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-23",
    name: "Young Photographers North London — Whetstone",
    provider: "Young Photographers North London",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Whetstone N20",
    postcodeSector: "N20",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "9–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check provider for course dates",
    website: "",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-24",
    name: "Future Film School Barnet",
    provider: "Future Film School",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Barnet area, N20",
    postcodeSector: "N20",
    distanceMiles: 0.8,
    cost: "Contact school for term fees",
    age: "10–18",
    interests: ["arts","stem","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production classes for older children and teens — scripting, shooting and editing short films and media projects collaboratively. Builds technical and storytelling skills.",
    times: "Check school for session schedule",
    website: "https://sparksarts.co.uk/locations/highgate",
    neighbourhoods: ["Whetstone & Totteridge"],
  },
  {
    id: "wtt-25",
    name: "Totteridge Nature Explorers",
    provider: "Totteridge Nature Explorers",
    type: "Club",
    neighbourhood: "Whetstone & Totteridge",
    location: "Totteridge N20",
    postcodeSector: "N20",
    distanceMiles: 0.7,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature club exploring the green spaces of Totteridge, one of London's most rural-feeling neighbourhoods. Den building, bug hunts, seasonal nature crafts and wildlife spotting.",
    times: "Check local listings for session dates",
    website: "https://www.facebook.com/natureexplorersbarnet/?locale=en_GB",
    neighbourhoods: ["Whetstone & Totteridge"],
  },


  // ── FINCHLEY & NORTH FINCHLEY ─────────────────────────────────────────────────
  {
    id: "fnt-1",
    name: "Finchley Rugby Club — Juniors",
    provider: "Finchley Rugby Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "Finchley Rugby Club, N3 1YN",
    postcodeSector: "N3",
    distanceMiles: 0.6,
    cost: "Junior membership · contact club for term fees",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Sunday mornings (main training day)",
    description: "Welcoming community rugby club with age-grade teams from minis through to colts. Mixed and girls' sections, RFU-accredited coaches and a strong family club atmosphere with a busy clubhouse on match days.",
    times: "Sunday mornings — check club for age-group times",
    website: "https://www.pitchero.com/clubs/finchley/contact",
    neighbourhoods: ["North Finchley","Finchley"],
  },
  {
    id: "fnt-2",
    name: "Finchley Victoria Bowling & Croquet Club — Juniors",
    provider: "Finchley Victoria Bowling & Croquet Club",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley Victoria Bowling & Croquet Club, N3 3QE",
    postcodeSector: "N3",
    distanceMiles: 0.6,
    cost: "Junior membership · contact club for pricing",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend afternoons (typically Saturday)",
    description: "Friendly local croquet club offering junior coaching in this strategic, skill-based lawn sport. A relaxed, sociable way for young people to develop tactical thinking and precision on a beautiful green.",
    times: "Check club for junior session times",
    website: "https://fvbcc.com/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-3",
    name: "Finchley Table Tennis Club",
    provider: "Finchley Table Tennis Club",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley Table Tennis Club, N3 1NB",
    postcodeSector: "N3",
    distanceMiles: 0.6,
    cost: "Junior membership · contact club for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Long-established table tennis club with junior coaching for all abilities. Builds reflexes, focus and competitive spirit in a friendly, supportive club environment.",
    times: "Check club for junior session times",
    website: "https://www.barnet.gov.uk/directories/directme/barnet-table-tennis-centre",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-4",
    name: "Barnet Taekwondo Club",
    provider: "Barnet Taekwondo Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Taekwondo classes building discipline, fitness and self-defence skills through structured belt grading. Suitable for complete beginners through to advanced practitioners, all abilities welcome.",
    times: "Check club for class times",
    website: "http://www.panteratkd.com/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-5",
    name: "North London Fencing Club",
    provider: "North London Fencing Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact club for term fees · equipment hire often included for beginners",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Fencing classes teaching foil, épée or sabre technique in a safe, structured environment. Builds quick thinking, precision and sportsmanship for beginners through to competitive fencers.",
    times: "Check club for class schedule",
    website: "https://highgatefencingclub.co.uk/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-6",
    name: "Studio 17 Performing Arts",
    provider: "Studio 17 Performing Arts",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "Studio 17, N12 8LY",
    postcodeSector: "N12",
    distanceMiles: 0.5,
    cost: "Termly fees · contact studio for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Drama, dance and singing classes building performance skills and confidence across a wide age range. Regular showcase opportunities and structured progression through different performing arts disciplines.",
    times: "Check studio for class schedule",
    website: "https://www.artsdepot.co.uk/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-7",
    name: "Encore! Theatre Arts",
    provider: "Encore! Theatre Arts",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Performing arts classes covering drama, singing and movement, building confidence and stage presence. All abilities welcome with regular performance opportunities for students.",
    times: "Check provider for class schedule",
    website: "https://www.centrestageuk.com/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-8",
    name: "Finchley Youth Orchestra",
    provider: "Finchley Youth Orchestra",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact orchestra for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with orchestra",
    description: "Youth orchestra offering ensemble playing experience for young musicians of varying levels. Builds musicianship, listening skills and the joy of playing together, with regular concert opportunities.",
    times: "Check orchestra for rehearsal schedule",
    website: "https://northlondonyouthorchestra.org/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-9",
    name: "Music House for Children",
    provider: "Music House Education",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact for term fees and private lesson rates",
    age: "4–16",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Play-based early music classes alongside instrumental tuition (piano, ukulele, violin and more) for children of all levels. Group classes and private lessons, with parents encouraged to attend younger children's sessions to support home practice.",
    times: "Check musichouseeducation.com for class schedule",
    website: "https://musichouseeducation.com",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-10",
    name: "Barnet & Finchley Choral Society — Youth Programme",
    provider: "Barnet & Finchley Choral Society",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact society for youth membership fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal (typically weekday evening)",
    description: "Choir and vocal training for young singers connected to the established Barnet & Finchley Choral Society. Builds technique, sight-reading and performance confidence through regular rehearsals and concerts.",
    times: "Check society for rehearsal schedule",
    website: "https://www.finchleychoralsociety.co.uk/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-11",
    name: "North Finchley Tutors — Maths",
    provider: "North Finchley Tutors",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley, N12 8QA",
    postcodeSector: "N12",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Maths tuition building core skills and exam confidence across primary and secondary levels. Small group or 1-2-1 sessions tailored to individual needs and curriculum requirements.",
    times: "Check provider for tutor availability",
    website: "https://www.northfinchleytutors.co.uk/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-12",
    name: "The Learning Centre Finchley — English Tuition",
    provider: "The Learning Centre",
    type: "Club",
    neighbourhood: "Finchley",
    location: "The Learning Centre Finchley, N3 2LN",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition centre with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.explorelearning.co.uk/tuition-centre/london-finchley/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-13",
    name: "Ivy House Tutors Finchley — 11+ Preparation",
    provider: "Ivy House Tutors",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group or 1-2-1 tuition tailored to grammar and independent school entrance requirements.",
    times: "Check tutors for availability",
    website: "https://www.ivyeducation.co.uk/tuition/11-plus-tuition/11-plus-tutors",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-14",
    name: "Code Ninjas Finchley",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "Code Ninjas Finchley, N12 0EQ",
    postcodeSector: "N12",
    distanceMiles: 0.5,
    cost: "Contact centre for term fees",
    age: "7–14",
    interests: ["stem","gaming"],
    dayOfWeek: "After-school sessions & weekends",
    description: "Coding centre teaching game design and programming through a self-paced belt system, similar to martial arts grading. Children build their own games while developing computational thinking skills.",
    times: "Check codeninjas.com for local centre schedule",
    website: "https://www.codeninjas.com/cam",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-15",
    name: "Little House of Science",
    provider: "Little House of Science",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "5–12",
    interests: ["stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Hands-on science classes covering chemistry, biology and physics experiments in a fun, accessible format. Builds curiosity and practical understanding through interactive, age-appropriate sessions.",
    times: "Check provider for class schedule",
    website: "https://www.littlehouseofscience.com/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-16",
    name: "Engineering for Kids Barnet",
    provider: "Engineering for Kids",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Robotics and engineering classes using hands-on kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured weekly challenges.",
    times: "Check provider for class schedule",
    website: "https://dev.e4kids.co.uk/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-17",
    name: "Barnet Knights Chess Club — Juniors",
    provider: "Barnet Knights Chess Club",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly junior chess club teaching strategy, tactics and sportsmanship for all levels from complete beginners to tournament players, with coaching from experienced club members.",
    times: "Check with club for session times",
    website: "https://www.barnetknights.com/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-18",
    name: "Hebrew Classes North London",
    provider: "Hebrew Classes North London",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Sundays",
    description: "Hebrew language classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Check provider for class schedule",
    website: "",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-19",
    name: "Japanese Club North London — Finchley",
    provider: "Japanese Club North London",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Japanese language and culture classes for children, covering basic conversation, writing systems and cultural traditions. Suitable for complete beginners through to more confident young learners.",
    times: "Check provider for class schedule",
    website: "https://en.nakayoshikai.co.uk/who-we-are",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-20",
    name: "Barnet Art Studio",
    provider: "Barnet Art Studio",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Drawing and art classes for children and young teens, building technical skill alongside creative confidence. Structured progression through different media and styles in small group sessions.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-high-barnet/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-21",
    name: "Creative Minds Design Club",
    provider: "Creative Minds Design Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Graphic design and digital art classes introducing children to design software, illustration and creative digital skills. Builds technical confidence alongside artistic expression.",
    times: "Check club for class schedule",
    website: "https://creativeminds.art/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-22",
    name: "Sew Inspired Kids",
    provider: "Sew Inspired Kids",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Sewing and fashion design classes teaching practical making skills — hand-stitching, basic machine sewing and simple garment design for children and young teens.",
    times: "Check provider for workshop dates",
    website: "",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-23",
    name: "Ceramic Art London — Youth Studio",
    provider: "Ceramic Art London",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "http://ceramicartlondon.com/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-24",
    name: "Snapshot Photography Academy",
    provider: "Snapshot Photography Academy",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "9–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check academy for course dates",
    website: "",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-25",
    name: "Young Directors Film Lab",
    provider: "Young Directors Film Lab",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact lab for term fees",
    age: "10–18",
    interests: ["arts","stem","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production classes for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers.",
    times: "Check lab for session schedule",
    website: "https://sparksarts.co.uk/product/young-filmmakers-lab-at-sparks-film-school-highgate/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-26",
    name: "Stephens House & Gardens — Nature Club",
    provider: "Stephens House & Gardens",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Stephens House & Gardens, 17 East End Road, N3 3QE",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Free or low cost — gardens are free to visit, events priced individually",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (varies by event)",
    description: "Nature and outdoor learning sessions at the Grade II listed Avenue House gardens, gifted for public enjoyment by Henry 'Inky' Stephens of the Stephens' Ink Company. Landscaped gardens by Robert Marnock, with a children's playground and café on site. Registered charity running a varied community events programme.",
    times: "Gardens open daily during daylight hours · events check stephenshouseandgardens.com",
    website: "https://stephenshouseandgardens.com/",
    neighbourhoods: ["Finchley"],
    phone: "020 8346 7812",
    notes: "Also known as Avenue House. Free garden access daily; house events and workshops priced individually. Disabled accessibility on site.",
  },
  {
    id: "fnt-27",
    name: "Finchley Youth Debate Academy",
    provider: "Finchley Youth Debate Academy",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Finchley N3",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "8–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Public speaking and debate classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check academy for session schedule",
    website: "",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-28",
    name: "Barnet Young Entrepreneurs Club",
    provider: "Barnet Young Entrepreneurs Club",
    type: "Club",
    neighbourhood: "North Finchley",
    location: "North Finchley N12",
    postcodeSector: "N12",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Business and entrepreneurship club introducing teens to practical skills — idea generation, basic finance, marketing and pitching. Builds confidence and real-world problem-solving through project-based learning.",
    times: "Check club for session schedule",
    website: "https://ultra.education/whats-on/",
    neighbourhoods: ["North Finchley"],
  },
  {
    id: "fnt-29",
    name: "Wilf Slack Sports Ground — Junior Activities",
    provider: "Wilf Slack Sports Ground",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Wilf Slack Sports Ground, N3 1XP",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact ground for session pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Multi-sport activities at Wilf Slack Sports Ground, named after the former Middlesex and England cricketer. Outdoor pitches and facilities supporting a range of junior sports sessions throughout the week.",
    times: "Check ground for current activity schedule",
    website: "https://hallschool.co.uk/wilf-slack-playing-fields/",
    neighbourhoods: ["Finchley"],
  },
  {
    id: "fnt-30",
    name: "Avenue House Creative Workshops",
    provider: "Stephens House & Gardens (Avenue House)",
    type: "Club",
    neighbourhood: "Finchley",
    location: "Avenue House, 17 East End Road, N3 3QE",
    postcodeSector: "N3",
    distanceMiles: 0.5,
    cost: "Contact venue for workshop pricing — gardens free to visit",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "Check venue for workshop schedule",
    description: "Arts and crafts workshops at the imposing Grade II listed Victorian mansion, set within beautifully landscaped gardens. Creative sessions for children held alongside the venue's wider community events programme.",
    times: "Check stephenshouseandgardens.com for workshop dates",
    website: "https://www.stephenshouseandgardens.com/events/renaissance",
    neighbourhoods: ["Finchley"],
    phone: "020 8346 7812",
    notes: "Same venue and charity as Stephens House & Gardens Nature Club — Avenue House is the historic mansion within the gardens.",
  },


  // ── HAMPSTEAD (term-time) ────────────────────────────────────────────────────
  {
    id: "hmt-1",
    name: "Hampstead Heath Croquet Club — Juniors",
    provider: "Hampstead Heath Croquet Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Parliament Hill Lawn, c/o Parliament Hill Bowling Club, Highgate Road, NW5 1QR",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Membership £20/year · lawn fees £4 members, £7.50 non-members per hour · Pay & Play sessions for newcomers",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings (except Tue/Wed) & weekend Pay & Play sessions",
    description: "Friendly croquet club (formed 2008, run by the City of London and volunteers) on Parliament Hill near the tennis courts. Plays both Association and Golf Croquet. Coaching sessions introduce newcomers — no strength or speed required, just hand-eye coordination and strategic thinking. Season runs April to September.",
    times: "Open daily 9am–1pm and 6pm–sunset (closed Tue/Wed evenings) · season Apr–Sep",
    website: "https://hampsteadheathcroquetclub.org.uk/",
    neighbourhoods: ["Hampstead"],
    notes: "Pay & Play sessions for non-members and beginners; Open Sessions for those with experience. Flat-soled shoes required on the lawn.",
  },
  {
    id: "hmt-2",
    name: "Hampstead Heath Pony Centre",
    provider: "Hampstead Heath Riding (City of London Corporation)",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Parliament Hill Fields, NW5 1QR",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact City of London Corporation for lesson pricing",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Horse riding lessons on Hampstead Heath, managed by the City of London Corporation. Pony care and riding instruction for all abilities, from complete beginners to more experienced young riders, in one of London's most iconic green spaces.",
    times: "Check City of London website for lesson schedule",
    website: "https://www.cityoflondon.gov.uk/things-to-do/green-spaces/hampstead-heath",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-3",
    name: "Parliament Hill Lido — Swim Academy",
    provider: "Parliament Hill Lido (City of London / Better Leisure)",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Parliament Hill Lido, Gordon House Road, NW5 1QR",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact lido for swim school pricing",
    age: "5–16",
    interests: ["swimming","outdoors"],
    dayOfWeek: "Weekday afternoons & weekend mornings (term-time)",
    description: "Swim lessons at the iconic open-air Parliament Hill Lido on Hampstead Heath. Structured levels for all abilities, with the unique experience of swimming outdoors year-round at one of London's best-loved lidos.",
    times: "Check lido for swim school times",
    website: "https://www.cityoflondon.gov.uk/things-to-do/green-spaces/hampstead-heath/where-to-go-at-hampstead-heath/parliament-hill-lido",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-4",
    name: "Hampstead Heath Rugby Development Sessions",
    provider: "Hampstead Heath Rugby Development",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead Heath, NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact provider for term fees",
    age: "6–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (typically Sunday)",
    description: "Community rugby development sessions on Hampstead Heath, teaching fundamental skills, teamwork and a love of the game. Welcoming, non-pressured environment for beginners through to keen young players.",
    times: "Check provider for session times",
    website: "https://www.hampsteadrugbyclub.co.uk/a/about-hampstead-rugby-44243.html?page=3",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-5",
    name: "Camden Fencing Club",
    provider: "Camden Fencing Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact club for term fees · equipment hire often available for beginners",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Fencing classes teaching foil, épée or sabre technique in a safe, structured environment. Builds quick thinking, precision and sportsmanship for beginners through to competitive fencers.",
    times: "Check club for class schedule",
    website: "https://www.camdenfencingclub.org/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-6",
    name: "Hampstead School of Dance",
    provider: "Hampstead School of Dance",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead School of Dance, NW3 1NH",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Termly fees · contact school for pricing",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Ballet, jazz and contemporary dance classes spanning toddlers through to teens. Structured technique training alongside performance opportunities, building confidence and stage presence.",
    times: "Check school for class schedule",
    website: "https://www.westhampsteadschoolofdance.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-7",
    name: "Hampstead Youth Dance Company",
    provider: "Hampstead Youth Dance Company",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact company for membership fees",
    age: "8–18",
    interests: ["dance"],
    dayOfWeek: "Weekday evenings",
    description: "Performance-focused dance company for committed young dancers, building technique and choreographic skill with regular performance opportunities. A step up from recreational classes for serious young dancers.",
    times: "Check company for rehearsal schedule",
    website: "https://hampsteadballetsch.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-8",
    name: "Hampstead Conservatoire",
    provider: "Hampstead Conservatoire",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead Conservatoire, NW3 5HT",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Termly fees · contact conservatoire for pricing",
    age: "4–18",
    interests: ["drama","music"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Drama and performing arts classes building confidence, technique and stage presence across a wide age range. Structured progression with regular performance opportunities in a supportive, skill-focused setting.",
    times: "Check conservatoire for class schedule",
    website: "https://the.nlconservatoire.org/home",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-9",
    name: "Hampstead Chamber Music Academy",
    provider: "Hampstead Chamber Music Academy",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact academy for instrumental lesson fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Instrumental tuition with a chamber music focus, building ensemble playing skills alongside individual technique. Supports progression from beginner to advanced level with regular group playing opportunities.",
    times: "Check academy for lesson schedule",
    website: "https://rachmaninoffmusicacademy.com/study/our-programmes/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-10",
    name: "Gospel Oak Choir for Young Voices",
    provider: "Gospel Oak Choir for Young Voices",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Gospel Oak, NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.7,
    cost: "Contact choir for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal (typically weekday evening)",
    description: "Community choir for young singers, building vocal technique, sight-reading and performance confidence through regular rehearsals and seasonal concerts in a warm, inclusive setting.",
    times: "Check choir for rehearsal schedule",
    website: "https://ahgo.co.uk/about/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-11",
    name: "The Tutor Pages Learning Hub",
    provider: "The Tutor Pages",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Maths and English tuition with a personalised approach, matching students with tutors suited to their level and learning style. Builds academic confidence across primary through to secondary level.",
    times: "Check provider for tutor availability",
    website: "https://www.thelearninghubuk.com/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-12",
    name: "Hampstead 11+ Centre",
    provider: "Hampstead 11+ Centre",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact centre for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check centre for class schedule",
    website: "https://11pluscentre.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-13",
    name: "AI & Coding Club London — Hampstead",
    provider: "AI & Coding Club London",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and introductory AI classes covering Python, machine learning basics and creative coding projects depending on age and level. Builds computational thinking and forward-looking tech literacy.",
    times: "Check club for class schedule",
    website: "https://www.softwareacademy.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-14",
    name: "Young Scientists of Hampstead",
    provider: "Young Scientists of Hampstead",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Hands-on science classes covering chemistry, biology and physics experiments in a fun, accessible format. Builds curiosity and practical understanding through interactive, age-appropriate sessions.",
    times: "Check provider for class schedule",
    website: "https://www.littlehouseofscience.com/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-15",
    name: "Hampstead Go Club — Juniors",
    provider: "Hampstead Go Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Junior club for the ancient strategy board game Go (Weiqi), teaching pattern recognition, planning and patience. Welcoming to complete beginners with experienced players on hand to teach.",
    times: "Check club for session times",
    website: "https://hampsteadgolfclub.co.uk/academy/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-16",
    name: "Russian School of Mathematics — Hampstead",
    provider: "Russian School of Mathematics",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Rigorous maths enrichment programme using the renowned Russian School of Mathematics curriculum, building deep conceptual understanding beyond standard school maths. Structured levels for all abilities.",
    times: "Check rsmath.com for local schedule",
    website: "https://www.mathschool.com/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-17",
    name: "Scandinavian School London — Hampstead",
    provider: "Scandinavian School London",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Saturday school teaching Scandinavian languages (Swedish, Norwegian, Danish or Finnish) and culture for children. Suitable for both heritage speakers and those new to the language.",
    times: "Saturday mornings — check school for exact times",
    website: "",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-18",
    name: "Mandarin Excellence Programme — Hampstead",
    provider: "Mandarin Excellence Programme",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Intensive Mandarin Chinese language programme building towards fluency, following the national Mandarin Excellence Programme model. Structured curriculum for committed language learners.",
    times: "Check provider for class schedule",
    website: "https://ci.ioe.ac.uk/mandarin-excellence-programme/mep-schools/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-19",
    name: "Hampstead School of Art — Youth Studio",
    provider: "Hampstead School of Art",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead School of Art, NW3 1QJ",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Art classes for children and teens covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check school for class times",
    website: "https://www.hampstead-school-of-art.org/learning/childrens-classes",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-20",
    name: "Jewellery & Design Club for Children",
    provider: "Jewellery & Design Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Jewellery-making and craft classes teaching basic metalwork, beading and design skills. Children create take-home pieces while developing fine motor skills and an eye for design.",
    times: "Check club for workshop dates",
    website: "https://barnetyouth.uk/detail_events_v2.aspx?id=8011",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-21",
    name: "Hampstead Ceramics Collective",
    provider: "Hampstead Ceramics Collective",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://ceramicssculpturestudio.com/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-22",
    name: "Keats House — Family Workshops",
    provider: "Keats House (City of London Corporation)",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Keats House, 10 Keats Grove, NW3 2RR",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Children under 17 FREE entry · workshops priced individually · garden always free",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "Weekends (check for specific workshop dates)",
    description: "Creative writing and poetry workshops at the elegant Regency villa where Romantic poet John Keats lived and wrote his most famous works. The Keats Foundation runs a Young Poets' Forum and Schools Programme to inspire new generations of young writers. Beautiful garden setting, family-friendly with baby changing and accessible facilities.",
    times: "Wed–Sun 11am–5pm · workshop dates vary, check cityoflondon.gov.uk/keats",
    website: "https://www.cityoflondon.gov.uk/things-to-do/attractions-museums-entertainment/keats-house/whats-on",
    neighbourhoods: ["Hampstead"],
    phone: "020 7332 3868 · keatshouse@cityoflondon.gov.uk",
    notes: "Children under 17 always free. Garden entry free. 2-minute walk from Hampstead Heath Overground; 10-minute walk from Hampstead tube (Northern Line).",
  },
  {
    id: "hmt-23",
    name: "Burgh House — Young Creatives",
    provider: "Burgh House & Hampstead Museum",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Burgh House, New End Square, NW3 1LT",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Contact Burgh House for workshop pricing · many events free",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends — check programme",
    description: "Art classes at this Grade I-listed Queen Anne building in the heart of Hampstead Village. Family art workshops tied to gallery exhibitions, plus a free art gallery, museum and café. 5 minutes from Hampstead Tube station.",
    times: "Wed–Fri & Sun 10am–4pm · Café Wed–Fri 10am–5pm, Sat–Sun 9:30am–5:30pm",
    website: "https://www.burghhouse.org.uk/whats-on/whats-on/exhibition/burgh-house-young-curators-club",
    neighbourhoods: ["Hampstead"],
    phone: "020 7431 0144 · info@burghhouse.org.uk",
    notes: "Ground floor step-free access. Free gallery admission.",
  },
  {
    id: "hmt-24",
    name: "Hampstead Photography Academy — Youth Programme",
    provider: "Hampstead Photography Academy",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact academy for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check academy for course dates",
    website: "https://classbento.co.uk/hvh-photography-academy",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-25",
    name: "Documentary Makers Club London — Hampstead",
    provider: "Documentary Makers Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Documentary filmmaking club for older children and teens — research, filming and editing real-world stories collaboratively. Builds journalistic and technical skills working on group projects.",
    times: "Check club for session schedule",
    website: "https://sparksarts.co.uk/about-us/film-school-locations/hampstead/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-26",
    name: "Hampstead Heath Ecology Club",
    provider: "Hampstead Heath Ecology Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead Heath, NW3 1TH",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature and ecology club exploring the diverse wildlife and habitats of Hampstead Heath. Pond dipping, bird watching, plant identification and seasonal nature study in nearly 800 acres of heathland.",
    times: "Check local listings for session dates",
    website: "https://www.heath-hands.org.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-27",
    name: "Urban Forest School Hampstead",
    provider: "Urban Forest School",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead Heath area, NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "4–12",
    interests: ["outdoors"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Forest school sessions on Hampstead Heath — den building, fire lighting basics, woodland craft and nature exploration. Develops resilience, teamwork and a connection to the natural world in one of London's wildest green spaces.",
    times: "Check provider for session schedule",
    website: "https://urbanoutdoorslondon.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-28",
    name: "Hampstead Young Architects Club",
    provider: "Hampstead Young Architects Club",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and design club introducing children to building design, model-making and spatial thinking. Combines creative and technical skills through hands-on projects.",
    times: "Check club for workshop dates",
    website: "https://www.soane.org/whats-on/young-architects-club",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-29",
    name: "Public Speaking Academy Hampstead",
    provider: "Public Speaking Academy",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "8–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Public speaking and debate classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check academy for session schedule",
    website: "https://publicspeakingacademy.co.uk/",
    neighbourhoods: ["Hampstead"],
  },
  {
    id: "hmt-30",
    name: "Financial Literacy for Kids London — Hampstead",
    provider: "Financial Literacy for Kids",
    type: "Club",
    neighbourhood: "Hampstead",
    location: "Hampstead NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Financial literacy and entrepreneurship classes introducing teens to practical money skills — budgeting, saving, basic investing concepts and business idea generation. Builds real-world confidence with finances.",
    times: "Check provider for session schedule",
    website: "https://smartmonies.co.uk/about/",
    neighbourhoods: ["Hampstead"],
  },


  // ── HAMPSTEAD GARDEN SUBURB (term-time) ──────────────────────────────────────
  {
    id: "gst-1",
    name: "Farm Walk Tennis Club — Juniors",
    provider: "Farm Walk LTC",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Farm Walk Tennis Club, Farm Walk, NW11 7TP",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Ages 12–18: £135/year · Parent membership: £115/year · 25% sibling discount · £25 play-in session for new members (no joining fee)",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Term-time Junior Courses (separate from holiday camps) — check website for days",
    description: "Welcoming members' club with 3 artificial grass and 2 artificial clay courts hidden away in the heart of leafy Hampstead Garden Suburb. LTA-accredited coaching team available 7 days a week. Junior members get discounted prices on courses and camps, free court booking and team/tournament play. Free Clubplay sessions Tue 11am–1pm, Wed 7–9pm, Sat 2–4pm; table tennis Thu 6:30–8:30pm.",
    times: "Club open 8am–9:30pm daily — check for junior course times",
    website: "https://www.farmwalktennis.net/juniors/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "07929 792112 (Head Coach Theo Hyman) · farmwalktennis@gmail.com",
    notes: "2026 waiting list open. No joining fee, but new members need a £25 play-in session with a coach.",
  },
  {
    id: "gst-2",
    name: "Farm Walk — Junior Table Tennis",
    provider: "Farm Walk LTC",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Farm Walk Tennis Club, NW11 7TP (also serving wider NW11)",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Included in club membership · contact for non-member rates",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Thursdays 6:30–8:30pm",
    description: "Free table tennis sessions for members at Farm Walk LTC's clubhouse, alongside the club's main tennis offering. A relaxed, social way to build hand-eye coordination and reflexes.",
    times: "Thursdays 6:30–8:30pm",
    website: "https://www.farmwalktennis.net/contact/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-3",
    name: "HGS Football Academy",
    provider: "HGS Football Academy",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings or weekday evenings",
    description: "Community football academy with structured coaching across age groups. FA-aligned training building technical skills, teamwork and a love of the game for beginners through to competitive players.",
    times: "Check academy for training schedule",
    website: "https://www.hgssoccerschool.com/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-4",
    name: "Maccabi GB — Junior Sports Programme",
    provider: "Maccabi GB",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Maccabi GB, Shield House, Harmony Way, NW4 2BZ",
    postcodeSector: "NW11",
    distanceMiles: 1.0,
    cost: "Contact Maccabi GB for term fees · bursaries available",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "UK's largest Jewish sports and community charity, running term-time junior sports programmes including football, basketball, cricket and multi-sports across North London. Strong community ethos building sportsmanship and pride through sport. Open to all abilities and backgrounds.",
    times: "Check maccabigb.org for current programme schedule",
    website: "https://www.maccabigb.org/communitysportevents",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8457 2333 · info@maccabigb.org",
    notes: "Bursaries available for families in financial need.",
  },
  {
    id: "gst-5",
    name: "Northway Judo Club",
    provider: "Northway Judo Club",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Traditional judo classes building discipline, fitness and self-defence skills. Structured belt grading system with qualified instructors, welcoming complete beginners through to competitive judoka.",
    times: "Check club for class times",
    website: "https://judointernationalschool.co.uk/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-6",
    name: "Suburb Dance Academy",
    provider: "Suburb Dance Academy",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Termly fees · contact academy for pricing",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Ballet and tap dance classes spanning toddlers through to teens. Structured technique training alongside creative movement classes for the youngest dancers, building confidence and coordination.",
    times: "Check academy for class schedule",
    website: "https://www.balletalto.com/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-7",
    name: "Free Church Hall — Performing Arts Classes",
    provider: "Hampstead Garden Suburb Free Church",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Free Church Hall, Hampstead Garden Suburb, NW11 7AG",
    postcodeSector: "NW11",
    distanceMiles: 0.4,
    cost: "Contact church for class fees",
    age: "4–16",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Drama and performing arts classes held at the Free Church Hall in the heart of Hampstead Garden Suburb. Builds confidence and performance skills in a warm, community-focused setting.",
    times: "Check church for class schedule",
    website: "https://www.perform.org.uk/drama-classes/hampstead-garden-suburb/hampstead-garden-suburb-free-church",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-8",
    name: "HGS Youth Choir",
    provider: "Hampstead Garden Suburb Youth Choir",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact choir for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal (typically weekday evening)",
    description: "Community choir for young singers, building vocal technique, sight-reading and performance confidence through regular rehearsals and seasonal concerts in a warm, inclusive setting.",
    times: "Check choir for rehearsal schedule",
    website: "https://www.highburyyouthchoir.com/about-us/our-choirs/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-9",
    name: "North Western Reform Synagogue — Music School",
    provider: "North Western Reform Synagogue (Alyth)",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Alyth Synagogue, Alyth Gardens, NW11 7RN",
    postcodeSector: "NW11",
    distanceMiles: 0.7,
    cost: "Contact synagogue for music school fees · member discounts available",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons or Sundays",
    description: "Instrumental music tuition run by Alyth Synagogue (North Western Reform Synagogue), one of the UK's largest Reform communities. Welcoming to all families, with beautiful garden grounds and a strong sense of community.",
    times: "Check alyth.org.uk for music school schedule",
    website: "https://www.alyth.org.uk/youth/day-schemes-camps/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8457 8861 · mail@alyth.org.uk",
  },
  {
    id: "gst-10",
    name: "Annemount School — Enrichment Clubs",
    provider: "Annemount School",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Annemount School, 18 Holne Chase, N2 0QN",
    postcodeSector: "NW11",
    distanceMiles: 0.6,
    cost: "Contact school for enrichment club fees",
    age: "4–11",
    interests: ["stem"],
    dayOfWeek: "Some clubs from 8am, others until 4pm — check school",
    description: "Long-established independent pre-prep school (founded 1936, around 78 pupils) offering an unusually wide range of extracurricular clubs including chess, computing, art, dance, drama, French, gym, music and swimming. Specialist teachers deliver a rich, personalised programme alongside the National Curriculum.",
    times: "Check school for current club timetable",
    website: "https://www.annemount.co.uk/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    notes: "Small, nurturing community school. Extensive club range relative to school size, some starting as early as 8am.",
  },
  {
    id: "gst-11",
    name: "Akiva School — Community Clubs",
    provider: "Akiva School",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Akiva School, 80 East End Road, Finchley, N3 2SY",
    postcodeSector: "NW11",
    distanceMiles: 0.9,
    cost: "Contact individual club coordinators for pricing — varies by club",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Lunchtime and after-school, term-time",
    description: "Voluntary aided Jewish primary school (around 420 pupils) running a wide range of lunchtime and after-school clubs, including Rising Stars activities and a ShowChoir programme. New club brochures published each term listing current offerings, contact details and coordinators.",
    times: "Lunchtimes and after-school — check current term's club brochure",
    website: "https://akivaschool.org/Information-For-Parents/Clubs/",
    neighbourhoods: ["Hampstead Garden Suburb"],
    phone: "020 8349 4980 · admin@akivaschool.org",
    notes: "Club brochures published each term (Summer 2025 and Autumn 2025 brochures available on school website). Contact individual club coordinators directly to enrol.",
  },
  {
    id: "gst-12",
    name: "HGS Tutors Collective — Maths",
    provider: "HGS Tutors Collective",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Maths tuition building core skills and exam confidence across primary and secondary levels. Small group or 1-2-1 sessions tailored to individual needs and curriculum requirements.",
    times: "Check provider for tutor availability",
    website: "https://www.globalmathsacademy.com/services/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-13",
    name: "Suburb Learning Hub — English Tuition",
    provider: "Suburb Learning Hub",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact hub for current pricing",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with a structured, personalised approach. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check hub for session times",
    website: "https://www.thelearninghubuk.com/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-14",
    name: "Eleven Plus Success NW11",
    provider: "Eleven Plus Success",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check provider for class schedule",
    website: "https://11plussuccess.co.uk/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },
  {
    id: "gst-15",
    name: "Young Coders NW11",
    provider: "Young Coders",
    type: "Club",
    neighbourhood: "Hampstead Garden Suburb",
    location: "Hampstead Garden Suburb NW11",
    postcodeSector: "NW11",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and game design depending on age and level. Small group sessions building computational thinking and creative tech skills.",
    times: "Check provider for class schedule",
    website: "https://codingcompetition.org/",
    neighbourhoods: ["Hampstead Garden Suburb"],
  },


  // ── ISLINGTON & HIGHBURY (term-time) ─────────────────────────────────────────
  {
    id: "iht-1",
    name: "Highbury Roundhouse Youth FC",
    provider: "Highbury Roundhouse Youth FC",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Highbury Roundhouse, N5 1RR",
    postcodeSector: "N5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football club with age-group teams across the junior and youth pathway. FA-affiliated coaching, weekend league matches and weekday training building skills and teamwork.",
    times: "Check club for training and match schedule",
    website: "https://www.highbury-roundhouse.org.uk/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-2",
    name: "Arsenal in the Community — Junior Programmes",
    provider: "Arsenal in the Community",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Arsenal Football Club, Highbury House, 75 Drayton Park, N5 1BU",
    postcodeSector: "N5",
    distanceMiles: 0.5,
    cost: "Contact Arsenal in the Community for term fees",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings & weekends — term-time courses",
    description: "Official Arsenal FC community football and multi-sport coaching for children. Learn the Arsenal Way in the shadow of the Emirates Stadium, delivered by Arsenal coaches. Term-time courses run alongside the popular holiday camps.",
    times: "Check arsenal.com/community for current term-time course dates",
    website: "https://www.arsenal.com/community",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7619 5000",
  },
  {
    id: "iht-3",
    name: "Islington Boat Club — Youth Programme",
    provider: "Islington Boat Club",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "City Road Basin, 16–34 Graham Street, N1 8JX",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "After-school £3–£5 · Saturday full day £5 · Full annual membership £100 (covers all youth sessions)",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Wednesday after-school · Saturday 11am–4pm (open access youth club)",
    description: "Charity dedicated to making watersports free or affordable for all young people. RYA-approved training centre with Canoe England registered instructors, teaching canoeing, kayaking, sailing, bell boating, mountain biking and powerboat skills on the Regent's Canal. Also runs an after-school club specifically for young people with SEN and disabilities (11+), supporting progression into the open youth club.",
    times: "Wed after-school · Sat 11am–4pm · holiday projects weekdays 11am–4pm (booking required)",
    website: "https://findyour.islington.gov.uk/kb5/islington/directory/service.page?id=ZvGqKbnZh_s",
    neighbourhoods: ["Islington & Highbury"],
    notes: "DisabledGo accessibility guide available. SEN/disability after-school provision for ages 11+. Booking required, especially for summer sessions.",
  },
  {
    id: "iht-4",
    name: "Sobell Leisure Centre — Climbing Club",
    provider: "Sobell Leisure Centre (Better Leisure)",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Sobell Leisure Centre, Hornsey Road, N7 7NY",
    postcodeSector: "N7",
    distanceMiles: 0.9,
    cost: "Contact centre for climbing club pricing",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Indoor climbing wall sessions at Sobell Leisure Centre, one of Islington's largest sports facilities. Structured climbing club building strength, problem-solving and confidence for beginners through to more experienced climbers.",
    times: "Check better.org.uk for climbing club schedule",
    website: "https://www.better.org.uk/leisure-centre/london/islington/sobell",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-5",
    name: "Islington Boxing Club — Juniors",
    provider: "Islington Boxing Club",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact club for junior membership fees",
    age: "8–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community boxing club teaching fitness, discipline and technique in a safe, well-supervised environment. Builds confidence and physical literacy for young people of all abilities, with a strong community ethos.",
    times: "Check club for training schedule",
    website: "https://booking.islingtonboxingclub.org/product/junior-academy/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-6",
    name: "Central School of Ballet — Associate Programme",
    provider: "Central School of Ballet",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Central School of Ballet, 10 Herbal Hill, N1 6NU",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "Contact school for associate programme fees",
    age: "8–18",
    interests: ["dance"],
    dayOfWeek: "Saturdays (typical associate programme format)",
    description: "Prestigious ballet training at one of the UK's leading vocational dance schools. The Associate Programme offers high-quality classical ballet tuition for talented young dancers alongside their regular schooling, building towards potential vocational training pathways.",
    times: "Check centralschoolofballet.co.uk for associate class times",
    website: "https://www.centralschoolofballet.co.uk/dance-classes-courses/associate-programme/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-7",
    name: "The Place — Youth Dance Classes",
    provider: "The Place",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "The Place, 17 Duke's Road, WC1H 9PY",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "Contact The Place for class fees",
    age: "7–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "London's most important contemporary dance venue and training centre runs term-time youth dance classes alongside its holiday courses. Ballet, street dance, contemporary and creative movement classes in a professional training environment.",
    times: "Check theplace.org.uk for term-time class schedule",
    website: "https://theplace.org.uk/dance/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-8",
    name: "Almeida Young Company",
    provider: "Almeida Theatre",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Almeida Theatre, Almeida Street, N1 1TA",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact Almeida for Young Company application details — typically free or low cost via Young Friends scheme",
    age: "14–18",
    interests: ["drama"],
    dayOfWeek: "Check almeida.co.uk for rehearsal schedule",
    description: "The Almeida exists to launch the next generation of British artists onto the world stage. The Young Company (14–18) creates a new piece of theatre together each year, working alongside professional theatre-makers. Wider Young Artists programmes also include Young Designers & Technicians, Young Producers and a Youth Advisory Board.",
    times: "Check almeida.co.uk/get-involved/young-artists for current programme dates",
    website: "https://almeida.co.uk/get-involved/young-artists/young-company/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Age range is 14–18 (not from 11 as commonly assumed) — a separate 18–25 group also runs. Application/audition process applies; check website for current opportunities.",
  },
  {
    id: "iht-9",
    name: "Little Angel Theatre — Term-Time Youth Workshops",
    provider: "Little Angel Theatre",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "14 Dagmar Passage, Cross Street, N1 2DN",
    postcodeSector: "N1",
    distanceMiles: 1.5,
    cost: "Contact Little Angel Theatre for term-time workshop pricing",
    age: "5–16",
    interests: ["drama","arts"],
    dayOfWeek: "Saturdays (Saturday Puppet Club) and other term-time sessions",
    description: "The Home of British Puppetry since 1961, one of only 3 building-based puppet theatres in England. Saturday Puppet Club and Crafty Kids Club run during term time alongside the popular half-term and summer workshops, teaching puppet making, performance and storytelling.",
    times: "Check littleangeltheatre.com for term-time club schedule",
    website: "https://www.littleangeltheatre.com/take-part/category/playcations-and-holiday-clubs/",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7226 1787 · info@littleangeltheatre.com",
    notes: "Nearest tube: Angel (Northern) or Highbury & Islington (Victoria). Time Credits accepted.",
  },
  {
    id: "iht-10",
    name: "Islington Centre for Music",
    provider: "Islington Centre for Music",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington Centre for Music, N1 2UD",
    postcodeSector: "N1",
    distanceMiles: 0.9,
    cost: "Subsidised rates · free instrumental lessons available for eligible families — contact centre for details",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Charity-run music education centre providing high-quality, affordable instrumental tuition and ensemble opportunities to children across Islington, regardless of family income. Strong commitment to widening access to music education.",
    times: "Check centre for lesson and ensemble schedule",
    website: "https://islingtonassemblyhall.co.uk/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-11",
    name: "London Youth Choirs — Islington Centre",
    provider: "London Youth Choirs",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington / Highbury N5 venue",
    postcodeSector: "N5",
    distanceMiles: 0.6,
    cost: "Annual membership subsidised by 65% · financial assistance scheme available",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Term-time weekly rehearsals",
    description: "One of the UK's leading youth choral organisations, with a local rehearsal base serving Islington and Highbury families. Junior Choir (7–11) and Youth Choir (12–18) options. Members perform at prestigious venues across London and internationally, with a strong financial assistance scheme.",
    times: "Check londonyouthchoirs.com for local rehearsal schedule",
    website: "https://www.londonyouthchoirs.com/experience-london-youth-choirs/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "30% of members receive financial support covering fees, travel, concert tickets and uniform. Auditions required for some choirs.",
  },
  {
    id: "iht-12",
    name: "City of London Academy — Enrichment Hub",
    provider: "City of London Academy Islington",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "City of London Academy, N1 8PQ",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact academy for enrichment programme fees",
    age: "11–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Debate and public speaking enrichment programme at City of London Academy, building confidence, critical thinking and persuasive communication skills for secondary-age students.",
    times: "Check academy for enrichment schedule",
    website: "https://www.colpai.org.uk/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-13",
    name: "First Story — Writing Programme",
    provider: "First Story",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Delivered in partner schools across Islington",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Free — delivered through partner schools",
    age: "11–18",
    interests: ["arts"],
    dayOfWeek: "Weekly during term time (in-school)",
    description: "National literacy charity placing professional writers in schools serving low-income communities to run free weekly creative writing workshops. Students' work is published in a professionally printed anthology each year, and First Story runs national writing competitions and events with renowned authors.",
    times: "Weekly in-school sessions — delivered via partner schools",
    website: "https://firststory.org.uk/",
    neighbourhoods: ["Islington & Highbury"],
    notes: "Free programme delivered through participating schools rather than direct enrolment — ask your child's school if they are a First Story partner school.",
  },
  {
    id: "iht-14",
    name: "Islington Tuition Centre — Maths",
    provider: "Islington Tuition Centre",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington Tuition Centre, N1 8HX",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition centre with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/islington/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-15",
    name: "Keystone Learning Studio Islington — English Tuition",
    provider: "Keystone Learning Studio",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact studio for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local studio for session times",
    website: "https://www.keystonetutors.com/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-16",
    name: "Highbury 11+ Academy",
    provider: "Highbury 11+ Academy",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Highbury N5",
    postcodeSector: "N5",
    distanceMiles: 0.5,
    cost: "Contact academy for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check academy for class schedule",
    website: "https://www.academy11.co.uk/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-17",
    name: "TechSpark Islington — Coding",
    provider: "TechSpark",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "https://techspark.co/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-18",
    name: "Maker Club London — Robotics",
    provider: "Maker Club London",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check club for class schedule",
    website: "https://robohub.org/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-19",
    name: "Islington Strategy Games Club — Chess",
    provider: "Islington Strategy Games Club",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact club for membership fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy games club teaching tactics and sportsmanship for all levels from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://www.islingtonmind.org.uk/events/board-games-group/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-20",
    name: "Instituto Cervantes — Junior Spanish Programme",
    provider: "Instituto Cervantes London",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington area N1 (check main Instituto Cervantes London site)",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "Contact Instituto Cervantes for junior course fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Official Spanish government cultural institute offering structured, high-quality Spanish language courses for children. Builds genuine fluency through immersive, culturally rich teaching for all levels.",
    times: "Check cervantes.es for junior course schedule",
    website: "https://londres.cervantes.es/en/courses_spanish/Kids_and_teens_spanish/spanish_courses_children_teenagers.htm",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-21",
    name: "Italian Cultural Institute — Youth Classes",
    provider: "Italian Cultural Institute London",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington area N1",
    postcodeSector: "N1",
    distanceMiles: 1.2,
    cost: "Contact Italian Cultural Institute for youth class fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Italian language and culture classes for children run by the official Italian government cultural institute. Builds language skills alongside cultural understanding for heritage speakers and complete beginners alike.",
    times: "Check Italian Cultural Institute for class schedule",
    website: "https://iiclondra.esteri.it/it/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-22",
    name: "Estorick Collection — Family Art Studio",
    provider: "Estorick Collection of Modern Italian Art",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Estorick Collection, 39a Canonbury Square, N1 4DY",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact gallery for family workshop pricing · gallery admission applies",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Art workshops at the UK's only gallery dedicated to modern Italian art, in an elegant Georgian townhouse setting. Family art studio sessions tied to the gallery's changing exhibitions, encouraging creative response to Futurism and Italian modern art.",
    times: "Check estorickcollection.com for family workshop dates",
    website: "https://www.estorickcollection.com/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-23",
    name: "Screen Academy — Youth Media Club",
    provider: "Screen Academy",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","stem","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production club for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers on creative projects.",
    times: "Check academy for session schedule",
    website: "https://saturday-club.org/club/london-screen-academy/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-24",
    name: "Canonbury Photography Collective — Youth Programme",
    provider: "Canonbury Photography Collective",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Canonbury area, N1",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact collective for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check collective for course dates",
    website: "",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-25",
    name: "Union Chapel — Creative Arts Lab",
    provider: "Union Chapel Project",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Union Chapel, Compton Avenue, N1 2UN",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact Union Chapel for pricing · many events free or low cost",
    age: "8–18",
    interests: ["music","drama"],
    dayOfWeek: "Term-time weekly sessions — check chapel for schedule",
    description: "Music and performing arts programmes at Union Chapel, one of London's most spectacular live music venues housed in a beautiful Victorian Gothic church. The Union Chapel Project supports community arts with subsidised and free provision for local young people.",
    times: "Check unionchapel.org.uk for current programme dates",
    website: "https://unionchapel.org.uk/projects/news-views/be-the-change--free-weekly-applied-theatre-creative-arts-sessions",
    neighbourhoods: ["Islington & Highbury"],
    phone: "020 7226 1686 · info@unionchapel.org.uk",
    notes: "Highbury & Islington tube station 0.4 miles. The Union Chapel Project also provides free meals and support to vulnerable people alongside arts programmes.",
  },
  {
    id: "iht-26",
    name: "Freightliners Farm — Youth Club",
    provider: "Freightliners City Farm",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Freightliners Farm, Sheringham Road, N7 0SL",
    postcodeSector: "N7",
    distanceMiles: 0.9,
    cost: "Free farm entry · youth club sessions low cost — contact farm for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekday afternoons or weekends — check farm for schedule",
    description: "Community city farm in the heart of Islington offering animal care and nature-based youth club sessions. Children learn about farm animals, gardening and environmental care in a hands-on, accessible green space.",
    times: "Check freightlinersfarm.org.uk for opening hours and youth club times",
    website: "https://www.freightlinersfarm.org.uk/",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-27",
    name: "Ecology Centre Islington",
    provider: "Islington Ecology Centre",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Free or low cost — contact centre for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature and outdoor learning sessions exploring local ecology, wildlife and environmental science. Hands-on activities including pond dipping, plant identification and conservation projects for young people.",
    times: "Check local listings for session dates",
    website: "https://www.islington.gov.uk/physical-activity-parks-and-trees/nature-reserves/gillespie-park-and-ecology-centre",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-28",
    name: "Young Architects Workshop London — Islington",
    provider: "Young Architects Workshop",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact workshop for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and design club introducing children to building design, model-making and spatial thinking. Combines creative and technical skills through hands-on projects.",
    times: "Check workshop for current dates",
    website: "",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-29",
    name: "Islington Young Journalists Club",
    provider: "Islington Young Journalists Club",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Journalism and writing club teaching news writing, interviewing and editorial skills. Young people produce their own articles and projects, building confidence in research and communication.",
    times: "Check club for session schedule",
    website: "https://www.islington.gov.uk/children-and-families/things-to-do/youth-hubs-and-clubs",
    neighbourhoods: ["Islington & Highbury"],
  },
  {
    id: "iht-30",
    name: "Future Leaders Academy Islington",
    provider: "Future Leaders Academy",
    type: "Club",
    neighbourhood: "Islington & Highbury",
    location: "Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Leadership and entrepreneurship classes introducing teens to practical skills — public speaking, project management, basic business concepts and team leadership. Builds confidence through project-based learning.",
    times: "Check academy for session schedule",
    website: "https://lfla.org.uk/en",
    neighbourhoods: ["Islington & Highbury"],
  },


  // ── STOKE NEWINGTON (term-time) ──────────────────────────────────────────────
  {
    id: "snt-1",
    name: "Stoke Newington Cricket Club — Juniors",
    provider: "Stoke Newington Cricket Club",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington Cricket Club, N16 9EX",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11) programmes",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · Sunday matches",
    description: "Friendly local cricket club with a thriving junior section. ECB-accredited coaching for all ages and abilities, progressing from All Stars and Dynamos through to club colts teams.",
    times: "Fri evening training · weekend matches",
    website: "https://www.stokenewingtoncricketclub.com/training/boys",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-2",
    name: "Hackney Wolves Basketball Academy",
    provider: "Hackney Wolves",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community basketball academy with structured coaching across age groups. Skills development, team play and competitive opportunities in a fun, inclusive club environment.",
    times: "Check academy for training times",
    website: "https://www.hackneywolvesbasketball.com/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-3",
    name: "Clissold Leisure Centre — Swim School",
    provider: "Better Leisure (Clissold)",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Clissold Leisure Centre, 63 Clissold Road, N16 9HJ",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "From £17.55/session · Hackney residents under 16 swim FREE",
    age: "4–16",
    interests: ["swimming"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Structured swim school at Clissold Leisure Centre next to Clissold Park. Three pools including two 25m pools and a toddler pool with water features. Hackney residents under 16 swim free all year.",
    times: "Mon–Fri 7am–10pm · Sat–Sun 7am–8pm",
    website: "https://www.better.org.uk/leisure-centre/london/hackney/clissold-leisure-centre/holiday-activities",
    neighbourhoods: ["Stoke Newington"],
    phone: "020 8986 9222",
  },
  {
    id: "snt-4",
    name: "Hackney BMX Club",
    provider: "Hackney BMX Club",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Hackney N16",
    postcodeSector: "N16",
    distanceMiles: 0.7,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "BMX riding club teaching technique, confidence and bike handling skills on local tracks. Builds fitness and resilience in a fun, supportive environment for all ability levels.",
    times: "Check club for session times",
    website: "https://www.hackneybmx.com/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-5",
    name: "Stoke Newington School Sports Academy",
    provider: "Stoke Newington School",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington School, Clissold Road, N16 9EX",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact school for community programme fees",
    age: "11–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Sports academy programme at Stoke Newington School using the school's excellent facilities, open to the wider community alongside school provision. Builds fitness and sporting skills for secondary-age young people.",
    times: "Check school for community programme times",
    website: "https://www.stokenewingtonschool.co.uk/about-us/facilities-hire",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-6",
    name: "Circus Space Youth Academy",
    provider: "Circus Space (National Centre for Circus Arts)",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "National Centre for Circus Arts, Hoxton, N1 (serving Stoke Newington)",
    postcodeSector: "N16",
    distanceMiles: 1.5,
    cost: "Contact for youth academy fees",
    age: "7–18",
    interests: ["sports","drama"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Circus skills and acrobatics classes at one of the UK's leading circus training centres. Builds strength, flexibility, confidence and teamwork through aerial, acrobatic and balance disciplines.",
    times: "Check circusarts.org.uk for youth class schedule",
    website: "https://www.nationalcircus.org.uk/youth/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-7",
    name: "Stokey Dance Co",
    provider: "Stokey Dance Co",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Jazz and street dance classes spanning young children through to teens. High-energy sessions building rhythm, confidence and performance skills with regular showcase opportunities.",
    times: "Check academy for class schedule",
    website: "http://www.expressitperformingarts.co.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-8",
    name: "Hackney Empire — Youth Theatre (Creative Futures)",
    provider: "Hackney Empire",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Hackney Empire, 291 Mare Street, E8 1EJ",
    postcodeSector: "E8",
    distanceMiles: 1.2,
    cost: "FREE — Creative Futures programme",
    age: "14–25",
    interests: ["drama","music","dance"],
    dayOfWeek: "Around 20 hours of weekly programming across 45 weeks of the year",
    description: "Free youth arts programme at the historic Grade II* listed 1901 Edwardian music hall designed by Frank Matcham. Creative Futures empowers around 4,000 young people annually as artists, leaders and creative entrepreneurs through workshops, performances and skill-building, including a 2025 rap-infused Romeo and Juliet performed by local young musicians.",
    times: "Term-time weekly programme — check hackneyempire.co.uk",
    website: "https://www.hackneyempire.co.uk/take-part/creative-futures",
    neighbourhoods: ["Stoke Newington"],
    phone: "020 8985 2424",
    notes: "Creative Futures age range is 14–25, free. SEND-friendly venue with step-free routes and accessible facilities. Hackney Central station 5 mins.",
  },
  {
    id: "snt-9",
    name: "Rising Tide Music Hub",
    provider: "Rising Tide Music Hub",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact hub for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check hub for lesson schedule",
    website: "https://rising-tide.org.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-10",
    name: "Hackney Youth Folk Ensemble",
    provider: "Hackney Youth Folk Ensemble",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact ensemble for membership fees",
    age: "8–18",
    interests: ["music","dance"],
    dayOfWeek: "Weekly rehearsal — check with ensemble",
    description: "Folk music and dance ensemble for young people, teaching traditional tunes, singing and folk dance in a friendly, sociable group setting. Regular performance opportunities at community events.",
    times: "Check ensemble for rehearsal schedule",
    website: "https://www.efdss.org/learning/young-people/london-youth-folk-ensemble",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-11",
    name: "Stoke Newington Tuition Centre — Maths",
    provider: "Stoke Newington Tuition Centre",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington Tuition Centre, N16 8BZ",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition centre with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://focuslearning.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-12",
    name: "Learning Lounge Stoke Newington — English Tuition",
    provider: "Learning Lounge",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.thelearninglounge.co.uk/english-tuition",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-13",
    name: "Selective Success Academy — 11+ Preparation",
    provider: "Selective Success Academy",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact academy for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check academy for class schedule",
    website: "https://selectivesuccess.co.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-14",
    name: "Digital Makers Hackney — Coding",
    provider: "Digital Makers Hackney",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington / wider Hackney N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-15",
    name: "Inventors Workshop London — Robotics",
    provider: "Inventors Workshop London",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact workshop for term fees",
    age: "7–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check workshop for class schedule",
    website: "",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-16",
    name: "Stoke Newington Chess Collective",
    provider: "Stoke Newington Chess Collective",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact collective for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess club teaching strategy, tactics and sportsmanship for all levels from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with collective for session times",
    website: "https://checkmateirl.com/venues/london/hackney-chess-club/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-17",
    name: "Turkish Education Group — Stoke Newington",
    provider: "Turkish Education Group",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact group for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Turkish language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency, reflecting Stoke Newington's vibrant Turkish community.",
    times: "Check group for class schedule",
    website: "https://findyour.islington.gov.uk/kb5/islington/directory/service.page?id=ZtgoEv_4BiY&healthchannelnew=6_6",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-18",
    name: "Hackney Hebrew School",
    provider: "Hackney Hebrew School",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Sundays",
    description: "Hebrew language classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Check school for class schedule",
    website: "https://education.hackney.gov.uk/school/lubavitch-primary-boys-school",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-19",
    name: "Abney Park Art Studio",
    provider: "Abney Park Art Studio",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Near Abney Park, N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes near the atmospheric Abney Park Cemetery and nature reserve. Drawing and mixed media for children and young teens, building technical skill alongside creative confidence.",
    times: "Check studio for class times",
    website: "https://abneypark.org/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-20",
    name: "Textile Creators Club",
    provider: "Textile Creators Club",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Sewing and fashion design classes teaching practical making skills — hand-stitching, basic machine sewing and simple garment design for children and young teens.",
    times: "Check provider for workshop dates",
    website: "https://nlstitchandtextiles.co.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-21",
    name: "Clay Hackney — Youth Studio",
    provider: "Clay Hackney",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington / Hackney N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.clayclubhackney.com/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-22",
    name: "Stokey Camera Club — Juniors",
    provider: "Stokey Camera Club",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact club for course pricing",
    age: "9–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check club for course dates",
    website: "",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-23",
    name: "Young Documentary Makers — Stoke Newington",
    provider: "Young Documentary Makers",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Documentary filmmaking club for older children and teens — research, filming and editing real-world stories collaboratively. Builds journalistic and technical skills through group projects.",
    times: "Check club for session schedule",
    website: "https://sparksarts.co.uk/about-us/film-school-locations/sparks-film-school-stoke-newington/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-24",
    name: "Woodberry Wetlands — Youth Nature Programme",
    provider: "London Wildlife Trust",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Woodberry Wetlands, 1 Newnton Close, N4 2RH",
    postcodeSector: "N4",
    distanceMiles: 0.8,
    cost: "FREE family learning events · Nature Tots pay-as-you-go for under-5s",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekends — check Wild London for current event dates",
    description: "17-hectare wildlife reserve at the former Stoke Newington East Reservoir, opened by Sir David Attenborough in 2016. Free family learning events include pond dipping, minibeast hunting and wildlife exploration, plus SENsory Explorers sessions for families with additional needs. Wheelchair accessible with a café on site.",
    times: "Reserve open Oct–Mar 9:30am–4pm, Apr–Sep 9:30am–5pm · events check wildlondon.org.uk",
    website: "https://www.wildlondon.org.uk/nature-reserves/woodberry-wetlands",
    neighbourhoods: ["Stoke Newington"],
    notes: "SENsory Explorers sessions specifically for families with additional needs/disabilities. Level access, no steep gradients, disabled parking bays at each entrance. Free entry.",
  },
  {
    id: "snt-25",
    name: "Abney Park Nature Explorers",
    provider: "Abney Park Trust",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Abney Park Cemetery & Nature Reserve, N16 0LH",
    postcodeSector: "N16",
    distanceMiles: 0.4,
    cost: "Free or low cost — contact Abney Park Trust for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature exploration sessions in Abney Park, a beautiful Victorian cemetery and registered nature reserve. Wildlife spotting, seasonal nature crafts and outdoor learning amid historic monuments and mature woodland in the heart of Stoke Newington.",
    times: "Check Abney Park Trust for session dates",
    website: "https://abneypark.org/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-26",
    name: "Hackney Spoken Word Academy",
    provider: "Hackney Spoken Word Academy",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing and spoken word performance classes for teens — poetry, storytelling and performance technique. Builds confidence in self-expression and public performance.",
    times: "Check academy for session schedule",
    website: "https://hackneyshed.org.uk/hackney-shed-slam/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-27",
    name: "Youth Radio Hackney",
    provider: "Youth Radio Hackney",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington / wider Hackney N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Podcasting and media production club teaching recording, editing and broadcast skills. Young people produce their own audio content, building confidence in communication and technical media skills.",
    times: "Check club for session schedule",
    website: "",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-28",
    name: "Young Social Entrepreneurs Network",
    provider: "Young Social Entrepreneurs Network",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact network for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Entrepreneurship club with a social impact focus, introducing teens to business planning, community-focused projects and presentation skills. Builds practical, real-world problem-solving abilities.",
    times: "Check network for session schedule",
    website: "https://yseuk.org/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-29",
    name: "Community Kitchen Kids — Cooking",
    provider: "Community Kitchen Kids",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Cooking classes teaching practical kitchen skills, food hygiene and simple recipes for children and young teens. Builds independence and a love of cooking in a fun, hands-on environment.",
    times: "Check provider for class schedule",
    website: "https://www.kidskitchen.org.uk/",
    neighbourhoods: ["Stoke Newington"],
  },
  {
    id: "snt-30",
    name: "Urban Gardening Club Stoke Newington",
    provider: "Urban Gardening Club",
    type: "Club",
    neighbourhood: "Stoke Newington",
    location: "Stoke Newington N16",
    postcodeSector: "N16",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact club for details",
    age: "5–14",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Gardening club teaching children to grow vegetables, herbs and flowers in a community growing space. Builds patience, responsibility and connection to nature through hands-on seasonal gardening.",
    times: "Check club for session dates",
    website: "https://allenscommunitygarden.org.uk/",
    neighbourhoods: ["Stoke Newington"],
  },


  // ── WINCHMORE HILL & SOUTHGATE (term-time) ───────────────────────────────────
  {
    id: "wst-1",
    name: "Southgate Hockey Centre — Juniors",
    provider: "Southgate Hockey Club",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate Hockey Centre, Waterfall Road, N14 6QB",
    postcodeSector: "N14",
    distanceMiles: 0.8,
    cost: "Contact club for junior membership fees",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Sundays (main club training day)",
    description: "One of the finest hockey facilities in the country at one of the UK's oldest and most successful clubs, with three European club titles. Term-time junior coaching for all age groups led by qualified coaches and senior squad players, alongside the club's well-known holiday camps.",
    times: "Sundays — check southgatehc.org.uk for age-group times",
    website: "https://southgatehc.org.uk/juniors/camps/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-2",
    name: "Walker Cricket Academy",
    provider: "Walker Cricket Academy",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Walker Primary School area, N21 3AP",
    postcodeSector: "N21",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees · ECB programmes may apply",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · weekend matches",
    description: "Cricket coaching academy in Winchmore Hill, building skills from beginner level through to competitive play. ECB-aligned coaching structure with a focus on technique and enjoyment of the game.",
    times: "Check academy for training and match schedule",
    website: "https://www.southgatecc.com/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-3",
    name: "Southgate Tennis Academy",
    provider: "Southgate Tennis Academy",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Member and non-member rates available",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekend mornings",
    description: "Junior tennis coaching for all abilities, from mini tennis through to competitive juniors. LTA-aligned coaching structure with excellent floodlit facilities in Southgate.",
    times: "Check academy for class schedule",
    website: "https://ph-tennis.co.uk/pages/tennis-southgate",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-4",
    name: "Lee Valley Ice Centre — Learn to Skate Programme",
    provider: "Lee Valley Ice Centre (Better Leisure)",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Lea Bridge Road, Leyton, E10 7QL",
    postcodeSector: "E10",
    distanceMiles: 6.5,
    cost: "Child skating session £10 · skate hire £3 · family session (2 adults + 2 children) £58.50 · Learn to Skate course pricing varies by level",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekly structured lessons — check Better for current schedule",
    description: "State-of-the-art venue rebuilt and reopened in 2023, with two Olympic-sized (60x30m) ice rinks under one roof — one of the largest ice skating venues in the south. Structured Learn to Skate courses take complete beginners through to confident skaters. Home rink of the Lee Valley Lions ice hockey club and Lee Valley London Skating Club, both welcoming new junior members. Fully accessible, 80-station gym and café on site.",
    times: "Mon–Fri 6am–11pm · Sat–Sun 7am–11pm",
    website: "https://www.better.org.uk/leisure-centre/lee-valley/ice-centre",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    phone: "03000 030 624 · LVIce@gll.org",
    notes: "A longer drive/journey from Winchmore Hill & Southgate but a uniquely excellent ice sports facility — Lea Bridge Overground station 10 mins walk, free 3-hour parking with centre use. Popular sessions sell out — book ahead.",
  },
  {
    id: "wst-5",
    name: "Southgate Olympic FC — Youth Programme",
    provider: "Southgate Olympic FC",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football club with age-group youth teams. FA-affiliated coaching, weekend league matches and weekday training building skills, fitness and teamwork.",
    times: "Check club for training and match schedule",
    website: "https://www.afcsouthgate.com/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-6",
    name: "Bush Hill Park — Gymnastics Sessions",
    provider: "Local gymnastics provider at Bush Hill Park",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Bush Hill Park area, EN1",
    postcodeSector: "EN1",
    distanceMiles: 1.5,
    cost: "Contact provider for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Coach-led gymnastics sessions for ages 3 and upwards, with classes for beginners, intermediate and advanced levels. Develops teamwork, confidence and physical skill in a secure, structured environment near Bush Hill Park's green spaces.",
    times: "Check provider for current class schedule",
    website: "https://www.ohanaacademy.co.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Bush Hill Park itself also has a free outdoor gym, basketball and tennis courts, plus an active Croquet Club — Enfield's only croquet green.",
  },
  {
    id: "wst-7",
    name: "Southgate Trampoline Academy",
    provider: "Southgate Trampoline Academy",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Trampolining classes building strength, coordination and confidence. Structured progression through British Gymnastics trampoline levels with qualified coaches for all abilities.",
    times: "Check academy for class schedule",
    website: "https://www.ashmolephoenixgymnastics.com/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-8",
    name: "Chickenshed Academy — Term-Time Classes",
    provider: "Chickenshed Theatre",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Chickenshed Theatre, 290 Chase Side, Southgate, N14 4PE",
    postcodeSector: "N14",
    distanceMiles: 1.0,
    cost: "Contact Chickenshed for term-time class fees · bursaries available",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekly term-time classes — check chickenshed.org.uk",
    description: "Iconic inclusive theatre company since 1974, running term-time drama, dance and music classes alongside the popular Summer Shed and Easter Shed holiday programmes. Over 800 young people take part in Chickenshed's youth theatre year-round. SEND-inclusive provision with experienced support.",
    times: "Check chickenshed.org.uk for term-time class schedule",
    website: "https://www.chickenshed.org.uk",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    phone: "020 8351 6161 · info@chickenshed.org.uk",
    notes: "SEND-inclusive — contact in advance for specific requirements. Bursaries available for families who need financial support.",
  },
  {
    id: "wst-9",
    name: "Southgate Opera — Youth Programme",
    provider: "Southgate Opera",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact for youth programme fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with company",
    description: "Singing and choir programme for young people connected to the established Southgate Opera company. Builds vocal technique, musicianship and performance confidence through regular rehearsals and concert opportunities.",
    times: "Check company for rehearsal schedule",
    website: "https://www.southgateopera.com/blank-1",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-10",
    name: "Palmers Green Music School",
    provider: "Palmers Green Music School",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Palmers Green Music School, N13 4PN",
    postcodeSector: "N13",
    distanceMiles: 0.7,
    cost: "Contact school for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check school for lesson schedule",
    website: "https://www.excelsismusic.co.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-11",
    name: "Enfield Youth Orchestra",
    provider: "Enfield Youth Orchestra",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact orchestra for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with orchestra",
    description: "Youth orchestra offering ensemble playing experience for young musicians across the borough. Builds musicianship, listening skills and the joy of playing together, with regular concert opportunities.",
    times: "Check orchestra for rehearsal schedule",
    website: "https://www.enfieldcommunityorchestra.co.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-12",
    name: "Southgate Study Centre — Maths Tuition",
    provider: "Southgate Study Centre",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition centre with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/southgate/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-13",
    name: "Bright Minds Tuition — English",
    provider: "Bright Minds Tuition",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check provider for session times",
    website: "https://bminds.co.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-14",
    name: "Grammar School Success Hub — 11+ Preparation",
    provider: "Grammar School Success Hub",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact hub for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements — particularly relevant given the strong local grammar school catchment.",
    times: "Check hub for class schedule",
    website: "https://prep4all.co.uk/11-plus-preparation-guide",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-15",
    name: "Tech Explorers Enfield — Coding",
    provider: "Tech Explorers Enfield",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "https://computerxplorers.co.uk/classes/holiday-camps",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-16",
    name: "Robolab Enfield — Robotics",
    provider: "Robolab Enfield",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Palmers Green N13",
    postcodeSector: "N13",
    distanceMiles: 0.7,
    cost: "Contact lab for term fees",
    age: "7–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check lab for class schedule",
    website: "https://www.robolabx.com/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-17",
    name: "Southgate Strategy Club — Chess",
    provider: "Southgate Strategy Club",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact club for membership fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy games club teaching tactics and sportsmanship for all levels from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://www.thebestof.co.uk/local/enfield/community-hub/local-guides/view/southgateandenfieldchessclub/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-18",
    name: "Polish Saturday School — Southgate",
    provider: "Polish Saturday School",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Weekly Polish language, culture and heritage classes for children. Suitable for heritage speakers maintaining their first language as well as those new to Polish.",
    times: "Saturday mornings — check school for exact times",
    website: "https://en.polskaszkolaenfield.com/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-19",
    name: "Turkish Community Language School — Winchmore Hill",
    provider: "Turkish Community Language School",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Turkish language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency, reflecting North London's significant Turkish community.",
    times: "Check school for class schedule",
    website: "https://turkegitimkonsorsiyumu.org.uk/uye-okullar/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-20",
    name: "Palmers Green Art Academy",
    provider: "Palmers Green Art Academy",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Palmers Green Art Academy, N13 4PN",
    postcodeSector: "N13",
    distanceMiles: 0.7,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes for children and young teens covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check academy for class times",
    website: "https://www.instagram.com/palmers_green_pottery/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-21",
    name: "Design and Create Studio — Graphic Design",
    provider: "Design and Create Studio",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "7–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Graphic design classes introducing children to design software, illustration and creative digital skills. Builds technical confidence alongside artistic expression for a digital age.",
    times: "Check studio for class schedule",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-22",
    name: "Sewing Studio Kids — Southgate",
    provider: "Sewing Studio Kids",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Sewing and fashion design classes teaching practical making skills — hand-stitching, basic machine sewing and simple garment design for children and young teens.",
    times: "Check provider for workshop dates",
    website: "https://www.silvanass.co.uk/products/beginners-sewing-class-teens",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-23",
    name: "Ceramics North London — Youth Studio",
    provider: "Ceramics North London",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.claytime.london/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-24",
    name: "Southgate Photography School",
    provider: "Southgate Photography School",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact school for course pricing",
    age: "9–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check school for course dates",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-25",
    name: "Junior Content Creators Club",
    provider: "Junior Content Creators Club",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Palmers Green N13",
    postcodeSector: "N13",
    distanceMiles: 0.7,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production club for older children and teens — scripting, shooting and editing short films and social content collaboratively. Builds technical and storytelling skills.",
    times: "Check club for session schedule",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-26",
    name: "Grovelands Park Nature Rangers",
    provider: "Grovelands Park Nature Rangers",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Grovelands Park, The Bourne, N21 2SJ",
    postcodeSector: "N21",
    distanceMiles: 0.6,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature exploration sessions in Grovelands Park, one of Enfield's most beautiful historic parks (Grade II* listed Repton landscape with boating lake). Den building, bug hunts, plant identification and outdoor games in stunning parkland surroundings.",
    times: "Check local listings for session dates",
    website: "https://friendsofgrovelands.co.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
    notes: "Grovelands Park features a café, boating lake, bowling green, tennis courts and children's play area.",
  },
  {
    id: "wst-27",
    name: "Broomfield Park — Environmental Club",
    provider: "Broomfield Park Environmental Club",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Broomfield Park, N13 4HE",
    postcodeSector: "N13",
    distanceMiles: 0.6,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature and outdoor learning sessions at Broomfield Park, a beloved local green space with walled garden, museum and woodland walks. Wildlife spotting, conservation activities and seasonal nature crafts in Palmers Green.",
    times: "Check local listings for session dates",
    website: "https://www.fobp.uk/",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-28",
    name: "Southgate Young Debaters",
    provider: "Southgate Young Debaters",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Debate and public speaking classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check club for session schedule",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-29",
    name: "Future Business Leaders Enfield",
    provider: "Future Business Leaders Enfield",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Winchmore Hill N21",
    postcodeSector: "N21",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Entrepreneurship and business club introducing teens to practical skills — idea generation, basic finance, marketing and pitching. Builds confidence and real-world problem-solving through project-based learning.",
    times: "Check club for session schedule",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },
  {
    id: "wst-30",
    name: "Junior Culinary Academy — Southgate",
    provider: "Junior Culinary Academy",
    type: "Club",
    neighbourhood: "Winchmore Hill & Southgate",
    location: "Southgate N14",
    postcodeSector: "N14",
    distanceMiles: 0.9,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Cooking classes teaching practical kitchen skills, food hygiene and simple recipes for children and young teens. Builds independence and a love of cooking in a fun, hands-on environment.",
    times: "Check academy for class schedule",
    website: "",
    neighbourhoods: ["Winchmore Hill & Southgate"],
  },


  // ── BARNET & HIGH BARNET (term-time) ─────────────────────────────────────────
  {
    id: "bht-1",
    name: "Old Elizabethans Football Club — Juniors",
    provider: "Old Elizabethans FC",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Old Elizabethans Memorial Playing Fields, Mays Lane, EN5 4HL",
    postcodeSector: "EN5",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football club with age-group youth teams sharing the historic Old Elizabethans sports ground with the cricket and rugby sections. FA-affiliated coaching, weekend league matches and weekday training.",
    times: "Check club for training and match schedule",
    website: "",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-2",
    name: "Barnet Elizabethans Rugby Academy",
    provider: "Barnet Elizabethans RFC",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Byng Road, Barnet, EN5 4HL",
    postcodeSector: "EN5",
    distanceMiles: 0.7,
    cost: "Membership £150/year (with free playing jersey for new members) · free taster sessions available",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Sundays (main training day)",
    description: "North London's premier rugby club with a thriving academy welcoming players from U6 upwards. Boys and girls welcome. Family-friendly club with a well-stocked clubhouse. STAGS membership scheme (£15/month) supports junior rugby development.",
    times: "Sundays — contact club for age-group times",
    website: "https://www.berfc.co.uk/contact",
    neighbourhoods: ["Barnet & High Barnet"],
    phone: "020 8449 0040",
    notes: "Free taster sessions available. All ages U6–U18 and seniors welcome.",
  },
  {
    id: "bht-3",
    name: "Arkley Golf Club — Junior Academy",
    provider: "Arkley Golf Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Arkley Golf Club, Rowley Green Road, EN5 3HF",
    postcodeSector: "EN5",
    distanceMiles: 1.5,
    cost: "Contact club for junior academy pricing",
    age: "7–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend group lessons",
    description: "Junior golf academy at Arkley Golf Club, an established parkland course in the Barnet countryside. PGA professional coaching with a structured pathway from beginners through to club membership.",
    times: "Check club for academy schedule",
    website: "https://www.arkleygolfclub.co.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-4",
    name: "Old Fold Manor Golf Club — Juniors",
    provider: "Old Fold Manor Golf Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Old Fold Manor Golf Club, Hadley Green Road, EN5 4QN",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact club for junior membership fees",
    age: "7–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend group lessons",
    description: "Junior golf coaching at Old Fold Manor, a historic golf club bordering Hadley Common. PGA professional instruction building skills from beginner level through to competitive junior golf.",
    times: "Check club for junior coaching schedule",
    website: "https://www.oldfoldmanor.co.uk/juniors",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-5",
    name: "Trent Park Equestrian Centre — Youth Riding School",
    provider: "Trent Park Equestrian Centre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "East Pole Farmhouse, Bramley Road, EN4 0PS",
    postcodeSector: "EN4",
    distanceMiles: 1.8,
    cost: "Children's lessons from age 4 · private lessons 45 minutes for ages 7–12 · contact centre for current pricing",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "One of the largest riding schools in the country and a British Horse Society Equestrian Centre of Excellence, established over 40 years. Over 100 horses and ponies, international-sized outdoor and covered arenas, showjumps and a cross-country course. BHS-approved training up to intermediate level (BHS II). On the edge of Trent Country Park with woodland bridleways.",
    times: "Check trentpark.com for lesson timetable",
    website: "https://trentpark.com",
    neighbourhoods: ["Barnet & High Barnet"],
    phone: "0208 363 8630",
    notes: "Weight limit typically 13.5 stone / 85kg for riders. Indoor arena means lessons run in all weather. On-site café.",
  },
  {
    id: "bht-6",
    name: "Barnet Archery Club — Juniors",
    provider: "Barnet Archery Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.6,
    cost: "Contact club for term fees · equipment provided for beginners",
    age: "8–18",
    interests: ["sports"],
    dayOfWeek: "Weekend mornings",
    description: "Archery club teaching focus, precision and technique in a safe, structured environment. Beginners welcome with all equipment provided, progressing through to more advanced shooting for keen young archers.",
    times: "Check club for session times",
    website: "https://www.aquarius-archers.org.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-7",
    name: "Queen Elizabeth's School — Sports Academy",
    provider: "Queen Elizabeth's School",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Queen Elizabeth's School, Queens Road, Barnet, EN5 4DQ",
    postcodeSector: "EN5",
    distanceMiles: 0.4,
    cost: "Contact school for community programme fees",
    age: "11–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Sports academy programme at one of the country's leading grammar schools, using its excellent sports facilities. Open to the wider community alongside school provision, building fitness and sporting skills for secondary-age young people.",
    times: "Check school for community programme times",
    website: "https://www.qebarnet.co.uk/beyond-the-classroom/sports/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-8",
    active: false, // marked not running in Oct 2026 review
    name: "Stage Door Performing Arts Barnet",
    provider: "Stage Door Performing Arts",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Drama, dance and singing classes building performance skills and confidence across a wide age range. Regular showcase opportunities and structured progression through different performing arts disciplines.",
    times: "Check studio for class schedule",
    website: "https://www.stagecoach.co.uk/barnet",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-9",
    name: "Barnet Youth Theatre",
    provider: "Barnet Youth Theatre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Drama classes building confidence, technique and stage presence across a wide age range. Structured progression with regular performance opportunities in a supportive, skill-focused setting.",
    times: "Check theatre for class schedule",
    website: "https://barnetyouth.uk/detail_events_v2.aspx?id=6549",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-10",
    name: "The Bull Theatre — Youth Company",
    provider: "The Bull Theatre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "The Bull Theatre, 68 High Street, Barnet, EN5 5SG",
    postcodeSector: "EN5",
    distanceMiles: 0.3,
    cost: "Contact theatre for youth company fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Youth theatre company at The Bull, a community arts venue on Barnet High Street. Builds performance skills, ensemble work and confidence with regular production opportunities for young people.",
    times: "Check thebulltheatre.com for youth company schedule",
    website: "https://www.thebulltheatre.com/platform-performing-arts",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-11",
    name: "Barnet Music Centre",
    provider: "Barnet Music Centre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Queen Elizabeth's School area, EN5 4DQ",
    postcodeSector: "EN5",
    distanceMiles: 0.4,
    cost: "Contact centre for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check centre for lesson schedule",
    website: "https://www.barnet.gov.uk/directories/directme/east-barnet-music-centre",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-12",
    name: "Barnet Schools Choir Programme",
    provider: "Barnet Schools Choir Programme",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact programme for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with programme",
    description: "Choir and vocal training for young singers across Barnet schools, building technique, sight-reading and performance confidence through regular rehearsals and seasonal concerts.",
    times: "Check programme for rehearsal schedule",
    website: "https://beatrust.org.uk/schools/school-music-development-plan/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-13",
    name: "Barnet Learning Partnership — Maths",
    provider: "Barnet Learning Partnership",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact for tuition rates",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Maths tuition building core skills and exam confidence across primary and secondary levels. Small group or 1-2-1 sessions tailored to individual needs and curriculum requirements.",
    times: "Check provider for tutor availability",
    website: "https://www.belstradedservices.uk/Page/30528",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-14",
    name: "Scholars Academy Barnet — English Tuition",
    provider: "Scholars Academy",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact academy for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local academy for session times",
    website: "",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-15",
    name: "QE Grammar Preparation Centre — 11+",
    provider: "QE Grammar Preparation Centre",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact centre for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English, with a particular focus on the local Queen Elizabeth's School entrance requirements. Small group tuition.",
    times: "Check centre for class schedule",
    website: "https://www.qehbs.com/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-16",
    name: "Barnet STEM Hub — Coding",
    provider: "Barnet STEM Hub",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact hub for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check hub for class schedule",
    website: "https://barnettuitionandexamcentre.co.uk/stem-centre/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-17",
    name: "Young Engineers Barnet — Innovation Lab",
    provider: "Young Engineers",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "7–14",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using LEGO-based building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured weekly challenges.",
    times: "Check lab for class schedule",
    website: "https://youngengineers.org/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-18",
    name: "Barnet Board Games and Chess Club",
    provider: "Barnet Board Games and Chess Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy board games club teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, in a relaxed social setting.",
    times: "Check with club for session times",
    website: "https://barnetknights.com/leagues/middlesex-league",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-19",
    name: "Barnet Language Academy — German",
    provider: "Barnet Language Academy",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "German language classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Check academy for class schedule",
    website: "",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-20",
    name: "Barnet Mandarin School",
    provider: "Barnet Mandarin School",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Mandarin Chinese language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels for children of all ages and backgrounds.",
    times: "Check school for class schedule",
    website: "https://www.churchhillmandarin.com/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-21",
    name: "High Barnet Art School",
    provider: "High Barnet Art School",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "High Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes for children and young teens covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check school for class times",
    website: "https://www.art-k.co.uk/class-high-barnet/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-22",
    name: "Creative Design Workshop Barnet",
    provider: "Creative Design Workshop",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Art and design classes covering a broad range of media and techniques for children and young teens. Builds creative confidence and technical skill in small, supportive group sessions.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-23",
    name: "Potters Wheel — Youth Studio",
    provider: "Potters Wheel",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.tokenstudio.co.uk/service-page/little-potter-s-wheel-experience",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-24",
    name: "Barnet Visual Media Academy — Photography",
    provider: "Barnet Visual Media Academy",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact academy for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check academy for course dates",
    website: "https://barnetyouth.uk/Events?CalendarID=213",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-25",
    name: "Young Producers Film Studio",
    provider: "Young Producers Film Studio",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "10–18",
    interests: ["arts","stem","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production classes for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers.",
    times: "Check studio for session schedule",
    website: "https://barnetyouth.uk/detail_events_v2.aspx?id=7977",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-26",
    name: "Hadley Green Nature Club",
    provider: "Hadley Green Nature Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Hadley Green, EN5 5SU",
    postcodeSector: "EN5",
    distanceMiles: 0.6,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature club exploring Hadley Green and the adjacent Hadley Common — one of Barnet's most picturesque open spaces, near Monken Hadley village. Den building, bug hunts and seasonal nature crafts in a beautiful historic setting.",
    times: "Check local listings for session dates",
    website: "https://www.instagram.com/natureexplorersbarnet/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-27",
    name: "Barnet Museum — Young Historians",
    provider: "Barnet Museum & Local History Society",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet Museum, 31 Wood Street, EN5 4BE",
    postcodeSector: "EN5",
    distanceMiles: 0.3,
    cost: "FREE — admission and most activities free of charge",
    age: "8–16",
    interests: ["arts"],
    dayOfWeek: "Tue–Thu 2:30–4:30pm · Sat 10:30am–4pm · Sun 2:30–4:30pm",
    description: "Free local history museum running learning activities for children, including a Key Stage 2 Learning Project designed to commemorate the 550th anniversary of the Battle of Barnet (1471) — one of the most important battles of the Wars of the Roses. Loan boxes and specialist volunteers also support local schools and outreach.",
    times: "Tue–Thu 2:30–4:30pm · Sat 10:30am–4pm · Sun 2:30–4:30pm (closed Mon)",
    website: "https://www.barnetmuseum.co.uk/learning-and-outreach.html",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Admission free. School sessions and learning resources tied to the historic Battle of Barnet. Run by Barnet Museum & Local History Society, a registered charity.",
  },
  {
    id: "bht-28",
    name: "Trent Park Adventure Club",
    provider: "Trent Park Adventure Club",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Trent Country Park, EN4 0JY",
    postcodeSector: "EN4",
    distanceMiles: 1.7,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings",
    description: "Outdoor adventure and nature exploration sessions in the historic Trent Country Park — 413 acres of woodland, lakes and meadows, once a royal hunting ground. Den building, bushcraft, wildlife spotting and orienteering in a stunning natural setting.",
    times: "Check local listings for session dates",
    website: "https://londonorienteering.co.uk/permanent-courses/",
    neighbourhoods: ["Barnet & High Barnet"],
    notes: "Trent Country Park also home to the Trent Park Equestrian Centre and extensive bridleways and trails.",
  },
  {
    id: "bht-29",
    name: "Future Leaders Barnet — Public Speaking",
    provider: "Future Leaders Barnet",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact for term fees",
    age: "10–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Public speaking and leadership classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check provider for session schedule",
    website: "https://www.futureleaders.uk/",
    neighbourhoods: ["Barnet & High Barnet"],
  },
  {
    id: "bht-30",
    name: "Young Enterprise Barnet",
    provider: "Young Enterprise",
    type: "Club",
    neighbourhood: "Barnet & High Barnet",
    location: "Barnet EN5",
    postcodeSector: "EN5",
    distanceMiles: 0.5,
    cost: "Contact local branch for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Entrepreneurship and business club connected to the national Young Enterprise charity, introducing teens to practical skills — idea generation, basic finance, marketing and pitching. Builds real-world confidence through project-based learning.",
    times: "Check local branch for session schedule",
    website: "https://young-enterprise.org.uk",
    neighbourhoods: ["Barnet & High Barnet"],
  },


  // ── ALEXANDRA PALACE & WOOD GREEN (term-time) ────────────────────────────────
  {
    id: "apt-1",
    name: "Alexandra Park Youth Football Club",
    provider: "Alexandra Park Youth FC",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Park area, N22",
    postcodeSector: "N22",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football club with age-group youth teams using the green spaces around Alexandra Park. FA-affiliated coaching, weekend league matches and weekday training building skills and teamwork.",
    times: "Check club for training and match schedule",
    website: "https://www.apyfc.org.uk/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-2",
    name: "New River Sport & Fitness — Junior Programme",
    provider: "New River Sport & Fitness",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "New River Sport & Fitness, White Hart Lane, N22 5QW",
    postcodeSector: "N22",
    distanceMiles: 0.8,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["sports","swimming"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Junior sports programme at New River Sport & Fitness, close to Alexandra Park. Swimming, sports hall activities and multi-sport coaching for all abilities, from beginners to competitive juniors.",
    times: "Check centre for term-time class schedule",
    website: "https://haringey.gov.uk/leisure-parks-culture/leisure-centres/new-river/events-new-river",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-3",
    name: "Alexandra Palace Ice Skating Academy",
    provider: "Alexandra Palace Ice Rink",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace Ice Rink, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "Skate UK levels 1–8 and Children's Skating Academy (Bronze, Silver, Gold) · term courses approx £65–£112 · skate hire included",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Term-time weekly lesson programme — check alexandrapalace.com",
    description: "Voted UK's number 1 ice rink in 2023. Structured Skate UK levels 1–8 progress complete beginners through to confident skaters, alongside the Children's Skating Academy (Bronze, Silver, Gold). Ice hockey and figure skating clubs also welcome junior members. All-weather indoor rink open year-round (except a short July closure).",
    times: "Public skating: Mon 11am–5:30pm · Tue 1–5pm · Wed–Thu 11am–5pm · Fri 1–5:30pm · Sat–Sun 11am–4:30pm",
    website: "https://www.alexandrapalace.com/the-ice-rink/learn-to-skate/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    phone: "020 8365 4386 · ice.rink@alexandrapalace.com",
    notes: "Skate hire included in ticket price. W3 bus from Wood Green station to Palace entrance.",
  },
  {
    id: "apt-4",
    name: "Haringey Tigers Basketball Academy",
    provider: "Haringey Tigers",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community basketball academy with structured coaching across age groups. Skills development, team play and competitive opportunities in a fun, inclusive club environment.",
    times: "Check academy for training times",
    website: "https://www.hoophavenbasketballclub.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-5",
    name: "Wood Green Futsal Academy",
    provider: "Wood Green Futsal Academy",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Futsal and indoor football coaching building fast-paced technical skills, close control and quick decision-making. Builds confidence and ball skills transferable to outdoor football.",
    times: "Check academy for training times",
    website: "https://londonfutsalstars.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-6",
    name: "Alexandra Circus Arts School",
    provider: "Alexandra Circus Arts School",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace & Wood Green area, N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "7–18",
    interests: ["sports","drama"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Circus skills and acrobatics classes building strength, flexibility, confidence and teamwork through aerial, acrobatic and balance disciplines. Welcoming to all abilities.",
    times: "Check school for class schedule",
    website: "https://www.showstoppersperformingarts.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-7",
    name: "Jacksons Lane — JL Circus & Drama (Triangle Family Hub)",
    provider: "Jacksons Lane",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Triangle Family Hub, plus Jacksons Lane, 269a Archway Road, N6 5AA",
    postcodeSector: "N6",
    distanceMiles: 1.4,
    cost: "FREE — drama and circus sessions at Triangle Family Hub",
    age: "4–18",
    interests: ["sports","drama"],
    dayOfWeek: "Thursdays — 4–7yrs 3:30–4:30pm, 8–12yrs 4:30–6pm",
    description: "Free weekly drama and circus sessions delivered by Jacksons Lane (the UK's leading supporter of contemporary circus, est. 1975) at Triangle Family Hub. JL Circus classes teach diabolo, juggling, acrobalance and more — expect to drop balls, fall off pyramids and laugh a lot along the way. Jacksons Lane itself, in a converted Grade II listed Edwardian church directly opposite Highgate tube, also runs term-time youth participation projects for a wide range of ages.",
    times: "Thursdays: 4–7yrs 3:30–4:30pm · 8–12yrs 4:30–6pm (Triangle Family Hub) · check jacksonslane.org.uk for main venue programme",
    website: "https://www.jacksonslane.org.uk/whats-on/classes-courses/",
    neighbourhoods: ["Alexandra Palace & Wood Green","Highgate"],
    phone: "020 8340 5226",
    notes: "Free sessions at Triangle Family Hub. Jacksons Lane began in 1975 when local residents wanted to provide holiday activities for their children — still true to that mission 50 years on. Step-free access, BSL interpreter available on request.",
  },
  {
    id: "apt-8",
    name: "Karamel — Youth Music Academy",
    provider: "Karamel",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Karamel, Gables Studios, 7 Hatherley Mews, N22 6UJ",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact Karamel for academy fees",
    age: "8–18",
    interests: ["music","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Music and performing arts academy at Karamel, a creative venue and live music space in Wood Green. Builds vocal and instrumental skills alongside performance confidence in a vibrant, professional setting.",
    times: "Check karamel.com for academy schedule",
    website: "https://karamel.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-9",
    name: "Collage Arts — Creative Studios (Music Production & Media)",
    provider: "Collage Arts",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Gables Studios, 7 Hatherley Mews, N22 6UJ",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact Collage Arts for course fees · some subsidised places available",
    age: "10–18",
    interests: ["music","stem","arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Music production and media classes at Collage Arts' creative studios in Wood Green, a long-established charity supporting creative talent across North London. Recording, mixing and digital media skills taught in a professional studio environment.",
    times: "Check collage-arts.org for course schedule",
    website: "https://www.collage-arts.org/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-10",
    name: "Haringey Sixth Form STEM Academy",
    provider: "Haringey Sixth Form Centre",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Haringey Sixth Form Centre, N22 8HQ",
    postcodeSector: "N22",
    distanceMiles: 0.7,
    cost: "Contact college for enrichment programme fees",
    age: "11–18",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or evenings",
    description: "Coding and STEM enrichment programme delivered at Haringey Sixth Form Centre, open to secondary-age students. Builds programming skills and computational thinking through structured, progressive sessions.",
    times: "Check college for enrichment schedule",
    website: "https://www.haringey6.ac.uk/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-11",
    name: "Tech Talent Lab Wood Green",
    provider: "Tech Talent Lab",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact lab for term fees",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check lab for class schedule",
    website: "",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-12",
    name: "Wood Green Chess & Strategy Club",
    provider: "Wood Green Chess & Strategy Club",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy club teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://checkmateirl.com/venues/london/wood-green-chess-club/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-13",
    name: "Greek School of Haringey",
    provider: "Greek School of Haringey",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Greek language and culture classes for children, reflecting Haringey's significant Greek and Greek Cypriot community. Suitable for heritage speakers and complete beginners alike.",
    times: "Saturday mornings — check school for exact times",
    website: "https://www.greekschool-london.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-14",
    name: "Portuguese Language Centre North London",
    provider: "Portuguese Language Centre",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact centre for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Portuguese language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive setting.",
    times: "Check centre for class schedule",
    website: "http://www.clubebra.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-15",
    active: false, // marked not running in Oct 2026 review
    name: "Chocolate Factory Arts Centre — Youth Programme",
    provider: "Chocolate Factory Arts",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Chocolate Factory, Clarendon Road, N22 6XJ",
    postcodeSector: "N22",
    distanceMiles: 0.7,
    cost: "Contact venue for youth programme fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Art classes at the Chocolate Factory, a converted industrial building now home to artist studios and creative workspaces in Wood Green. Drawing, painting and mixed media in an inspiring working-artist environment.",
    times: "Check venue for youth programme schedule",
    website: "https://www.collage-arts.org/voices/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-16",
    name: "Wood Green Creative Makers",
    provider: "Wood Green Creative Makers",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Art classes for children and young teens covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check studio for class times",
    website: "https://www.collage-arts.org/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-17",
    name: "Ceramic Studio North — Youth Programme",
    provider: "Ceramic Studio North",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check studio for class times",
    website: "https://www.claytime.london/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-18",
    name: "Haringey Photography Project",
    provider: "Haringey Photography Project",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact project for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check project for course dates",
    website: "",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-19",
    name: "Reel Stories Youth Media",
    provider: "Reel Stories Youth Media",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking and media production club for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers.",
    times: "Check club for session schedule",
    website: "https://bavc.org/programs/youth/reel-stories/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-20",
    name: "Bernie Grant Arts Centre — Youth Company",
    provider: "Bernie Grant Arts Centre",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Bernie Grant Arts Centre, Town Hall Approach Road, Tottenham, N15 4RX",
    postcodeSector: "N15",
    distanceMiles: 1.2,
    cost: "Contact centre for youth company fees",
    age: "8–18",
    interests: ["drama","music"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Drama and performing arts youth company at the striking £15 million purpose-built arts centre, designed by Sir David Adjaye OBE and opened in 2007. Named after Tottenham's late MP Bernie Grant, the centre is a home for aspiring, emerging and established Black and culturally diverse creative practitioners. Features a 274-seat theatre, rehearsal studios and a boutique cinema.",
    times: "Check berniegrantcentre.co.uk for youth company schedule",
    website: "https://www.berniegrantcentre.co.uk/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    phone: "020 8054 4447 · hires@berniegrantcentre.co.uk",
    notes: "Seven Sisters station 0.3 miles. Also hosts BAP Theatre's youth and adult acting workshops.",
  },
  {
    id: "apt-21",
    name: "Alexandra Palace — Nature Rangers",
    provider: "Alexandra Palace",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace & Park, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "Contact Alexandra Palace for programme pricing",
    age: "4–12",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings — check alexandrapalace.com",
    description: "Nature exploration sessions across the iconic 196-acre Alexandra Palace and Park. Wildlife spotting in the deer enclosure, seasonal nature crafts and outdoor learning in one of London's best-loved green spaces, visited by over a million people annually.",
    times: "Check alexandrapalace.com for children's nature programme dates",
    website: "https://www.alexandrapalace.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    phone: "020 8365 2121",
  },
  {
    id: "apt-22",
    name: "Friends of Alexandra Park — Junior Conservation Club",
    provider: "Friends of Alexandra Park",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Park, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "Free or low cost — contact Friends group for details",
    age: "5–14",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Conservation and nature club run by the Friends of Alexandra Park community group. Children get hands-on with habitat care, planting and wildlife monitoring, learning to look after this much-loved local park.",
    times: "Check Friends of Alexandra Park for session dates",
    website: "https://sites.google.com/view/newfoap/home/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-23",
    name: "Haringey Youth Broadcasters — Podcasting",
    provider: "Haringey Youth Broadcasters",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Podcasting and broadcasting club teaching recording, editing and presenting skills. Young people produce their own audio content, building confidence in communication and technical media production.",
    times: "Check club for session schedule",
    website: "https://haringey.gov.uk/children-young-people-families/early-help-haringey/haringey-youth-service/our-youth-hubs/rising-green-youth-hub",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-24",
    name: "Future Creators Animation Club",
    provider: "Future Creators Animation Club",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Animation classes teaching stop-motion, 2D and digital animation techniques. Children create their own short animated projects, building both technical and storytelling skills.",
    times: "Check club for workshop dates",
    website: "https://www.wfchub.org/stronger-futures",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-25",
    name: "Wood Green Young Entrepreneurs Hub",
    provider: "Wood Green Young Entrepreneurs Hub",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact hub for term fees",
    age: "10–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Entrepreneurship and business club introducing teens to practical skills — idea generation, basic finance, marketing and pitching. Builds confidence and real-world problem-solving through project-based learning.",
    times: "Check hub for session schedule",
    website: "https://haringey.gov.uk/children-young-people-families/early-help-haringey/haringey-youth-service/our-youth-hubs/rising-green-youth-hub",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-26",
    name: "Debate Society Haringey",
    provider: "Debate Society Haringey",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact society for term fees",
    age: "8–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Debate and public speaking classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check society for session schedule",
    website: "",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-27",
    name: "Community Kitchen Haringey — Youth Cooking Programme",
    provider: "Community Kitchen Haringey",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact for term fees — some subsidised places available",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Cooking classes teaching practical kitchen skills, food hygiene and simple recipes for children and young teens. Builds independence and a love of cooking in a fun, hands-on community setting.",
    times: "Check provider for class schedule",
    website: "https://www.youthspace.haringey.gov.uk/rising-green-youth-hub",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-28",
    name: "Architecture for Kids North London — Wood Green",
    provider: "Architecture for Kids",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and design club introducing children to building design, model-making and spatial thinking. Combines creative and technical skills through hands-on projects.",
    times: "Check club for workshop dates",
    website: "https://www.architecturekids.co.uk/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-29",
    name: "Makerspace Wood Green — Design",
    provider: "Makerspace Wood Green",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Wood Green N22",
    postcodeSector: "N22",
    distanceMiles: 0.6,
    cost: "Contact makerspace for term fees",
    age: "8–18",
    interests: ["stem","arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Hands-on design and making club with access to tools and materials for creative projects — from simple builds to more involved design work. Builds practical skills and creative problem-solving.",
    times: "Check makerspace for session schedule",
    website: "",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
  },
  {
    id: "apt-30",
    name: "Alexandra Palace — History Explorers",
    provider: "Alexandra Palace",
    type: "Club",
    neighbourhood: "Alexandra Palace & Wood Green",
    location: "Alexandra Palace, N22 7AY",
    postcodeSector: "N22",
    distanceMiles: 0.4,
    cost: "Contact Alexandra Palace for programme pricing",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops — check alexandrapalace.com",
    description: "History workshops exploring the fascinating story of 'The People's Palace' — built in 1873, twice destroyed by fire, and the birthplace of BBC television broadcasting in 1936. Children learn about the Palace's remarkable history through guided activities and storytelling.",
    times: "Check alexandrapalace.com for history programme dates",
    website: "https://www.alexandrapalace.com/",
    neighbourhoods: ["Alexandra Palace & Wood Green"],
    phone: "020 8365 2121",
  },


  // ── PRIMROSE HILL & BELSIZE PARK (term-time) ─────────────────────────────────
  {
    id: "pbt-1",
    name: "Primrose Hill Netball Club — Juniors",
    provider: "Primrose Hill Netball Club",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community netball club with junior teams across age groups. Skills development, match play and team spirit for beginners through to competitive young players.",
    times: "Check club for training schedule",
    website: "https://www.netballstars.com/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-2",
    name: "Regent's Park Cycling Academy",
    provider: "Regent's Park Cycling Academy",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Regent's Park, NW1 4NR",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Cycling skills academy using Regent's Park's paths and cycle-friendly spaces. Builds bike handling, road safety awareness and confidence for young cyclists of all abilities.",
    times: "Check academy for session schedule",
    website: "https://www.regentsparkcyclists.com/our-members",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-3",
    name: "Primrose Hill Running Club — Youth Programme",
    provider: "Primrose Hill Running Club",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings or weekend mornings",
    description: "Youth running programme using the iconic open space of Primrose Hill. Builds fitness, technique and a love of running in a friendly, supportive group setting for all abilities.",
    times: "Check club for session schedule",
    website: "https://www.serpentine.org.uk/location-primrosehill/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-4",
    name: "Belsize Park Swim School",
    provider: "Belsize Park Swim School",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact school for lesson pricing",
    age: "4–16",
    interests: ["swimming"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Structured swim lessons for children of all abilities, from beginners to confident swimmers. Small class sizes and a focus on water confidence and technique.",
    times: "Check school for class schedule",
    website: "https://swimmingnature.com/our-pools/rfc-recreation-club",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-5",
    name: "Camden Gymnastics Academy",
    provider: "Camden Gymnastics Academy",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park / Camden NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.7,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons & weekend mornings",
    description: "Recreational gymnastics classes for all ages and abilities, building strength, flexibility and confidence. Structured progression through British Gymnastics levels with qualified coaches.",
    times: "Check academy for class schedule",
    website: "https://www.camden.gov.uk/gymnastics",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-6",
    name: "Primrose Hill Performing Arts Academy",
    provider: "Primrose Hill Performing Arts Academy",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["dance","drama","music"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Dance and performing arts classes for a wide age range, building confidence, technique and stage presence. Regular showcase opportunities and structured progression through different disciplines.",
    times: "Check academy for class schedule",
    website: "https://www.stagecoach.co.uk/primrosehill",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-7",
    name: "Hampstead Theatre — Youth Theatre Project",
    provider: "Hampstead Theatre",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Hampstead Theatre, Eton Avenue, Swiss Cottage, NW3 3EU",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact Hampstead Theatre for current youth project details — many participation projects are free",
    age: "14–18",
    interests: ["drama"],
    dayOfWeek: "Weekly sessions, typically a weekday evening — check hampsteadtheatre.com",
    description: "Youth theatre projects at one of London's leading new-writing theatres. Working with professional theatre artists, young people play theatre games, develop creative skills and make new performance material showcased at the theatre. Hampstead Theatre also runs the INSPIRE programme for emerging playwrights and frequently partners with Wac Arts on intergenerational and writing projects open to Camden residents.",
    times: "Check hampsteadtheatre.com/get-involved for current youth programme dates",
    website: "https://www.hampsteadtheatre.com/about-us/get-involved/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    phone: "020 7722 9301",
    notes: "Youth theatre typically for ages 14-18. Some projects via Wac Arts are free for those who live, learn or earn in Camden.",
  },
  {
    id: "pbt-8",
    name: "Cecil Sharp House — Youth Folk Arts",
    provider: "English Folk Dance and Song Society (EFDSS)",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Cecil Sharp House, 2 Regent's Park Road, NW1 7AY",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact EFDSS for term-time course fees · FREE Folk Unlimited course for learning disabled young people (7–25)",
    age: "7–18",
    interests: ["music","dance","arts"],
    dayOfWeek: "Saturdays — term-time youth folk arts classes",
    description: "England's dedicated folk arts centre, a Grade II listed building dating to 1929–30 and named after folk collector Cecil Sharp. Home to the English Folk Dance and Song Society and the Vaughan Williams Memorial Library, England's national folk music and dance archive. Regular half-term workshops bring in around 70 children and young people from across London and beyond — including Folk Hop, a course fusing hip hop and folk dance for ages 12–18.",
    times: "Term-time Saturday classes — check cecilsharphouse.org for current schedule",
    website: "http://www.cecilsharphouse.org/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
    phone: "020 7485 2206 · efdss@efdss.org",
    notes: "Building survived a 1940 bombing raid that destroyed the entrance and stairwell — the famous folk dance mural by Ivon Hitchens (69x20 feet, completed 1954) remains a centrepiece. 5–10 mins from Regent's Park, Primrose Hill and Camden Market.",
  },
  {
    id: "pbt-9",
    name: "Primrose Hill Music Collective",
    provider: "Primrose Hill Music Collective",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact collective for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check collective for lesson schedule",
    website: "https://encoremusicians.com/Primrose-Hill-Collective",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-10",
    name: "Belsize Young Composers Programme",
    provider: "Belsize Young Composers Programme",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact programme for term fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Music composition and songwriting classes for young people interested in creating their own music. Builds technical understanding of harmony, structure and arrangement alongside creative experimentation.",
    times: "Check programme for session schedule",
    website: "https://www.orasingers.com/young-composers",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-11",
    name: "Primrose Hill Education Hub — Maths",
    provider: "Primrose Hill Education Hub",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact hub for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local hub for session times",
    website: "https://camdenlearning.org.uk/learning-hub/primary-maths-hub/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-12",
    name: "Belsize Learning Studio — English Tuition",
    provider: "Belsize Learning Studio",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact studio for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local studio for session times",
    website: "https://www.tutorhunt.com/locations/london/belsize-park/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-13",
    name: "Academic Achievers NW3 — 11+ Preparation",
    provider: "Academic Achievers",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact for course pricing",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check provider for class schedule",
    website: "https://www.academicachieversacademy.co.uk/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-14",
    name: "Coding Creators Camden",
    provider: "Coding Creators",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill / Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "https://learn.modernagecoders.com/coding-classes-in-camden-london",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-15",
    name: "Digital Inventors Lab — Robotics",
    provider: "Digital Inventors Lab",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact lab for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check lab for class schedule",
    website: "https://www.getyounginventors.online/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-16",
    name: "Primrose Hill Puzzle Club — Chess",
    provider: "Primrose Hill Puzzle Club",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and puzzle club teaching strategy, tactics and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://www.phcl.org/events/chess-club/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-17",
    name: "French Institute — Children's Programme",
    provider: "Institut français du Royaume-Uni",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Serving Belsize Park / NW3 (main institute in South Kensington)",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact French Institute for children's class fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "French language classes for children run by the official French government cultural institute. Builds genuine fluency through immersive, culturally rich teaching for all levels, from heritage speakers to complete beginners.",
    times: "Check institut-francais.org.uk for class schedule",
    website: "https://www.institut-francais.org.uk/french-courses/french-for-kids-and-teens/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-18",
    name: "Japanese Saturday School London",
    provider: "Japanese Saturday School London",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Japanese language and culture classes for children, suitable for heritage speakers maintaining their first language as well as those new to Japanese.",
    times: "Saturday mornings — check school for exact times",
    website: "http://www.thejapaneseschool.ltd.uk/londonhoshuko/top.html",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-19",
    name: "Primrose Hill Art Studio",
    provider: "Primrose Hill Art Studio",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes for children and young teens covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check studio for class times",
    website: "https://thenewschoolart.org/art-courses/art-courses-primrose-hill/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-20",
    name: "Printmakers Youth Workshop",
    provider: "Printmakers Youth Workshop",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact workshop for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Printmaking classes teaching techniques such as lino cut, screen print and monoprint. Builds technical skill and creative experimentation in a hands-on studio environment.",
    times: "Check workshop for current dates",
    website: "https://medwayfineprintmakers.co.uk/courses-access/printmaking-classes-for-young-people/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-21",
    name: "Belsize Ceramics Workshop",
    provider: "Belsize Ceramics Workshop",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check workshop for class times",
    website: "",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-22",
    name: "Primrose Hill Photography Collective",
    provider: "Primrose Hill Photography Collective",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact collective for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check collective for course dates",
    website: "",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-23",
    name: "Young Screenwriters Academy",
    provider: "Young Screenwriters Academy",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","drama","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Screenwriting and filmmaking classes for older children and teens — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers.",
    times: "Check academy for session schedule",
    website: "https://www.youngfilmacademy.co.uk/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-24",
    name: "Camden Podcast Studio — Youth Programme",
    provider: "Camden Podcast Studio",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill / Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Podcasting and audio production club teaching recording, editing and presenting skills. Young people produce their own podcast content, building confidence in communication and technical media skills.",
    times: "Check studio for session schedule",
    website: "https://www.roundhouse.org.uk/young-creatives-11-30/youth-programme/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-25",
    name: "Primrose Hill Nature Club",
    provider: "Primrose Hill Nature Club",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact organisers for details",
    age: "4–12",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature club exploring the iconic open green space of Primrose Hill. Seasonal nature crafts, wildlife spotting and outdoor games with stunning views across London.",
    times: "Check local listings for session dates",
    website: "https://volunteer.royalparks.org.uk/volunteers/opportunity/10173486",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-26",
    name: "Regent's Park — Ecology Explorers",
    provider: "The Royal Parks",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Regent's Park, NW1 4NR",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Free or low cost — contact Royal Parks for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings",
    description: "Nature and ecology exploration in one of London's most beautiful royal parks — boating lake, Queen Mary's Gardens, wildlife habitats and open meadows. Pond dipping, plant identification and seasonal wildlife study.",
    times: "Check royalparks.org.uk for family event dates",
    website: "https://www.field-studies-council.org/courses-and-experiences/families/eco-adventures/regents-park-eco-adventures/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-27",
    name: "Poetry and Performance Lab",
    provider: "Poetry and Performance Lab",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact lab for term fees",
    age: "8–18",
    interests: ["arts","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing and spoken word performance classes — poetry, storytelling and performance technique. Builds confidence in self-expression and public performance.",
    times: "Check lab for session schedule",
    website: "https://applesandsnakes.org/event/the-poets-performance-lab/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-28",
    name: "Young Curators Programme",
    provider: "Young Curators Programme",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact programme for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Museum studies and curation programme introducing young people to how exhibitions are designed and built. Visits to local museums and galleries alongside hands-on curatorial projects.",
    times: "Check programme for session schedule",
    website: "https://theshowroom.org/news/young-curators-2025",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-29",
    name: "Design Futures Studio",
    provider: "Design Futures Studio",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Belsize Park NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Design classes covering product, graphic and spatial design fundamentals. Combines creative and technical skills through hands-on projects, building confidence in the design process.",
    times: "Check studio for workshop dates",
    website: "https://designfuture.studio/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },
  {
    id: "pbt-30",
    name: "Youth Wellbeing and Mindfulness Workshops",
    provider: "Youth Wellbeing and Mindfulness Workshops",
    type: "Club",
    neighbourhood: "Primrose Hill & Belsize Park",
    location: "Primrose Hill NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact provider for term fees",
    age: "8–18",
    interests: ["sports"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Wellbeing and mindfulness sessions for children and teens — breathing techniques, gentle movement and reflection exercises supporting emotional regulation, focus and resilience.",
    times: "Check provider for session schedule",
    website: "https://www.mindinharrow.org.uk/young-people/headsup-training-workshops/wellbeing-workshops-for-young-people/",
    neighbourhoods: ["Primrose Hill & Belsize Park"],
  },


  // ── CAMDEN (term-time) ────────────────────────────────────────────────────────
  {
    id: "camt-1",
    name: "Camden Town Youth Rowing Project",
    provider: "Camden Town Youth Rowing Project",
    type: "Club",
    neighbourhood: "Camden",
    location: "Regent's Canal area, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact project for term fees",
    age: "10–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Rowing and water sports project using the Regent's Canal. Builds fitness, teamwork and technique for young people, with experienced coaches teaching safe water sports practice.",
    times: "Check project for session schedule",
    website: "https://thepiratecastle.org/about/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-2",
    name: "Somers Town Community Sports Centre — Juniors",
    provider: "Somers Town Community Sports Centre",
    type: "Club",
    neighbourhood: "Camden",
    location: "Somers Town Community Sports Centre, NW1 1RX",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Standard £85/week · Camden subsidised £65 · concession £40",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Term-time junior sports sessions — check Camden for schedule",
    description: "Community sports centre offering term-time junior sports sessions alongside its well-known holiday play scheme. Multi-sports, team games and physical activity for children from the local Somers Town community.",
    times: "Check camden.gov.uk for term-time programme",
    website: "https://www.somerstown.org.uk/how-we-can-help/youth/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-3",
    name: "Camden Skate Academy",
    provider: "Camden Skate Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Skateboarding classes teaching technique, balance and confidence on local skate spots. Beginners welcome, with structured progression for those wanting to develop tricks and style.",
    times: "Check academy for session schedule",
    website: "https://northlondonskateclub.com/cantelowes-skatepark",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-4",
    name: "Market Road Football Development Centre",
    provider: "Market Road Football Development Centre",
    type: "Club",
    neighbourhood: "Camden",
    location: "Market Road, N7 9PL",
    postcodeSector: "N7",
    distanceMiles: 0.9,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Football development centre with age-group coaching from grassroots through to more advanced levels. FA-aligned coaching structure building technical skills and match awareness.",
    times: "Check centre for training and match schedule",
    website: "https://www.uniteddragonsfc.com/market-road-league",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-5",
    name: "Talacre Community Sports Centre — Badminton Club",
    provider: "Better Leisure / Camden Council",
    type: "Club",
    neighbourhood: "Camden",
    location: "Talacre Community Sports Centre, Dalby Street, NW5 3AF",
    postcodeSector: "NW5",
    distanceMiles: 0.6,
    cost: "Contact centre for badminton club pricing · 30 free HAF places per holiday for FSM-eligible children",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Badminton coaching at Talacre Community Sports Centre, alongside table tennis, pickleball, mini tennis and the popular floodlit 3G astro pitch. Wheelchair accessible facility with a sensory dance studio also on site. Entrance via Talacre Park, not Dalby Street.",
    times: "Check better.org.uk for badminton club schedule",
    website: "https://www.better.org.uk/leisure-centre/london/camden/talacre-community-sports-centre",
    neighbourhoods: ["Camden"],
    phone: "020 3343 6020 · talacre@gll.org",
  },
  {
    id: "camt-6",
    name: "Roundhouse Youth Theatre — Centre 59",
    provider: "Roundhouse",
    type: "Club",
    neighbourhood: "Camden",
    location: "Roundhouse, Chalk Farm Road, NW1 8EH",
    postcodeSector: "NW1",
    distanceMiles: 1.0,
    cost: "Free or low cost — check roundhouse.org.uk for current programme",
    age: "11–25",
    interests: ["drama"],
    dayOfWeek: "Term-time weekly sessions, plus a 3-week summer programme",
    description: "Youth theatre company at the iconic Roundhouse, a Grade II listed former railway engine shed built in 1846. Centre 59 was launched with Daniel Kaluuya as the Roundhouse's first Associate Artistic Director, giving young people the chance to devise and perform original theatre work professionally, including productions performed as part of the Roundhouse Three Sixty Festival. Centre 59 Theatre Week runs across three weeks in August for 11–14s, 15–17s and 18–25s.",
    times: "Check roundhouse.org.uk/young-creatives-11-30 for current application windows",
    website: "https://www.roundhouse.org.uk/young-creatives-11-30/centre-59/",
    neighbourhoods: ["Camden"],
    phone: "020 7424 8455",
    notes: "Applications open periodically — sign up to the mailing list to be notified. Step-free access throughout via lift, accessible toilets on all floors. Chalk Farm tube 2 mins.",
  },
  {
    id: "camt-7",
    name: "Roundhouse Music Collective",
    provider: "Roundhouse",
    type: "Club",
    neighbourhood: "Camden",
    location: "Roundhouse, Chalk Farm Road, NW1 8EH",
    postcodeSector: "NW1",
    distanceMiles: 1.0,
    cost: "Many programmes free or subsidised · Creative Studios Membership from £5/month (13–25)",
    age: "11–18",
    interests: ["music","drama"],
    dayOfWeek: "Term-time weekly sessions — check roundhouse.org.uk",
    description: "Music-making collective for young people at the Roundhouse Studios, a state-of-the-art creative facility. Recording, performance and collaborative music-making for ages 11–18, feeding into the wider 11–30 youth creative programme including the Roundhouse Rising Festival.",
    times: "Check roundhouse.org.uk/young-creatives-11-30 for schedule",
    website: "https://www.roundhouse.org.uk/young-creatives-11-30/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-8",
    name: "Camden Music Production Academy",
    provider: "Camden Music Production Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["music","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Music production classes teaching recording, mixing and digital production skills. Builds technical confidence alongside creative songwriting and beat-making in a professional studio-style setting.",
    times: "Check academy for class schedule",
    website: "https://www.roundhouse.org.uk/young-creatives-11-30/youth-programme/music/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-9",
    name: "London Contemporary Voices — Youth Programme",
    provider: "London Contemporary Voices",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact for youth programme fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with choir",
    description: "Contemporary choir programme for young singers, building vocal technique and ensemble skills through a varied repertoire spanning classical to popular music. Regular performance opportunities.",
    times: "Check choir for rehearsal schedule",
    website: "https://www.londoncontemporaryvoices.co.uk/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-10",
    name: "Camden Writing Centre",
    provider: "Camden Writing Centre",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact centre for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing classes building narrative, poetry and storytelling skills. Encourages imaginative expression through guided exercises, workshops and peer feedback in a supportive group setting.",
    times: "Check centre for class schedule",
    website: "https://camdenartcentre.org/whats-on/near-and-far-creative-writing-anthology",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-11",
    name: "Somers Town Learning Hub — Maths",
    provider: "Somers Town Learning Hub",
    type: "Club",
    neighbourhood: "Camden",
    location: "Somers Town NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact hub for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local hub for session times",
    website: "https://www.somerstown.org.uk/how-we-can-help/youth/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-12",
    name: "Camden Academic Centre — English Tuition",
    provider: "Camden Academic Centre",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "https://preply.com/en/Camden-Town-UK/english-tutors",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-13",
    name: "Selective Futures Camden — 11+ Preparation",
    provider: "Selective Futures",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact for course pricing",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check provider for class schedule",
    website: "https://www.camdenfuturesinstitute.com/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-14",
    name: "Code Club Camden Labs",
    provider: "Code Club Camden",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact lab for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check lab for class schedule",
    website: "https://codeclub.org/news?after=YXJyYXljb25uZWN0aW9uOjQwMg%3D%3D",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-15",
    name: "Tech Makers Studio — Robotics",
    provider: "Tech Makers Studio",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check studio for class schedule",
    website: "https://tech-studio.co.uk/robotics-sr",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-16",
    name: "Camden Strategy Academy — Chess",
    provider: "Camden Strategy Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy club teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with academy for session times",
    website: "https://www.londonchessacademy.com/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-17",
    name: "Arabic Language and Culture School",
    provider: "Arabic Language and Culture School",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Arabic language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive setting.",
    times: "Check school for class schedule",
    website: "https://www.arabicclub.co.uk/our-classes/our-weekend-school/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-18",
    name: "Mandarin Youth Academy Camden",
    provider: "Mandarin Youth Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Mandarin Chinese language classes through games, songs, stories and conversation practice. Small groups progressing through structured levels for children of all ages and backgrounds.",
    times: "Check academy for class schedule",
    website: "https://www.camdenccc.co.uk/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-19",
    name: "Camden Arts Factory",
    provider: "Camden Arts Factory",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes for children covering drawing, painting and mixed media. Structured technical progression alongside creative expression, with regular opportunities to showcase work.",
    times: "Check studio for class times",
    website: "https://camdenartcentre.org/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-20",
    name: "Cartoon and Comics Club London — Camden",
    provider: "Cartoon and Comics Club London",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Comic and cartoon drawing classes teaching character design, sequential storytelling and illustration technique. Children create their own comic strips and characters in a fun, supportive setting.",
    times: "Check club for workshop dates",
    website: "",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-21",
    name: "Ceramics Lab Camden",
    provider: "Ceramics Lab",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact lab for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check lab for class times",
    website: "https://camdenartcentre.org/whats-on/ceramics-lab-technical-studio-management-from-clay-to-kiln",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-22",
    name: "Photo Stories Collective",
    provider: "Photo Stories Collective",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact collective for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check collective for course dates",
    website: "https://storiescollective.com/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-23",
    name: "Camden Documentary School",
    provider: "Camden Documentary School",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact school for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Documentary filmmaking classes for older children and teens — research, filming and editing real-world stories collaboratively. Builds journalistic and technical skills through group projects.",
    times: "Check school for session schedule",
    website: "https://www.afperformingarts.com/theatre-and-film-school/schools/camden",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-24",
    name: "Community Radio Youth Academy",
    provider: "Community Radio Youth Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Broadcasting and radio production classes teaching recording, editing and presenting skills. Young people produce their own radio content, building confidence in communication and technical media skills.",
    times: "Check academy for session schedule",
    website: "https://www.kicradio.co.uk/kicacademy/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-25",
    name: "London Wildlife Trust — Camley Street Natural Park",
    provider: "London Wildlife Trust",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camley Street Natural Park, Camley Street, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.8,
    cost: "Free entry · school visits bookable in advance",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Check wildlondon.org.uk for family session dates",
    description: "A secret 0.8-hectare wildlife garden right on Regent's Canal near St Pancras Lock, cared for by London Wildlife Trust since 1985. Wetlands, meadows and woodland habitats packed into a small urban space, with a visitor centre and special sessions for children. London Wildlife Trust also runs a wider Youth Programme with traineeships and a Youth Board for older teens.",
    times: "Check wildlondon.org.uk for opening times and family sessions",
    website: "https://www.wildlondon.org.uk/nature-reserves/camley-street-natural-park",
    neighbourhoods: ["Camden"],
    notes: "Founded 1981, London Wildlife Trust manages 36 nature reserves across Greater London. Camley Street sits right next to the King's Cross Central development.",
  },
  {
    id: "camt-26",
    name: "Regent's Canal Explorers",
    provider: "Regent's Canal Explorers",
    type: "Club",
    neighbourhood: "Camden",
    location: "Regent's Canal towpath, NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.6,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Nature and outdoor learning sessions along Regent's Canal, exploring urban wildlife, canal ecology and the historic waterway that runs through the heart of Camden. Wildlife spotting and waterside nature crafts.",
    times: "Check local listings for session dates",
    website: "https://canalrivertrust.org.uk/explorers",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-27",
    name: "Young Fashion Creators Studio",
    provider: "Young Fashion Creators Studio",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Fashion design classes teaching sketching, pattern basics and garment design fundamentals. Builds creative confidence and an eye for style in a hands-on, inspiring studio environment.",
    times: "Check studio for workshop dates",
    website: "https://www.youngfashiondesigneruk.com/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-28",
    name: "Camden Makers and Inventors Club",
    provider: "Camden Makers and Inventors Club",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["stem","arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Product design and invention club giving young people access to tools and materials for creative projects, from simple builds to more involved design work. Builds practical skills and creative problem-solving.",
    times: "Check club for session schedule",
    website: "https://www.inventorsandmakers.com/",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-29",
    name: "Youth Law and Citizenship Academy",
    provider: "Youth Law and Citizenship Academy",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact academy for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings",
    description: "Public affairs and citizenship classes introducing teens to law, government and civic engagement. Builds critical thinking and an understanding of how democratic institutions work through discussion and debate.",
    times: "Check academy for session schedule",
    website: "",
    neighbourhoods: ["Camden"],
  },
  {
    id: "camt-30",
    name: "Camden Young Journalists Network",
    provider: "Camden Young Journalists Network",
    type: "Club",
    neighbourhood: "Camden",
    location: "Camden NW1",
    postcodeSector: "NW1",
    distanceMiles: 0.5,
    cost: "Contact network for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Journalism and media writing club teaching news writing, interviewing and editorial skills. Young people produce their own articles and projects, building confidence in research and communication.",
    times: "Check network for session schedule",
    website: "https://www.camdennj.gov/cynn/",
    neighbourhoods: ["Camden"],
  },


  // ── KENTISH TOWN (term-time) ──────────────────────────────────────────────────
  {
    id: "ktt-1",
    name: "Kentish Town City Farm — Pony Club & Riding Programme",
    provider: "Kentish Town City Farm",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town City Farm, 1 Cressfield Close, NW5 4BN",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "Free farm entry · weekend pony rides low cost · afterschool Pony Club at affordable price",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend pony rides Sat–Sun · afterschool Pony Club term-time",
    description: "Founded 1972, the UK's very first City Farm, kickstarting the entire city farm movement. RDA (Riding for the Disabled Association) accredited horse programme, fully accessible riding arena with London skyline views. Pony Club for ages 8–15 hones riding and horse care skills. Three horses, two donkeys, sheep, goats and more on a four-acre site alongside two railway lines.",
    times: "Farm open 9am–5pm daily (free entry) · pony rides Sat–Sun · Pony Club term-time afterschool",
    website: "https://ktcityfarm.org.uk/riding-programme/",
    neighbourhoods: ["Kentish Town"],
    notes: "Afterschool Pony Club specifically supports local disadvantaged children and young people. Equine wellbeing sessions also offered for those with mental health challenges. Free parking on Grafton Road after 11am weekdays and all weekend.",
  },
  {
    id: "ktt-2",
    name: "Parliament Hill Athletics Development Squad",
    provider: "Highgate Harriers / Parliament Hill Track",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Parliament Hill Athletics Track, Hampstead Heath, NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.6,
    cost: "Contact for junior development squad fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings (track) · Saturday cross country",
    description: "Development squad training at the newly refurbished Parliament Hill athletics track on Hampstead Heath. Structured coaching across track and field disciplines for young athletes progressing through to competitive level.",
    times: "Check highgateharriers.org.uk for development squad schedule",
    website: "https://www.highgateharriers.org.uk/young-athletes",
    neighbourhoods: ["Kentish Town","Hampstead"],
  },
  {
    id: "ktt-3",
    name: "Kentish Town Community Table Tennis Project",
    provider: "Kentish Town Community Table Tennis Project",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact project for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community table tennis coaching for all abilities, building reflexes, focus and competitive spirit. Welcoming local club atmosphere for beginners through to keen young players.",
    times: "Check project for session times",
    website: "https://nw5project.com/clubs-and-schemes/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-4",
    name: "Gospel Oak Basketball Project",
    provider: "Gospel Oak Basketball Project",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Gospel Oak, NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.6,
    cost: "Contact project for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Community basketball coaching with structured sessions across age groups. Skills development, team play and competitive opportunities in a fun, inclusive environment near Gospel Oak.",
    times: "Check project for training times",
    website: "https://golondon.org.uk/about/funded-projects/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-5",
    name: "Kentish Town Karate Academy",
    provider: "Kentish Town Karate Academy",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Karate classes building discipline, fitness and self-defence skills through structured belt grading. Suitable for complete beginners through to advanced students.",
    times: "Check academy for class times",
    website: "https://kentishtownkarate.com/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-6",
    name: "Kentish Town Dance Collective",
    provider: "Kentish Town Dance Collective",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Termly fees · contact for pricing",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Dance classes for a wide age range, building confidence, technique and performance skills. Regular showcase opportunities and structured progression across styles.",
    times: "Check collective for class schedule",
    website: "https://harmsworthschoolofdance.uk/ballet-classes-kentish-town-north-london-ballet-school/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-7",
    name: "The People's Theatre — Youth Company",
    provider: "The People's Theatre",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.5,
    cost: "Contact theatre for youth company fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Community youth theatre company building performance skills, ensemble work and confidence. Regular production opportunities for young people in a supportive, inclusive setting.",
    times: "Check theatre for company schedule",
    website: "https://www.peoplestheatre.co.uk/young-people-s-theatre",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-8",
    name: "Kentish Town Rock School",
    provider: "Kentish Town Rock School",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact school for term fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Band and rock music classes teaching guitar, bass, drums, keys and vocals in a group band format. Builds musicianship and the thrill of playing together, with regular gig opportunities.",
    times: "Check school for session schedule",
    website: "https://www.londonguitaracademy.com/guitar-lessons-south-london",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-9",
    name: "SoundLab — Youth Recording Studio",
    provider: "SoundLab",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "10–18",
    interests: ["music","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Recording and music technology classes teaching production, mixing and engineering skills in a real studio environment. Builds technical confidence alongside creative songwriting.",
    times: "Check studio for class schedule",
    website: "https://www.soundlabstudios.co.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-10",
    name: "Kentish Town Learning Studio — Maths",
    provider: "Kentish Town Learning Studio",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact studio for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local studio for session times",
    website: "https://www.thelearning-studio.co.uk/hello",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-11",
    active: false, // marked not running in Oct 2026 review
    name: "English Excellence NW5",
    provider: "English Excellence",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-12",
    name: "Grammar Pathways Academy — 11+",
    provider: "Grammar Pathways Academy",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact academy for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check academy for class schedule",
    website: "https://www.academy11.co.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-13",
    name: "App Creators Club — Coding",
    provider: "App Creators Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding and app development classes covering Scratch, Python and basic app design depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check club for class schedule",
    website: "https://codecreators.club/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-14",
    name: "Electronics Discovery Lab — Robotics",
    provider: "Electronics Discovery Lab",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact lab for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on electronics and robotics classes using building kits to teach circuit basics, mechanical design and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check lab for class schedule",
    website: "https://www.discoverylab.org/News/posts/discovery-lab-workshop-lets-kids-break-apart-old-technology-and-turn-it-into-something-new",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-15",
    name: "Kentish Town Games Club — Chess",
    provider: "Kentish Town Games Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy games club teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://www.townspot.co/event/chess-club-83f5bf17-fd61-4825-8715-0c3a5990a609",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-16",
    name: "Korean Language School North London",
    provider: "Korean Language School",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Korean language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency.",
    times: "Saturday mornings — check school for exact times",
    website: "",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-17",
    name: "Latin Language and Culture Club",
    provider: "Latin Language and Culture Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Latin language classes exploring grammar, vocabulary and classical history and mythology. Builds analytical thinking and a strong foundation for further language study.",
    times: "Check club for class schedule",
    website: "https://www.aclandburghley.camden.sch.uk/Curriculum/Classics-Latin/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-18",
    name: "Kentish Town Community Arts Workshop",
    provider: "Kentish Town Community Arts Workshop",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact workshop for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art and media classes covering drawing, painting and basic digital media for children and young teens. Builds creative confidence and technical skill in a friendly community setting.",
    times: "Check workshop for class times",
    website: "https://www.ktcc.org.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-19",
    name: "Young Illustrators Studio",
    provider: "Young Illustrators Studio",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "8–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Illustration classes teaching character design, narrative drawing and visual storytelling. Builds technical skill and creative confidence for young artists.",
    times: "Check studio for class times",
    website: "https://www.youngillustratorsstudio.com/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-20",
    name: "Clay and Sculpture Lab",
    provider: "Clay and Sculpture Lab",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact lab for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and sculpture classes teaching hand-building, wheel-throwing basics and three-dimensional design. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check lab for class times",
    website: "https://www.houseofclay.co.uk/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-21",
    name: "Urban Photography Collective — Kentish Town",
    provider: "Urban Photography Collective",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact collective for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Hands-on sessions building a creative eye and technical understanding using digital cameras.",
    times: "Check collective for course dates",
    website: "https://rps.org/regions/london/london-urban/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-22",
    name: "Mobile Film Makers Club",
    provider: "Mobile Film Makers Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Filmmaking classes using smartphones and accessible equipment — scripting, shooting and editing short films collaboratively. Builds technical and storytelling skills working with peers.",
    times: "Check club for session schedule",
    website: "https://sparksarts.co.uk/about-us/film-school-locations/islington-finsbury-park/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-23",
    name: "Ecology Explorers Kentish Town",
    provider: "Ecology Explorers Kentish Town",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Outdoor nature and ecology club exploring the green spaces of Kentish Town, near Hampstead Heath and the wildlife pond at Kentish Town City Farm. Wildlife spotting, seasonal nature crafts and outdoor learning.",
    times: "Check local listings for session dates",
    website: "https://ktcityfarm.org.uk/under-5s-please-bring-your-grown-ups-to-young-explorers-nature-club/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-24",
    name: "Camley Street Natural Park — Youth Nature Programme",
    provider: "London Wildlife Trust",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Camley Street Natural Park, 12 Camley Street, N1C 4PW",
    postcodeSector: "N1C",
    distanceMiles: 1.0,
    cost: "FREE entry · most family events free · some themed workshops £10/family",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Check wildlondon.org.uk for current family session dates",
    description: "London Wildlife Trust's flagship reserve — a 0.8-hectare urban nature oasis on the banks of Regent's Canal between King's Cross and St Pancras, created from a former coal yard in 1985 and rebuilt with a new visitor and learning centre in 2022. Pond dipping, bug hunting, wildlife crafts and guided walks across wetland, meadow and woodland habitats, home to kingfishers, herons and rare fungi. Visitor centre is the base for the Wildlife Watch Club. SENsory Explorers sessions for families with additional needs.",
    times: "Mon–Sun 10am–5pm (4pm in winter) · free entry, events check website",
    website: "https://www.wildlondon.org.uk/nature-reserves/camley-street-natural-park",
    neighbourhoods: ["Kentish Town","Camden"],
    phone: "020 7833 2311",
    notes: "Floating Viewpoint platform offers unique views of the canal. Fully accessible visitor centre. King's Cross St Pancras tube/rail a short walk.",
  },
  {
    id: "ktt-25",
    name: "Young Historians of Camden — Archaeology",
    provider: "Young Historians of Camden",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact for term fees",
    age: "8–16",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Archaeology and local history club exploring Camden's rich past through hands-on activities, artefact handling and site visits. Builds research skills and historical curiosity.",
    times: "Check club for workshop dates",
    website: "https://www.camdenhistorysociety.org/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-26",
    name: "Creative Writing Circle NW5",
    provider: "Creative Writing Circle NW5",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact circle for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing classes building narrative, poetry and storytelling skills. Encourages imaginative expression through guided exercises, workshops and peer feedback.",
    times: "Check circle for class schedule",
    website: "https://torriano.org/whats-on/meeting/nw-writers-group",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-27",
    name: "Podcast Producers' Academy",
    provider: "Podcast Producers' Academy",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Podcasting classes teaching recording, editing and presenting skills. Young people produce their own audio content, building confidence in communication and technical media production.",
    times: "Check academy for session schedule",
    website: "https://thepodcastguys.co.uk/services/full-service-podcast-production-agency/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-28",
    name: "Young Architects and Engineers Club",
    provider: "Young Architects and Engineers Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and engineering club introducing children to building design, model-making and structural thinking. Combines creative and technical skills through hands-on projects.",
    times: "Check club for workshop dates",
    website: "https://www.soane.org/whats-on/young-architects-club",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-29",
    name: "Sustainable Futures Youth Club",
    provider: "Sustainable Futures Youth Club",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Environmental and sustainability club exploring climate science, conservation projects and practical green skills. Builds awareness and real-world action around sustainability for engaged teens.",
    times: "Check club for session schedule",
    website: "https://nya.org.uk/young-future-hubs/",
    neighbourhoods: ["Kentish Town"],
  },
  {
    id: "ktt-30",
    name: "Culinary Skills for Kids — Kentish Town",
    provider: "Culinary Skills for Kids",
    type: "Club",
    neighbourhood: "Kentish Town",
    location: "Kentish Town NW5",
    postcodeSector: "NW5",
    distanceMiles: 0.4,
    cost: "Contact for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Cooking classes teaching practical kitchen skills, food hygiene and simple recipes for children and young teens. Builds independence and a love of cooking in a fun, hands-on environment.",
    times: "Check provider for class schedule",
    website: "https://classbento.co.uk/royal-cakes-baking-club",
    neighbourhoods: ["Kentish Town"],
  },


  // ── SWISS COTTAGE (term-time) ─────────────────────────────────────────────────
  {
    id: "sct-1",
    name: "Swiss Cottage Leisure Centre — Dive Academy",
    provider: "Better Leisure (Swiss Cottage)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage Leisure Centre, 4a Adelaide Road, NW3 3NF",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact Better Leisure for dive academy pricing",
    age: "8–18",
    interests: ["swimming","sports"],
    dayOfWeek: "Weekday evenings & weekend mornings",
    description: "Diving and water skills academy at Swiss Cottage Leisure Centre, using its two pools (25m main and 20m training pool). Structured progression from beginner water skills through to recreational diving, building confidence and technique in a purpose-built aquatic facility.",
    times: "Check better.org.uk for dive academy class times",
    website: "https://better.org.uk/leisure-centre/london/camden/swiss-cottage-leisure-centre",
    neighbourhoods: ["Swiss Cottage"],
    phone: "Contact centre directly",
  },
  {
    id: "sct-2",
    name: "Swiss Cottage Leisure Centre — Badminton Academy",
    provider: "Better Leisure (Swiss Cottage)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage Leisure Centre, 4a Adelaide Road, NW3 3NF",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact Better Leisure for badminton academy pricing",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Badminton coaching academy at Swiss Cottage Leisure Centre's 4-court sports hall. Structured coaching for all abilities from beginners through to competitive juniors, building technique, speed and footwork.",
    times: "Check better.org.uk for badminton academy class times",
    website: "https://www.better.org.uk/leisure-centre/london/camden/swiss-cottage-leisure-centre/holiday-activities",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-3",
    name: "Hampstead Cricket Club — Junior Development",
    provider: "Hampstead Cricket Club",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Lymington Road, West Hampstead, NW6 1HY",
    postcodeSector: "NW3",
    distanceMiles: 0.6,
    cost: "Junior membership · ECB All Stars (5–8) and Dynamos (8–11) programmes",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · weekend matches",
    description: "Junior development programme at one of London's oldest cricket clubs (Est. 1866). ECB-accredited coaching for all ages and abilities, with strong girls' cricket support and progression from All Stars through to club colts teams.",
    times: "Fri evening training · weekend match fixtures",
    website: "https://www.southhampsteadcc.org.uk/",
    neighbourhoods: ["Swiss Cottage","Hampstead"],
  },
  {
    id: "sct-4",
    name: "Camden Judo Development Squad",
    provider: "Camden Judo",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Judo development squad building discipline, fitness and self-defence skills through structured belt grading. Suitable for complete beginners through to competitive judoka.",
    times: "Check club for class times",
    website: "https://www.mikesjudoclub.co.uk/new-page",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-5",
    name: "Swiss Cottage Fencing Academy",
    provider: "Swiss Cottage Fencing Academy",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees · equipment hire often available for beginners",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Fencing classes teaching foil, épée or sabre technique in a safe, structured environment. Builds quick thinking, precision and sportsmanship for beginners through to competitive fencers.",
    times: "Check academy for class schedule",
    website: "https://www.zfw-fencing.co.uk/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-6",
    name: "South Hampstead High School — Dance Programme",
    provider: "South Hampstead High School (GDST)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "South Hampstead High School, NW3 5SS",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact school for community dance programme fees",
    age: "5–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Ballet and modern dance programme using South Hampstead High School's excellent dance facilities. Open to the wider community alongside the school's own arts provision. A leading GDST independent school with a particularly strong performing arts curriculum.",
    times: "Check school for community programme schedule",
    website: "https://www.shhs.gdst.net/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-7",
    name: "Hampstead Fine Arts College — Youth Theatre",
    provider: "Hampstead Fine Arts College",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Hampstead Fine Arts College, NW3 6NP",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact college for youth theatre fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Drama and performing arts youth theatre at North London's leading independent arts sixth form. Professional studio facilities and experienced arts educators, with a strong emphasis on craft and creative development.",
    times: "Check hfac.co.uk for youth theatre schedule",
    website: "https://www.hampsteadfinearts.com/about/news-events/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-8",
    name: "North London Chamber Orchestra — Youth Programme",
    provider: "North London Chamber Orchestra",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact orchestra for youth programme fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with orchestra",
    description: "Youth orchestral programme offering ensemble playing experience for young musicians. Builds musicianship, listening skills and the joy of playing together, with regular concert opportunities.",
    times: "Check orchestra for rehearsal schedule",
    website: "https://northlondonyouthorchestra.org/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-9",
    name: "South Hampstead Music Academy",
    provider: "South Hampstead Music Academy",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact academy for instrumental lesson fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens. Structured progression with options for graded music exams, plus ensemble and group playing opportunities.",
    times: "Check academy for lesson schedule",
    website: "https://www.shhs.gdst.net/senior-school/beyond-the-classroom/the-arts/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-10",
    name: "Swiss Cottage Study Centre — Maths",
    provider: "Swiss Cottage Study Centre",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.kumon.co.uk/swiss-cottage",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-11",
    name: "Academic Success NW3 — English Tuition",
    provider: "Academic Success",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local centre for session times",
    website: "https://academicsuccess.uk/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-12",
    name: "North London Selective School Hub — 11+",
    provider: "North London Selective School Hub",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact hub for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check hub for class schedule",
    website: "https://www.leadingtuition.co.uk/11-plus/north-london/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-13",
    name: "Computer Explorers Swiss Cottage — Coding",
    provider: "ComputerXplorers / Computer Explorers",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact for term fees",
    age: "7–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills.",
    times: "Check provider for class schedule",
    website: "https://computerxplorers.co.uk/classes/holiday-camps",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-14",
    name: "STEM Discovery Club NW3 — Robotics",
    provider: "STEM Discovery Club",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Hands-on robotics and engineering classes using building kits to teach mechanical design, basic coding and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check club for class schedule",
    website: "https://stemdiscoveryacademy.org/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-15",
    name: "Swiss Cottage Chess Academy",
    provider: "Swiss Cottage Chess Academy",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact academy for membership fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess academy teaching strategy, tactics and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with academy for session times",
    website: "https://wallacechess.com/activities/weekly-chess-clubs/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-16",
    name: "Alliance Française — Youth French Classes",
    provider: "Alliance Française London",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact Alliance Française for youth class fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Official French cultural institute offering structured, high-quality French language classes for children. Builds genuine fluency through immersive, culturally rich teaching for all levels, from complete beginners to near-native speakers.",
    times: "Check alliancefrancaise.org.uk for class schedule",
    website: "https://alliancefrancaise.london/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-17",
    name: "North London Russian School",
    provider: "North London Russian School",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Russian language, literature and culture classes for children. Suitable for both heritage speakers maintaining their first language and those new to Russian. Includes cultural programmes alongside language learning.",
    times: "Saturday mornings — check school for exact times",
    website: "https://www.znaniye.com/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-18",
    name: "Camden Arts Centre — Youth Studio",
    provider: "Camden Arts Centre",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Camden Arts Centre, Arkwright Road, NW3 6DG",
    postcodeSector: "NW3",
    distanceMiles: 0.8,
    cost: "Contact CAC for youth studio fees · gallery admission always free",
    age: "5–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Contemporary art classes and media workshops at Camden Arts Centre, a free contemporary art gallery in a Grade II listed Victorian building (former Hampstead Central Library, 1897). Short energetic courses by professional artists for ages 5–11, plus youth media and design workshops for older students.",
    times: "Wed–Sun 11am–6pm · check camdenartcentre.org for youth programme schedule",
    website: "https://camdenartcentre.org/",
    neighbourhoods: ["Swiss Cottage","Primrose Hill & Belsize Park"],
    phone: "020 7472 5500",
    notes: "Free gallery admission. Tranquil garden and café. Step-free access. Finchley Road & Frognal Overground station 0.1 mile.",
  },
  {
    id: "sct-19",
    name: "Print and Design Lab NW3 — Graphic Design",
    provider: "Print and Design Lab",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact lab for term fees",
    age: "8–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Graphic design classes introducing children to design software, typography and visual communication. Builds technical confidence alongside creative expression for a digital age.",
    times: "Check lab for workshop dates",
    website: "",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-20",
    name: "Swiss Cottage Ceramics Workshop",
    provider: "Swiss Cottage Ceramics Workshop",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check workshop for class times",
    website: "https://ceramicssculpturestudio.com/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-21",
    name: "JW3 Youth Photography Programme",
    provider: "JW3",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "JW3, 341–351 Finchley Road, NW3 6ET",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Contact JW3 for programme fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens at JW3, a vibrant Jewish cultural centre on Finchley Road open to all. Composition, lighting and editing sessions building a creative eye and technical understanding.",
    times: "Check jw3.org.uk for youth programme schedule",
    website: "https://www.jw3.org.uk/course",
    neighbourhoods: ["Swiss Cottage"],
    phone: "020 7433 8988",
    notes: "JW3 is open to everyone regardless of background — also home to Rising Stars holiday camps.",
  },
  {
    id: "sct-22",
    name: "Young Film Critics Academy — Swiss Cottage",
    provider: "Young Film Critics Academy",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Film studies and criticism club for older children and teens — analysing films, writing reviews and understanding cinematic language. Builds critical thinking, media literacy and writing skills.",
    times: "Check academy for session schedule",
    website: "https://www.youngfilmacademy.co.uk/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-23",
    name: "British Library — Family Learning & Young Readers",
    provider: "British Library",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "British Library, 96 Euston Road, NW1 2DB",
    postcodeSector: "NW1",
    distanceMiles: 1.5,
    cost: "FREE — all British Library family events are free",
    age: "8–18",
    interests: ["arts","stem"],
    dayOfWeek: "Last Sunday of each month (Family Station) · school holiday events",
    description: "The national library of the UK reaches over 20,000 young learners a year through its learning programme. Free Family Station (drop-in, last Sunday of each month, 11am–3pm) includes creative resources, sensory items, a pop-up reading corner with braille and tactile editions, and art materials. Holds Harry Potter manuscript drafts, the Magna Carta, Lindisfarne Gospels and more — curriculum-linked workshops for schools also available.",
    times: "Mon–Thu 9:30am–8pm · Fri 9:30am–6:30pm · Sat 9:30am–5pm · Sun 11am–5pm",
    website: "https://www.bl.uk/hubs/family",
    neighbourhoods: ["Swiss Cottage","Camden"],
    notes: "All floors step-free, accessible toilets on all floors, manual wheelchairs free to borrow. Terrace restaurant and cafés on site. King's Cross St Pancras tube/rail station 5 mins.",
  },
  {
    id: "sct-24",
    name: "Swiss Cottage Library — Makers Club",
    provider: "Camden Libraries (Swiss Cottage Library)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage Library, 88 Avenue Road, NW3 3HA",
    postcodeSector: "NW3",
    distanceMiles: 0.3,
    cost: "Free or low cost · Camden Council",
    age: "7–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekday afternoons or weekends — check library",
    description: "Technology and making club at Swiss Cottage Library, one of Camden's busiest and best-equipped public libraries. Coding, STEM challenges, Lego Club and digital making activities during term time. Part of Camden's wider libraries learning programme.",
    times: "Mon–Thu 9am–8pm · Fri 9am–6pm · Sat 9am–5pm · Sun 11am–5pm",
    website: "https://www.camden.gov.uk/swiss-cottage-library",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Swiss Cottage tube station adjacent. Also runs Summer Reading Challenge and regular story times.",
  },
  {
    id: "sct-25",
    name: "London Centre for Book Arts — Youth Workshops",
    provider: "London Centre for Book Arts (LCBA)",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "London Centre for Book Arts, Unit 18 Ground Floor, 134 Harvard Road, N1 9PD",
    postcodeSector: "N1",
    distanceMiles: 3.5,
    cost: "Contact LCBA for youth workshop fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Independent publisher and open studio space offering hands-on workshops in traditional printing, letterpress, bookbinding, screen printing and zine-making. A unique creative space in Hackney Wick accessible to families from Swiss Cottage/NW3 looking for specialist arts provision.",
    times: "Check londoncentreforbookarts.org for workshop dates",
    website: "https://londonbookarts.org/collections/workshops",
    neighbourhoods: ["Swiss Cottage"],
    notes: "Located in Hackney Wick (N1 9PD) — a journey from Swiss Cottage but genuinely unique provision not easily found locally.",
  },
  {
    id: "sct-26",
    name: "Primrose Hill Science Forum — Swiss Cottage",
    provider: "Primrose Hill Science Forum",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact forum for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Science enrichment classes covering hands-on experiments, scientific thinking and engineering challenges. Builds curiosity and practical understanding through interactive, age-appropriate sessions.",
    times: "Check forum for session schedule",
    website: "https://www.phcl.org/events/chess-club/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-27",
    name: "Swiss Cottage Debate Society",
    provider: "Swiss Cottage Debate Society",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact society for term fees",
    age: "8–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Debate and public speaking classes building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice opportunities for a wide age range.",
    times: "Check society for session schedule",
    website: "https://www.debate.london/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-28",
    name: "Youth Investment and Enterprise Club",
    provider: "Youth Investment and Enterprise Club",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Finance and entrepreneurship club introducing teens to investing concepts, financial literacy and business planning. Builds practical financial confidence and understanding through project-based learning.",
    times: "Check club for session schedule",
    website: "https://ultra.education/young-investors-club/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-29",
    name: "Urban Design Studio NW3 — Architecture",
    provider: "Urban Design Studio",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage NW3",
    postcodeSector: "NW3",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and urban design club introducing children to building design, city planning and model-making. Combines creative and technical skills through hands-on projects inspired by London's built environment.",
    times: "Check studio for workshop dates",
    website: "https://www.artdesignhive.com/",
    neighbourhoods: ["Swiss Cottage"],
  },
  {
    id: "sct-30",
    name: "Community Volunteering Network — Youth Programme",
    provider: "Community Volunteering Network",
    type: "Club",
    neighbourhood: "Swiss Cottage",
    location: "Swiss Cottage / wider NW area",
    postcodeSector: "NW3",
    distanceMiles: 0.5,
    cost: "Free",
    age: "11–18",
    interests: ["outdoors"],
    dayOfWeek: "Weekends (community volunteering projects)",
    description: "Leadership and community volunteering programme connecting young people with local causes. Builds civic engagement, teamwork and leadership skills through practical volunteering projects across the local area.",
    times: "Check programme for volunteering opportunities",
    website: "https://londonyouth.org/what-we-do/",
    neighbourhoods: ["Swiss Cottage"],
  },


  // ── ST JOHN'S WOOD (term-time) ────────────────────────────────────────────────
  {
    id: "sjwt-1",
    name: "Lord's Cricket Ground — Junior Cricket Academy",
    provider: "Lord's Cricket Ground (MCC / Indoor Cricket Centre)",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "Lord's Cricket Ground, St John's Wood Road, NW8 8QN",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Term-time coaching varies by programme · contact Lord's Indoor Cricket Centre",
    age: "5–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Year-round junior cricket coaching at the Home of Cricket. The Lord's Indoor Cricket Centre runs term-time junior development programmes alongside the popular holiday courses, covering batting, bowling and fielding skills. February Half Term 2026: 16–19 February, ages 6–13. New Batting & Bowling Masterclasses for ages 13–18. All sessions delivered by MCC-accredited coaches.",
    times: "Check lords.org/indoor-cricket-centre for term-time programme",
    website: "https://www.lords.org/cricketcoaching",
    neighbourhoods: ["St John's Wood"],
    phone: "020 7616 8500 · indoorcricketcentre@lords.org",
    notes: "St John's Wood tube station 5 minutes. Iconic venue — one of the world's most famous sporting grounds.",
  },
  {
    id: "sjwt-2",
    name: "Regent's Park Baseball & Softball — Youth Programme",
    provider: "Regent's Park Baseball & Softball",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "Regent's Park, NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.6,
    cost: "Contact programme for term fees",
    age: "7–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Baseball and softball coaching for young people using the open spaces of Regent's Park. Builds batting, pitching and fielding skills in a fun, team-focused environment, with one of London's most beautiful royal parks as the setting.",
    times: "Check programme for session schedule",
    website: "https://sportsandleisure.royalparks.org.uk/the-pitches/softball",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-3",
    name: "St John's Wood Water Polo Club",
    provider: "St John's Wood Water Polo Club",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["swimming","sports"],
    dayOfWeek: "Weekday evenings",
    description: "Water polo and water sports coaching for young people. Builds swimming fitness, teamwork and ball-handling skills in an exciting, competitive team sport.",
    times: "Check club for training schedule",
    website: "https://www.nlwaterpolo.co.uk/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-4",
    name: "Jubilee Hall Sports Academy — NW8",
    provider: "Jubilee Hall Sports Academy",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Multi-sport coaching programme building fundamental movement skills and specific sports techniques across a range of activities. Welcoming, structured environment for beginners through to more advanced young athletes.",
    times: "Check academy for class schedule",
    website: "https://www.everyoneactive.com/centre/jubilee-community-leisure-centre/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-5",
    name: "St John's Wood Taekwondo Academy",
    provider: "St John's Wood Taekwondo Academy",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings",
    description: "Taekwondo classes building discipline, fitness and self-defence skills through structured belt grading. Suitable for complete beginners through to advanced practitioners.",
    times: "Check academy for class times",
    website: "https://meijimartialarts.co.uk/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-6",
    name: "Sylvia Young Theatre School — Associates",
    provider: "Sylvia Young Theatre School",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "Sylvia Young Theatre School, NW8 area",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "Contact school for associate programme fees",
    age: "7–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Saturdays (typical associate format)",
    description: "Saturday associate programme at one of the UK's most prestigious theatre schools, famous alumni including Emma Bunton, Billie Piper and Amy Winehouse. Weekly performing arts training in drama, singing and dance for talented young people alongside their regular schooling.",
    times: "Check sylviayoung.co.uk for associate programme details",
    website: "https://www.syts.co.uk/agency",
    neighbourhoods: ["St John's Wood"],
    notes: "Part-time Associate Programme is separate from the full-time vocational training. Contact school directly for current Associate availability.",
  },
  {
    id: "sjwt-7",
    name: "American School in London — Performing Arts Programme",
    provider: "American School in London (ASL)",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "American School in London, 1 Waverley Place, NW8 0NP",
    postcodeSector: "NW8",
    distanceMiles: 0.3,
    cost: "Contact ASL for community programme fees",
    age: "5–18",
    interests: ["drama","dance","music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Performing arts programme at the American School in London using its outstanding facilities — theatres, rehearsal studios and music practice rooms across a 14-acre campus. Drama, dance and music open to wider community participants alongside school provision.",
    times: "Check asl.org for community programme details",
    website: "https://www.asl.org/academics/high-school/curriculum/performing-arts",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-8",
    name: "St John's Wood Youth Orchestra",
    provider: "St John's Wood Youth Orchestra",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact orchestra for membership fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekly rehearsal — check with orchestra",
    description: "Youth orchestra offering ensemble playing experience for young musicians of varying levels. Builds musicianship, listening skills and the joy of playing together, with regular concert opportunities.",
    times: "Check orchestra for rehearsal schedule",
    website: "https://www.osj.org.uk/young-artists-scheme/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-9",
    name: "Suzuki Music School — NW8",
    provider: "Suzuki Music School",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact school for lesson fees",
    age: "3–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental music lessons using the internationally recognised Suzuki method — a natural, nurturing approach to learning music through listening and positive reinforcement, suitable from age 3. Parent involvement encouraged in younger children's lessons.",
    times: "Check school for lesson schedule",
    website: "https://www.suzukihub.com/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-10",
    name: "Abbey Road Young Producers Programme",
    provider: "Abbey Road Studios (Universal Music Group)",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "Abbey Road Studios, 3 Abbey Road, NW8 9AY",
    postcodeSector: "NW8",
    distanceMiles: 0.2,
    cost: "Contact Abbey Road for youth programme details",
    age: "10–18",
    interests: ["music","stem"],
    dayOfWeek: "Check Abbey Road for programme dates",
    description: "Music production and recording workshops at the world-famous Abbey Road Studios, where The Beatles, Pink Floyd and countless iconic artists have recorded. Abbey Road Institute and education partners run youth programmes introducing young people to professional studio production, engineering and music technology. A genuinely once-in-a-lifetime venue for music-loving teens.",
    times: "Check abbeyroad.com for youth education programme dates",
    website: "https://www.abbeyroad.com/",
    neighbourhoods: ["St John's Wood"],
    phone: "020 7266 7000",
    notes: "Abbey Road is one of the world's most famous recording studios — opened 1931. The zebra crossing outside is a global landmark. Youth education programmes run periodically.",
  },
  {
    id: "sjwt-11",
    name: "St John's Wood Academic Centre — Maths",
    provider: "St John's Wood Academic Centre",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact centre for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local centre for session times",
    website: "https://www.firstclasslearning.co.uk/centre/st-johns-wood/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-12",
    name: "Westminster Learning Studio — English Tuition",
    provider: "Westminster Learning Studio",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact studio for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local studio for session times",
    website: "https://www.westminstertutors.co.uk/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-13",
    name: "Independent School Preparation Hub — 11+",
    provider: "Independent School Preparation Hub",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact hub for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements — well-suited to the highly competitive NW8 independent school catchment.",
    times: "Check hub for class schedule",
    website: "https://prep4all.co.uk/11-plus-iseb-hub",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-14",
    name: "Python Coders Academy — St John's Wood",
    provider: "Python Coders Academy",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "8–16",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes with a Python focus, covering text-based programming, game design and basic web development. Builds strong computational thinking and coding fluency for motivated young learners.",
    times: "Check academy for class schedule",
    website: "https://www.minideveloper.co.uk/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-15",
    name: "Future Technology Lab — Robotics & AI",
    provider: "Future Technology Lab",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact lab for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Robotics and introductory AI classes using hands-on kits and coding platforms. Teaches mechanical design, basic machine learning concepts and problem-solving through structured, age-appropriate projects.",
    times: "Check lab for class schedule",
    website: "",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-16",
    name: "St John's Wood Strategy Society — Chess",
    provider: "St John's Wood Strategy Society",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact society for membership fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy society teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with society for session times",
    website: "https://stjohnswaterloo.org/event/games-and-chess-club/2026-01-20/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-17",
    name: "International Children's Language School — German",
    provider: "International Children's Language School",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "German language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Check school for class schedule",
    website: "https://www.dslondon.org.uk/en",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-18",
    name: "Japanese Cultural School London — NW8",
    provider: "Japanese Cultural School London",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Saturdays",
    description: "Japanese language and culture classes for children. Suitable for both heritage speakers and those new to Japanese, covering language, writing systems and cultural traditions.",
    times: "Saturday mornings — check school for exact times",
    website: "https://www.thejapaneseschool.com/welcome",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-19",
    name: "St John's Wood Art Atelier",
    provider: "St John's Wood Art Atelier",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact atelier for term fees",
    age: "5–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Art classes covering drawing, painting and mixed media for children and young teens. Structured technical progression alongside creative expression in a dedicated studio environment.",
    times: "Check atelier for class times",
    website: "",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-20",
    name: "Young Fashion Illustration Studio",
    provider: "Young Fashion Illustration Studio",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Fashion illustration and design classes teaching sketching, figure drawing and garment illustration. Builds creative confidence and an eye for design and style.",
    times: "Check studio for workshop dates",
    website: "https://fashiontextilemuseum.org/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-21",
    name: "Creative Ceramics Academy — NW8",
    provider: "Creative Ceramics Academy",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check academy for class times",
    website: "https://www.waes.ac.uk/courses/cat/ccu/ceramics/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-22",
    name: "Abbey Road Photography Collective",
    provider: "Abbey Road Photography Collective",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact collective for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Photography workshops for older children and teens covering composition, lighting and editing basics. Taking inspiration from the world-famous Abbey Road zebra crossing and surrounding iconic streets of NW8.",
    times: "Check collective for course dates",
    website: "https://abbeyroadmusicphotographyawards.com/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-23",
    name: "Young Music Video Creators",
    provider: "Young Music Video Creators",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts","stem","music"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Music video production club combining filmmaking and music skills — conceptualising, directing, shooting and editing short music videos. A unique blend of visual storytelling and music appreciation.",
    times: "Check club for session schedule",
    website: "",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-24",
    name: "Regent's Canal Exploration Club",
    provider: "Regent's Canal Exploration Club",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "Regent's Canal towpath, NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.5,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Nature and outdoor exploration along Regent's Canal through St John's Wood and Little Venice. Canal wildlife, waterway history and outdoor crafts along one of London's most picturesque stretches of historic waterway.",
    times: "Check local listings for session dates",
    website: "https://thepiratecastle.org/about/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-25",
    name: "St John's Wood Literary Society — Youth Programme",
    provider: "St John's Wood Literary Society",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact society for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing and literary appreciation club building narrative, poetry and storytelling skills. Encourages imaginative expression through guided writing exercises, reading discussion and peer feedback.",
    times: "Check society for class schedule",
    website: "https://www.westminster.gov.uk/leisure-libraries-and-community/libraries/st-johns-wood-library/events",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-26",
    name: "Junior Broadcasters Network",
    provider: "Junior Broadcasters Network",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact network for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Broadcasting and presenting skills club teaching recording, editing and on-camera performance. Young people produce their own audio-visual content, building confidence and technical media skills.",
    times: "Check network for session schedule",
    website: "https://www.funkidslive.com/",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-27",
    name: "Young Diplomats Academy",
    provider: "Young Diplomats Academy",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Public speaking, negotiation and international affairs club introducing teens to diplomacy, debate and global issues. Builds confident public presentation and critical reasoning skills.",
    times: "Check academy for session schedule",
    website: "https://www.youngdiplomat.org/academy",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-28",
    name: "Future Investors Club",
    provider: "Future Investors Club",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact club for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Investing and economics club introducing teens to financial markets, investment concepts and economic thinking. Builds practical financial literacy and analytical skills through real-world project-based learning.",
    times: "Check club for session schedule",
    website: "https://www.linkedin.com/company/future-investor-clubs-of-america-inc.",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-29",
    name: "Architecture and Interior Design Studio",
    provider: "Architecture and Interior Design Studio",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Contact studio for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and interior design club introducing children to spatial design, model-making and creative technical drawing. Combines creative and analytical skills through hands-on projects inspired by London's built environment.",
    times: "Check studio for workshop dates",
    website: "",
    neighbourhoods: ["St John's Wood"],
  },
  {
    id: "sjwt-30",
    name: "Community Action Leaders Programme",
    provider: "Community Action Leaders Programme",
    type: "Club",
    neighbourhood: "St John's Wood",
    location: "St John's Wood NW8",
    postcodeSector: "NW8",
    distanceMiles: 0.4,
    cost: "Free",
    age: "11–18",
    interests: ["outdoors"],
    dayOfWeek: "Weekends (community projects)",
    description: "Community volunteering and leadership programme connecting young people with local causes in St John's Wood and the wider NW8 area. Builds civic engagement, teamwork and leadership skills through practical volunteering projects.",
    times: "Check programme for volunteering opportunities",
    website: "https://www.edenprojectcommunities.com/events/community-action-programme",
    neighbourhoods: ["St John's Wood"],
  },


  // ── CANONBURY (term-time) ─────────────────────────────────────────────────────
  {
    id: "cnt-1",
    name: "De Beauvoir Tennis Club — Juniors",
    provider: "De Beauvoir Tennis Club",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "De Beauvoir Tennis Club, N1 5SR",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Member and non-member rates available",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Weekday evenings & weekend mornings",
    description: "Junior tennis coaching at De Beauvoir Tennis Club. LTA-affiliated programme covering mini tennis through to competitive juniors in a welcoming community club setting.",
    times: "Check LTA ClubSpark for class times",
    website: "https://www.hackneytennis.co.uk/juniors/holiday-camps/4-8yrs-holiday-camp-bookings-2/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-2",
    name: "Shoreditch Park — Running & Athletics Group",
    provider: "Shoreditch Park Running & Athletics Group",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Shoreditch Park, N1 5EW",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Free or low cost — contact organisers for details",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekday evenings or weekend mornings",
    description: "Running and athletics group using the open spaces of Shoreditch Park. Builds fitness, technique and a love of running and athletics in a friendly, community setting.",
    times: "Check group for session schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-3",
    name: "Regent's Canal Canoe Youth Project",
    provider: "Regent's Canal Canoe Youth Project",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Regent's Canal, Canonbury area, N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact project for term fees",
    age: "8–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings",
    description: "Canoeing and paddling skills on the Regent's Canal. Teaches water safety, paddling technique and canal ecology for young people in a safe, supervised environment.",
    times: "Check project for session schedule",
    website: "https://www.regentscanoeclub.co.uk/home/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-4",
    name: "De Beauvoir Cricket Club — Juniors",
    provider: "De Beauvoir Cricket Club",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "De Beauvoir area, N1 5SF",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact club for junior membership fees",
    age: "6–18",
    interests: ["sports","outdoors"],
    dayOfWeek: "Friday evening training · weekend matches",
    description: "Junior cricket club with ECB-aligned coaching for all ages and abilities. Builds batting, bowling and fielding skills from grassroots level through to competitive club cricket.",
    times: "Fri evening training · weekend matches",
    website: "https://www.oldfallopians.co.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-5",
    name: "St Paul's Shrubbery — Football Development Group",
    provider: "St Paul's Shrubbery Football Development Group",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact group for term fees",
    age: "5–16",
    interests: ["sports","outdoors"],
    dayOfWeek: "Weekend mornings (matches) · weekday training evenings",
    description: "Community football development group with age-group coaching and weekend match fixtures. FA-aligned coaching structure building technical skills and enjoyment of the game.",
    times: "Check group for training and match schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-6",
    name: "DanceSmith Studio Canonbury",
    provider: "DanceSmith Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Termly fees · contact studio for pricing",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Weekday afternoons & Saturdays",
    description: "Dance classes for a wide age range, building confidence, technique and performance skills across various styles. Regular showcase opportunities and structured progression.",
    times: "Check studio for class schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-7",
    name: "De Beauvoir Theatre Workshop",
    provider: "De Beauvoir Theatre Workshop",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact workshop for term fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Drama and performing arts workshop building acting technique, confidence and ensemble skills. Regular performance opportunities in a supportive, inclusive setting.",
    times: "Check workshop for class schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-8",
    name: "St Peter's Music Collective Canonbury",
    provider: "St Peter's Music Collective",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact collective for instrumental lesson fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Instrumental tuition across a range of instruments for children and teens, plus ensemble playing opportunities. Structured progression with options for graded music exams.",
    times: "Check collective for lesson schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-9",
    name: "Shoreditch Youth Jazz Lab",
    provider: "Shoreditch Youth Jazz Lab",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Shoreditch N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact lab for term fees",
    age: "8–18",
    interests: ["music","drama"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Jazz and improvisation classes for young musicians. Builds ensemble playing, improvisation skills and an appreciation for jazz performance in a fun, collaborative setting.",
    times: "Check lab for session schedule",
    website: "https://nationalyouthjazz.co.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-10",
    name: "Islington Strings Academy",
    provider: "Islington Strings Academy",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Islington N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact academy for instrumental lesson fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "String instrument tuition (violin, cello, viola, double bass) for children and teens. Structured progression through ABRSM grades with options for ensemble and orchestra participation.",
    times: "Check academy for lesson schedule",
    website: "https://www.londonstringsacademy.co.uk",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-11",
    name: "Canonbury Learning Studio — Maths",
    provider: "Canonbury Learning Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact studio for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "Maths tuition with structured, personalised learning plans. Builds core skills and exam confidence with regular progress tracking against UK curriculum standards.",
    times: "Check local studio for session times",
    website: "https://www.thelearning-studio.co.uk/pricing",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-12",
    name: "De Beauvoir Academic Hub — English Tuition",
    provider: "De Beauvoir Academic Hub",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact hub for current pricing",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Twice weekly sessions",
    description: "English tuition with personalised assessment and structured learning plans. Builds reading, writing and comprehension skills with regular progress tracking against curriculum standards.",
    times: "Check local hub for session times",
    website: "https://www.englishtuitionhub.com/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-13",
    name: "North London Entrance Exam Studio — 11+",
    provider: "North London Entrance Exam Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact studio for course fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Weekday evenings & weekends",
    description: "Specialist 11+ exam preparation covering verbal reasoning, non-verbal reasoning, maths and English. Small group tuition tailored to grammar school and independent school entrance requirements.",
    times: "Check studio for class schedule",
    website: "https://london11plus.co.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-14",
    name: "East London Code Forge — Coding",
    provider: "East London Code Forge",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Shoreditch N1",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact forge for term fees",
    age: "8–18",
    interests: ["stem","gaming"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Coding classes covering Scratch, Python and web development depending on age and level. Small group sessions building computational thinking and digital skills with a creative, maker-culture approach.",
    times: "Check forge for class schedule",
    website: "https://www.codeninjas.co.uk/blackheath-ldn-uk",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-15",
    name: "Hackney Electronics & Robotics Lab",
    provider: "Hackney Electronics & Robotics Lab",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact lab for term fees",
    age: "8–16",
    interests: ["stem"],
    dayOfWeek: "After-school sessions",
    description: "Electronics and robotics classes using hands-on kits and circuits to teach mechanical design, electronics basics and problem-solving. Builds STEM confidence through fun, structured project work.",
    times: "Check lab for class schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-16",
    name: "De Beauvoir Strategy Club — Chess",
    provider: "De Beauvoir Strategy Club",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact club for membership fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Weekday evening",
    description: "Friendly chess and strategy club teaching tactics, planning and sportsmanship for all levels, from complete beginners to tournament players, with coaching from experienced members.",
    times: "Check with club for session times",
    website: "https://findyour.islington.gov.uk/kb5/islington/directory/service.page?id=9EKTjp1InmY&communitychannelnew=5",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-17",
    name: "Turkish Cultural School Hackney — Canonbury",
    provider: "Turkish Cultural School Hackney",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact school for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Turkish language and culture classes for children, reflecting the area's significant Turkish community. Suitable for both heritage speakers maintaining their first language and complete beginners.",
    times: "Check school for class schedule",
    website: "https://www.halkevi.org.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-18",
    name: "East London Arabic Language Centre",
    provider: "East London Arabic Language Centre",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact centre for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or Saturdays",
    description: "Arabic language and culture classes for children, suitable for both heritage speakers and complete beginners. Builds reading, writing and conversational fluency in a supportive group setting.",
    times: "Check centre for class schedule",
    website: "https://tawhidfoundation.org.uk/event/arabic-language-course-east-london/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-19",
    name: "Canonbury Print & Illustration Studio",
    provider: "Canonbury Print & Illustration Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons & weekends",
    description: "Printmaking and illustration classes teaching lino cut, screen print and illustration technique. Builds technical skill and creative experimentation in a hands-on studio setting.",
    times: "Check studio for class times",
    website: "https://canonburypress.com/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-20",
    name: "De Beauvoir Ceramics Collective",
    provider: "De Beauvoir Ceramics Collective",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Weekday afternoons or weekends",
    description: "Pottery and ceramics classes teaching hand-building, wheel-throwing basics and glazing techniques. Children create take-home pieces while developing fine motor skills and patience.",
    times: "Check collective for class times",
    website: "https://www.skandihus.co.uk/contact",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-21",
    name: "Shoreditch Photography Darkroom Project",
    provider: "Shoreditch Photography Darkroom Project",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Shoreditch / Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact project for course pricing",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekend workshops",
    description: "Traditional and digital photography workshops for older children and teens. A unique opportunity to learn darkroom film photography alongside digital techniques, building a deep understanding of the craft.",
    times: "Check project for course dates",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-22",
    name: "Hackney Independent Film Lab",
    provider: "Hackney Independent Film Lab",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact lab for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Independent filmmaking lab for older children and teens — scripting, shooting and editing short films collaboratively with a strong emphasis on creative vision over commercial formats.",
    times: "Check lab for session schedule",
    website: "https://eastlondonfilm.co.uk/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-23",
    name: "Canal Museum — Youth Heritage Programme",
    provider: "London Canal Museum",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "London Canal Museum, 12–13 New Wharf Road, N1C 4MZ",
    postcodeSector: "N1",
    distanceMiles: 1.0,
    cost: "Adult £7.50 · child £3.50 · under 5 free · group visits and school sessions available",
    age: "8–16",
    interests: ["arts","stem"],
    dayOfWeek: "Tue–Sun (museum open) · school sessions and family events check website",
    description: "The London Canal Museum tells the story of London's canals and the people and horses that worked them, also exploring the history of the ice trade. Located in a former ice warehouse on Regent's Canal near King's Cross. Youth heritage programme includes guided learning sessions, handling collections and canal history activities. Open Tue–Sun 10am–4:30pm (Thu until 7:30pm).",
    times: "Tue–Sun 10am–4:30pm (Thu until 7:30pm, closed Mon except Bank Holidays)",
    website: "http://www.regentscanalheritage.org.uk/about/",
    neighbourhoods: ["Canonbury","Camden"],
    phone: "020 7713 0836 · info@canalmuseum.org.uk",
    notes: "Step-free access throughout. One of London's most underrated family museums. King's Cross tube/rail 5 mins.",
  },
  {
    id: "cnt-24",
    name: "Hackney Ecology Field School",
    provider: "Hackney Ecology Field School",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Free or low cost — contact organisers for details",
    age: "5–14",
    interests: ["outdoors","stem"],
    dayOfWeek: "Weekend mornings (typically Saturday)",
    description: "Nature and ecology field school exploring local green spaces, parks and canal-side habitats. Wildlife spotting, pond dipping, plant identification and environmental science in accessible urban nature settings.",
    times: "Check local listings for session dates",
    website: "https://www.ecoactive.org.uk/at-the-leaside-trust",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-25",
    name: "De Beauvoir Writers' Workshop",
    provider: "De Beauvoir Writers' Workshop",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact workshop for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekday evenings",
    description: "Creative writing workshop building narrative, poetry and storytelling skills. Encourages imaginative expression through guided exercises, workshops and peer feedback in a supportive group setting.",
    times: "Check workshop for class schedule",
    website: "https://www.debeauvoirblock.com/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-26",
    name: "Shoreditch Podcast & Audio Studio",
    provider: "Shoreditch Podcast & Audio Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Shoreditch N1",
    postcodeSector: "N1",
    distanceMiles: 0.8,
    cost: "Contact studio for term fees",
    age: "10–18",
    interests: ["arts","stem"],
    dayOfWeek: "Weekday evenings or weekends",
    description: "Podcasting and audio production classes teaching recording, editing and presenting skills. Young people produce their own audio content, building confidence in communication and technical media production.",
    times: "Check studio for session schedule",
    website: "https://outsetstudio.com/east-london-podcast-studio-shoreditch/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-27",
    name: "East London Fashion & Textiles Studio",
    provider: "East London Fashion & Textiles Studio",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact studio for term fees",
    age: "8–18",
    interests: ["arts"],
    dayOfWeek: "Weekend workshops",
    description: "Fashion design and textiles classes teaching sewing, pattern basics and garment construction alongside fashion illustration. Builds creative confidence and practical making skills.",
    times: "Check studio for workshop dates",
    website: "",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-28",
    name: "Young Urban Designers Collective",
    provider: "Young Urban Designers Collective",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem","arts"],
    dayOfWeek: "Weekend workshops",
    description: "Architecture and urban design club introducing children to building design, city planning and model-making. Combines creative and analytical skills through hands-on projects inspired by London's dynamic built environment.",
    times: "Check collective for workshop dates",
    website: "https://eustonengagementhub.commonplace.is/en-GB/proposals/young-urban-designers-programme/start",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-29",
    name: "Hackney Youth Leadership Forum",
    provider: "Hackney Youth Leadership Forum",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Hackney N1",
    postcodeSector: "N1",
    distanceMiles: 0.6,
    cost: "Contact forum for term fees",
    age: "11–18",
    interests: ["stem","drama"],
    dayOfWeek: "Weekday evenings",
    description: "Debate and leadership forum building confidence, critical thinking and persuasive communication skills. Structured debate formats and regular practice alongside civic engagement activities for secondary-age young people.",
    times: "Check forum for session schedule",
    website: "https://www.younghackney.org/get-involved/hackney-youth-parliament/",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-30",
    name: "De Beauvoir Community Cooking Club",
    provider: "De Beauvoir Community Cooking Club",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury N1",
    postcodeSector: "N1",
    distanceMiles: 0.5,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "After-school or weekend sessions",
    description: "Community cooking club teaching practical kitchen skills, food hygiene and simple recipes for children and young teens. Builds independence and a love of cooking in a fun, community-focused setting.",
    times: "Check club for class schedule",
    website: "https://www.cookforgood.uk/community-cooking",
    neighbourhoods: ["Canonbury"],
  },
  {
    id: "cnt-31",
    name: "Shoreditch Social Entrepreneurs Hub",
    provider: "Shoreditch Social Entrepreneurs Hub",
    type: "Club",
    neighbourhood: "Canonbury",
    location: "Canonbury / Shoreditch N1",
    postcodeSector: "N1",
    distanceMiles: 0.7,
    cost: "Contact hub for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Weekend workshops",
    description: "Social entrepreneurship club introducing teens to business planning, community-focused projects and pitching skills. Builds practical, real-world problem-solving with a social impact focus.",
    times: "Check hub for session schedule",
    website: "",
    neighbourhoods: ["Canonbury"],
  },

];

export const TERM_TIME_NEIGHBOURHOODS = [
  "Muswell Hill", "East Finchley", "Highgate", "Crouch End", "Whetstone & Totteridge", "Finchley", "North Finchley", "Hampstead", "Hampstead Garden Suburb", "Islington & Highbury", "Stoke Newington", "Winchmore Hill & Southgate", "Barnet & High Barnet", "Alexandra Palace & Wood Green", "Primrose Hill & Belsize Park", "Camden", "Kentish Town", "Swiss Cottage", "St John's Wood", "Canonbury"
];

// ─── South West London Term-Time Activities ─────────────────────────────────
// Same shape as TERM_TIME_ACTIVITIES. distanceMiles measured from Clapham Common,
// consistent with SOUTH_WEST_LONDON_CAMPS.
export const SOUTH_WEST_LONDON_TERM_TIME_ACTIVITIES = [

  // ── BALHAM ───────────────────────────────────────────────────────────────────
  {
    id: "ttbal-1",
    name: "Kumon Balham Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham Methodist Church Hall, SW12 9BZ",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Balham Methodist Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/balham",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-2",
    name: "Explore Learning",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Balham",
    location: "Northcote Road area, SW11 1NT",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre serving the Balham catchment from the Northcote Road area. Maths and English tuition, 11+ preparation and homework support.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/nine-elms/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-3",
    name: "First Class Learning Balham",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "Balham",
    location: "Chestnut Grove School (venue varies), SW12 8JZ",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition programme at Chestnut Grove School, with venue varying by term.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/centre/balham",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-4",
    name: "Stagecoach Balham",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Balham",
    location: "Chestnut Grove School, SW12 8JZ",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Chestnut Grove School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/battersea",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-5",
    name: "Perform Drama Classes Balham",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Balham",
    location: "Church halls / Hildreth Street venues, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls and Hildreth Street venues in Balham. Drama, improvisation and storytelling.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-6",
    name: "Young Stars Theatre School",
    provider: "Young Stars Theatre School",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham Baptist Church Hall, SW12 9AY",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "4–14",
    interests: ["drama"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Theatre school at Balham Baptist Church Hall. Drama, performance and confidence building.",
    times: "Check school for class times",
    website: "https://slyt.co.uk/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-7",
    name: "Rock Choir",
    provider: "Rock Choir",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham venues, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "16+",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Group singing sessions across Balham venues, serving the Wandsworth/Balham area. Vocal training and performance for adults.",
    times: "Check provider for class times",
    website: "https://rockchoir.com/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-8",
    name: "Balham Karate Club",
    provider: "Balham Karate Club",
    type: "Club",
    neighbourhood: "Balham",
    location: "Tooting Leisure Centre, SW17 0NE",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Tooting Leisure Centre. Training, grading and discipline for a wide age range.",
    times: "Check club for class times",
    website: "https://www.southlondonkarate.co.uk/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-9",
    name: "Tiger Martial Arts Balham",
    provider: "Tiger Martial Arts",
    type: "Club",
    neighbourhood: "Balham",
    location: "Chestnut Grove School, SW12 8JZ",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "4–14",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Martial arts club at Chestnut Grove School. Karate and taekwondo, fitness and confidence building.",
    times: "Check club for class times",
    website: "https://fightcitygym.co.uk/balham/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-10",
    name: "Gracie Barra Wandsworth",
    provider: "Gracie Barra",
    type: "Club",
    neighbourhood: "Balham",
    location: "Earlsfield / Wandsworth, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Brazilian jiu-jitsu club in Earlsfield/Wandsworth, serving the Balham catchment. Technique training and self-defence.",
    times: "Check club for class times",
    website: "https://gbtooting.com/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-11",
    name: "Balham FC Youth Academy",
    provider: "Balham FC",
    type: "Club",
    neighbourhood: "Balham",
    location: "Tooting Bec Common, SW16 / SW17",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy on Tooting Bec Common. Training, matches and development pathways.",
    times: "Check academy for class times",
    website: "https://www.balhamfc.com/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-12",
    name: "Wandsworth Common Tennis Coaching",
    provider: "Wandsworth Common Tennis",
    type: "Club",
    neighbourhood: "Balham",
    location: "Wandsworth Common, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching on Wandsworth Common. Lessons and match play for a wide age range.",
    times: "Check provider for class times",
    website: "https://www.allstartennis.co.uk/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-13",
    name: "Better Leisure Balham Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham Leisure Centre, SW12 9BS",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Balham Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.placesleisure.org/centres/balham-leisure-centre/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-14",
    name: "AFC Wimbledon Foundation Coaching",
    provider: "AFC Wimbledon Foundation",
    type: "Club",
    neighbourhood: "Balham",
    location: "School venues (Balham catchment), SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact foundation for term fees",
    age: "5–14",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching at school venues across the Balham catchment, run by the AFC Wimbledon Foundation. Development pathways for a wide age range.",
    times: "Check foundation for class times",
    website: "https://afcwimbledonfoundation.com/football-for-all",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-15",
    name: "Art-K Balham",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham High Road studio, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Balham High Road. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-wandsworth/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-16",
    name: "Code Ninjas Balham",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Balham",
    location: "Balham branch, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Balham branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-17",
    name: "Chess at St Luke's Balham",
    provider: "St Luke's Chess Club",
    type: "Club",
    neighbourhood: "Balham",
    location: "St Luke's Church Hall, SW12 8SS",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Chess tuition and competitions at St Luke's Church Hall in Balham.",
    times: "Check club for class times",
    website: "https://www.englishchess.org.uk/venue/st-lukes-church-2/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-18",
    name: "Balham Art Club",
    provider: "Balham Art Club",
    type: "Club",
    neighbourhood: "Balham",
    location: "Community centres, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "5–12",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Art classes across Balham community centres. Drawing and painting for younger children.",
    times: "Check club for class times",
    website: "https://gallerymidnight.com/pages/art-classes-balham",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-19",
    name: "Balham Music School",
    provider: "Balham Music School",
    type: "Club",
    neighbourhood: "Balham",
    location: "Chestnut Grove School, SW12 8JZ",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school at Chestnut Grove School. Instrument tuition, theory and ensembles for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://swsmusic.co.uk/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-20",
    name: "Inspire Music Tuition Balham",
    provider: "Inspire Music Tuition",
    type: "Club",
    neighbourhood: "Balham",
    location: "Home/studio-based, SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact tutor for term fees",
    age: "4–18",
    interests: ["music"],
    dayOfWeek: "Check tutor for term-time schedule",
    description: "Home and studio-based music tuition in Balham. Instrument lessons and graded exam preparation.",
    times: "Check tutor for class times",
    website: "https://www.inspiredplaying.com/",
    neighbourhoods: ["Balham"],
  },
  {
    id: "ttbal-21",
    name: "Rocksteady Music School",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Balham",
    location: "Primary schools (Balham catchment), SW12",
    postcodeSector: "SW12",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Balham catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Balham"],
  },

  // ── BARNES ───────────────────────────────────────────────────────────────────
  {
    id: "ttbrn-1",
    name: "Kumon Barnes Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Green Centre, SW13 9PY",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Barnes Green Centre. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/centre-search-results",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-2",
    name: "Explore Learning",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Hammersmith / Barnes edge centre, SW6 6DX",
    postcodeSector: "SW6",
    distanceMiles: 2.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre serving the Barnes catchment from the Hammersmith/Barnes edge. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/find-a-centre/greater-london/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-3",
    name: "Barnes Tuition Centre",
    provider: "Barnes Tuition Centre",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Church Road, SW13 9HE",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Church Road in Barnes. Maths, English, exam preparation and school support.",
    times: "Check centre for class times",
    website: "https://www.torchlight-tuition.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-4",
    name: "The Bridge Learning Centre Barnes",
    provider: "The Bridge Learning Centre",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Community Centre, SW13 9QQ",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact centre for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic and SEN support centre at Barnes Community Centre. Literacy, numeracy support and SEN learning programmes.",
    times: "Check centre for class times",
    website: "",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-5",
    name: "Barnes Music Academy",
    provider: "Barnes Music Academy",
    type: "Club",
    neighbourhood: "Barnes",
    location: "St Mary's Church Hall, SW13 9HL",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at St Mary's Church Hall in Barnes. Piano, violin, guitar, choir and music theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "http://www.pieceofmusic.co.uk/barnes",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-6",
    name: "London Suzuki Group",
    provider: "London Suzuki Group",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Methodist Church, SW13 9HY",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact group for term fees",
    age: "3–16",
    interests: ["music"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Barnes branch of the London Suzuki Group at Barnes Methodist Church. Suzuki violin, cello and early years music education.",
    times: "Check group for class times",
    website: "https://londonsuzukigroup.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-7",
    name: "Barnes Choir Academy",
    provider: "Barnes Choir Academy",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Village Hall, SW13 0HT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at Barnes Village Hall. Choir training, vocal coaching and performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.barneschoir.org.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-8",
    name: "Barnes Drama Workshop",
    provider: "Barnes Drama Workshop",
    type: "Club",
    neighbourhood: "Barnes",
    location: "St Osmund's Church Hall, SW13 9JP",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact workshop for term fees",
    age: "4–14",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop at St Osmund's Church Hall in Barnes. Acting, improvisation and performance skills.",
    times: "Check workshop for class times",
    website: "https://www.perform.org.uk/drama-classes/barnes/barnes-methodist-church-hall",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-9",
    name: "Barnes Young Actors Studio",
    provider: "Barnes Young Actors Studio",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Green Centre, SW13 9PY",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Young actors studio at Barnes Green Centre. Acting, stagecraft, audition preparation and confidence training.",
    times: "Check studio for class times",
    website: "https://www.osoarts.org.uk/oso-actors-studio",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-10",
    name: "Barnes Theatre School",
    provider: "Barnes Theatre School",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Roxburgh Hall, SW13 0HG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Theatre school at Roxburgh Hall in Barnes. Musical theatre, dance, acting and singing for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://letsactdramaclasses.co.uk/barnes/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-11",
    name: "Barnes Ballet School",
    provider: "Barnes Ballet School",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Village studios, SW13 9NW",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Barnes village studios. Ballet, contemporary dance and RAD syllabus training.",
    times: "Check school for class times",
    website: "https://www.thebarnesballetschool.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-12",
    name: "Barnes Judo Club",
    provider: "Barnes Judo Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Sports Club, SW13 9JW",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Judo club at Barnes Sports Club. Discipline, grading system and fitness.",
    times: "Check club for class times",
    website: "http://www.rockslane.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-13",
    name: "Barnes Karate Academy",
    provider: "Barnes Karate Academy",
    type: "Club",
    neighbourhood: "Barnes",
    location: "St Paul's School sports hall (community use), SW13 9JT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Karate academy at St Paul's School sports hall, open for community use. Training, self-defence and grading.",
    times: "Check academy for class times",
    website: "https://www.lifekarate.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-14",
    name: "Barnes Football Academy",
    provider: "Barnes Football Academy",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, SW13 0DG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Barn Elms Sports Trust. Coaching, league play and skills training.",
    times: "Check academy for class times",
    website: "https://www.barneseagles.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-15",
    name: "Barnes Netball Club Junior Programme",
    provider: "Barnes Netball Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barn Elms Sports Trust, SW13 0DG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball programme at Barn Elms Sports Trust. Coaching, match play and skills development.",
    times: "Check club for class times",
    website: "https://rockslane.co.uk/barnes/netball/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-16",
    name: "Barnes Rowing Development Programme",
    provider: "Barnes Rowing",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Thames riverside (Barnes Bridge area), SW13 0BA",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact programme for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Rowing development programme along the Thames near Barnes Bridge. Water training, technique and endurance for teens.",
    times: "Check programme for class times",
    website: "https://www.enableleisure.co.uk/rowing",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-17",
    name: "Barnes Tennis Coaching Programme",
    provider: "Barnes Sports Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Sports Club, SW13 9JW",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching programme at Barnes Sports Club. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://rockslane.co.uk/barnes/tennis/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-18",
    name: "Barn Elms Outdoor Adventure Club",
    provider: "Barn Elms",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barn Elms Fields, SW13 0DG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "5–14",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Outdoor adventure club at Barn Elms Fields. Multi-sport, outdoor games, fitness and team challenges.",
    times: "Check club for class times",
    website: "https://www.enableleisure.co.uk/barn-elms-sports-centre-gym",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-19",
    name: "Wetland Centre Wildlife Club",
    provider: "WWT London Wetland Centre",
    type: "Club",
    neighbourhood: "Barnes",
    location: "WWT London Wetland Centre, SW13 9WT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact centre for term fees",
    age: "5–14",
    interests: ["stem", "outdoors"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Nature and wildlife club at the WWT London Wetland Centre in Barnes. Nature study, wildlife education and conservation learning.",
    times: "Check centre for class times",
    website: "https://www.wwt.org.uk/wetland-centres/london/wildlife",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-20",
    name: "Barnes Coding Club",
    provider: "Barnes Coding Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Community Centre, SW13 9QQ",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Coding club at Barnes Community Centre. Scratch, Python basics and robotics.",
    times: "Check club for class times",
    website: "https://www.compasslearningclub.co.uk/about-us/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-21",
    name: "Code Club Barnes",
    provider: "Code Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Local primary schools, SW13 catchment",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact provider for term fees",
    age: "7–11",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Coding club delivered across a network of local primary schools in Barnes. Digital skills and programming basics.",
    times: "Check provider for class times",
    website: "https://merton.events.mylibrary.digital/event?id=375780",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-22",
    name: "ArtBarnes Studio",
    provider: "ArtBarnes",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes High Street studio, SW13 9LR",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on Barnes High Street. Drawing, painting, sculpture and portfolio development.",
    times: "Check studio for class times",
    website: "http://www.artbarnclasses.com/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-23",
    name: "Barnes Pottery & Ceramics Club",
    provider: "Barnes Pottery & Ceramics Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Village workshops, SW13 9LW",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Pottery and ceramics club across Barnes Village workshops. Ceramics, sculpture and clay modelling.",
    times: "Check club for class times",
    website: "http://www.ceramicsclasseslondon.com/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-24",
    name: "Little Gym Barnes",
    provider: "The Little Gym",
    type: "Club",
    neighbourhood: "Barnes",
    location: "West London studio, SW13 catchment",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact studio for term fees",
    age: "3–12",
    interests: ["sports"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Gymnastics and physical development classes at a West London studio, serving the Barnes catchment. Movement and coordination.",
    times: "Check studio for class times",
    website: "https://thelittlegym.co.uk/",
    neighbourhoods: ["Barnes"],
  },
  {
    id: "ttbrn-25",
    name: "Barnes Chess Club Juniors",
    provider: "Barnes Chess Club",
    type: "Club",
    neighbourhood: "Barnes",
    location: "Barnes Village Hall, SW13 0HT",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Barnes Village Hall. Coaching, tournaments and strategy skills.",
    times: "Check club for class times",
    website: "https://www.barnessportsclub.com/post/desert-penguins-junior-chess-club",
    neighbourhoods: ["Barnes"],
  },

  // ── BATTERSEA ────────────────────────────────────────────────────────────────
  {
    id: "ttbtt-1",
    name: "Kumon Battersea Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Library Hall, SW11 1JB",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Battersea Library Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/battersea/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-2",
    name: "Explore Learning Battersea",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Power Station area centre, SW11 8DD",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre near Battersea Power Station. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/nine-elms/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-3",
    name: "Kip McGrath Battersea",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea High Street studio, SW11 3JS",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Battersea High Street. English, maths tuition and school support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-4",
    name: "Stagecoach Battersea",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Belleville Primary School, SW11 6PR",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Belleville Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/battersea",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-5",
    name: "Perform Drama Battersea",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Northcote Library / church halls, SW11",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across Northcote Library and church halls in Battersea. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-6",
    name: "Battersea Arts Centre Youth Theatre",
    provider: "Battersea Arts Centre",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Arts Centre, SW11 5TN",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["drama"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Youth theatre at Battersea Arts Centre. Theatre training, performance and creative writing for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.bac.org.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-7",
    name: "Little Voices Battersea",
    provider: "Little Voices",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Local school halls, SW11 catchment",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["music", "drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts classes at local school halls across the Battersea catchment. Singing, acting and confidence building.",
    times: "Check provider for class times",
    website: "https://littlevoices.org.uk/locations/locations/south-west-london/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-8",
    name: "Battersea Dance Academy",
    provider: "Battersea Dance Academy",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Lavender Hill studios, SW11 5QW",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact academy for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Lavender Hill studios. Ballet, jazz and contemporary dance.",
    times: "Check academy for class times",
    website: "https://www.royalacademyofdance.org/dance-classes/dance-school-in-london/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-9",
    name: "Battersea Karate Club",
    provider: "Battersea Karate Club",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Sports Centre, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Battersea Sports Centre. Discipline, grading and sparring.",
    times: "Check club for class times",
    website: "https://batterseakarate.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-10",
    name: "Gracie Barra Battersea",
    provider: "Gracie Barra",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Clapham Junction area, SW11",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Brazilian jiu-jitsu club near Clapham Junction. Technique training and self-defence.",
    times: "Check club for class times",
    website: "https://graciebarralondon.com/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-11",
    name: "Tiger Martial Arts Battersea",
    provider: "Tiger Martial Arts",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Belleville School hall, SW11 6PR",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "4–14",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Martial arts club at Belleville School hall. Karate and taekwondo, fitness and confidence building.",
    times: "Check club for class times",
    website: "https://taekwondotigerslondon.co.uk/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-12",
    name: "Battersea Park Football Academy",
    provider: "Battersea Park Football Academy",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Park Millennium Arena, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Battersea Park Millennium Arena. Coaching, matches and development.",
    times: "Check academy for class times",
    website: "https://wemakefootballers.com/uk/battersea",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-13",
    name: "Battersea Park Tennis Coaching",
    provider: "Battersea Park Tennis",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Park Tennis Courts, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Battersea Park Tennis Courts. Lessons, drills and match play.",
    times: "Check provider for class times",
    website: "https://clubspark.lta.org.uk/BatterseaParkTennisCourts/Coaching?PageView=Calendar",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-14",
    name: "Better Leisure Battersea Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Sports Centre, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Battersea Sports Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.placesleisure.org/centres/latchmere-leisure-centre/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-15",
    name: "Battersea Netball Club Juniors",
    provider: "Battersea Netball Club",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Park, SW11 4NJ",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club at Battersea Park. Training and match play.",
    times: "Check club for class times",
    website: "https://www.wandsworthnetball.club/a/juniors-37854.html?page=2",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-16",
    name: "Chelsea FC Foundation Coaching",
    provider: "Chelsea FC Foundation",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea / local schools, SW11 catchment",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact foundation for term fees",
    age: "5–14",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching hub across Battersea and local schools, run by the Chelsea FC Foundation. Skills training and academy pathway.",
    times: "Check foundation for class times",
    website: "https://chelseafoundation.chelseafc.com/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-17",
    name: "Art-K Battersea",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Rise studio, SW11",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Battersea Rise. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-wandsworth/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-18",
    name: "Battersea Art School",
    provider: "Battersea Art School",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Arts Centre area, SW11 5TN",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Art school near Battersea Arts Centre. Painting, mixed media and portfolio work.",
    times: "Check school for class times",
    website: "https://thenewschoolart.org/art-courses/art-courses-battersea/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-19",
    name: "Code Ninjas Battersea",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea branch, SW11",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Battersea branch of Code Ninjas. Roblox, Minecraft and robotics.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-20",
    name: "Tech Explorers Battersea",
    provider: "Tech Explorers",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Local school halls, SW11 catchment",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "6–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across local school halls in Battersea. Coding, robotics and digital creativity.",
    times: "Check provider for class times",
    website: "https://computerxplorers.co.uk/classes/holiday-camps",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-21",
    name: "Chess Club Battersea Juniors",
    provider: "Chess Club Battersea",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Northcote Library, SW11",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Northcote Library. Coaching, tournaments and strategy skills.",
    times: "Check club for class times",
    website: "https://www.batterseachessclub.org.uk/category/battersea-juniors/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-22",
    name: "Battersea Music School",
    provider: "Battersea Music School",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea High Street studios, SW11 3JS",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Battersea High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://worldheartbeat.org/music-bambinos/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-23",
    name: "Rocksteady Battersea",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Primary schools, SW11 catchment",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Battersea catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Battersea"],
  },
  {
    id: "ttbtt-24",
    name: "Battersea Choir School",
    provider: "Battersea Choir School",
    type: "Club",
    neighbourhood: "Battersea",
    location: "Battersea Arts Centre, SW11 5TN",
    postcodeSector: "SW11",
    distanceMiles: 1.2,
    cost: "Contact school for term fees",
    age: "6–16",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Choir school at Battersea Arts Centre. Choir, vocal training and performance.",
    times: "Check school for class times",
    website: "https://www.batterseachoralsociety.org/",
    neighbourhoods: ["Battersea"],
  },

  // ── CHISWICK ─────────────────────────────────────────────────────────────────
  {
    id: "ttchw-1",
    name: "Kumon Chiswick Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Town Hall, W4 4JN",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Chiswick Town Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/chiswick/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-2",
    name: "Explore Learning Chiswick",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick High Road centre, W4 5RT",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Chiswick High Road. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/london-westfield-shepherds-bush/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-3",
    name: "Tutor Doctor Chiswick",
    provider: "Tutor Doctor",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Home-based tutoring network, W4 catchment",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based tutoring network across the Chiswick catchment. One-to-one maths, English and exam preparation for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.tutordoctor.co.uk/locations/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-4",
    name: "Chiswick School Homework Club",
    provider: "Chiswick School",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick School, W4 3UN",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact school for term fees",
    age: "11–16",
    interests: ["stem"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Homework club at Chiswick School. Homework support, exam preparation and study skills.",
    times: "Check school for class times",
    website: "https://www.chiswickschool.org/key-information/independent-study",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-5",
    name: "Stagecoach Chiswick",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Belmont Primary School, W4 5UL",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Belmont Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/chiswick",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-6",
    name: "Chiswick Playhouse Youth Theatre",
    provider: "Chiswick Playhouse",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Playhouse, W4 1LR",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact playhouse for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check playhouse for term-time schedule",
    description: "Youth theatre at Chiswick Playhouse. Acting, drama training and performance projects for a wide age range up to 18.",
    times: "Check playhouse for class times",
    website: "https://www.tabard.org.uk/about",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-7",
    name: "Perform Drama Chiswick",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "St Michael & All Angels Church Hall, W4 5DX",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes at St Michael & All Angels Church Hall. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-8",
    name: "Chiswick Ballet School",
    provider: "Chiswick Ballet School",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick studios, W4 5YA",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Chiswick studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://elitedancersacademy.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-9",
    name: "Chiswick Karate Club",
    provider: "Chiswick Karate Club",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Sports Hall, W4 1RX",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Chiswick Sports Hall. Grading, discipline and sparring.",
    times: "Check club for class times",
    website: "https://www.seido-london.co.uk/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-10",
    name: "Gracie Barra Chiswick",
    provider: "Gracie Barra",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Turnham Green area, W4",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Brazilian jiu-jitsu club near Turnham Green. Technique training and self-defence.",
    times: "Check club for class times",
    website: "https://graciebarralondon.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-11",
    name: "Chiswick Taekwondo Academy",
    provider: "Chiswick Taekwondo Academy",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Local leisure centre, W4 1RX",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Taekwondo academy at a local leisure centre in Chiswick. Training and competition preparation.",
    times: "Check academy for class times",
    website: "http://ilyeo.org/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-12",
    name: "Chiswick Football Academy",
    provider: "Chiswick Football Academy",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Turnham Green / Duke's Meadow, W4 5RF",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy across Turnham Green and Duke's Meadow. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.wemakefootballers.com/chiswick",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-13",
    name: "Chiswick Rugby Mini Academy",
    provider: "Chiswick Rugby Mini Academy",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Duke's Meadow, W4 5RF",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Mini rugby academy at Duke's Meadow. Rugby training, teamwork and matches.",
    times: "Check academy for class times",
    website: "https://www.chiswickrugby.co.uk/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-14",
    name: "Chiswick Tennis Club Coaching Programme",
    provider: "Chiswick Tennis Club",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Tennis Club, W4 2SP",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching programme at Chiswick Tennis Club. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://clubspark.lta.org.uk/ChiswickTennisClub",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-15",
    name: "Duke's Meadows Golf & Tennis Coaching",
    provider: "Duke's Meadows",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Duke's Meadows, W4 2SH",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Golf and tennis coaching at Duke's Meadows. Golf basics, tennis and coordination training.",
    times: "Check provider for class times",
    website: "https://dukesmeadows.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-16",
    name: "Better Leisure Chiswick Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Pool & Fitness Centre, W4 1RX",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Chiswick Pool & Fitness Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/what-we-offer/lessons-and-courses/swimming",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-17",
    name: "Chiswick Netball Club Juniors",
    provider: "Chiswick Netball Club",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick Common / school courts, W4 catchment",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across Chiswick Common and school courts. Training and match play.",
    times: "Check club for class times",
    website: "https://rockslane.co.uk/chiswick/netball/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-18",
    name: "Art-K Chiswick",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick High Road studio, W4",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Chiswick High Road. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-shepherds-bush/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-19",
    name: "Chiswick Art House",
    provider: "Chiswick Art House",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Devonshire Road, W4 2EU",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on Devonshire Road. Fine art and portfolio development.",
    times: "Check studio for class times",
    website: "https://chiswickhouseandgardens.org.uk/community-schools/studios/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-20",
    name: "Code Ninjas Chiswick",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick branch, W4",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Chiswick branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.co.uk/isleworth-ldn-uk",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-21",
    name: "Digital Creators Club Chiswick",
    provider: "Digital Creators Club",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Local school halls, W4 catchment",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across local school halls in Chiswick. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "https://digitalcreator.club/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-22",
    name: "Chiswick Chess Club Juniors",
    provider: "Chiswick Chess Club",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "St Michael & All Angels Church Hall, W4 5DX",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St Michael & All Angels Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.westlondonchess.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-23",
    name: "Chiswick Music School",
    provider: "Chiswick School of Music",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Chiswick School of Music, W4",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school in Chiswick. Piano, violin, guitar and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.chiswicksaturdaymusic.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-24",
    name: "Rocksteady Chiswick",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "Primary schools, W4 catchment",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Chiswick catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Chiswick"],
  },
  {
    id: "ttchw-25",
    name: "Chiswick Choir Academy",
    provider: "Chiswick Choir Academy",
    type: "Club",
    neighbourhood: "Chiswick",
    location: "St Nicholas Church, W4 5DT",
    postcodeSector: "W4",
    distanceMiles: 5.8,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at St Nicholas Church in Chiswick. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://westlondonchorus.co.uk/",
    neighbourhoods: ["Chiswick"],
  },

  // ── CLAPHAM ──────────────────────────────────────────────────────────────────
  {
    id: "ttclp-1",
    name: "Kumon Clapham Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Library Hall, SW4 7DB",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Clapham Library Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/clapham-common/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-2",
    name: "Explore Learning Clapham",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham High Street centre, SW4 7UH",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Clapham High Street. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-3",
    name: "Kip McGrath Clapham",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Old Town studio, SW4 0JG",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Clapham Old Town. English, maths tuition and exam support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-4",
    name: "Clapham Tutors Collective",
    provider: "Clapham Tutors Collective",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Home-based network, SW4 catchment",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across the Clapham catchment. 11+ and GCSE support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.claphamtutors.org/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-5",
    name: "St Mary's Clapham Homework Club",
    provider: "St Mary's RC Primary School",
    type: "Club",
    neighbourhood: "Clapham",
    location: "St Mary's RC Primary School, SW4 6BX",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact school for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Homework club at St Mary's RC Primary School. Homework support and study skills.",
    times: "Check school for class times",
    website: "https://www.st-marys.lambeth.sch.uk/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-6",
    name: "Stagecoach Clapham",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Manor Primary School, SW4 6BX",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Clapham Manor Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/battersea",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-7",
    name: "Clapham Youth Theatre Company",
    provider: "Clapham Youth Theatre Company",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Omnibus Theatre, SW4 0QW",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact company for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check company for term-time schedule",
    description: "Youth theatre company at the Omnibus Theatre. Acting, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check company for class times",
    website: "https://www.omnibus-clapham.org/omnibus-young-company",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-8",
    name: "Perform Drama Clapham",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Church halls (Old Town), SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in Clapham Old Town. Drama, improvisation and storytelling.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-9",
    name: "Little Voices Clapham",
    provider: "Little Voices",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Local school halls, SW4 catchment",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["music", "drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts classes at local school halls across the Clapham catchment. Singing, acting and confidence building.",
    times: "Check provider for class times",
    website: "https://littlevoices.org.uk/locations/locations/south-west-london/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-10",
    name: "Omnibus Theatre Academy",
    provider: "Omnibus Theatre",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Omnibus Theatre, SW4 0QW",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Professional theatre training academy at the Omnibus Theatre. Workshops for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.city-academy.com/locations/omnibus-theatre-sw4/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-11",
    name: "Clapham Ballet School",
    provider: "Clapham Ballet School",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham studios, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Clapham studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://danceacacia.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-12",
    name: "Clapham Karate Club",
    provider: "Clapham Karate Club",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Leisure Centre, SW4 7EX",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Clapham Leisure Centre. Discipline, grading and sparring.",
    times: "Check club for class times",
    website: "https://www.claphammartialarts.co.uk/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-13",
    name: "Gracie Barra Clapham",
    provider: "Gracie Barra",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham North / High Street, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Brazilian jiu-jitsu club near Clapham North and the High Street. Technique training and self-defence.",
    times: "Check club for class times",
    website: "http://www.bjjsouthlondon.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-14",
    name: "Clapham Taekwondo Academy",
    provider: "Clapham Taekwondo Academy",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Local leisure centre, SW4 7EX",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Taekwondo academy at a local leisure centre in Clapham. Training and competition preparation.",
    times: "Check academy for class times",
    website: "https://claphamtaekwondo.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-15",
    name: "Clapham Football Academy",
    provider: "Clapham Football Academy",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Common, SW4 9DE",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy on Clapham Common. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.claphamrangersfc.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-16",
    name: "Clapham Common Tennis Coaching",
    provider: "Clapham Common Tennis",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Common courts, SW4 9DE",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching on Clapham Common courts. Lessons, drills and match play.",
    times: "Check provider for class times",
    website: "https://www.lifetimetennis.co.uk/clapham-tennis/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-17",
    name: "Better Leisure Clapham Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Leisure Centre, SW4 7EX",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Clapham Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://active.lambeth.gov.uk/locations/clapham/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-18",
    name: "Clapham Netball Club Juniors",
    provider: "Clapham Netball Club",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Common, SW4 9DE",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club on Clapham Common. Training and match play.",
    times: "Check club for class times",
    website: "https://mynetball.club/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-19",
    name: "Chelsea FC Foundation Clapham Hub",
    provider: "Chelsea FC Foundation",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Local school pitches, SW4 catchment",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact foundation for term fees",
    age: "5–14",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching hub across local school pitches in Clapham, run by the Chelsea FC Foundation. Academy pathway.",
    times: "Check foundation for class times",
    website: "https://chelseafoundation.chelseafc.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-20",
    name: "Art-K Clapham",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham High Street studio, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Clapham High Street. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-clapham/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-21",
    name: "Clapham Art Studio",
    provider: "Clapham Art Studio",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Old Town, SW4 0JG",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio in Clapham Old Town. Fine art and portfolio development.",
    times: "Check studio for class times",
    website: "https://www.lavenderleonardos.art/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-22",
    name: "Code Ninjas Clapham",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham branch, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Clapham branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-23",
    name: "Digital Creators Clapham",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Clapham",
    location: "School halls, SW4 catchment",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Clapham. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-24",
    name: "Clapham Chess Club Juniors",
    provider: "Clapham Chess Club",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham Library Hall, SW4 7DB",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Clapham Library Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.batterseachessclub.org.uk/category/battersea-juniors/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-25",
    name: "Clapham Music School",
    provider: "Clapham Music School",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Clapham High Street studios, SW4",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Clapham High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.lambethmusic.co.uk/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-26",
    name: "Rocksteady Clapham",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Primary schools, SW4 catchment",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Clapham catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Clapham"],
  },
  {
    id: "ttclp-27",
    name: "Clapham Choir Academy",
    provider: "Clapham Choir Academy",
    type: "Club",
    neighbourhood: "Clapham",
    location: "Holy Trinity Church, SW4 0QZ",
    postcodeSector: "SW4",
    distanceMiles: 0.3,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at Holy Trinity Church in Clapham. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.singclapham.org/",
    neighbourhoods: ["Clapham"],
  },

  // ── EARLSFIELD ───────────────────────────────────────────────────────────────
  {
    id: "ttefd-1",
    name: "Kumon Earlsfield Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "St Andrew's Church Hall, SW18 3HJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at St Andrew's Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-2",
    name: "Explore Learning Wandsworth East",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Lane centre, SW18 4SR",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Garratt Lane, serving the Earlsfield catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/find-a-centre/greater-london/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-3",
    name: "First Class Learning Earlsfield",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Local school halls, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition programme across local school halls in Earlsfield.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/wandsworth/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-4",
    name: "Earlsfield Tuition Collective",
    provider: "Earlsfield Tuition Collective",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Home + micro-centres, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Private tutoring across home settings and micro-centres in Earlsfield. Exam preparation and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://gyminds.co.uk/tutoring-earlsfield-location/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-5",
    name: "St Andrew's Homework Club Earlsfield",
    provider: "St Andrew's Church",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "St Andrew's Church Hall, SW18 3HJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St Andrew's Church Hall. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "https://www.wandsworth.gov.uk/cost-of-living-hub/community-spaces/community-spaces-directory/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-6",
    name: "Stagecoach Earlsfield",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Earlsfield Primary School, SW18 3JY",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Earlsfield Primary School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/putneyandbarnes",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-7",
    name: "Perform Drama Earlsfield",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Church halls (Garratt Lane), SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls on Garratt Lane. Drama, improvisation and storytelling.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-8",
    name: "Little Voices Earlsfield",
    provider: "Little Voices",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Local school venues, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["music", "drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts classes at local school venues across the Earlsfield catchment. Singing, acting and confidence building.",
    times: "Check provider for class times",
    website: "https://littlevoices.org.uk/locations/locations/south-west-london/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-9",
    name: "Earlsfield Youth Drama Collective",
    provider: "Earlsfield Youth Drama Collective",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Community halls, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Youth drama collective across Earlsfield community halls. Acting, performance and stagecraft.",
    times: "Check collective for class times",
    website: "https://letsactdramaclasses.co.uk/earlsfield/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-10",
    name: "Earlsfield Ballet & Dance School",
    provider: "Earlsfield Ballet & Dance School",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Local studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet and dance school across local Earlsfield studios. Ballet, jazz and contemporary dance.",
    times: "Check school for class times",
    website: "https://deborahjayneschoolofdance.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-11",
    name: "Earlsfield Karate Club",
    provider: "Earlsfield Karate Club",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Earlsfield Leisure Centre, SW18 4RL",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Earlsfield Leisure Centre. Discipline and grading system.",
    times: "Check club for class times",
    website: "https://earlsfieldjiujitsu.com/services",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-12",
    name: "Gracie Barra Earlsfield",
    provider: "Gracie Barra",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Lane studio, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Brazilian jiu-jitsu club at the Garratt Lane studio. Technique training and self-defence.",
    times: "Check club for class times",
    website: "https://earlsfieldmartialartsandyoga.com/classes/Brazilian-Jiu-Jitsu",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-13",
    name: "Earlsfield Taekwondo Academy",
    provider: "Earlsfield Taekwondo Academy",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "School sports halls, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Taekwondo academy across school sports halls in Earlsfield. Training and competition preparation.",
    times: "Check academy for class times",
    website: "https://earlsfieldmartialartsandyoga.com/classes/Kids-Martial-Arts",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-14",
    name: "Earlsfield Football Academy",
    provider: "Earlsfield Football Academy",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Park, SW18 4SR",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Garratt Park. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.earlscliffe.co.uk/sports-culture-service/football-academy-in-partnership-with-tottenham-hotspur/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-15",
    name: "Wandsworth Common Football Coaching",
    provider: "Wandsworth Common Football",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Wandsworth Common, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Football coaching on Wandsworth Common, popular with Earlsfield families. Training and development squads.",
    times: "Check provider for class times",
    website: "https://www.ramptonbaseley.com/articles/junior-football-clubs-in-wandsworth",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-16",
    name: "Earlsfield Tennis Coaching Programme",
    provider: "Earlsfield Tennis",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Park courts, SW18 4SR",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching programme at Garratt Park courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://clubspark.lta.org.uk/spencerlawntennisclub/Coaching/Camps",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-17",
    name: "Better Leisure Earlsfield Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Wandle Recreation Centre, SW18 4DN",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Wandle Recreation Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/what-we-offer/lessons-and-courses/swimming",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-18",
    name: "Earlsfield Netball Club Juniors",
    provider: "Earlsfield Netball Club",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Park, SW18 4SR",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club at Garratt Park. Training and match play.",
    times: "Check club for class times",
    website: "https://spencernetball.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-19",
    name: "Chelsea FC Foundation",
    provider: "Chelsea FC Foundation",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Local school pitches, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact foundation for term fees",
    age: "5–14",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching hub across local school pitches in Earlsfield, run by the Chelsea FC Foundation. Academy pathway.",
    times: "Check foundation for class times",
    website: "https://chelseafoundation.chelseafc.com/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-20",
    name: "Art-K Earlsfield",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Lane studio, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Garratt Lane. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-wandsworth/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-21",
    name: "Earlsfield Art Collective",
    provider: "Earlsfield Art Collective",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Community studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Earlsfield community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-22",
    name: "Code Ninjas Earlsfield",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Earlsfield branch, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Earlsfield branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-23",
    name: "Digital Creators Earlsfield",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "School halls, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Earlsfield. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-24",
    name: "Earlsfield Chess Club Juniors",
    provider: "Earlsfield Chess Club",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "St Andrew's Church Hall, SW18 3HJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St Andrew's Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.facebook.com/ukchessacademy/?locale=en_GB",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-25",
    name: "Earlsfield Music School",
    provider: "Earlsfield Music School",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Garratt Lane studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Garratt Lane. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://swsmusic.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-26",
    name: "Rocksteady Earlsfield",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "Primary schools, SW18 catchment",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Earlsfield catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Earlsfield"],
  },
  {
    id: "ttefd-27",
    name: "Earlsfield Choir Academy",
    provider: "Earlsfield Choir Academy",
    type: "Club",
    neighbourhood: "Earlsfield",
    location: "St Andrew's Church, SW18 3HJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at St Andrew's Church in Earlsfield. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://swsmusic.co.uk/",
    neighbourhoods: ["Earlsfield"],
  },

  // ── EAST SHEEN ───────────────────────────────────────────────────────────────
  {
    id: "ttesh-1",
    name: "Kumon East Sheen Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Lane Centre, SW14 8LS",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Sheen Lane Centre. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/east-sheen/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-2",
    name: "Explore Learning Richmond Park",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Lane / Richmond edge centre, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre near Sheen Lane and the Richmond edge, serving the East Sheen catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-3",
    name: "Sheen Tuition Centre",
    provider: "Sheen Tuition Centre",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Upper Richmond Road West, SW14 8QT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Upper Richmond Road West. Maths, English, exam preparation and school support.",
    times: "Check centre for class times",
    website: "https://www.tutorhunt.com/locations/london/east-sheen/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-4",
    name: "Tutor Doctor East Sheen",
    provider: "Tutor Doctor",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Home-based tutoring network, SW14 catchment",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based tutoring network across the East Sheen catchment. One-to-one tutoring, GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.tutordoctor.co.uk/locations/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-5",
    name: "St Leonard's Homework Club",
    provider: "St Leonard's Church",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "St Leonard's Church Hall, SW14 8NU",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St Leonard's Church Hall. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-6",
    name: "Stagecoach East Sheen",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Tower House School, SW14 8QG",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Tower House School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/richmond",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-7",
    name: "Sheen Drama Academy",
    provider: "Sheen Drama Academy",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Christ Church Hall, SW14 7AF",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Drama academy at Christ Church Hall. Acting, improvisation and stagecraft.",
    times: "Check academy for class times",
    website: "https://www.shenestageschool.com/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-8",
    name: "Perform Drama East Sheen",
    provider: "Perform",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Local church halls, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across local church halls in East Sheen. Storytelling and confidence building.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-9",
    name: "Little Voices East Sheen",
    provider: "Little Voices",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "School halls, SW14 catchment",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["music", "drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts classes at school halls across the East Sheen catchment. Singing, acting and confidence training.",
    times: "Check provider for class times",
    website: "https://littlevoices.org.uk/locations/locations/south-west-london/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-10",
    name: "Sheen Ballet School",
    provider: "Sheen Ballet School",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Lane studios, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school at Sheen Lane studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.lxdanceacademy.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-11",
    name: "East Sheen Tennis Academy",
    provider: "East Sheen Tennis Academy",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Common / courts, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Tennis academy at Sheen Common. Coaching, drills and match play.",
    times: "Check academy for class times",
    website: "http://www.sheenltsc.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-12",
    name: "Richmond Park Running & Athletics Club",
    provider: "Richmond Park Running & Athletics Club",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Richmond Park, SW14 / SW13",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Sheen branch of the Richmond Park Running & Athletics Club. Running, athletics and endurance training.",
    times: "Check club for class times",
    website: "https://www.richmondrunningfestival.com/richmond-clubs/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-13",
    name: "Sheen FC Youth Academy",
    provider: "Sheen FC",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Common, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy on Sheen Common. Training and league matches.",
    times: "Check academy for class times",
    website: "https://sheenlions.football/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-14",
    name: "Barnes / Sheen Cricket Development Programme",
    provider: "Barnes / Sheen Cricket",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Palewell Common, SW14 8RE",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at Palewell Common, spanning Barnes and Sheen. Coaching, batting, bowling and matches.",
    times: "Check programme for class times",
    website: "https://www.barnescc.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-15",
    name: "Better Leisure East Sheen Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Sports Centre, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Sheen Sports Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/shene-sports-and-fitness-centre",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-16",
    name: "East Sheen Netball Club Juniors",
    provider: "East Sheen Netball Club",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Common, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club on Sheen Common. Training and match play.",
    times: "Check club for class times",
    website: "https://richmondtwickenhamfamilyhub.com/netstars-netball/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-17",
    name: "Palewell Common Football Coaching",
    provider: "Palewell Common Football",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Palewell Common, SW14 8RE",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Football coaching at Palewell Common. Grassroots development for a wide age range.",
    times: "Check provider for class times",
    website: "https://www.kidsworks.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-18",
    name: "Art-K East Sheen",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Lane studio, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Sheen Lane. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-19",
    name: "Sheen Art House",
    provider: "Sheen Art House",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Upper Richmond Road West, SW14 8QT",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on Upper Richmond Road West. Fine art and portfolio development.",
    times: "Check studio for class times",
    website: "https://sheenartists.com/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-20",
    name: "Code Ninjas East Sheen",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "East Sheen branch, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the East Sheen branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-21",
    name: "Digital Creators Sheen",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "School halls, SW14 catchment",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in East Sheen. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-22",
    name: "East Sheen Chess Club Juniors",
    provider: "East Sheen Chess Club",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "St Leonard's Church Hall, SW14 8NU",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St Leonard's Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.rjcc.org.uk/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-23",
    name: "East Sheen Music School",
    provider: "East Sheen Music School",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Sheen Lane studios, SW14 8LP",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school at Sheen Lane studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-24",
    name: "Rocksteady East Sheen",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Primary schools, SW14 catchment",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the East Sheen catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["East Sheen"],
  },
  {
    id: "ttesh-25",
    name: "Sheen Choir Academy",
    provider: "Sheen Choir Academy",
    type: "Club",
    neighbourhood: "East Sheen",
    location: "Christ Church, SW14 7AF",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at Christ Church in East Sheen. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://sheengatechoir.co.uk/",
    neighbourhoods: ["East Sheen"],
  },

  // ── HAMPTON ──────────────────────────────────────────────────────────────────
  {
    id: "tthmp-1",
    name: "Kumon Hampton Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill Church Hall, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Hampton Hill Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/peterborough-hampton",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-2",
    name: "Explore Learning Kingston",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Kingston / Hampton border centre, KT1 1TP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on the Kingston/Hampton border, serving the Hampton catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/kingston-upon-thames/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-3",
    name: "Hampton Tutors Collective",
    provider: "Hampton Tutors Collective",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Home-based network, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across the Hampton catchment. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.hamptontutors.com/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-4",
    name: "Hampton Junior Homework Club",
    provider: "Hampton Community Centre",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Community Centre, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact centre for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Homework club at Hampton Community Centre. Homework support, literacy and numeracy.",
    times: "Check centre for class times",
    website: "",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-5",
    name: "Stagecoach Hampton",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill Theatre School, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Hampton Hill Theatre School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/teddington",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-6",
    name: "Hampton Youth Theatre Company",
    provider: "Hampton Youth Theatre Company",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill Theatre, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact company for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check company for term-time schedule",
    description: "Youth theatre company at Hampton Hill Theatre. Acting, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check company for class times",
    website: "https://www.starlightytc.com/-ruislip-adult-new-1",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-7",
    name: "Perform Drama Hampton",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Church halls (Hampton area), TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in the Hampton area. Drama, improvisation and storytelling.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/drama-classes/hampton",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-8",
    name: "Hampton Ballet School",
    provider: "Hampton Ballet School",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Local dance studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Hampton dance studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://hamptonballettheatreschool.com/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-9",
    name: "Hampton Karate Club",
    provider: "Hampton Karate Club",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Sports & Fitness Centre, TW12 1AQ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Hampton Sports & Fitness Centre. Discipline, grading and sparring.",
    times: "Check club for class times",
    website: "https://www.gkrkarate.com/locations/listing/gkr-karate-hampton/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-10",
    name: "Hampton Judo Academy",
    provider: "Hampton Judo Academy",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Local school sports halls, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Judo academy across local school sports halls in Hampton. Grappling and discipline training.",
    times: "Check academy for class times",
    website: "https://clubfinder.britishjudo.org.uk/england/middlesex/hampton/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-11",
    name: "Hampton Football Academy",
    provider: "Hampton Football Academy",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Bushy Park sports fields, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Bushy Park sports fields. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://hamrichfc.com/pages/academy",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-12",
    name: "Bushy Park Athletics & Running Club",
    provider: "Bushy Park Athletics & Running Club",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Bushy Park, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Athletics and running club at Bushy Park. Running, athletics and endurance training.",
    times: "Check club for class times",
    website: "https://bushyparksportsclub.org.uk/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-13",
    name: "Hampton Tennis Coaching Programme",
    provider: "Hampton Tennis",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Carlisle Park Tennis Courts, TW12 2BX",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching programme at Carlisle Park Tennis Courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://www.twtennis.co.uk/classes",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-14",
    name: "Hampton Swimming Lessons",
    provider: "Hampton Pool",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Pool, TW12 2ST",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Hampton Pool. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://hamptonpool.co.uk/kids/childrens-swimming-lessons/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-15",
    name: "Hampton Netball Club Juniors",
    provider: "Hampton Netball Club",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Local school courts, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across local school courts in Hampton. Training and match play.",
    times: "Check club for class times",
    website: "https://hamptonnetball.weebly.com/juniors.html",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-16",
    name: "Thames Young Rowing Academy",
    provider: "Thames Young Rowing Academy",
    type: "Club",
    neighbourhood: "Hampton",
    location: "River Thames (Hampton stretch), TW12 riverfront",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior rowing academy along the Hampton reach of the River Thames. Water training and technique coaching for teens.",
    times: "Check academy for class times",
    website: "https://www.thamesrc.co.uk/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-17",
    name: "Hampton Cricket Development Programme",
    provider: "Hampton Cricket",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Bushy Park cricket pitches, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at the Bushy Park cricket pitches. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://hamptonhillcricket.co.uk/pages/juniors",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-18",
    name: "Art-K Hampton",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill studio, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio in Hampton Hill. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-teddington/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-19",
    name: "Hampton Art Collective",
    provider: "Hampton Art Collective",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Community studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Hampton community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "https://hamptonartstudios.com/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-20",
    name: "Code Ninjas Hampton",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton branch, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Hampton branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://hamptonschool.org.uk/co-curricular/school-clubs-societies/code-ninjas/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-21",
    name: "Hampton Robotics & Coding Club",
    provider: "Hampton Robotics & Coding Club",
    type: "Club",
    neighbourhood: "Hampton",
    location: "School halls, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics and coding club across school halls in Hampton. Programming and STEM projects.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-22",
    name: "Hampton Chess Club Juniors",
    provider: "Hampton Chess Club",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Library Hall, TW12 1NT",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Hampton Library Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.rjcc.org.uk/junior-tournaments/ljcc-london-junior-chess-championship/hampton-junior-chess-congress/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-23",
    active: false, // marked not running in Oct 2026 review
    name: "Hampton Music School",
    provider: "Hampton Music School",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school at Hampton Hill studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-24",
    name: "Rocksteady Hampton",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Primary schools, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Hampton catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Hampton"],
  },
  {
    id: "tthmp-25",
    name: "Hampton Choir Academy",
    provider: "Hampton Choir Academy",
    type: "Club",
    neighbourhood: "Hampton",
    location: "Hampton Hill Theatre Church Hall, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at Hampton Hill Theatre Church Hall. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.chapelroyalhamptoncourt.org.uk/the-choir/",
    neighbourhoods: ["Hampton"],
  },

  // ── HAMPTON HILL ─────────────────────────────────────────────────────────────
  {
    id: "tthhl-1",
    name: "Kumon Hampton Hill Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "St James's Church Hall, TW12 1DG",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at St James's Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/teddington/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-2",
    name: "Explore Learning",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Kingston Road / Hampton Hill edge centre, TW11 8SX",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on the Kingston Road/Hampton Hill edge. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-3",
    name: "First Class Learning Hampton Hill",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Local school halls, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition programme across local school halls in Hampton Hill.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/hampton/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-4",
    name: "Hampton Hill Tuition Centre",
    provider: "Hampton Hill Tuition Centre",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "High Street studio, TW12 1NJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Hampton Hill High Street. Maths, English, exam preparation and school support.",
    times: "Check centre for class times",
    website: "https://www.maldenoaks.org/page/?title=Hampton+Hill&pid=577",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-5",
    name: "St James's Homework Club",
    provider: "St James's Church",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "St James's Church Hall, TW12 1DG",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St James's Church Hall. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-6",
    name: "Stagecoach Hampton Hill",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill Theatre School, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Hampton Hill Theatre School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/teddington",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-7",
    name: "Hampton Hill Youth Theatre",
    provider: "Hampton Hill Youth Theatre",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill Theatre, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact theatre for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre at Hampton Hill Theatre. Drama, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.yat.org.uk/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-8",
    name: "Perform Drama Hampton Hill",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Church halls (High Street), TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls on Hampton Hill High Street. Drama, improvisation and storytelling.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/drama-classes/hampton",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-9",
    name: "Hampton Hill Ballet School",
    provider: "Hampton Hill Ballet School",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Local studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Hampton Hill studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.laurenhaithdance.com/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-10",
    name: "Fulwell Dance Academy",
    provider: "Fulwell Dance Academy",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Fulwell / Hampton Hill studios, TW12 / TW11",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "4–16",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across Fulwell and Hampton Hill studios. Ballet, jazz and contemporary dance.",
    times: "Check academy for class times",
    website: "https://ballet-bears.classforkids.io/venue/2/fulwell-methodist-church",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-11",
    name: "Hampton Hill Karate Club",
    provider: "Hampton Hill Karate Club",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill Community Centre, TW12 1NY",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at Hampton Hill Community Centre. Discipline and grading system.",
    times: "Check club for class times",
    website: "https://wildcatscombat.com/kids-martial-arts-classes-hampton-richmond/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-12",
    name: "Hampton Hill Judo Academy",
    provider: "Hampton Hill Judo Academy",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "School sports halls, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Judo academy across school sports halls in Hampton Hill. Grappling and discipline training.",
    times: "Check academy for class times",
    website: "https://www.ipponjudolondon.com/the-hampton-hub.html",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-13",
    name: "Bushy Park Mini Football Academy",
    provider: "Bushy Park Mini Football Academy",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Bushy Park edge fields, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy based at the Bushy Park edge fields, serving Hampton Hill. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://wemakefootballers.com/uk/teddington/near/bushy-park",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-14",
    name: "Hampton Hill Tennis Coaching",
    provider: "Hampton Hill Tennis",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Fulwell Tennis Courts, TW12 1JQ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Fulwell Tennis Courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://www.twtennis.co.uk/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-15",
    name: "Hampton Hill Swimming Lessons",
    provider: "Teddington Pools & Fitness Centre",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Teddington Pools & Fitness Centre (catchment users), TW11 9NN",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Teddington Pools & Fitness Centre, serving Hampton Hill families. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://hamptonpool.co.uk/kids/childrens-swimming-lessons/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-16",
    name: "Hampton Hill Netball Club Juniors",
    provider: "Hampton Hill Netball Club",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Local school courts, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across local school courts in Hampton Hill. Training and match play.",
    times: "Check club for class times",
    website: "https://hamptonnetball.weebly.com/juniors.html",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-17",
    name: "Bushy Park Athletics",
    provider: "Bushy Park Athletics",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Bushy Park, TW12 2EJ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Hampton Hill group of the Bushy Park athletics club. Running, athletics and endurance training.",
    times: "Check club for class times",
    website: "https://bushyparksportsclub.org.uk/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-18",
    name: "Art-K Hampton Hill",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "High Street studio, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Hampton Hill High Street. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-teddington/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-19",
    name: "Hampton Hill Art Collective",
    provider: "Hampton Hill Art Collective",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Community studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Hampton Hill community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-20",
    name: "Code Ninjas Hampton Hill",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill branch, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Hampton Hill branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-hns/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-21",
    name: "Hampton Hill Robotics Club",
    provider: "Hampton Hill Robotics Club",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "School halls, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics club across school halls in Hampton Hill. Programming and STEM projects.",
    times: "Check club for class times",
    website: "https://www.robobrain.academy.co.uk",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-22",
    name: "Hampton Hill Chess Club Juniors",
    provider: "Hampton Hill Chess Club",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "St James's Church Hall, TW12 1DG",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St James's Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.chessmates.club/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-23",
    name: "Hampton Hill Music School",
    provider: "Hampton Hill Music School",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "High Street studios, TW12",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Hampton Hill High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-24",
    name: "Rocksteady Hampton Hill",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Primary schools, TW12 catchment",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Hampton Hill catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Hampton Hill"],
  },
  {
    id: "tthhl-25",
    name: "Hampton Hill Choir Academy",
    provider: "Hampton Hill Choir Academy",
    type: "Club",
    neighbourhood: "Hampton Hill",
    location: "Hampton Hill Theatre Church Hall, TW12 1NZ",
    postcodeSector: "TW12",
    distanceMiles: 10.3,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at Hampton Hill Theatre Church Hall. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://e-voice.org.uk/hhcc/",
    neighbourhoods: ["Hampton Hill"],
  },

  // ── HAMPTON WICK ─────────────────────────────────────────────────────────────
  {
    id: "tthpw-1",
    name: "Kumon Hampton Wick Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Hampton Wick Baptist Church Hall, KT1 4AP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Hampton Wick Baptist Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/centre-search-results",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-2",
    name: "Explore Learning Kingston",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Kingston town centre edge, KT1 1TP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on the edge of Kingston town centre, serving the Hampton Wick catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/kingston-upon-thames/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-3",
    name: "Kingston Tutors Collective",
    provider: "Kingston Tutors Collective",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Home-based network, KT1 catchment",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network serving Hampton Wick users. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-4",
    name: "Hampton Wick Homework Club",
    provider: "St John the Baptist Church",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "St John the Baptist Church Hall, KT1 4DA",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St John the Baptist Church Hall. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-5",
    name: "Stagecoach Kingston",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Nearby Kingston venues, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts term-time classes at nearby Kingston venues, serving the Hampton Wick catchment. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.stagecoach.co.uk/kingstonandsurbiton",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-6",
    name: "Hampton Wick Drama Workshop",
    provider: "Hampton Wick Drama Workshop",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Church halls (Hampton Wick), KT1 4DA",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Hampton Wick church halls. Drama, improvisation and performance skills.",
    times: "Check workshop for class times",
    website: "https://www.dragondrama.co.uk/contact.php",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-7",
    name: "Perform Drama Hampton Wick",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Local community halls, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across local community halls in Hampton Wick. Storytelling and confidence building.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-8",
    name: "Kingston Ballet School",
    provider: "Kingston Ballet School",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Kingston studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Kingston studios, serving Hampton Wick users. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "http://www.kingstonballetschool.com/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-9",
    name: "Hampton Wick Football Academy",
    provider: "Hampton Wick Football Academy",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Bushy Park edge pitches, KT1 4AZ",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at the Bushy Park edge pitches. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.hamptonrangers.co.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-10",
    name: "Bushy Park Athletics",
    provider: "Bushy Park Athletics",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Bushy Park, KT1 / TW12",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Hampton Wick group of the Bushy Park athletics club. Running, athletics and endurance training.",
    times: "Check club for class times",
    website: "https://bushyparksportsclub.org.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-11",
    name: "Hampton Wick Tennis Coaching",
    provider: "Hampton Wick Tennis",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Canbury Gardens courts, KT1 4AU",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Canbury Gardens courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://besttennis.co.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-12",
    name: "Kingston Rowing Development",
    provider: "Kingston Rowing",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "River Thames (Hampton Wick stretch), KT1 riverfront",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact programme for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Rowing development programme along the Hampton Wick stretch of the River Thames. Water training and technique coaching for teens.",
    times: "Check programme for class times",
    website: "https://www.kingstonrc.co.uk/rowing/squads/development",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-13",
    name: "Hampton Wick Netball Club Juniors",
    provider: "Hampton Wick Netball Club",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Local school courts, KT1 catchment",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across local school courts in Hampton Wick. Training and match play.",
    times: "Check club for class times",
    website: "https://hamptonnetball.weebly.com/juniors.html",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-14",
    name: "Better Leisure Kingston",
    provider: "Better",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Kingfisher Leisure Centre, KT1 3PB",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Kingfisher Leisure Centre, serving Hampton Wick families. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/kensington-and-chelsea/kensington-leisure-centre",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-15",
    name: "Art-K Kingston",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Kingston studio, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio in Kingston, serving the Hampton Wick catchment. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-kingston",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-16",
    name: "Hampton Wick Art Collective",
    provider: "Hampton Wick Art Collective",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Community studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Hampton Wick community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "https://www.rupturexibit.com/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-17",
    name: "Code Ninjas Kingston",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Kingston branch, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Kingston branch of Code Ninjas, serving Hampton Wick users. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-sry/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-18",
    name: "Digital Creators Kingston",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "School halls, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Kingston, serving the Hampton Wick catchment. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-19",
    name: "Hampton Wick Chess Club Juniors",
    provider: "Hampton Wick Chess Club",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "St John the Baptist Church Hall, KT1 4DA",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St John the Baptist Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://rtchessclub.com/junior-chess/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-20",
    name: "Hampton Wick Music School",
    provider: "Hampton Wick Music School",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Local studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school across local Hampton Wick studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.outloudmusicschool.co.uk/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-21",
    name: "Rocksteady Kingston",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Primary schools, KT1 catchment",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in Hampton Wick primary schools, part of the Rocksteady Kingston network.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Hampton Wick"],
  },
  {
    id: "tthpw-22",
    name: "Hampton Wick Choir Academy",
    provider: "Hampton Wick Choir Academy",
    type: "Club",
    neighbourhood: "Hampton Wick",
    location: "Church halls, KT1 4DA",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy across Hampton Wick church halls. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.hamptonchoral.org.uk/",
    neighbourhoods: ["Hampton Wick"],
  },

  // ── KEW ──────────────────────────────────────────────────────────────────────
  {
    id: "ttkew-1",
    name: "Kumon Kew Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Kew",
    location: "St Luke's Church Hall, TW9 3LU",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at St Luke's Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.com/kew-gardens",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-2",
    name: "Explore Learning Richmond",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Kew",
    location: "Richmond town centre, TW9 1HY",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Richmond town centre, serving the Kew catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/london-hampton/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-3",
    name: "Kew Tutors Collective",
    provider: "Kew Tutors Collective",
    type: "Club",
    neighbourhood: "Kew",
    location: "Home-based network, TW9 catchment",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across the Kew catchment. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.kewtutors.co.uk/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-4",
    name: "St Luke's Homework Club",
    provider: "St Luke's Church",
    type: "Club",
    neighbourhood: "Kew",
    location: "St Luke's Church, TW9 3LU",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St Luke's Church. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-5",
    name: "Stagecoach Kew",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Community Centre, TW9 3HJ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Performing arts term-time classes at Kew Community Centre. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.stagecoach.co.uk/kew",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-6",
    name: "Kew Youth Theatre Company",
    provider: "Kew Youth Theatre Company",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Gardens / local halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact company for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check company for term-time schedule",
    description: "Youth theatre company around Kew Gardens and local halls. Drama, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check company for class times",
    website: "https://www.stagecoach.co.uk/kew",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-7",
    name: "Perform Drama Kew",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Kew",
    location: "Church halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes at church halls in Kew. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/drama-classes/kew",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-8",
    name: "Kew Ballet School",
    provider: "Kew Ballet School",
    type: "Club",
    neighbourhood: "Kew",
    location: "Local dance studios, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Kew dance studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.kewacademydance.co.uk/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-9",
    name: "Kew Music Academy",
    provider: "Kew Music Academy",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Village studios, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Kew Village studios. Piano, violin, guitar and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.essentialsurrey.co.uk/locations/kew-music-academy/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-10",
    name: "Royal Academy-linked Junior Music",
    provider: "Royal Academy-linked Junior Music",
    type: "Club",
    neighbourhood: "Kew",
    location: "Richmond/Kew studios, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Classical music training with Royal Academy links, across Richmond/Kew studios. Orchestral preparation for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.ram.ac.uk/courses/junior-academy-course",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-11",
    name: "Rocksteady Kew",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Kew",
    location: "Primary schools, TW9 catchment",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Kew catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-12",
    name: "Kew Choir Academy",
    provider: "Kew Choir Academy",
    type: "Club",
    neighbourhood: "Kew",
    location: "St Luke's Church, TW9 3LU",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at St Luke's Church in Kew. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "http://stanneskew.org.uk/children-families/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-13",
    name: "Kew Tennis Coaching Programme",
    provider: "Kew Tennis",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Gardens Tennis Courts, TW9 3AF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching programme at Kew Gardens Tennis Courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://clubspark.lta.org.uk/k5tenniscoaching",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-14",
    name: "Richmond Park Running Club",
    provider: "Richmond Park Running Club",
    type: "Club",
    neighbourhood: "Kew",
    location: "Richmond Park, TW9 / SW13",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Kew access group of the Richmond Park Running Club. Running, athletics and endurance training.",
    times: "Check club for class times",
    website: "https://www.richmondrunningfestival.com/richmond-clubs/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-15",
    name: "Kew FC Youth Academy",
    provider: "Kew FC",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Riverside pitches, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at the Kew Riverside pitches. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "http://www.richmondandkewfc.co.uk/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-16",
    name: "Kew Cricket Development Programme",
    provider: "Kew Cricket",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Green, TW9 3AP",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme on Kew Green. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.kewcc.com/a/juniors-68779.html?page=1",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-17",
    name: "Kew Rowing Association Juniors",
    provider: "Kew Rowing Association",
    type: "Club",
    neighbourhood: "Kew",
    location: "River Thames (Kew stretch), TW9 riverside",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact association for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check association for term-time schedule",
    description: "Junior rowing programme along the Kew stretch of the River Thames. Water training and technique coaching for teens.",
    times: "Check association for class times",
    website: "https://www.chiswickrowingtrust.co.uk/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-18",
    name: "Better Leisure Richmond",
    provider: "Better",
    type: "Club",
    neighbourhood: "Kew",
    location: "Pools on the Park, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Pools on the Park, serving Kew families. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/shene-sports-and-fitness-centre",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-19",
    name: "Kew Netball Club Juniors",
    provider: "Kew Netball Club",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Riverside / school courts, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across Kew Riverside and school courts. Training and match play.",
    times: "Check club for class times",
    website: "https://wljnl.englandnetball.org/information/wljnl-clubs",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-20",
    name: "Art-K Kew",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Kew",
    location: "Kew Village studio, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio in Kew Village. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-21",
    name: "Kew Art Collective",
    provider: "Kew Art Collective",
    type: "Club",
    neighbourhood: "Kew",
    location: "Community studios, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Kew community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "https://kewstudio.org/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-22",
    name: "Kew Chess Club Juniors",
    provider: "Kew Chess Club",
    type: "Club",
    neighbourhood: "Kew",
    location: "St Luke's Church Hall, TW9 3LU",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St Luke's Church Hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://www.rjcc.org.uk/junior-tournaments/ljcc-london-junior-chess-championship/hampton-junior-chess-congress/hampton-congress-details/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-23",
    name: "Code Ninjas Richmond",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Kew",
    location: "Richmond branch, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Richmond branch of Code Ninjas, serving the Kew catchment. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/richmond-bc-ca/",
    neighbourhoods: ["Kew"],
  },
  {
    id: "ttkew-24",
    name: "Digital Creators Kew",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Kew",
    location: "School halls, TW9 catchment",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Kew. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Kew"],
  },

  // ── KINGSTON ─────────────────────────────────────────────────────────────────
  {
    id: "ttkng-1",
    name: "Kumon Kingston Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston Town Hall area, KT1 1EU",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre near Kingston Town Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/kingston-hill",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-2",
    name: "Explore Learning Kingston",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston town centre, KT1 1TP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Kingston town centre. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/kingston-upon-thames/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-3",
    name: "Kip McGrath Kingston",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Eden Street, KT1 1BW",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Eden Street in Kingston. English, maths tuition and exam support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-4",
    name: "Kingston Tutors Collective",
    provider: "Kingston Tutors Collective",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Home-based + micro-centres, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Private tutoring across home settings and micro-centres in Kingston. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-5",
    name: "The Study Lounge Kingston",
    provider: "The Study Lounge",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Clarence Street, KT1 1NX",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic coaching centre on Clarence Street. Revision and exam preparation for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-6",
    name: "Kingston Homework Hub",
    provider: "Kingston Library",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston Library, KT1 1EU",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact hub for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Homework hub at Kingston Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://kr.afcinfo.org.uk/pages/community-information/information-and-advice/family-hubs/family-hubs-kingston/family-hubs-kingston-services",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-7",
    name: "Stagecoach Kingston",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston Grammar / local halls, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts term-time classes at Kingston Grammar and local halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.stagecoach.co.uk/search-results?location=Kingston",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-8",
    name: "Kingston Youth Theatre Company",
    provider: "Kingston Youth Theatre Company",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Rose Theatre Kingston, KT1 1HL",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact company for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check company for term-time schedule",
    description: "Youth theatre company at the Rose Theatre Kingston. Acting, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check company for class times",
    website: "https://www.rosetheatre.org/rose-youth-theatre-trlc",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-9",
    name: "Perform Drama Kingston",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Church halls (central Kingston), KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across central Kingston church halls. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/drama-classes/kingston",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-10",
    name: "Rose Theatre Academy",
    provider: "Rose Theatre",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Rose Theatre, KT1 1HL",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Professional theatre training academy at the Rose Theatre. Workshops for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.rosetheatre.org/rose-youth-theatre-trlc",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-11",
    name: "Kingston Ballet School",
    provider: "Kingston Ballet School",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Kingston studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.kingstonballetschool.com/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-12",
    name: "Kingston Dance Academy",
    provider: "Kingston Dance Academy",
    type: "Club",
    neighbourhood: "Kingston",
    location: "High Street studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across Kingston High Street studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.danceshack.co.uk/locations/kingston",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-13",
    name: "Kingston FC Academy",
    provider: "Kingston FC",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingsmeadow / local pitches, KT1 3PB",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Kingsmeadow and local pitches. Coaching and development squads.",
    times: "Check academy for class times",
    website: "https://www.afckingstonyouth.co.uk/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-14",
    name: "AFC Wimbledon Foundation",
    provider: "AFC Wimbledon Foundation",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingsmeadow, KT1 3PB",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact foundation for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching delivered at Kingsmeadow by the AFC Wimbledon Foundation. Elite development pathways.",
    times: "Check foundation for class times",
    website: "https://afcwimbledonfoundation.com/football-for-all",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-15",
    name: "Kingston Rugby Youth Programme",
    provider: "Kingston Rugby",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Richmond Road / local fields, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Youth rugby programme across Richmond Road and local fields in Kingston. Coaching, matches and skills.",
    times: "Check programme for class times",
    website: "https://www.kingstonrfc.com/junior",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-16",
    name: "Kingston Tennis & Rackets Club Coaching",
    provider: "Kingston Tennis & Rackets Club",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Canbury Gardens, KT1 3JS",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Canbury Gardens. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://www.kingston.gov.uk/leisure-and-community/parks-sports-and-activities/sports-and-leisure-club-directory/surbiton-racket-and-fitness-club",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-17",
    name: "Canbury Gardens Cricket Programme",
    provider: "Canbury Gardens Cricket",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Canbury Gardens, KT1 3JS",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket programme at Canbury Gardens. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-18",
    name: "Better Leisure Kingston Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingfisher Leisure Centre, KT1 3PB",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Kingfisher Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/teddington-pools-and-fitness-centre/activities",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-19",
    name: "Kingston Rowing Club Juniors",
    provider: "Kingston Rowing Club",
    type: "Club",
    neighbourhood: "Kingston",
    location: "River Thames (Kingston reach), KT1 riverfront",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club along the Kingston reach of the River Thames. Water training and technique coaching for teens.",
    times: "Check club for class times",
    website: "https://www.kingstonrc.co.uk/rowing/squads/juniors",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-20",
    name: "Surbiton & Kingston Athletics Club",
    provider: "Surbiton & Kingston Athletics Club",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingsmeadow track, KT1 3PB",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Athletics club at the Kingsmeadow track. Sprinting and endurance training.",
    times: "Check club for class times",
    website: "https://kingstonandpoly.org/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-21",
    name: "Art-K Kingston",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston High Street studio, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio on Kingston High Street. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-kingston/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-22",
    name: "Kingston Art School",
    provider: "Kingston Art School",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Market Place, KT1 1JS",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Art school at Market Place in Kingston. Fine art and portfolio development for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.kingston.ac.uk/about/faculties/kingston-school-of-art",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-23",
    name: "Kingston Chess Academy",
    provider: "Kingston Chess Academy",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston Library, KT1 1EU",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Chess academy at Kingston Library. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://kingstonchess.com/kingston-chess-academy/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-24",
    name: "Code Ninjas Kingston",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Clarence Street, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes on Clarence Street, Kingston. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.co.uk/epsom-sry-uk",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-25",
    name: "Kingston Robotics & Coding Hub",
    provider: "Kingston Robotics & Coding Hub",
    type: "Club",
    neighbourhood: "Kingston",
    location: "School halls / STEM centres, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact hub for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Robotics and coding hub across school halls and STEM centres in Kingston. Programming and STEM projects.",
    times: "Check hub for class times",
    website: "https://www.kingston.ac.uk/study/postgraduate/robotics-msc",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-26",
    name: "Digital Creators Kingston",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Community + school venues, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across community and school venues in Kingston. Digital media and tech skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-27",
    name: "Kingston Music School",
    provider: "Kingston Music School",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston High Street studios, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Kingston High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "http://www.kingstonmusicschool.com/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-28",
    name: "Kingston School of Music & Arts",
    provider: "Kingston School of Music & Arts",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Kingston town centre, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music and arts school in Kingston town centre. Classical and modern music training for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.kingstonmusicschool.com/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-29",
    name: "Rocksteady Kingston",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Kingston",
    location: "Primary schools, KT1",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across Kingston.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/schools/",
    neighbourhoods: ["Kingston"],
  },
  {
    id: "ttkng-30",
    name: "Kingston Choir Academy",
    provider: "Kingston Choir Academy",
    type: "Club",
    neighbourhood: "Kingston",
    location: "All Saints Church, KT1 1JP",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy at All Saints Church in Kingston. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://kingstonchoralsociety.org.uk/",
    neighbourhoods: ["Kingston"],
  },

  // ── MORTLAKE ─────────────────────────────────────────────────────────────────
  {
    id: "ttmtl-1",
    name: "Kumon Mortlake Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Mortlake Church Hall, SW14 8AH",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Mortlake Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/east-sheen/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-2",
    name: "Mortlake Tutors Collective",
    provider: "Mortlake Tutors Collective",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Home-based network, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Mortlake. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-3",
    name: "Explore Learning Richmond",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Richmond town centre, TW9 1HY",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Richmond town centre, serving the Richmond/Mortlake catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/find-a-centre/greater-london/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-4",
    name: "St Mary Magdalen Homework Club",
    provider: "St Mary Magdalen Church",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "St Mary Magdalen Church Hall, SW14 8HB",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at St Mary Magdalen Church Hall. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-5",
    name: "Mortlake Youth Study Group",
    provider: "Mortlake Youth Study Group",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Community hall, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Youth study group at a Mortlake community hall. Study skills and revision support.",
    times: "Check group for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-6",
    name: "Stagecoach Mortlake",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Local primary school halls, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts term-time classes across local primary school halls in Mortlake. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.stagecoach.co.uk/kew",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-7",
    name: "Mortlake Drama Workshop",
    provider: "Mortlake Drama Workshop",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Church halls, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Mortlake church halls. Acting, improvisation and stagecraft.",
    times: "Check workshop for class times",
    website: "https://www.perform.org.uk/drama-classes/richmond/holy-trinity-church-centre",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-8",
    name: "Perform Drama Mortlake",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Community venues, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across community venues in Mortlake. Storytelling and confidence building.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/drama-classes/richmond/holy-trinity-church-centre",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-9",
    name: "Mortlake Ballet School",
    provider: "Mortlake Ballet School",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Local studios (Barnes/Mortlake border), SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school at local studios on the Barnes/Mortlake border. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.schoolofdancemortlake.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-10",
    name: "Mortlake Music Collective",
    provider: "Mortlake Music Collective",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "St Mary Magdalen Hall, SW14 8HB",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact collective for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Music collective at St Mary Magdalen Hall. Piano, violin, guitar and ensemble training for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-11",
    name: "Rocksteady Mortlake",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Primary schools, SW14 catchment",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across the Mortlake catchment.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-12",
    name: "Mortlake Choir Academy",
    provider: "Mortlake Choir Academy",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Church venues, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy across Mortlake church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.mortlakecommunityassociation.org.uk/mortlake-community-singing/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-13",
    name: "Mortlake Tennis Coaching",
    provider: "Mortlake Tennis",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Barn Elms / Riverside courts (Mortlake access), SW13 / SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Barn Elms and riverside courts, with Mortlake access. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://teddytennisuk.co.uk/centre/richmond-mortlake/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-14",
    name: "Barnes & Mortlake Rowing Juniors",
    provider: "Barnes & Mortlake Rowing",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "River Thames (Mortlake stretch), SW14 riverside",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club along the Mortlake stretch of the River Thames. Water training and technique coaching for teens.",
    times: "Check club for class times",
    website: "https://www.maabc.com/join-maabc/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-15",
    name: "Mortlake FC Youth Academy",
    provider: "Mortlake FC",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Local pitches / Barn Elms, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy across local pitches and Barn Elms. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://kr.afcinfo.org.uk/pages/young-people/information-and-advice/youth-centres-and-activities/powerstation-youth-centre",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-16",
    name: "Barn Elms Sports Trust",
    provider: "Barn Elms Sports Trust",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Barn Elms Sports Centre, SW13 0DG",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact trust for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check trust for term-time schedule",
    description: "Multi-sport term-time programme at Barn Elms Sports Centre, popular with Mortlake families. Football, rugby and athletics.",
    times: "Check trust for class times",
    website: "https://www.bestfields.co.uk/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-17",
    name: "Mortlake Cricket Development Programme",
    provider: "Mortlake Cricket",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Barn Elms / local greens, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme across Barn Elms and local greens in Mortlake. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.crickettots.com/surrey/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-18",
    name: "Better Leisure Richmond",
    provider: "Better",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Pools on the Park, TW9 2SF",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Pools on the Park, serving Mortlake families. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-19",
    name: "Mortlake Netball Club Juniors",
    provider: "Mortlake Netball Club",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "School courts, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Mortlake. Training and match play.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-20",
    name: "Art-K Mortlake",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Mortlake/Barnes studio, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at a Mortlake/Barnes studio. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-teddington/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-21",
    name: "Mortlake Art Collective",
    provider: "Mortlake Art Collective",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Community studios, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Mortlake community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-22",
    name: "Code Ninjas Richmond",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Richmond branch, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Richmond branch of Code Ninjas, serving the Mortlake catchment. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/richmond-bc-ca/",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-23",
    name: "Mortlake Robotics Club",
    provider: "Mortlake Robotics Club",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "School halls, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics club across school halls in Mortlake. Coding and STEM projects.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-24",
    name: "Mortlake Chess Club Juniors",
    provider: "Mortlake Chess Club",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Church hall, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at a Mortlake church hall. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://azbukafoundation.org/?p=433",
    neighbourhoods: ["Mortlake"],
  },
  {
    id: "ttmtl-25",
    name: "Mortlake Music School",
    provider: "Mortlake Music School",
    type: "Club",
    neighbourhood: "Mortlake",
    location: "Local studios, SW14",
    postcodeSector: "SW14",
    distanceMiles: 5.6,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school across local Mortlake studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "http://richmondmusicacademy.co.uk/",
    neighbourhoods: ["Mortlake"],
  },

  // ── NEW MALDEN ───────────────────────────────────────────────────────────────
  {
    id: "ttnml-1",
    name: "Kumon New Malden Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Methodist Church Hall, KT3 3NZ",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at New Malden Methodist Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/new-malden",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-2",
    name: "Explore Learning New Malden",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "New Malden",
    location: "High Street centre, KT3 4EX",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on New Malden High Street. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/kingston-upon-thames/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-3",
    name: "Kip McGrath New Malden",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden town centre, KT3 4BY",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in New Malden town centre. English, maths tuition and exam support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-4",
    name: "Tutor Doctor New Malden",
    provider: "Tutor Doctor",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Home-based network, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based tutoring network across New Malden. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.tutordoctor.co.uk/east-surrey/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-5",
    name: "First Class Learning New Malden",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "New Malden",
    location: "School halls / church venues, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition programme across school halls and church venues in New Malden.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/new-malden/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-6",
    name: "New Malden Homework Hub",
    provider: "New Malden Library",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Library, KT3 4DQ",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact hub for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Homework hub at New Malden Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://merton.events.mylibrary.digital/event?id=375774",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-7",
    name: "Stagecoach New Malden",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Burlington Infant School, KT3 4SU",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact school for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Performing arts term-time classes at Burlington Infant School. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.stagecoach.co.uk/newmalden",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-8",
    name: "New Malden Youth Theatre",
    provider: "New Malden Youth Theatre",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Local community theatres, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact theatre for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across local New Malden community theatres. Drama, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.newmaldentheatreacademy.com/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-9",
    name: "Perform Drama New Malden",
    provider: "Perform",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Church halls, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in New Malden. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-10",
    name: "New Malden Ballet School",
    provider: "New Malden Ballet School",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Dance studios (High Street), KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school at dance studios on New Malden High Street. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.edadance.co.uk/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-11",
    name: "New Malden Dance Academy",
    provider: "New Malden Dance Academy",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Local studios, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across local New Malden studios. Ballet, jazz and street dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://newmaldenstudios.org/dance/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-12",
    name: "New Malden Karate Club",
    provider: "New Malden Karate Club",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Leisure Centre, KT3 4TA",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Karate club at New Malden Leisure Centre. Discipline and grading system.",
    times: "Check club for class times",
    website: "http://www.ekkassociation.com/classes",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-13",
    name: "New Malden Taekwondo Academy",
    provider: "New Malden Taekwondo Academy",
    type: "Club",
    neighbourhood: "New Malden",
    location: "School sports halls, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Taekwondo academy across school sports halls in New Malden. Sparring and competition preparation.",
    times: "Check academy for class times",
    website: "https://wildtigermartialarts.co.uk/taekwondo-new-malden/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-14",
    name: "New Malden Judo Club",
    provider: "New Malden Judo Club",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Community centre, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Judo club at a New Malden community centre. Grappling and discipline training.",
    times: "Check club for class times",
    website: "https://www.raystevensacademy.co.uk/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-15",
    name: "New Malden Football Academy",
    provider: "New Malden Football Academy",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Beverley Park / local pitches, KT3 4BJ",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Football academy at Beverley Park and local pitches. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://wemakefootballers.com/uk/new-malden",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-16",
    name: "AFC Wimbledon Foundation",
    provider: "AFC Wimbledon Foundation",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Local school pitches, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact foundation for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching delivered across local school pitches in New Malden by the AFC Wimbledon Foundation. Development squads.",
    times: "Check foundation for class times",
    website: "https://afcwimbledonfoundation.com/football-for-all",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-17",
    name: "New Malden Cricket Development Programme",
    provider: "New Malden Cricket",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Beverley Park, KT3 4BJ",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at Beverley Park. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://maldenwanderers.play-cricket.com/home",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-18",
    name: "New Malden Tennis Coaching",
    provider: "New Malden Tennis",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Sports Ground, KT3 4BY",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at New Malden Sports Ground. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://www.newmaldenclub.co.uk/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-19",
    name: "Better Leisure New Malden Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Leisure Centre, KT3 4TA",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at New Malden Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.placesleisure.org/centres/malden-centre/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-20",
    name: "New Malden Netball Club Juniors",
    provider: "New Malden Netball Club",
    type: "Club",
    neighbourhood: "New Malden",
    location: "School courts, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in New Malden. Training and match play.",
    times: "Check club for class times",
    website: "https://www.kdnl.co.uk/new-malden-netball-club.html",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-21",
    name: "Malden Manor Athletics & Running Group",
    provider: "Malden Manor Athletics & Running Group",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Local parks, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group across local parks in New Malden. Running, athletics and endurance training.",
    times: "Check group for class times",
    website: "",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-22",
    name: "New Malden Art Studio",
    provider: "New Malden Art Studio",
    type: "Club",
    neighbourhood: "New Malden",
    location: "High Street studio, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on New Malden High Street. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-23",
    name: "Art-K New Malden",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Studio centre, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio centre in New Malden. Drawing, painting and portfolio work.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-kingston/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-24",
    name: "New Malden Art Collective",
    provider: "New Malden Art Collective",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Community studios, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across New Malden community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "https://www.newmaldenpartnership.com/portfolio-collections/my-portfolio/community-arts",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-25",
    name: "Code Ninjas New Malden",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "New Malden",
    location: "High Street branch, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the New Malden High Street branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-26",
    name: "New Malden Robotics Club",
    provider: "New Malden Robotics Club",
    type: "Club",
    neighbourhood: "New Malden",
    location: "School halls, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics club across school halls in New Malden. STEM projects.",
    times: "Check club for class times",
    website: "https://kingston.events.mylibrary.digital/event?id=351665",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-27",
    name: "Digital Creators New Malden",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Community venues, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across community venues in New Malden. Coding and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-28",
    name: "New Malden Chess Club Juniors",
    provider: "New Malden Chess Club",
    type: "Club",
    neighbourhood: "New Malden",
    location: "New Malden Library, KT3 4DQ",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at New Malden Library. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://kingstonchess.com/juniors/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-29",
    name: "New Malden Music School",
    provider: "New Malden Music School",
    type: "Club",
    neighbourhood: "New Malden",
    location: "High Street studios, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on New Malden High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.reganschoolofmusic.com/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-30",
    name: "Rocksteady New Malden",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Primary schools, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across New Malden.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["New Malden"],
  },
  {
    id: "ttnml-31",
    name: "New Malden Choir Academy",
    provider: "New Malden Choir Academy",
    type: "Club",
    neighbourhood: "New Malden",
    location: "Church halls, KT3",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy across New Malden church halls. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.janewilkinson.com/teaching",
    neighbourhoods: ["New Malden"],
  },

  // ── PUTNEY ───────────────────────────────────────────────────────────────────
  {
    id: "ttptn-1",
    name: "Kumon Putney Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Methodist Church Hall, SW15 2JQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Putney Methodist Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/putney",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-2",
    name: "Explore Learning Putney",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney High Street centre, SW15 1TW",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Putney High Street. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-3",
    name: "Kip McGrath Putney",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Putney",
    location: "Upper Richmond Road, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Upper Richmond Road in Putney. English, maths tuition and exam support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-4",
    name: "Putney Tutors Collective",
    provider: "Putney Tutors Collective",
    type: "Club",
    neighbourhood: "Putney",
    location: "Home-based network, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Putney. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.putneytutors.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-5",
    name: "The Study Lounge Putney",
    provider: "The Study Lounge",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney High Street, SW15 1RT",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "6–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic coaching centre on Putney High Street. Revision and exam preparation for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.thestudy-lounge.com/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-6",
    name: "Putney Homework Hub",
    provider: "Putney Library",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Library, SW15 1PF",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact hub for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Homework hub at Putney Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-7",
    name: "Stagecoach Putney",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Putney",
    location: "Hotham Hall / school venues, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts term-time classes at Hotham Hall and school venues. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.stagecoach.co.uk/putneyandbarnes",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-8",
    name: "Putney Youth Theatre",
    provider: "Putney Arts Theatre",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Arts Theatre, SW15 1SN",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact theatre for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre at Putney Arts Theatre. Acting, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.putneyartstheatre.org.uk/group-64",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-9",
    name: "Putney Theatre Company Youth",
    provider: "Putney Theatre Company",
    type: "Club",
    neighbourhood: "Putney",
    location: "Local theatres, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact company for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check company for term-time schedule",
    description: "Youth arm of the Putney Theatre Company across local theatres. Drama, performance and production work for a wide age range up to 18.",
    times: "Check company for class times",
    website: "https://www.putneyartstheatre.org.uk/group-64",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-10",
    name: "Perform Drama Putney",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Putney",
    location: "Church halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in Putney. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-11",
    name: "Putney Ballet School",
    provider: "Putney Ballet School",
    type: "Club",
    neighbourhood: "Putney",
    location: "Dance studios (Putney High Street), SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school at dance studios on Putney High Street. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://thelittledanceacademy.com/putney/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-12",
    name: "Putney Dance Academy",
    provider: "Putney Dance Academy",
    type: "Club",
    neighbourhood: "Putney",
    location: "Local studios, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across local Putney studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://hmacademyofdance.com/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-13",
    name: "Putney FC Youth Academy",
    provider: "Putney FC",
    type: "Club",
    neighbourhood: "Putney",
    location: "Richardson Evans Memorial Fields, SW15 3PQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at the Richardson Evans Memorial Fields. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://wemakefootballers.com/uk/putney",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-14",
    name: "Barnes / Putney Rowing Club Juniors",
    provider: "Barnes / Putney Rowing Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "River Thames (Putney Embankment), SW15 riverside",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club along the Putney Embankment, spanning Barnes and Putney. Water training and technique coaching for teens.",
    times: "Check club for class times",
    website: "https://www.londonyouthrowing.com",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-15",
    name: "Putney Town Rowing Club Juniors",
    provider: "Putney Town Rowing Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Thames embankment, SW15 2JQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club at Putney Town Rowing Club. Racing and elite development for teens.",
    times: "Check club for class times",
    website: "https://www.putneytownrc.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-16",
    name: "Thames Rowing Club Juniors",
    provider: "Thames Rowing Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Embankment, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club at Thames Rowing Club, with Putney access. Regattas and elite training for teens.",
    times: "Check club for class times",
    website: "https://www.thamesrc.co.uk/news/2019/8/7/junior-rowing-at-thames",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-17",
    name: "Putney Cricket Club Youth Programme",
    provider: "Putney Cricket Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Lower Common, SW15 1LQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Youth cricket programme at Putney Lower Common. Coaching, batting and bowling.",
    times: "Check club for class times",
    website: "https://www.putneycricketclub.com/juniors/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-18",
    name: "Putney Lawn Tennis Club Coaching",
    provider: "Putney Lawn Tennis Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Lower Common, SW15 1LQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Putney Lower Common. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://www.pltc.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-19",
    name: "Better Leisure Putney Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Leisure Centre, SW15 1BL",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Putney Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/what-we-offer/lessons-and-courses/swimming",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-20",
    name: "Putney Netball Club Juniors",
    provider: "Putney Netball Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Local school courts, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across local school courts in Putney. Training and match play.",
    times: "Check club for class times",
    website: "https://www.putneynetball.com/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-21",
    name: "Richardson Evans Athletics Programme",
    provider: "Richardson Evans Athletics Programme",
    type: "Club",
    neighbourhood: "Putney",
    location: "Fields at Roehampton edge, SW15 3PQ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Athletics programme on fields at the Roehampton edge, near Putney. Running and endurance training.",
    times: "Check programme for class times",
    website: "https://www.wpcc.org.uk/the-commons/richardson-evans-memorial-playing-fields",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-22",
    name: "Art-K Putney",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney studio, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio in Putney. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-putney/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-23",
    name: "Putney Art Collective",
    provider: "Putney Art Collective",
    type: "Club",
    neighbourhood: "Putney",
    location: "Community studios, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Putney community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "https://www.psad.org.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-24",
    name: "Code Ninjas Putney",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney High Street, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes on Putney High Street with Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.com/locations/en-gb/gb-lnd/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-25",
    name: "Digital Creators Putney",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Putney",
    location: "School halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Putney. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-26",
    name: "Putney Chess Club Juniors",
    provider: "Putney Chess Club",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney Library, SW15 1PF",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Putney Library. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://putneychess.club/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-27",
    name: "Putney Music School",
    provider: "Putney Music School",
    type: "Club",
    neighbourhood: "Putney",
    location: "Putney High Street studios, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Putney High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.putneymusic.co.uk/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-28",
    name: "Rocksteady Putney",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Putney",
    location: "Primary schools, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across Putney.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Putney"],
  },
  {
    id: "ttptn-29",
    name: "Putney Choir Academy",
    provider: "Putney Choir Academy",
    type: "Club",
    neighbourhood: "Putney",
    location: "Local churches, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy across local Putney churches. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.putneychoralsoc.org.uk/",
    neighbourhoods: ["Putney"],
  },

  // ── RAYNES PARK ──────────────────────────────────────────────────────────────
  {
    id: "ttrpk-1",
    name: "Kumon Raynes Park Study Centre",
    provider: "Kumon",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Methodist Church Hall, SW20 8LA",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Raynes Park Methodist Church Hall. Maths and English through Kumon's structured independent learning programme.",
    times: "Check centre for class times",
    website: "https://www.kumon.co.uk/raynes-park/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-2",
    name: "Explore Learning Wimbledon",
    provider: "Explore Learning",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Wimbledon Broadway centre, SW19 1QG",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact centre for term fees",
    age: "4–14",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Wimbledon Broadway, serving the Raynes Park catchment. Maths and English tuition and 11+ preparation.",
    times: "Check centre for class times",
    website: "https://www.explorelearning.co.uk/tuition-centre/london-merton/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-3",
    name: "Kip McGrath Raynes Park",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park High Street, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Raynes Park High Street. English, maths tuition and exam support.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-4",
    name: "First Class Learning Raynes Park",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "School halls / church venues, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition programme across school halls and church venues in Raynes Park.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/raynes-park/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-5",
    name: "Raynes Park Tutors Collective",
    provider: "Raynes Park Tutors Collective",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Home-based network, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Raynes Park. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-6",
    name: "Raynes Park Homework Hub",
    provider: "Raynes Park Library",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Library, SW20 8EF",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact hub for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Homework hub at Raynes Park Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://merton.events.mylibrary.digital/event?id=375633",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-7",
    name: "Stagecoach Raynes Park",
    provider: "Stagecoach",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "West Wimbledon / school halls, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama", "dance"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts term-time classes across West Wimbledon and school halls. Singing, acting, dance and musical theatre for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.stagecoach.co.uk/mertonpark",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-8",
    name: "Raynes Park Youth Theatre",
    provider: "Raynes Park Youth Theatre",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Community theatres (Wimbledon fringe), SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact theatre for term fees",
    age: "6–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatres on the Wimbledon fringe. Drama, stagecraft and performance projects for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.lanternarts.org/childrens-theatre-club",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-9",
    name: "Perform Drama Raynes Park",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Church halls, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in Raynes Park. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-10",
    name: "Raynes Park Ballet School",
    provider: "Raynes Park Ballet School",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Local studios, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Raynes Park studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://motionartsdance.com/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-11",
    name: "Raynes Park Dance Academy",
    provider: "Raynes Park Dance Academy",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Wimbledon fringe studios, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across Wimbledon fringe studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.siegerisdance.com/raynes-park",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-12",
    name: "Raynes Park FC Youth Academy",
    provider: "Raynes Park FC",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Sports Ground, SW20 0LW",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Raynes Park Sports Ground. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.raynesparkvale.com/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-13",
    name: "Wimbledon FC Foundation",
    provider: "Wimbledon FC Foundation",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Local school pitches, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact foundation for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check foundation for term-time schedule",
    description: "Football coaching delivered across local school pitches in Raynes Park by the Wimbledon FC Foundation. Development squads.",
    times: "Check foundation for class times",
    website: "https://afcwimbledonfoundation.com/football-for-all",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-14",
    name: "Raynes Park Tennis Coaching",
    provider: "Raynes Park Tennis",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Cannon Hill / local courts, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Cannon Hill and local courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://raynespark.communitysport.aeltc.com/Tennis/HolidayCamps",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-15",
    name: "Raynes Park Cricket Development Programme",
    provider: "Raynes Park Cricket",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Cannon Hill Common, SW20 9BA",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at Cannon Hill Common. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.surreycricketfoundation.org/schools-cricket-day-surrey-south-london-2026/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-16",
    name: "Raynes Park Netball Club Juniors",
    provider: "Raynes Park Netball Club",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "School courts, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Raynes Park. Training and match play.",
    times: "Check club for class times",
    website: "https://www.gomammoth.co.uk/netball/wimbledon-raynes-park/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-17",
    name: "Better Leisure Raynes Park Swimming Lessons",
    provider: "Better",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "New Malden Leisure Centre (Raynes spillover), KT3 4TA",
    postcodeSector: "KT3",
    distanceMiles: 6.7,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at New Malden Leisure Centre, serving Raynes Park families as spillover capacity. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/what-we-offer/lessons-and-courses/swimming",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-18",
    name: "Raynes Park Athletics & Running Group",
    provider: "Raynes Park Athletics & Running Group",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Cannon Hill Common, SW20 9BA",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group at Cannon Hill Common. Running, athletics and endurance training.",
    times: "Check group for class times",
    website: "https://sportverse.co.uk/clubs/running/raynes-park-running",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-19",
    name: "Art-K Raynes Park",
    provider: "Art-K",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park studio, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact studio for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art classes at the Art-K studio in Raynes Park. Drawing, painting and sculpture.",
    times: "Check studio for class times",
    website: "https://www.art-k.co.uk/class-wimbledon-chase/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-20",
    name: "Raynes Park Art Collective",
    provider: "Raynes Park Art Collective",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Community studios, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Raynes Park community studios. Fine art, mixed media and portfolio work.",
    times: "Check collective for class times",
    website: "https://www.lanternarts.org/childrens-theatre-club",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-21",
    name: "Code Ninjas Raynes Park",
    provider: "Code Ninjas",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park branch, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact branch for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check branch for term-time schedule",
    description: "Coding classes at the Raynes Park branch of Code Ninjas. Roblox, Minecraft and game design.",
    times: "Check branch for class times",
    website: "https://www.codeninjas.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-22",
    name: "Digital Creators Raynes Park",
    provider: "Digital Creators",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "School halls, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tech and coding club across school halls in Raynes Park. Coding, robotics and digital skills.",
    times: "Check provider for class times",
    website: "",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-23",
    name: "Raynes Park Chess Club Juniors",
    provider: "Raynes Park Chess Club",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Raynes Park Library, SW20 8EF",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Raynes Park Library. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://merton.events.mylibrary.digital/event?id=358094",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-24",
    name: "Raynes Park Music School",
    provider: "Raynes Park Music School",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "High Street studios, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school on Raynes Park High Street. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.rockpopschool.com/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-25",
    name: "Rocksteady Raynes Park",
    provider: "Rocksteady",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Primary schools, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["music"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Band-based music lessons delivered in primary schools across Raynes Park.",
    times: "Check provider for class times",
    website: "https://www.rocksteadymusicschool.com/",
    neighbourhoods: ["Raynes Park"],
  },
  {
    id: "ttrpk-26",
    name: "Raynes Park Choir Academy",
    provider: "Raynes Park Choir Academy",
    type: "Club",
    neighbourhood: "Raynes Park",
    location: "Church halls, SW20",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Choir academy across Raynes Park church halls. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.academychoirwimbledon.co.uk/",
    neighbourhoods: ["Raynes Park"],
  },

  // ── RICHMOND ─────────────────────────────────────────────────────────────────
  {
    id: "ttrch-1",
    name: "Richmond Rugby Club Juniors",
    provider: "Richmond Rugby Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Old Deer Park, TW9 2AZ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rugby section at Old Deer Park. Training, matches and development squads for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.richmondfc.co.uk/teams/242477",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-2",
    name: "Richmond Hockey Club Youth Section",
    provider: "Richmond Hockey Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Teddington / Richmond shared pitches, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Youth hockey section across shared Teddington/Richmond pitches. Field hockey training and leagues for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://richmondhockeyclub.com/juniorProgramme",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-3",
    name: "Richmond Athletics Club",
    provider: "Richmond Athletics Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Park / track facilities, TW10",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Athletics club at Richmond Park track facilities. Running, sprinting, endurance and competitions for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.rag-events.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-4",
    name: "Barnes Eagles FC",
    provider: "Barnes Eagles FC",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Barn Elms / Richmond park pitches, SW13",
    postcodeSector: "SW13",
    distanceMiles: 4.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Football club across Barn Elms and Richmond park pitches, serving the Richmond catchment. Coaching, leagues and development.",
    times: "Check club for class times",
    website: "https://www.barneseagles.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-5",
    name: "Richmond Gymnastics Association",
    provider: "Richmond Gymnastics Association",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Ice Rink / sports halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact association for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check association for term-time schedule",
    description: "Gymnastics association across Richmond Ice Rink and sports halls. Tumbling and balance training.",
    times: "Check association for class times",
    website: "https://richmondgymnastics.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-6",
    name: "Richmond Tennis Club Coaching",
    provider: "Richmond Tennis Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Hill courts, TW10",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Richmond Hill courts. Match play and drills.",
    times: "Check club for class times",
    website: "https://besttennis.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-7",
    name: "Old Deer Park Cricket Club Juniors",
    provider: "Old Deer Park Cricket Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Old Deer Park, TW9 2AZ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cricket section at Old Deer Park. Batting and bowling coaching for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.richmondcricketclub.com/a/juniors-44320.html?page=3",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-8",
    name: "Richmond Rowing Association Juniors",
    provider: "Richmond Rowing Association",
    type: "Club",
    neighbourhood: "Richmond",
    location: "River Thames (Richmond stretch), TW9 riverside",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact association for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check association for term-time schedule",
    description: "Junior rowing programme along the Richmond stretch of the River Thames. Water training and regattas for teens.",
    times: "Check association for class times",
    website: "https://www.richmondbridgeboatclub.com/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-9",
    name: "Thames Rowing Club Juniors",
    provider: "Thames Rowing Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Putney Embankment access training, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "13–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Elite rowing development pathway at Thames Rowing Club, with training access from Putney Embankment, serving Richmond athletes.",
    times: "Check club for class times",
    website: "https://www.thamesrc.co.uk/join-us",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-10",
    name: "Richmond Park Running Group Juniors",
    provider: "Richmond Park Running Group",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Park, TW10 5HS",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Junior running group in Richmond Park. Cross-country running and endurance training.",
    times: "Check group for class times",
    website: "https://riverthamesrunning.co.uk/junior-clubs/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-11",
    name: "Orleans Park School Enrichment Programme",
    provider: "Orleans Park School",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Twickenham/Richmond fringe, TW1 3BB",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact school for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Academic enrichment programme at Orleans Park School, on the Twickenham/Richmond fringe. STEM clubs, debate and revision support for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.orleanspark.school/students/beyond-the-curriculum",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-12",
    name: "Grey Court School Enrichment Hub",
    provider: "Grey Court School",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Ham, Richmond, TW10 7HN",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact school for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Academic enrichment hub at Grey Court School in Ham. STEM clubs and academic extension for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.greycourt.richmond.sch.uk/page/?title=Enrichment&pid=148",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-13",
    name: "Richmond Library Learning Hub",
    provider: "Richmond Library",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Library, TW9 1DN",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Learning hub at Richmond Library. Homework support, literacy and study groups.",
    times: "Check hub for class times",
    website: "https://www.richmond.gov.uk/services/libraries/libraries_whats_on/it_taster_sessions",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-14",
    name: "Richmond Debate Society Juniors",
    provider: "Richmond Debate Society",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Community halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact society for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Check society for term-time schedule",
    description: "Junior debate society across community halls in Richmond. Public speaking and debate training for a wide age range up to 18.",
    times: "Check society for class times",
    website: "https://littlevoices.org.uk/south-west-london/schools/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-15",
    name: "Richmond Maths Circle",
    provider: "Richmond Maths Circle",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Church halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact circle for term fees",
    age: "7–16",
    interests: ["stem"],
    dayOfWeek: "Check circle for term-time schedule",
    description: "Maths enrichment circle across church halls in Richmond. Problem solving and maths enrichment.",
    times: "Check circle for class times",
    website: "https://www.e4s.co.uk/organization/richmond-maths-ltd",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-16",
    name: "Richmond Youth Theatre",
    provider: "Richmond Youth Theatre",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Theatre Studio spaces, TW9 1QJ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre at Richmond Theatre studio spaces. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.atgtickets.com/shows/young-company/richmond-theatre/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-17",
    name: "Richmond Theatre Youth Academy",
    provider: "Richmond Theatre",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Theatre, TW9 1QJ",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact academy for term fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Professional acting workshops at Richmond Theatre. Youth academy for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.atgtickets.com/shows/young-company/richmond-theatre/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-18",
    name: "Richmond Dance Collective",
    provider: "Richmond Dance Collective",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Hill studios, TW10",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact collective for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Dance collective at Richmond Hill studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://richmonddancecollective.co.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-19",
    name: "Richmond Opera Youth Choir",
    provider: "Richmond Opera Youth Choir",
    type: "Club",
    neighbourhood: "Richmond",
    location: "St Mary Magdalene Church, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact choir for term fees",
    age: "7–18",
    interests: ["music"],
    dayOfWeek: "Check choir for term-time schedule",
    description: "Youth opera choir at St Mary Magdalene Church. Classical singing and choir performance for a wide age range up to 18.",
    times: "Check choir for class times",
    website: "https://richmondopera.org.uk/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-20",
    name: "Richmond Music Academy",
    provider: "Richmond Music Academy",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond town studios, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Richmond town studios. Piano, violin, guitar and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.richmondmusictrust.org.uk/site/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-21",
    name: "Richmond Youth Orchestra",
    provider: "Richmond Youth Orchestra",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Local school venues, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact orchestra for term fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Check orchestra for term-time schedule",
    description: "Youth orchestra across local school venues in Richmond. Orchestral training and ensemble performance for a wide age range up to 18.",
    times: "Check orchestra for class times",
    website: "https://www.richmondmusictrust.org.uk/site/ensembles/ryto-training-orchestra/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-22",
    name: "Richmond Chess Society Juniors",
    provider: "Richmond Chess Society",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Library, TW9 1DN",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact society for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check society for term-time schedule",
    description: "Junior chess society at Richmond Library. Coaching and tournaments.",
    times: "Check society for class times",
    website: "https://www.rjcc.org.uk/our-classes/",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-23",
    name: "Richmond Coding & Robotics Club",
    provider: "Richmond Coding & Robotics Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "School halls, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Coding and robotics club across school halls in Richmond. STEM projects.",
    times: "Check club for class times",
    website: "https://funtech.co.uk/locations/richmond",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-24",
    name: "Richmond Art Studio Collective",
    provider: "Richmond Art Studio Collective",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Richmond Hill studios, TW10",
    postcodeSector: "TW10",
    distanceMiles: 7.0,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art studio collective at Richmond Hill studios. Painting, sculpture and mixed media for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.richmond.gov.uk/services/arts/arts_development/artists_make_space_2024_25",
    neighbourhoods: ["Richmond"],
  },
  {
    id: "ttrch-25",
    name: "Richmond Youth Photography Club",
    provider: "Richmond Youth Photography Club",
    type: "Club",
    neighbourhood: "Richmond",
    location: "Community venues, TW9",
    postcodeSector: "TW9",
    distanceMiles: 7.1,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Youth photography club across community venues in Richmond. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://sharpshotsphotoclub.co.uk/richmond/",
    neighbourhoods: ["Richmond"],
  },

  // ── ROEHAMPTON ───────────────────────────────────────────────────────────────
  {
    id: "ttrmp-1",
    name: "Roehampton Tuition Centre",
    provider: "Roehampton Tuition Centre",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Lane community rooms, SW15 5PH",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Roehampton Lane community rooms. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.theprofs.co.uk/tutors-in-roehampton/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-2",
    name: "University of Roehampton Outreach Learning Hub",
    provider: "University of Roehampton",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "University campus, SW15 5PJ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact hub for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Outreach learning hub at the University of Roehampton campus. Study skills, mentoring and academic enrichment for a wide age range up to 18.",
    times: "Check hub for class times",
    website: "https://www.roehampton.ac.uk/schools-and-colleges-engagement/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-3",
    name: "Roehampton Homework Club",
    provider: "Roehampton Library",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Library, SW15 5NX",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at Roehampton Library. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "https://fis.wandsworth.gov.uk/advice/study-spaces/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-4",
    name: "Roehampton Youth Study Group",
    provider: "Roehampton Youth Study Group",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Community halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Youth study group across Roehampton community halls. Revision support and structured study.",
    times: "Check group for class times",
    website: "https://www.wandsworth.gov.uk/news/news-february-2026/new-youth-hub-opens-at-roehampton-to-support-play-creativity-and-fun-for-young-people/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-5",
    name: "Roehampton Park STEM Club",
    provider: "Roehampton Park STEM Club",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "School halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Roehampton. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.stem-club.co.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-6",
    name: "Roehampton Chess Society Juniors",
    provider: "Roehampton Chess Society",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Library, SW15 5NX",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact society for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check society for term-time schedule",
    description: "Junior chess society at Roehampton Library. Coaching and tournaments.",
    times: "Check society for class times",
    website: "https://www.rjcc.org.uk/category/classes/beginners/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-7",
    name: "Roehampton Coding Collective",
    provider: "Roehampton Coding Collective",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "University of Roehampton spaces, SW15 5PJ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective at University of Roehampton spaces. Programming, digital skills and game design.",
    times: "Check collective for class times",
    website: "https://uk.linkedin.com/company/roecodingsociety",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-8",
    name: "Roehampton FC Youth Academy",
    provider: "Roehampton FC",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Playing Fields, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Roehampton Playing Fields. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.roehamptonelitefc.com/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-9",
    name: "Roehampton Rangers Football Programme",
    provider: "Roehampton Rangers",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Richmond Park edge pitches, SW15 / TW10",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Football programme at the Richmond Park edge pitches. Training and development squads.",
    times: "Check programme for class times",
    website: "https://csrangers.co.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-10",
    name: "Roehampton Tennis Coaching",
    provider: "Roehampton Club",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Club courts, SW15 5LR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Roehampton Club courts. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://roehampton.communitysport.aeltc.com/Programmes",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-11",
    name: "Roehampton Cricket Development Programme",
    provider: "Roehampton Cricket",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Playing Fields, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at Roehampton Playing Fields. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.roehamptoncricketclub.com/a/juniors-56094.html",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-12",
    name: "Roehampton Athletics & Running Group",
    provider: "Roehampton Athletics & Running Group",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Richmond Park (Roehampton Gate), SW15 5JP",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group at the Roehampton Gate of Richmond Park. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-13",
    name: "Roehampton Netball Club Juniors",
    provider: "Roehampton Netball Club",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "School courts, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Roehampton. Training and match play.",
    times: "Check club for class times",
    website: "https://www.roehamptonclub.co.uk/junior",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-14",
    name: "Roehampton Swimming Lessons",
    provider: "Putney Leisure Centre",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Putney Leisure Centre (Roehampton catchment), SW15 / SW13",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Putney Leisure Centre, serving the Roehampton catchment. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.roehamptonclub.co.uk/articles/swimming-lessons",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-15",
    name: "Roehampton Golf Junior Academy",
    provider: "Roehampton Club",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton Club, SW15 5LR",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior golf academy at Roehampton Club. Coaching, technique and tournaments.",
    times: "Check academy for class times",
    website: "https://www.roehamptonclub.co.uk/articles/junior-golf-academy",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-16",
    name: "Roehampton Ballet School",
    provider: "Roehampton Ballet School",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Community studios, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Roehampton community studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.roehamptonstudent.com/activities/societies/society/dancesociety",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-17",
    name: "Roehampton Dance Collective",
    provider: "Roehampton Dance Collective",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "University halls / studios, SW15 5PJ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact collective for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Dance collective across university halls and studios. Jazz, contemporary and street dance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://richmonddancecollective.co.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-18",
    name: "Roehampton Drama Workshop",
    provider: "Roehampton Drama Workshop",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Community halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Roehampton community halls. Acting, improvisation and stagecraft.",
    times: "Check workshop for class times",
    website: "https://www.pqacademy.com/academies/putney/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-19",
    name: "Roehampton Youth Theatre",
    provider: "Roehampton Youth Theatre",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Local school theatres, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across local school theatres in Roehampton. Theatre production and acting for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.g64.org.uk/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-20",
    name: "Roehampton Music Academy",
    provider: "Roehampton Music Academy",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Community music rooms, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Roehampton community music rooms. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://worldheartbeat.org/music-bambinos/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-21",
    name: "Roehampton Choir Collective",
    provider: "Roehampton Choir Collective",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Church halls, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Roehampton church halls. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.holytrinityroehampton.org/",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-22",
    name: "Roehampton Art Studio",
    provider: "Roehampton Art Studio",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Roehampton University arts spaces, SW15 5PJ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio at Roehampton University arts spaces. Painting, sculpture and mixed media for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://www.roehamptonclub.co.uk/articles/painting-club",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-23",
    name: "Roehampton Art Collective",
    provider: "Roehampton Art Collective",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "Community studios, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Roehampton community studios. Fine art and portfolio development.",
    times: "Check collective for class times",
    website: "https://www.roehamptonclub.co.uk/articles/art-classes",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-24",
    name: "Roehampton Photography Club",
    provider: "Roehampton Photography Club",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "University + community venues, SW15",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across university and community venues in Roehampton. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.roehamptonclub.co.uk/articles/photoclub-classes",
    neighbourhoods: ["Roehampton"],
  },
  {
    id: "ttrmp-25",
    name: "Roehampton Music & Media Lab",
    provider: "University of Roehampton",
    type: "Club",
    neighbourhood: "Roehampton",
    location: "University of Roehampton, SW15 5PJ",
    postcodeSector: "SW15",
    distanceMiles: 3.9,
    cost: "Contact lab for term fees",
    age: "11–18",
    interests: ["stem", "music"],
    dayOfWeek: "Check lab for term-time schedule",
    description: "Music and media lab at the University of Roehampton. Digital media and audio production for a wide age range up to 18.",
    times: "Check lab for class times",
    website: "https://worldheartbeat.org/music-bambinos/",
    neighbourhoods: ["Roehampton"],
  },

  // ── SOUTHFIELDS ──────────────────────────────────────────────────────────────
  {
    id: "ttsfd-1",
    name: "Southfields Tuition Centre",
    provider: "Southfields Tuition Centre",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Replingham Road community rooms, SW18 5LY",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre at Replingham Road community rooms. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.gotutors.com/tuition-centre/gotutors-southfields",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-2",
    name: "Wimbledon Park Academic Hub",
    provider: "Wimbledon Park Academic Hub",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Wimbledon Park area halls, SW19 6PE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact hub for term fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Academic hub across Wimbledon Park area halls, serving the Southfields catchment. Study skills, exam preparation and mentoring for a wide age range up to 18.",
    times: "Check hub for class times",
    website: "",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-3",
    name: "Southfields Homework Club",
    provider: "Southfields Library",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Southfields Library, SW18 5LU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at Southfields Library. Homework support, literacy and numeracy.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-4",
    name: "Southfields Youth Study Group",
    provider: "Southfields Youth Study Group",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Church halls, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Youth study group across Southfields church halls. Revision support and structured learning.",
    times: "Check group for class times",
    website: "",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-5",
    name: "Southfields STEM Club",
    provider: "Southfields STEM Club",
    type: "Club",
    neighbourhood: "Southfields",
    location: "School halls, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Southfields. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.littlehandsldn.com/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-6",
    name: "Southfields Chess Academy Juniors",
    provider: "Southfields Chess Academy",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Southfields Library, SW18 5LU",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy at Southfields Library. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://chessacademy.uk/course/wimbledon-chess-rising-stars-5-8-y-o/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-7",
    name: "Southfields Coding Collective",
    provider: "Southfields Coding Collective",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Local school venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across local school venues in Southfields. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://www.wandsworthsw18.com/page/shared/common/conwandscoding001.htm",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-8",
    name: "Southfields FC Youth Academy",
    provider: "Southfields FC",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Southfields Recreation Ground, SW18 5JH",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Southfields Recreation Ground. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://fulltime.thefa.com/index.html?league=1428060&selectedSeason=780756890&selectedDivision=171410239&selectedCompetition=0&selectedFixtureGroupKey=1_274788617",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-9",
    name: "Wimbledon Park Football Development",
    provider: "Wimbledon Park Football Development",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Wimbledon Park, SW19 6PE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Football development programme at Wimbledon Park, serving Southfields users. Coaching and leagues.",
    times: "Check provider for class times",
    website: "https://afcwimbledonfoundation.com/player-development-centre-1",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-10",
    name: "Southfields Tennis Coaching Programme",
    provider: "Southfields Tennis",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Local courts / Wimbledon Park courts, SW19 / SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching across local courts and Wimbledon Park courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://metrotennis.co.uk/camps/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-11",
    name: "Wimbledon Tennis Pathway Juniors",
    provider: "Wimbledon Tennis Pathway",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Wimbledon Park / All England Lawn Tennis area, SW19 5AG",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact pathway for term fees",
    age: "7–18",
    interests: ["sports"],
    dayOfWeek: "Check pathway for term-time schedule",
    description: "Tennis development pathway around Wimbledon Park and the All England Lawn Tennis area, serving the Southfields catchment. Structured development training for a wide age range up to 18.",
    times: "Check pathway for class times",
    website: "https://www.twctennis.co.uk/juniorprogramme",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-12",
    name: "Southfields Cricket Development Programme",
    provider: "Southfields Cricket",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Wimbledon Park / local greens, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme across Wimbledon Park and local greens. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://southboroughcc.hitssports.com/pages/page_23386/Junior-Cricket.aspx",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-13",
    name: "Southfields Netball Club Juniors",
    provider: "Southfields Netball Club",
    type: "Club",
    neighbourhood: "Southfields",
    location: "School courts, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Southfields. Training and match play.",
    times: "Check club for class times",
    website: "https://www.wandsworthnetball.club/a/juniors-37854.html?page=3",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-14",
    name: "Southfields Swimming Lessons",
    provider: "Tooting / Wimbledon leisure centres",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Tooting / Wimbledon leisure centres (Southfields catchment), SW17 / SW19",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons across Tooting and Wimbledon leisure centres, serving the Southfields catchment. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://www.puddleducks.com/local-teams/south-west-london/our-pools-classes/aspire-centre/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-15",
    name: "Southfields Athletics & Running Group",
    provider: "Southfields Athletics & Running Group",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Wimbledon Park, SW19 6PE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact group for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group at Wimbledon Park. Running, athletics and endurance training.",
    times: "Check group for class times",
    website: "https://www.herculeswimbledonac.org.uk/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-16",
    name: "Southfields Gymnastics Club",
    provider: "Southfields Gymnastics Club",
    type: "Club",
    neighbourhood: "Southfields",
    location: "School sports halls, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Southfields. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://www.swlgymnastics.co.uk/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-17",
    name: "Southfields Ballet School",
    provider: "Southfields Ballet School",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Dance studios (Replingham Road), SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school at dance studios on Replingham Road. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://deborahjayneschoolofdance.co.uk/southfields/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-18",
    name: "Southfields Dance Academy",
    provider: "Southfields Dance Academy",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Local studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across local Southfields studios. Ballet, jazz and street dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://elitedancersacademy.com/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-19",
    name: "Southfields Youth Theatre",
    provider: "Southfields Youth Theatre",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Community theatre spaces, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatre spaces in Southfields. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.westwayperformingarts.co.uk/our-blog/drama-amp-musical-theatre-classes-for-kids-in-southfields-amp-earlsfield",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-20",
    name: "Perform Drama Southfields",
    provider: "Perform",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Church halls, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Drama term-time classes across church halls in Southfields. Drama, storytelling and improvisation.",
    times: "Check provider for class times",
    website: "https://www.perform.org.uk/near-me",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-21",
    name: "Southfields Music Academy",
    provider: "Southfields Music Academy",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Local studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy across local Southfields studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://worldheartbeat.org/music-bambinos/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-22",
    name: "Southfields Choir Collective",
    provider: "Southfields Choir Collective",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Church venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Southfields church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.singswell.co.uk/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-23",
    name: "Southfields Art Studio",
    provider: "Southfields Art Studio",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Replingham Road studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on Replingham Road. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://www.naturalvoice.net/choir/southfields-sings/",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-24",
    name: "Southfields Art Collective",
    provider: "Southfields Art Collective",
    type: "Club",
    neighbourhood: "Southfields",
    location: "Community studios, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Southfields community studios. Fine art and portfolio development.",
    times: "Check collective for class times",
    website: "https://www.psad.org.uk/wp-content/uploads/V6-Southfields-Leaflet.pdf",
    neighbourhoods: ["Southfields"],
  },
  {
    id: "ttsfd-25",
    name: "Southfields Photography Club",
    provider: "Southfields Photography Club",
    type: "Club",
    neighbourhood: "Southfields",
    location: "School + community venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across school and community venues in Southfields. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://dunscameraclub.co.uk/section495147.html",
    neighbourhoods: ["Southfields"],
  },

  // ── STREATHAM ────────────────────────────────────────────────────────────────
  {
    id: "ttstr-1",
    name: "Streatham Tuition Centre",
    provider: "Streatham Tuition Centre",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham High Road, SW16 1BB",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on Streatham High Road. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/streatham/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-2",
    name: "Streatham Study Hub",
    provider: "Streatham Library",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Library, SW16 1PN",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Study hub at Streatham Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-3",
    name: "Kip McGrath Streatham",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Hill, SW2 4PA",
    postcodeSector: "SW2",
    distanceMiles: 1.3,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre on Streatham Hill. English, maths tuition and exam preparation.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk/educational-programme",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-4",
    name: "Streatham Tutors Collective",
    provider: "Streatham Tutors Collective",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Home-based network, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Streatham. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-5",
    name: "Streatham STEM Club",
    provider: "Streatham STEM Club",
    type: "Club",
    neighbourhood: "Streatham",
    location: "School halls, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Streatham. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.stem-club.co.uk/what-we-do/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-6",
    name: "Streatham Chess Academy Juniors",
    provider: "Streatham Chess Academy",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Library, SW16 1PN",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy at Streatham Library. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://streathamchess.org/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-7",
    name: "Streatham Coding Collective",
    provider: "Streatham Coding Collective",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Community centres, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across Streatham community centres. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://www.communitytechaid.org.uk/post/coding-classes-for-children-in-streatham-millennium-community-solutions",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-8",
    name: "Streatham FC Youth Academy",
    provider: "Streatham FC",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Common, SW16 3BX",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Streatham Common. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://streathamacademyfc.wordpress.com/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-9",
    name: "Streatham & Marlborough Football Programme",
    provider: "Streatham & Marlborough",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Local school pitches, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Football programme across local school pitches in Streatham. Training and leagues.",
    times: "Check programme for class times",
    website: "https://wemakefootballers.com/streatham",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-10",
    name: "Streatham Tennis Coaching",
    provider: "Streatham Tennis",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Common courts, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Streatham Common courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://clubspark.lta.org.uk/streathamvalepark/Coaching",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-11",
    name: "Streatham Cricket Development Programme",
    provider: "Streatham Cricket",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Common, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme at Streatham Common. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://streathammarlboroughcc.co.uk/index.php/club-development-plan/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-12",
    name: "Streatham Netball Club Juniors",
    provider: "Streatham Netball Club",
    type: "Club",
    neighbourhood: "Streatham",
    location: "School courts, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Streatham. Training and match play.",
    times: "Check club for class times",
    website: "https://cpnc.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-13",
    name: "Streatham Swimming Lessons",
    provider: "Streatham Leisure Centre",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Leisure Centre, SW16 1RU",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Streatham Leisure Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://active.lambeth.gov.uk/lessons/swimming-lessons/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-14",
    name: "Streatham Athletics & Running Group",
    provider: "Streatham Athletics & Running Group",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Common, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group at Streatham Common. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "https://www.tootingrunclub.com/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-15",
    name: "Streatham Gymnastics Club",
    provider: "Streatham Gymnastics Club",
    type: "Club",
    neighbourhood: "Streatham",
    location: "School sports halls, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Streatham. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://www.streathamgymnasticsclub.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-16",
    name: "Streatham Dance Academy",
    provider: "Streatham Dance Academy",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Hill studios, SW2",
    postcodeSector: "SW2",
    distanceMiles: 1.3,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Streatham Hill studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.whizzdance.com/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-17",
    name: "Streatham Ballet School",
    provider: "Streatham Ballet School",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Local studios, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Streatham studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://elitedancersacademy.com/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-18",
    name: "Streatham Youth Theatre",
    provider: "Streatham Space Project",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Space Project, SW16 1EQ",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre at the Streatham Space Project. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://pssa.co.uk/venues-streatham",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-19",
    name: "Streatham Drama Workshop",
    provider: "Streatham Drama Workshop",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Community halls, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Streatham community halls. Drama, improvisation and storytelling.",
    times: "Check workshop for class times",
    website: "https://www.jigsaw-arts.co.uk/franchise/streatham",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-20",
    name: "Streatham Music Academy",
    provider: "Streatham Music Academy",
    type: "Club",
    neighbourhood: "Streatham",
    location: "High Road studios, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at High Road studios in Streatham. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.lambethmusic.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-21",
    name: "Streatham Choir Collective",
    provider: "Streatham Choir Collective",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Church venues, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Streatham church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://singstreatham.co.uk/",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-22",
    name: "Streatham Art Studio",
    provider: "Streatham Art Studio",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Streatham Hill studios, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio at Streatham Hill. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-23",
    name: "Streatham Art Collective",
    provider: "Streatham Art Collective",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Community studios, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Streatham community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Streatham"],
  },
  {
    id: "ttstr-24",
    name: "Streatham Photography Club",
    provider: "Streatham Photography Club",
    type: "Club",
    neighbourhood: "Streatham",
    location: "Community centres, SW16",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across Streatham community centres. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://southlondonps.org.uk/",
    neighbourhoods: ["Streatham"],
  },

  // ── SURBITON ─────────────────────────────────────────────────────────────────
  {
    id: "ttsrb-1",
    name: "Surbiton Tuition Centre",
    provider: "Surbiton Tuition Centre",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton High Street, KT6 4QG",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on Surbiton High Street. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/surbiton/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-2",
    name: "Kip McGrath Surbiton",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton town centre, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Surbiton town centre. English, maths tuition and exam preparation.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-3",
    name: "Surbiton Study Hub",
    provider: "Surbiton Library",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Library, KT6 4NG",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Study hub at Surbiton Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://www.facebook.com/kumonsurbitonstudycentre/?locale=en_GB",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-4",
    name: "Surbiton Tutors Collective",
    provider: "Surbiton Tutors Collective",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Home-based network, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Surbiton. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-5",
    name: "Surbiton STEM Club",
    provider: "Surbiton STEM Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "School halls, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Surbiton. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "http://www.beyondblocks.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-6",
    name: "Surbiton Chess Academy Juniors",
    provider: "Surbiton Chess Academy",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Library, KT6 4NG",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy at Surbiton Library. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://jollygoodchess.com/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-7",
    name: "Surbiton Coding Collective",
    provider: "Surbiton Coding Collective",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Community + school venues, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across community and school venues in Surbiton. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://beyondblocks.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-8",
    name: "Surbiton FC Youth Academy",
    provider: "Surbiton FC",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Sports Ground, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Surbiton Sports Ground. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://keysports.org/football-club/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-9",
    name: "Surbiton Hockey Club Juniors",
    provider: "Surbiton Hockey Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton / Tolworth sports facilities, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior hockey section across Surbiton and Tolworth sports facilities. Field hockey training and leagues for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://surbitonhc.com/colts/holiday-hockey-camps/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-10",
    name: "Surbiton Tennis Coaching",
    provider: "Surbiton Lawn Tennis Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Lawn Tennis Club, KT6 4QH",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Surbiton Lawn Tennis Club. Coaching, drills and match play.",
    times: "Check club for class times",
    website: "https://www.surbiton.org/tennis-coaching",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-11",
    name: "Surbiton Lawn Tennis Club Juniors",
    provider: "Surbiton Lawn Tennis Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton LTC grounds, KT6 4QH",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis development pathway training at Surbiton LTC grounds, for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.surbitonmethodisttennis.org/juniors",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-12",
    name: "Surbiton Cricket Development Programme",
    provider: "Surbiton Cricket",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Park / local greens, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme across Surbiton Park and local greens. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.surreycricketfoundation.org/schools-cricket-day-surrey-south-london-2026/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-13",
    name: "Surbiton Netball Club Juniors",
    provider: "Surbiton Netball Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "School courts, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Surbiton. Training and match play.",
    times: "Check club for class times",
    website: "https://www.tdjuniors.co.uk/fyi",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-14",
    name: "Surbiton Swimming Lessons",
    provider: "Kingston leisure centres",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Kingston leisure centres (Surbiton catchment), KT1 / KT6",
    postcodeSector: "KT1",
    distanceMiles: 7.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons across Kingston leisure centres, serving the Surbiton catchment. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://higginsaquatics.co.uk/swimming-lessons-surbiton-grand-avenue/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-15",
    name: "Surbiton Athletics & Running Group",
    provider: "Surbiton Athletics & Running Group",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Park / Thames towpath, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group across Surbiton Park and the Thames towpath. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "https://www.26point2.co.uk/join-our-club/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-16",
    name: "Surbiton Gymnastics Club",
    provider: "Surbiton Gymnastics Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "School sports halls, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Surbiton. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://www.goodlifegymnastics.com/venues/good-life-surbiton/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-17",
    name: "Surbiton Dance Academy",
    provider: "Surbiton Dance Academy",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Hill studios, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Surbiton Hill studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://surbiton.dance/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-18",
    name: "Surbiton Ballet School",
    provider: "Surbiton Ballet School",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Local studios, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Surbiton studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.arnouldschool.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-19",
    name: "Surbiton Youth Theatre",
    provider: "Surbiton Youth Theatre",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Community theatre spaces, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatre spaces in Surbiton. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.southsidetheatre.com/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-20",
    name: "Surbiton Drama Workshop",
    provider: "Surbiton Drama Workshop",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Church halls, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Surbiton church halls. Drama, improvisation and storytelling.",
    times: "Check workshop for class times",
    website: "https://www.donraeacademy.com/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-21",
    name: "Surbiton Music Academy",
    provider: "Surbiton Music Academy",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "High Street studios, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Surbiton High Street studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.yoursurbitonmusicschool.co.uk/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-22",
    name: "Surbiton Choir Collective",
    provider: "Surbiton Choir Collective",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Church venues, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Surbiton church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.sing-now.co.uk/sing-now-choir",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-23",
    name: "Surbiton Art Studio",
    provider: "Surbiton Art Studio",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Surbiton Hill studios, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio at Surbiton Hill. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://cygnetsartschool.com/branch/surbiton/",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-24",
    name: "Surbiton Art Collective",
    provider: "Surbiton Art Collective",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Community studios, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Surbiton community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Surbiton"],
  },
  {
    id: "ttsrb-25",
    name: "Surbiton Photography Club",
    provider: "Surbiton Photography Club",
    type: "Club",
    neighbourhood: "Surbiton",
    location: "Community venues, KT6",
    postcodeSector: "KT6",
    distanceMiles: 8.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across Surbiton community venues. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "http://www.clubs4uk.co.uk/photography/surrey-surbiton-photography-club.html",
    neighbourhoods: ["Surbiton"],
  },

  // ── TEDDINGTON ───────────────────────────────────────────────────────────────
  {
    id: "tttdd-1",
    name: "Teddington Tuition Centre",
    provider: "Teddington Tuition Centre",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington High Street, TW11 8HG",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on Teddington High Street. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/teddington/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-2",
    name: "Teddington Study Hub",
    provider: "Teddington Library",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington Library, TW11 8HF",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Study hub at Teddington Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://www.richmond.gov.uk/services/libraries/libraries_venue_hire/teddington-library-co-working-space",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-3",
    name: "Teddington Tutors Collective",
    provider: "Teddington Tutors Collective",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Home-based network, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Teddington. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.thetutorcollective.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-4",
    name: "Teddington STEM Club",
    provider: "Teddington STEM Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "School halls, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Teddington. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.stem-club.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-5",
    name: "Teddington Chess Academy Juniors",
    provider: "Teddington Chess Academy",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Library / community halls, TW11 8HF",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy across Teddington Library and community halls. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://rtchessclub.com/junior-chess/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-6",
    name: "Teddington Coding Collective",
    provider: "Teddington Coding Collective",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Community venues, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across Teddington community venues. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://www.richmond.gov.uk/services/libraries/library_services_for_children_and_teens/library_events_for_children_and_teens/code_club",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-7",
    name: "Teddington FC Youth Academy",
    provider: "Teddington FC",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Bushy Park / local pitches, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy across Bushy Park and local pitches. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.teddingtonathleticfc.com/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-8",
    name: "Teddington Hockey Club Juniors",
    provider: "Teddington Hockey Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington Sports Centre, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior hockey section at Teddington Sports Centre. Field hockey training and leagues for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.teddingtonhockey.club/junior-section",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-9",
    name: "Teddington Cricket Club Juniors",
    provider: "Teddington Cricket Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Bushy Park cricket grounds, TW11 8QZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cricket section at the Bushy Park cricket grounds. Batting and bowling coaching for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.teddingtoncc.co.uk/juniors",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-10",
    name: "Teddington Rugby Club Juniors",
    provider: "Teddington Rugby Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Bushy Park sports fields, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rugby section at Bushy Park sports fields. Training, matches and development squads for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.teddingtonrfc.co.uk/teams/64540",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-11",
    name: "Teddington Lawn Tennis Club Coaching",
    provider: "Teddington Lawn Tennis Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington LTC, TW11 8EZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Tennis coaching at Teddington LTC. Coaching, drills and match play for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://clubspark.lta.org.uk/teddingtonlawntennisclub/Coaching/Adult",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-12",
    name: "Teddington Rowing Club Juniors",
    provider: "Teddington Rowing Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "River Thames (Teddington Lock), TW11 riverside",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "12–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior rowing club at Teddington Lock. Water training and regattas for teens.",
    times: "Check club for class times",
    website: "https://www.walbrookrc.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-13",
    name: "Thames Junior Rowing Pathway",
    provider: "Thames Junior Rowing Pathway",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington Lock area, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact pathway for term fees",
    age: "13–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check pathway for term-time schedule",
    description: "Elite rowing development pathway based at the Teddington Lock area. Structured elite training for teens.",
    times: "Check pathway for class times",
    website: "",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-14",
    name: "Teddington Athletics & Running Group",
    provider: "Teddington Athletics & Running Group",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Bushy Park, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group at Bushy Park. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "https://www.stragglers.org/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-15",
    name: "Teddington Netball Club Juniors",
    provider: "Teddington Netball Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "School courts, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Teddington. Training and match play.",
    times: "Check club for class times",
    website: "https://netballforall.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-16",
    name: "Teddington Swimming Lessons",
    provider: "Teddington Pools & Fitness Centre",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Teddington Pools & Fitness Centre, TW11 8EZ",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons at Teddington Pools & Fitness Centre. Water confidence and structured levels for a wide age range.",
    times: "Check centre for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/teddington-pools-and-fitness-centre/swimming",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-17",
    name: "Teddington Gymnastics Club",
    provider: "Teddington Gymnastics Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "School sports halls, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Teddington. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://www.better.org.uk/leisure-centre/london/richmond-upon-thames/teddington-sports-centre/gymnastics",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-18",
    name: "Teddington Dance Academy",
    provider: "Teddington Dance Academy",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Local studios, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy across local Teddington studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://teddingtondancestudio.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-19",
    name: "Teddington Ballet School",
    provider: "Teddington Ballet School",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Studio spaces, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across Teddington studio spaces. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.suzannaraymondschoolofdance.com/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-20",
    name: "Teddington Youth Theatre",
    provider: "Teddington Youth Theatre",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Community theatre spaces, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatre spaces in Teddington. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.yat.org.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-21",
    name: "Teddington Drama Workshop",
    provider: "Teddington Drama Workshop",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Church halls, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Teddington church halls. Drama, improvisation and storytelling.",
    times: "Check workshop for class times",
    website: "https://www.stagecoach.co.uk/teddington",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-22",
    name: "Teddington Music Academy",
    provider: "Teddington Music Academy",
    type: "Club",
    neighbourhood: "Teddington",
    location: "High Street studios, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Teddington High Street studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.outloudmusicschool.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-23",
    name: "Teddington Choir Collective",
    provider: "Teddington Choir Collective",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Church venues, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Teddington church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.teddingtonchoral.co.uk/",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-24",
    name: "Teddington Art Studio",
    provider: "Teddington Art Studio",
    type: "Club",
    neighbourhood: "Teddington",
    location: "High Street studios, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio on Teddington High Street. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-25",
    name: "Teddington Art Collective",
    provider: "Teddington Art Collective",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Community studios, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Teddington community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "https://teddingtonsociety.org/arts-culture",
    neighbourhoods: ["Teddington"],
  },
  {
    id: "tttdd-26",
    name: "Teddington Photography Club",
    provider: "Teddington Photography Club",
    type: "Club",
    neighbourhood: "Teddington",
    location: "Community venues, TW11",
    postcodeSector: "TW11",
    distanceMiles: 8.8,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across Teddington community venues. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.rtps.org.uk/",
    neighbourhoods: ["Teddington"],
  },

  // ── TOOTING ──────────────────────────────────────────────────────────────────
  {
    id: "ttttg-1",
    name: "Tooting Tuition Centre",
    provider: "Tooting Tuition Centre",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting High Road, SW17 0RG",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on Tooting High Road. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/tooting/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-2",
    name: "Kip McGrath Tooting",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Broadway area, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in the Tooting Broadway area. English, maths tuition and exam preparation.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-3",
    name: "Tooting Study Hub",
    provider: "Tooting Library",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Library, SW17 8TA",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Study hub at Tooting Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "https://www.better.org.uk/library/london/wandsworth/tooting-library",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-4",
    name: "Tooting Tutors Collective",
    provider: "Tooting Tutors Collective",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Home-based network, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Tooting. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.tootingtutors.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-5",
    name: "Tooting STEM Club",
    provider: "Tooting STEM Club",
    type: "Club",
    neighbourhood: "Tooting",
    location: "School halls, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Tooting. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.stem-club.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-6",
    name: "Tooting Chess Academy Juniors",
    provider: "Tooting Chess Academy",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Library / community halls, SW17 8TA",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy across Tooting Library and community halls. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://www.scca.co.uk/lib/lib.html",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-7",
    name: "Tooting Coding Collective",
    provider: "Tooting Coding Collective",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Community venues, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across Tooting community venues. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://www.codingcollective.com/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-8",
    name: "AFC Wimbledon Community",
    provider: "AFC Wimbledon Community",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Local school pitches, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Football coaching delivered across local school pitches in Tooting by AFC Wimbledon Community. Development squads.",
    times: "Check provider for class times",
    website: "https://afcwimbledonfoundation.com/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-9",
    name: "Tooting & Mitcham FC Youth Academy",
    provider: "Tooting & Mitcham FC",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Imperial Fields, CR4 1RU",
    postcodeSector: "CR4",
    distanceMiles: 4.2,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy at Imperial Fields. Coaching, leagues and an elite pathway for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://tmunited.org/academy-under-18s/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-10",
    name: "Tooting Bec Athletics Club Juniors",
    provider: "Tooting Bec Athletics Club",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Bec Stadium, SW17 8JU",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior athletics club at Tooting Bec Stadium. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.hernehillharriers.org/upcoming-activities/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-11",
    name: "Tooting Bec Lido Swimming Programme",
    provider: "Tooting Bec Lido",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Bec Lido, SW16 1RU",
    postcodeSector: "SW16",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "3–16",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming programme at Tooting Bec Lido. Swimming lessons and open water confidence.",
    times: "Check centre for class times",
    website: "https://www.placesleisure.org/centres/tooting-bec-lido/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-12",
    name: "Tooting Cricket Development Programme",
    provider: "Tooting Cricket",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Common, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact programme for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Cricket development programme on Tooting Common. Coaching, batting and bowling.",
    times: "Check programme for class times",
    website: "https://www.ballancecoaching.com/cricket-coaching-for-children-in-tooting",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-13",
    name: "Tooting Netball Club Juniors",
    provider: "Tooting Netball Club",
    type: "Club",
    neighbourhood: "Tooting",
    location: "School courts, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Tooting. Training and match play.",
    times: "Check club for class times",
    website: "https://www.gomammoth.co.uk/netball/tooting-bec/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-14",
    name: "Tooting Tennis Coaching",
    provider: "Tooting Tennis",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Common courts, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Tooting Common courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "http://www.allstartennis.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-15",
    name: "Tooting Gymnastics Club",
    provider: "Tooting Gymnastics Club",
    type: "Club",
    neighbourhood: "Tooting",
    location: "School sports halls, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Tooting. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://www.tootinggymclub.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-16",
    name: "Tooting Athletics & Running Group",
    provider: "Tooting Athletics & Running Group",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Bec Stadium / Common, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group across Tooting Bec Stadium and Common. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "https://www.tootingrunclub.com/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-17",
    name: "Tooting Dance Academy",
    provider: "Tooting Dance Academy",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting Broadway studios, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Tooting Broadway studios. Ballet, jazz and street dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://legatodance.co.uk/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-18",
    name: "Tooting Ballet School",
    provider: "Tooting Ballet School",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Local studios, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Tooting studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://www.abcballetschool.org/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-19",
    name: "Tooting Youth Theatre",
    provider: "Tooting Youth Theatre",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Community theatre spaces, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatre spaces in Tooting. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://tootingbaby.co.uk/services/lets-act-drama-school/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-20",
    name: "Tooting Drama Workshop",
    provider: "Tooting Drama Workshop",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Church halls, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Tooting church halls. Drama, improvisation and storytelling.",
    times: "Check workshop for class times",
    website: "https://www.perform.org.uk/drama-classes/tooting",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-21",
    name: "Tooting Music Academy",
    provider: "Tooting Music Academy",
    type: "Club",
    neighbourhood: "Tooting",
    location: "High Road studios, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Tooting High Road studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://wandsworthmusic.co.uk/music-academy/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-22",
    name: "Tooting Choir Collective",
    provider: "Tooting Choir Collective",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Church venues, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Tooting church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://www.swlondoner.co.uk/news/06082019-real-voices-of-tooting-choir-royal-trinity-hospice",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-23",
    name: "Tooting Art Studio",
    provider: "Tooting Art Studio",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Tooting studios, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio in Tooting. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://welcometowandsworth.com/venues/kj-tooting-works/",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-24",
    name: "Tooting Art Collective",
    provider: "Tooting Art Collective",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Community studios, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Tooting community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Tooting"],
  },
  {
    id: "ttttg-25",
    name: "Tooting Photography Club",
    provider: "Tooting Photography Club",
    type: "Club",
    neighbourhood: "Tooting",
    location: "Community venues, SW17",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across Tooting community venues. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://rps.org/regions/london/london-sw/",
    neighbourhoods: ["Tooting"],
  },

  // ── TWICKENHAM ───────────────────────────────────────────────────────────────
  {
    id: "tttwk-1",
    name: "Twickenham Tuition Centre",
    provider: "Twickenham Tuition Centre",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham High Street, TW1 3RR",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on Twickenham High Street. Maths, English and GCSE support for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.headwaytutors.co.uk/locations/headway-tutors-twickenham/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-2",
    name: "Kip McGrath Twickenham",
    provider: "Kip McGrath",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham town centre, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact centre for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Tutoring centre in Twickenham town centre. English, maths tuition and exam preparation.",
    times: "Check centre for class times",
    website: "https://www.kipmcgrath.co.uk",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-3",
    name: "Twickenham Study Hub",
    provider: "Twickenham Library",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham Library, TW1 3BZ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact hub for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check hub for term-time schedule",
    description: "Study hub at Twickenham Library. Homework support, literacy and numeracy.",
    times: "Check hub for class times",
    website: "",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-4",
    name: "Twickenham Tutors Collective",
    provider: "Twickenham Tutors Collective",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Home-based network, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Home-based private tutoring network across Twickenham. GCSE and 11+ support for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://richmondtutoring.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-5",
    name: "Twickenham STEM Club",
    provider: "Twickenham STEM Club",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "School halls, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "STEM club across school halls in Twickenham. Coding, robotics and STEM projects.",
    times: "Check club for class times",
    website: "https://www.mnature.co.uk/venues/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-6",
    name: "Twickenham Chess Academy Juniors",
    provider: "Twickenham Chess Academy",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Library / community halls, TW1 3BZ",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact academy for term fees",
    age: "6–14",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior chess academy across Twickenham Library and community halls. Coaching and tournaments.",
    times: "Check academy for class times",
    website: "https://rtchessclub.com/junior-chess/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-7",
    name: "Twickenham Coding Collective",
    provider: "Twickenham Coding Collective",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Community venues, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact collective for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Coding collective across Twickenham community venues. Programming, game design and robotics.",
    times: "Check collective for class times",
    website: "https://www.richmond.gov.uk/services/libraries/library_services_for_children_and_teens/library_events_for_children_and_teens/code_club",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-8",
    name: "Harlequins Community Rugby Programme",
    provider: "Harlequins Community",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham Stadium outreach sites, TW2 7BA",
    postcodeSector: "TW2",
    distanceMiles: 8.9,
    cost: "Contact programme for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Rugby community programme across Twickenham Stadium outreach sites, run by Harlequins. Coaching and elite development pathway for a wide age range up to 18.",
    times: "Check programme for class times",
    website: "https://www.quins.co.uk/community-rugby",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-9",
    name: "London Irish Foundation Rugby Academy",
    provider: "London Irish Foundation",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Local school pitches, TW1 / TW2",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact academy for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Rugby academy across local school pitches, run by the London Irish Foundation. Training, skills and leagues for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.londonirishrugby.club/teams/academy/109/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-10",
    name: "Twickenham FC Youth Academy",
    provider: "Twickenham FC",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Local pitches, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact academy for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Youth football academy across local Twickenham pitches. Coaching, matches and skills.",
    times: "Check academy for class times",
    website: "https://www.twickenhamtigers.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-11",
    name: "Twickenham Cricket Club Juniors",
    provider: "Twickenham Cricket Club",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham Green, TW2",
    postcodeSector: "TW2",
    distanceMiles: 8.9,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cricket section on Twickenham Green. Batting and bowling coaching for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.twickenhamcc.net/a/junior-cricket-at-twickenham-cc-49242.html?page=1",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-12",
    name: "Orleans Park Sports Development",
    provider: "Orleans Park School",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Orleans Park School, TW1 3BB",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact school for term fees",
    age: "11–18",
    interests: ["sports"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Sports development programme at Orleans Park School, serving the Twickenham catchment. Multi-sport, athletics and football for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.orleanspark.school/students/sport-at-orleans-park",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-13",
    name: "Twickenham Tennis Coaching",
    provider: "Twickenham Tennis",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Radnor Gardens courts, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at Radnor Gardens courts. Coaching, drills and match play.",
    times: "Check provider for class times",
    website: "https://www.twtennis.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-14",
    name: "Twickenham Netball Club Juniors",
    provider: "Twickenham Netball Club",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "School courts, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior netball club across school courts in Twickenham. Training and match play.",
    times: "Check club for class times",
    website: "https://hotshotsnetball.com/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-15",
    name: "Twickenham Athletics & Running Group",
    provider: "Twickenham Athletics & Running Group",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Eel Pie Island / riverside, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact group for term fees",
    age: "6–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check group for term-time schedule",
    description: "Athletics and running group around Eel Pie Island and the riverside. Running, athletics and endurance training for a wide age range up to 18.",
    times: "Check group for class times",
    website: "https://www.bearcatrunningclub.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-16",
    name: "Twickenham Swimming Lessons",
    provider: "Teddington / Richmond leisure centres",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Teddington / Richmond leisure centres (catchment), TW1 / TW11",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact centre for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Swimming lessons across Teddington and Richmond leisure centres, serving the Twickenham catchment. Water confidence and structured levels.",
    times: "Check centre for class times",
    website: "https://firstswimlondon.co.uk/swimming-lessons-twickenham/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-17",
    name: "Twickenham Gymnastics Club",
    provider: "Twickenham Gymnastics Club",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "School sports halls, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club across school sports halls in Twickenham. Tumbling and coordination.",
    times: "Check club for class times",
    website: "https://richmondgymnastics.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-18",
    name: "Twickenham Dance Academy",
    provider: "Twickenham Dance Academy",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Twickenham studios, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact academy for term fees",
    age: "4–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Twickenham studios. Ballet, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.twickenhamacademyofdance.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-19",
    name: "Twickenham Ballet School",
    provider: "Twickenham Ballet School",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Local studios, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact school for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Ballet school across local Twickenham studios. Ballet, RAD syllabus and contemporary dance.",
    times: "Check school for class times",
    website: "https://balletforchildren.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-20",
    name: "Twickenham Youth Theatre",
    provider: "Twickenham Youth Theatre",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Community theatre spaces, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across community theatre spaces in Twickenham. Acting, performance and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.twickenhamyouththeatre.com/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-21",
    name: "Twickenham Drama Workshop",
    provider: "Twickenham Drama Workshop",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Church halls, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact workshop for term fees",
    age: "6–16",
    interests: ["drama"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Drama workshop across Twickenham church halls. Drama, improvisation and storytelling.",
    times: "Check workshop for class times",
    website: "https://www.whiterabbitdramaclub.com/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-22",
    name: "Twickenham Music Academy",
    provider: "Twickenham Music Academy",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "High Street studios, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Twickenham High Street studios. Piano, guitar, violin and theory for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.schoolofrock.com/locations/london",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-23",
    name: "Twickenham Choir Collective",
    provider: "Twickenham Choir Collective",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Church venues, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact collective for term fees",
    age: "6–18",
    interests: ["music"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Choir collective across Twickenham church venues. Choir training and vocal performance for a wide age range up to 18.",
    times: "Check collective for class times",
    website: "https://schoolchoircollective.magicbooking.co.uk/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-24",
    name: "Twickenham Art Studio",
    provider: "Twickenham Art Studio",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Town centre studios, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact studio for term fees",
    age: "6–18",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio in Twickenham town centre. Painting, drawing and sculpture for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://cygnetsartschool.com/branch/twickenham/",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-25",
    name: "Twickenham Art Collective",
    provider: "Twickenham Art Collective",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Community studios, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact collective for term fees",
    age: "6–16",
    interests: ["arts"],
    dayOfWeek: "Check collective for term-time schedule",
    description: "Art collective across Twickenham community studios. Fine art and mixed media.",
    times: "Check collective for class times",
    website: "",
    neighbourhoods: ["Twickenham"],
  },
  {
    id: "tttwk-26",
    name: "Twickenham Photography Club",
    provider: "Twickenham Photography Club",
    type: "Club",
    neighbourhood: "Twickenham",
    location: "Community venues, TW1",
    postcodeSector: "TW1",
    distanceMiles: 8.2,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Photography club across Twickenham community venues. Photography and digital media for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://twickenhamphotographygroup.co.uk/",
    neighbourhoods: ["Twickenham"],
  },

  // ── WANDSWORTH ───────────────────────────────────────────────────────────────
  {
    id: "ttwds-1",
    name: "Wandsworth Tutoring Centre",
    provider: "Wandsworth Tutoring Centre",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "53 East Hill, SW18 2QE",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact centre for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Academic tutoring centre on East Hill. Maths, English, GCSE and 11+ tuition for a wide age range up to 18.",
    times: "Check centre for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/wandsworth/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-2",
    name: "Keystone Tutors",
    provider: "Keystone Tutors",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Riverside Business Centre, Bendon Valley, SW18 4UQ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact office for term fees",
    age: "7–18",
    interests: ["stem"],
    dayOfWeek: "Check office for term-time schedule",
    description: "Private tutoring office at Riverside Business Centre, Bendon Valley. GCSE, A Level and 11+ preparation for a wide age range up to 18.",
    times: "Check office for class times",
    website: "https://www.keystonetutors.com/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-3",
    name: "Wandsworth Library Homework Club",
    provider: "Wandsworth Town Library",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Town Library, Garratt Lane, SW18 4AQ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact library for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check library for term-time schedule",
    description: "Homework club at Wandsworth Town Library on Garratt Lane. Homework support, reading and literacy.",
    times: "Check library for class times",
    website: "https://fis.wandsworth.gov.uk/advice/study-spaces/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-4",
    name: "Wandsworth Science Club",
    provider: "Wandsworth Library",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Library, SW18 4AQ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Science club at Wandsworth Library. Science workshops and experiments.",
    times: "Check club for class times",
    website: "https://forestfreedom.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-5",
    name: "Young Engineers Wandsworth",
    provider: "Young Engineers",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Community venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "6–12",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Engineering club across Wandsworth community venues. LEGO engineering and robotics.",
    times: "Check provider for class times",
    website: "https://youngengineers.org/franchise_locations/inner-west-london/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-6",
    name: "Wandsworth Chess Club Juniors",
    provider: "Wandsworth Chess Club",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "St Anne's Church Hall, SW18 2RS",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at St Anne's Church Hall. Coaching and competitions.",
    times: "Check club for class times",
    website: "https://www.scca.co.uk/lib/lib.html",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-7",
    name: "South Thames College Saturday Academy",
    provider: "South Thames College",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "South Thames College, SW18 2PP",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact college for term fees",
    age: "14–18",
    interests: ["stem", "arts"],
    dayOfWeek: "Check college for term-time schedule",
    description: "Saturday academy at South Thames College. Creative media, computing and business for teens up to 18.",
    times: "Check college for class times",
    website: "",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-8",
    name: "Spencer Cricket Club Juniors",
    provider: "Spencer Cricket Club",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Fieldview, Earlsfield Road, SW18 3HF",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–17",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cricket section at Fieldview. Coaching and league teams.",
    times: "Check club for class times",
    website: "https://www.spencercricket.com/junior-training",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-9",
    name: "Wandsworth Town FC Youth",
    provider: "Wandsworth Town FC",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "King George's Park, SW18 2GJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Youth football section at King George's Park. Coaching and league teams.",
    times: "Check club for class times",
    website: "https://wandsworthtownfc.wordpress.com/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-10",
    name: "Wandsworth Common Tennis Coaching",
    provider: "Wandsworth Common Tennis Courts",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Wandsworth Common Tennis Courts, SW18 3RT",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tennis coaching at the Wandsworth Common Tennis Courts.",
    times: "Check provider for class times",
    website: "https://www.allstartennis.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-11",
    name: "Aspire2Dance",
    provider: "Aspire2Dance",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Ram Quarter Studios, SW18 1TH",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact studio for term fees",
    age: "3–16",
    interests: ["dance"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Dance studio at Ram Quarter. Ballet, modern, tap and commercial dance.",
    times: "Check studio for class times",
    website: "https://www.aspireschoolofdance.com/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-12",
    name: "Gravity Trampoline Clubs",
    provider: "Gravity",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Ram Quarter, SW18 1TH",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "5–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Trampolining club at Ram Quarter. Trampolining and fitness.",
    times: "Check club for class times",
    website: "https://gravity-global.com/max/wandsworth",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-13",
    name: "Wandsworth Gymnastics Club",
    provider: "Wandsworth Gymnastics Club",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Burntwood School Sports Centre, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Gymnastics club at Burntwood School Sports Centre.",
    times: "Check club for class times",
    website: "https://www.wandsworthschoolofgymnastics.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-14",
    name: "King George's Park Athletics Club",
    provider: "King George's Park Athletics Club",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "King George's Park, SW18 2GJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Athletics club at King George's Park. Athletics and running.",
    times: "Check club for class times",
    website: "https://www.wandsworth.gov.uk/info/644/leisure_centres_and_facilities/407/wandsworth_venues_by_sport_activity_or_facility",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-15",
    name: "Dolphins Swimming School",
    provider: "Dolphins Swimming School",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Wandle Recreation Centre, SW18 4DN",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact school for term fees",
    age: "3–14",
    interests: ["swimming"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Swimming school at Wandle Recreation Centre. Learn to swim programme.",
    times: "Check school for class times",
    website: "https://www.dolphinswimschool.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-16",
    name: "Wandsworth Tigers Netball Club",
    provider: "Wandsworth Tigers Netball Club",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Burntwood School, SW17 0AQ",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Netball club at Burntwood School. Netball coaching.",
    times: "Check club for class times",
    website: "https://www.wandsworthnetball.club/a/juniors-37854.html?page=",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-17",
    name: "Little Voices Wandsworth",
    provider: "Little Voices",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "St Michael's Hall, SW18 1HS",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–18",
    interests: ["music", "drama"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Performing arts classes at St Michael's Hall. Drama, singing and confidence building for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://littlevoices.org.uk/locations/locations/south-west-london/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-18",
    name: "Wandsworth Youth Theatre",
    provider: "Wandsworth Youth Theatre",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Battersea Arts Centre outreach venue, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact theatre for term fees",
    age: "7–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre at a Battersea Arts Centre outreach venue in Wandsworth. Acting and productions for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.putneyartstheatre.org.uk/group-64",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-19",
    name: "Wandsworth Music Academy",
    provider: "Wandsworth Music Academy",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Old York Road Studios, SW18 1SS",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Old York Road Studios. Piano, guitar, violin and drums for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "http://wandsworthmusic.co.uk",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-20",
    name: "Wandsworth Children's Choir",
    provider: "Wandsworth Children's Choir",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "All Saints Church, SW18 1SL",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact choir for term fees",
    age: "6–16",
    interests: ["music"],
    dayOfWeek: "Check choir for term-time schedule",
    description: "Children's choir at All Saints Church. Choir and vocal training.",
    times: "Check choir for class times",
    website: "https://wandsworthmusic.co.uk/updates/wandsworth-youth-choir/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-21",
    name: "Art Space Wandsworth",
    provider: "Art Space Wandsworth",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Ram Quarter Studios, SW18 1TH",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio at Ram Quarter Studios. Drawing, painting and mixed media.",
    times: "Check studio for class times",
    website: "https://welcometowandsworth.com/discover/people/creative-directory/VaD-wimbledon-art-studios/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-22",
    name: "ColourWheel Art Classes",
    provider: "ColourWheel",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Community venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Art classes across Wandsworth community venues. Painting, sculpture and crafts.",
    times: "Check provider for class times",
    website: "https://colourwheelartclass.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-23",
    name: "Pottery Kids Wandsworth",
    provider: "Pottery Kids",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "Local studio workshops, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "6–14",
    interests: ["arts"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Pottery workshops at a local Wandsworth studio. Ceramics and pottery.",
    times: "Check provider for class times",
    website: "https://potterylifeuk.com/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-24",
    name: "Coding Club Wandsworth",
    provider: "Coding Club Wandsworth",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "School venues, SW18",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact club for term fees",
    age: "7–14",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Coding club across school venues in Wandsworth. Scratch, Python and coding fundamentals.",
    times: "Check club for class times",
    website: "https://www.thecodezone.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },
  {
    id: "ttwds-25",
    name: "Nature Explorers Wandsworth",
    provider: "Nature Explorers",
    type: "Club",
    neighbourhood: "Wandsworth",
    location: "King George's Park, SW18 2GJ",
    postcodeSector: "SW18",
    distanceMiles: 2.4,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["outdoors"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Outdoor learning club at King George's Park. Forest school, wildlife and nature crafts.",
    times: "Check provider for class times",
    website: "https://naturescope.co.uk/",
    neighbourhoods: ["Wandsworth"],
  },

  // ── WIMBLEDON ────────────────────────────────────────────────────────────────
  {
    id: "ttwmb-1",
    name: "Wimbledon Tutors",
    provider: "Wimbledon Tutors",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "38 The Broadway, SW19 1RQ",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tutoring service on The Broadway. Maths, English, 11+, GCSE and A Level tuition for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.wimbledon-tutors.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-2",
    name: "First Class Learning Wimbledon",
    provider: "First Class Learning",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Dundonald Primary School, SW19 3QH",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "5–16",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Structured maths and English tuition at Dundonald Primary School.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/wimbledon/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-3",
    name: "Wimbledon Library Homework Club",
    provider: "Wimbledon Library",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Library, Wimbledon Hill Road, SW19 7NB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact library for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check library for term-time schedule",
    description: "Homework club at Wimbledon Library on Wimbledon Hill Road. Homework support, reading and literacy.",
    times: "Check library for class times",
    website: "https://merton.events.mylibrary.digital/event?id=375775",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-4",
    name: "Wimbledon Debate Club",
    provider: "Wimbledon Debate Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Community venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "9–16",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Debate club across Wimbledon community venues. Public speaking, debating and critical thinking.",
    times: "Check club for class times",
    website: "https://wimbledonspeakers.org.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-5",
    name: "Wimbledon Science Club",
    provider: "Wimbledon Science Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Local school venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Science club across local school venues in Wimbledon. Science experiments and engineering challenges.",
    times: "Check club for class times",
    website: "https://www.mnature.co.uk/holiday-science-activity-camps/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-6",
    name: "Wimbledon Robotics Club",
    provider: "Wimbledon Robotics Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Community learning venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics club across community learning venues in Wimbledon. Robotics, coding and electronics.",
    times: "Check club for class times",
    website: "https://www.codekids.org.uk/locations/wimbledon/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-7",
    name: "Wimbledon Chess Club Juniors",
    provider: "Wimbledon Chess Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Library, SW19 7NB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior chess club at Wimbledon Library. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://chessacademy.uk/course/wimbledon-chess-rising-stars/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-8",
    name: "Wimbledon RFC Minis & Juniors",
    provider: "Wimbledon RFC",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Beverley Meads, SW20 0ET",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Minis and juniors rugby section at Beverley Meads. Coaching and league teams for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.wimbledonrfc.co.uk/teams/164978",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-9",
    name: "Wimbledon Cricket Club Juniors",
    provider: "Wimbledon Cricket Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Church Road, SW19 5AG",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cricket section on Church Road. Coaching and competitive teams for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.twccricket.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-10",
    name: "Wimbledon Hockey Club Juniors",
    provider: "Wimbledon Hockey Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Raynes Park High School, SW20 0JL",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact club for term fees",
    age: "6–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior hockey section at Raynes Park High School. Coaching and league play for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.twchockey.co.uk/juniorhockey",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-11",
    name: "Wimbledon Park Bowls Club Juniors",
    provider: "Wimbledon Park Bowls Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Park, SW19 7HR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior lawn bowls coaching at Wimbledon Park.",
    times: "Check club for class times",
    website: "http://www.wimbledonparkbowlsclub.co.uk/about/membership/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-12",
    name: "Wimbledon Fencing Club",
    provider: "Wimbledon Fencing Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Rutlish School Sports Hall, SW20 9AD",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact club for term fees",
    age: "8–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Fencing club at Rutlish School Sports Hall. Foil, épée and sabre coaching for a wide age range up to 18.",
    times: "Check club for class times",
    website: "http://www.wimbledonfencingclub.org.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-13",
    name: "Wimbledon Gymnastics Centre",
    provider: "Wimbledon Gymnastics Centre",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Durnsford Road, SW19 8DZ",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact centre for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check centre for term-time schedule",
    description: "Gymnastics centre on Durnsford Road. Gymnastics and tumbling.",
    times: "Check centre for class times",
    website: "https://www.wimbledongymnastics.com/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-14",
    name: "Wimbledon Athletics Club",
    provider: "Wimbledon Athletics Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Park Athletics Track, SW19 7HR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "7–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Athletics club at the Wimbledon Park Athletics Track. Track and field, endurance and athletics for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://www.herculeswimbledonac.org.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-15",
    name: "Wimbledon Dance Academy",
    provider: "Wimbledon Dance Academy",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Broadway Studios, SW19 1RY",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Dance academy at Broadway Studios. Ballet, tap, jazz and contemporary dance for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://wimbledondanceacademy.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-16",
    name: "Polka Theatre Creative Learning",
    provider: "Polka Theatre",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "The Broadway, SW19 1SB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact theatre for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Creative learning programme at Polka Theatre on The Broadway. Drama, storytelling and theatre workshops.",
    times: "Check theatre for class times",
    website: "https://polkatheatre.com/creative-learning-programme/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-17",
    name: "Wimbledon Youth Theatre",
    provider: "Wimbledon Youth Theatre",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Community venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact theatre for term fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across Wimbledon community venues. Acting, productions and stagecraft for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.thebritishtheatreacademy.com/academies/wimbledon/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-18",
    name: "Wimbledon Music School",
    provider: "Wimbledon Music School",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Chase, SW20 8DB",
    postcodeSector: "SW20",
    distanceMiles: 5.3,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Music school at Wimbledon Chase. Piano, violin, guitar, singing and theory for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.wimbledonmusicschool.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-19",
    name: "Wimbledon Children's Choir",
    provider: "Wimbledon Children's Choir",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Trinity Church, SW19 4QY",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact choir for term fees",
    age: "6–16",
    interests: ["music"],
    dayOfWeek: "Check choir for term-time schedule",
    description: "Children's choir at Trinity Church. Choir and vocal performance.",
    times: "Check choir for class times",
    website: "http://wimbledonyouthchoir.com/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-20",
    name: "ColourWheel Wimbledon",
    provider: "ColourWheel",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon studios, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "5–14",
    interests: ["arts"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Art classes across Wimbledon studios. Drawing, painting and mixed media.",
    times: "Check provider for class times",
    website: "https://colourwheelartclass.co.uk/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-21",
    name: "Wimbledon Art Studios Youth Workshops",
    provider: "Wimbledon Art Studios",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Riverside Road, SW17 0BB",
    postcodeSector: "SW17",
    distanceMiles: 2.6,
    cost: "Contact studio for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Youth art workshops at Wimbledon Art Studios on Riverside Road. Fine art, illustration and printmaking.",
    times: "Check studio for class times",
    website: "https://www.wimbledonartstudios.co.uk/workshops",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-22",
    name: "Wimbledon Pottery Studio",
    provider: "Wimbledon Pottery Studio",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Local studio, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact studio for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Pottery studio in Wimbledon. Pottery, ceramics and sculpture.",
    times: "Check studio for class times",
    website: "https://www.rachelcoxceramics.com/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-23",
    name: "Forest Explorers Wimbledon",
    provider: "Forest Explorers",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "4–11",
    interests: ["outdoors"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Outdoor learning club on Wimbledon Common. Forest school, bushcraft and wildlife learning.",
    times: "Check provider for class times",
    website: "https://www.littleforestfolk.com/wimbledon",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-24",
    name: "Wimbledon Nature Club",
    provider: "Wimbledon Nature Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "5–12",
    interests: ["outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Nature club on Wimbledon Common. Environmental education and nature discovery.",
    times: "Check club for class times",
    website: "https://wimbledonvillage.com/lifestyle-culture/childrens-activities/the-wimbledon-common-nature-club/",
    neighbourhoods: ["Wimbledon"],
  },
  {
    id: "ttwmb-25",
    name: "Wimbledon Photography Club Juniors",
    provider: "Wimbledon Photography Club",
    type: "Club",
    neighbourhood: "Wimbledon",
    location: "Community venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior photography club across Wimbledon community venues. Photography and digital editing for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://amateurphotographer.com/latest/photo-news/inaugural-shine-camera-club-exhibition-set-for-wimbledon/",
    neighbourhoods: ["Wimbledon"],
  },

  // ── WIMBLEDON VILLAGE ────────────────────────────────────────────────────────
  {
    id: "ttwmv-1",
    name: "Wimbledon Village Tutors",
    provider: "Wimbledon Village Tutors",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "High Street, Wimbledon Village, SW19 5DX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "5–18",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Tutoring service on Wimbledon Village High Street. Maths, English, 11+, GCSE and A Level tuition for a wide age range up to 18.",
    times: "Check provider for class times",
    website: "https://www.firstclasslearning.co.uk/tuition-centres/wimbledon/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-2",
    name: "The Study Prep Club",
    provider: "The Study Prep Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Near Peek Crescent, SW19 5ER",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Independent school preparation near Peek Crescent. Verbal and non-verbal reasoning.",
    times: "Check club for class times",
    website: "",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-3",
    name: "Wimbledon Village Homework Club",
    provider: "Wimbledon Village Homework Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village community venue, SW19 5DX",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "5–11",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Homework club at a Wimbledon Village community venue. Homework support, literacy and maths.",
    times: "Check club for class times",
    website: "https://merton.events.mylibrary.digital/event?id=379715",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-4",
    name: "Wimbledon Village Science Workshops",
    provider: "Wimbledon Village Science Workshops",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Community venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "7–13",
    interests: ["stem"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Science workshops across Wimbledon Village community venues. Practical science, engineering and experiments.",
    times: "Check provider for class times",
    website: "https://www.mnature.co.uk/holiday-science-activity-camps/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-5",
    name: "Wimbledon Village Robotics Club",
    provider: "Wimbledon Village Robotics Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "School venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["stem", "gaming"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Robotics club across Wimbledon Village school venues. Robotics, coding and engineering.",
    times: "Check club for class times",
    website: "https://www.codekids.org.uk/locations/wimbledon/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-6",
    name: "Wimbledon Village Chess Club",
    provider: "Wimbledon Village Chess Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Cannizaro House community rooms, SW19 4UE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "6–16",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Chess club at Cannizaro House community rooms. Coaching and tournaments.",
    times: "Check club for class times",
    website: "https://scca.co.uk/clubs/Wimbledon.php",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-7",
    name: "All England Lawn Tennis Club Junior Coaching",
    provider: "All England Lawn Tennis Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Church Road, SW19 5AE",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "5–18",
    interests: ["sports"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior tennis coaching at the All England Lawn Tennis Club on Church Road. Performance pathway training for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://raynespark.communitysport.aeltc.com/Tennis/HolidayCamps",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-8",
    name: "Wimbledon Common Golf Club Junior Academy",
    provider: "Wimbledon Common Golf Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Camp Road, SW19 4UW",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "8–18",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Junior golf academy at Wimbledon Common Golf Club on Camp Road. Golf coaching and junior competitions for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.wcgc.co.uk/juniors.html",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-9",
    name: "Wimbledon Village Stables Riding School",
    provider: "Wimbledon Village Stables",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Bathgate Road, SW19 5PQ",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact school for term fees",
    age: "5–18",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check school for term-time schedule",
    description: "Riding school on Bathgate Road. Horse riding, pony care and equestrian skills for a wide age range up to 18.",
    times: "Check school for class times",
    website: "https://www.wvstables.com/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-10",
    name: "Wimbledon Common Cycling Club Juniors",
    provider: "Wimbledon Common Cycling Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "7–16",
    interests: ["sports", "outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Junior cycling club on Wimbledon Common. Cycling skills and off-road riding.",
    times: "Check club for class times",
    website: "https://www.londondynamo.co.uk/juniors/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-11",
    name: "Wimbledon Village Cricket Coaching",
    provider: "Wimbledon Village Cricket",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Common fields, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact provider for term fees",
    age: "6–16",
    interests: ["sports"],
    dayOfWeek: "Check provider for term-time schedule",
    description: "Cricket coaching on the Wimbledon Common fields. Coaching and match play.",
    times: "Check provider for class times",
    website: "https://www.twccricket.co.uk/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-12",
    name: "Wimbledon Village Tennis Academy",
    provider: "Wimbledon Village Tennis Academy",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Local private courts, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "4–16",
    interests: ["sports"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Tennis academy at local private courts in Wimbledon Village. Coaching and match play.",
    times: "Check academy for class times",
    website: "https://clubspark.lta.org.uk/wpta",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-13",
    name: "Wimbledon Village Dance Studio",
    provider: "Wimbledon Village Dance Studio",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "High Street studios, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact studio for term fees",
    age: "3–18",
    interests: ["dance"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Dance studio at Wimbledon Village High Street studios. Ballet, jazz and lyrical dance for a wide age range up to 18.",
    times: "Check studio for class times",
    website: "https://wimbledonvillage.com/lifestyle-culture/childrens-activities/the-village-school-of-dance/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-14",
    name: "Polka Theatre Creative Workshops",
    provider: "Polka Theatre",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Nearby venue, SW19 1SB",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact theatre for term fees",
    age: "4–12",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Creative workshops from Polka Theatre, serving the Wimbledon Village catchment. Drama, storytelling and theatre.",
    times: "Check theatre for class times",
    website: "https://polkatheatre.com/workshops/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-15",
    name: "Wimbledon Village Youth Theatre",
    provider: "Wimbledon Village Youth Theatre",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Community venues, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact theatre for term fees",
    age: "8–18",
    interests: ["drama"],
    dayOfWeek: "Check theatre for term-time schedule",
    description: "Youth theatre across Wimbledon Village community venues. Acting, productions and improvisation for a wide age range up to 18.",
    times: "Check theatre for class times",
    website: "https://www.perform.org.uk/drama-classes/wimbledon",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-16",
    name: "Wimbledon Village Music Academy",
    provider: "Wimbledon Village Music Academy",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village studios, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "5–18",
    interests: ["music"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Music academy at Wimbledon Village studios. Piano, violin, guitar and singing for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.wimbledonmusicschool.co.uk/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-17",
    name: "Wimbledon Festival Chorus Youth Programme",
    provider: "Wimbledon Festival Chorus",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "St Mary's Church, SW19 7BP",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact programme for term fees",
    age: "8–18",
    interests: ["music"],
    dayOfWeek: "Check programme for term-time schedule",
    description: "Youth choir programme at St Mary's Church, run by the Wimbledon Festival Chorus. Choir and vocal performance for a wide age range up to 18.",
    times: "Check programme for class times",
    website: "http://wimbledonyouthchoir.com/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-18",
    name: "Wimbledon Village Art Studio",
    provider: "Wimbledon Village Art Studio",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "High Street studios, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact studio for term fees",
    age: "5–16",
    interests: ["arts"],
    dayOfWeek: "Check studio for term-time schedule",
    description: "Art studio at Wimbledon Village High Street studios. Painting, drawing and mixed media.",
    times: "Check studio for class times",
    website: "",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-19",
    name: "Wimbledon Common Nature Club",
    provider: "Wimbledon Common Nature Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "4–11",
    interests: ["outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Nature club on Wimbledon Common. Forest school and wildlife exploration.",
    times: "Check club for class times",
    website: "https://wimbledonvillage.com/lifestyle-culture/childrens-activities/the-wimbledon-common-nature-club/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-20",
    name: "Wimbledon Common Rangers Club",
    provider: "Wimbledon Common Rangers Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Wimbledon Common, SW19 5NR",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "6–14",
    interests: ["outdoors"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Rangers club on Wimbledon Common. Conservation, bushcraft and outdoor skills.",
    times: "Check club for class times",
    website: "https://www.wpcc.org.uk/contact-us/contact-us",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-21",
    name: "Wimbledon Pottery Workshop",
    provider: "Wimbledon Pottery Workshop",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village studio, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact workshop for term fees",
    age: "7–16",
    interests: ["arts"],
    dayOfWeek: "Check workshop for term-time schedule",
    description: "Pottery workshop at a Wimbledon Village studio. Pottery, ceramics and sculpture.",
    times: "Check workshop for class times",
    website: "https://www.clover-lee.com/potteryclasses",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-22",
    name: "Wimbledon Photography Academy",
    provider: "Wimbledon Photography Academy",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village arts venue, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "10–18",
    interests: ["arts"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Photography academy at a Wimbledon Village arts venue. Photography and digital editing for a wide age range up to 18.",
    times: "Check academy for class times",
    website: "https://www.dslrphotographycourses.com/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-23",
    name: "Wimbledon Young Writers Club",
    provider: "Wimbledon Young Writers Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village community venue, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "8–16",
    interests: ["arts"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Young writers club at a Wimbledon Village community venue. Creative writing and storytelling.",
    times: "Check club for class times",
    website: "https://www.wimbledonbookfest.org/young-writers-competition/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-24",
    name: "Wimbledon Public Speaking Academy",
    provider: "Wimbledon Public Speaking Academy",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Village venue, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact academy for term fees",
    age: "9–16",
    interests: ["stem"],
    dayOfWeek: "Check academy for term-time schedule",
    description: "Public speaking academy at a Wimbledon Village venue. Communication, debating and confidence.",
    times: "Check academy for class times",
    website: "https://letsactdramaclasses.co.uk/wimbledon/lamda-and-musical-theatre/",
    neighbourhoods: ["Wimbledon Village"],
  },
  {
    id: "ttwmv-25",
    name: "Wimbledon Young Entrepreneurs Club",
    provider: "Wimbledon Young Entrepreneurs Club",
    type: "Club",
    neighbourhood: "Wimbledon Village",
    location: "Community venue, SW19",
    postcodeSector: "SW19",
    distanceMiles: 3.8,
    cost: "Contact club for term fees",
    age: "11–18",
    interests: ["stem"],
    dayOfWeek: "Check club for term-time schedule",
    description: "Young entrepreneurs club at a Wimbledon Village community venue. Business skills and entrepreneurship for a wide age range up to 18.",
    times: "Check club for class times",
    website: "https://youngentrepreneursclub.co.uk/",
    neighbourhoods: ["Wimbledon Village"],
  },

];

export const SOUTH_WEST_LONDON_TERM_TIME_NEIGHBOURHOODS = [
  "Balham", "Barnes", "Battersea", "Chiswick", "Clapham", "Earlsfield", "East Sheen", "Hampton", "Hampton Hill", "Hampton Wick", "Kew", "Kingston", "Mortlake", "New Malden", "Putney", "Raynes Park", "Richmond", "Roehampton", "Southfields", "Streatham", "Surbiton", "Teddington", "Tooting", "Twickenham", "Wandsworth", "Wimbledon", "Wimbledon Village"
];

export const DISTANCE_OPTIONS = [
  { label: "½ mile", miles: 0.5 },
  { label: "1 mile", miles: 1 },
  { label: "2 miles", miles: 2 },
  { label: "5 miles", miles: 5 },
  { label: "10 miles", miles: 10 },
  { label: "Any", miles: 999 },
];

// ─────────────────────────────────────────────────────────────────────────────
// POSTCODE_DB, lookupPostcode(), haversine() and distanceBadgeStyle() used to
// live here. They have been removed and replaced by src/geo.js + src/campCoords.js.
//
// Why: POSTCODE_DB was missing N1, N7, N16, N11, N13, N15, E2, E8, EN4, EN5 and
// others, and lookupPostcode() silently fell back to the first outcode sharing
// the same letter prefix — so "N16 8EA" resolved to N2 (East Finchley), placing
// users ~4 miles from where they actually live, with no error shown.
//
// The `distanceMiles` field still present on each camp below is LEGACY and is no
// longer read by the app. Real distances are now computed per-user in geo.js.
// ─────────────────────────────────────────────────────────────────────────────
